import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type {
  CSSProperties,
  ChangeEvent,
  Dispatch,
  DragEvent,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  SetStateAction,
} from 'react';
import { createPortal } from 'react-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  ArrowLeft,
  ArrowRightLeft,
  ArrowDownCircle,
  ArrowUpCircle,
  BadgeHelp,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  Cloud,
  Copy,
  EllipsisVertical,
  ExternalLink,
  Eye,
  EyeOff,
  FolderOpen,
  Globe,
  Info,
  KeyRound,
  LoaderCircle,
  LogOut,
  Mail,
  LayoutGrid,
  Pause,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Settings,
  ShieldCheck,
  Star,
  TerminalSquare,
  Trash2,
  Ticket,
  UserRound,
  WifiOff,
  X,
} from 'lucide-react';
import type {
  AppSummary,
  AuthActionResult,
  AuthFormMode,
  AuthState,
  BackupEntry,
  CloudProfileBackup,
  CloudProfileSyncDirection,
  ExecutablePathSource,
  FileDialogResult,
  FloatingWidgetPreviewTransitionInput,
  FloatingWidgetState,
  HeartbeatBootstrap,
  LicenseHostRemoteProfileSnapshot,
  LicenseSummary,
  LocalProfileImportCandidate,
  ManagedDesktopAppKind,
  NetworkAccessState,
  OAuthProviderId,
  OpenClawModelOption,
  PlanType,
  PostSwitchDesktopLaunchResult,
  ProfileSummary,
  ProfilesState,
  ScanChannel,
  ScanTicket,
  ScanTicketStatus,
  StartupProgressEvent,
  StatisticsDashboard,
  StatisticsRange,
  SwitchProgressEvent,
  SwitchMode,
  TrayCardState,
  UpdateActionResult,
  UpdateState,
  UsageQuota,
  UserProfile,
  WorkspaceOAuthLaunchResult,
  WindowState,
} from '../shared/types';
import { IPC_CHANNELS, type CodexWorkspaceApi } from '../shared/ipc';
import { IS_LOCAL_FAST_IMPORT_BUILD } from '../shared/app-variant';
import { AddProfileDialog, type ProfileFormValues } from './components/AddProfileDialog';
import {
  AboutDialog,
  type AboutDialogLinkItem,
  type AboutDialogProductItem,
  type AboutDialogQrItem,
} from './components/AboutDialog';
import { ConfirmActionDialog } from './components/ConfirmActionDialog';
import { FooterHelpCard, type FooterHelpCardQrItem } from './components/FooterHelpCard';
import { LicenseHostView } from './components/LicenseHostView';
import {
  LicenseCenterDialog,
  type LicenseCenterSnapshot,
} from './components/LicenseCenterDialog';
import { UpdateDialog } from './components/UpdateDialog';
import {
  UserCenterDrawer,
  type UserCenterAuthForm,
  type UserCenterSession,
} from './components/UserCenterDrawer';
import { WorkspaceAutoDisabledDialog } from './components/WorkspaceAutoDisabledDialog';
import { AuthGate, type AuthGateEntryMode } from './components/AuthGate';
import { StatisticsCenter } from './components/StatisticsCenter';
import { authClient } from './services/auth-client';
import {
  playMessageDropletTone,
  showDesktopNotification,
} from './lib/message-notification';
import { OAUTH_PROVIDER_ICON_SRC } from './lib/oauth-provider-icon-src';
import packageMetadata from '../package.json';
import openaiBrandIconSrc from './assets/icons/openai.png';
import cardFlipFlickSoundSrc from './assets/audio/card-flip-flick.mp3';
import supportWechatQrSrc from './assets/support/wechat-support.png';
import './shell.css';
import './shell-ui-overrides.css';

const OPENAI_BRAND_ICON_SRC = openaiBrandIconSrc;
const SUPPORT_WECHAT_QR_SRC = supportWechatQrSrc;
const STARTUP_LOADING_MIN_DURATION_MS = 7_200;
const STARTUP_ICON_ONLY_DURATION_MS = 3_500;
const STARTUP_LOADING_HINT_DELAY_MS = 7_800;
const STARTUP_LOADING_NETWORK_PROBE_DELAY_MS = 1_800;
const APP_DISPLAY_NAME = 'AINIVOX - OAuth切换中心';
const APP_SUBTITLE = '登录、授权与更新入口';
const APP_SUPPORT_EMAIL = '18705011741@qq.com';
const FEEDBACK_SERVICE_DISPLAY_NAME = '客服';
const FEEDBACK_ACTION_TIMEOUT_MS = 15_000;
const EMAIL_BINDING_REMINDER_INTERVAL_MS = 15 * 60 * 1000;
const CLOUD_SYNC_AUTOMATION_SETTLE_MS = 20_000;
const APP_WEBSITE_URL = 'https://www.ainivox.cn/';
const APP_ACTIVATION_CENTER_URL = 'https://app.ainivox.cn/';

const hasStartupHandoffFromSplash = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return new URLSearchParams(window.location.search).get('startupHandoff') === '1';
};
const APP_DOWNLOAD_CENTER_URL = 'https://www.ainivox.cn/download.html';
const APP_NIUQ_URL = 'https://niuq.ainivox.cn/';
const APP_DOUBAO_URL = 'https://doubao.ainivox.cn/';
const APP_MINI_PROGRAM_PREVIEW_URL = 'https://demo.ainivox.cn/';
const CHATGPT_HOME_URL = 'https://chatgpt.com/';
const APP_FALLBACK_VERSION =
  typeof packageMetadata.version === 'string' && packageMetadata.version.trim()
    ? packageMetadata.version.trim()
    : '--';
const OAUTH_QUICK_DOCK_BASE_VIEWPORT = {
  width: 1280,
  height: 760,
} as const;
const OAUTH_QUICK_DOCK_MIN_SCALE = 0.92;
const OAUTH_QUICK_DOCK_MAX_SCALE = 2;
const QUOTA_ALERT_SNOOZE_OPTIONS = [
  { key: 'none', label: '无', minutes: 0 },
  { key: '5m', label: '5分钟', minutes: 5 },
  { key: '15m', label: '15分钟', minutes: 15 },
  { key: '30m', label: '半小时', minutes: 30 },
] as const;
const DEFAULT_QUOTA_ALERT_DISMISS_MS = 60_000;
type StatisticsRefreshSource = 'background' | 'range' | 'mode' | 'manual';
type QrPreviewSource = 'auth-gate' | 'footer-help' | 'about' | 'feedback';
type ScanFlowState =
  | 'waiting_scan'
  | 'waiting_confirm'
  | 'pending_manual_bind'
  | 'resume_binding'
  | 'binding_success'
  | 'error';
type PendingScanBindingSource = 'guest-scan' | 'user-center-bind';

interface PendingScanBindingContext {
  ticketId: string;
  source: PendingScanBindingSource;
  reason: 'identity-missing';
  requiresRescanAfterRegister: boolean;
  createdAt: string;
}

interface RuntimeIpcErrorInfo {
  message: string;
  status: number | null;
  code: string | null;
  details: string | null;
}

const IPC_ERROR_MESSAGE_PREFIX = '[codex-ipc-error]';

interface QrPreviewItem {
  id: string;
  title: string;
  subtitle: string;
  hint: string;
  imageSrc?: string | null;
}

interface QrPreviewState {
  source: QrPreviewSource;
  items: QrPreviewItem[];
  activeId: string;
}

type OAuthQuickProviderId = OAuthProviderId;

interface OAuthQuickProviderEntry {
  id: OAuthQuickProviderId;
  label: string;
  tooltip: string;
}

const OAUTH_QUICK_PROVIDER_ENTRIES: OAuthQuickProviderEntry[] = [
  {
    id: 'codex',
    label: 'Codex',
    tooltip: '使用 OpenAI 官方 OAuth 链路登录并回写当前 auth.json。',
  },
  {
    id: 'anthropic',
    label: 'Anthropic',
    tooltip: '使用 Claude 官方 OAuth 链路登录，并写入本地 provider 认证文件。',
  },
  {
    id: 'antigravity',
    label: 'Antigravity',
    tooltip: '使用 Google Antigravity OAuth 链路登录，并写入本地 provider 认证文件。',
  },
  {
    id: 'gemini',
    label: 'Gemini',
    tooltip: '使用 Gemini CLI OAuth 链路登录，并写入本地 provider 认证文件。',
  },
  {
    id: 'kimi',
    label: 'Kimi',
    tooltip: '使用 Kimi 设备授权链路登录，并写入本地 provider 认证文件。',
  },
  {
    id: 'qwen',
    label: 'Qwen',
    tooltip: '使用 Qwen 设备授权链路登录，并写入本地 provider 认证文件。',
  },
];
const INVITE_REWARD_PREVIEW = [
  '第 1 次成功：+7 天，2 张卡片',
  '第 2 次成功：+30 天，3 张卡片',
  '第 3 次成功：+90 天，3 张卡片',
  '第 4 次成功：永久，4 张卡片',
];
const INVITE_RULE_SUMMARY =
  '正式授权用户可申请 one-time invite code；分享奖励按 7 天 2 卡 / 30 天 3 卡 / 90 天 3 卡 / 永久 4 卡递增，想获得更多卡片请联系开发者或客服。';
const INVITE_RULE_DETAILS = [
  '第 1 次邀请成功：奖励 7 天使用权，开放 2 张卡片。',
  '第 2 次邀请成功：奖励 30 天使用权，开放 3 张卡片。',
  '第 3 次邀请成功：奖励 90 天使用权，开放 3 张卡片。',
  '第 4 次邀请成功：奖励永久使用权，开放 4 张卡片。',
  '分享奖励的最高卡片权限为 4 张；需要更多卡片请联系开发者或客服。',
];

const ABOUT_CONTACT_LINKS: AboutDialogLinkItem[] = [
  {
    id: 'website',
    label: '官方网站',
    description: '查看产品介绍、使用说明与服务入口',
    value: APP_WEBSITE_URL,
    action: 'external',
  },
  {
    id: 'email',
    label: '联系客服',
    description: '通过邮箱反馈问题、合作或授权需求',
    value: `mailto:${APP_SUPPORT_EMAIL}`,
    action: 'external',
  },
  {
    id: 'download',
    label: '下载中心',
    description: '手动下载安装包并查看版本说明',
    value: APP_DOWNLOAD_CENTER_URL,
    action: 'external',
  },
  {
    id: 'doubao',
    label: '无印豆包',
    description: '打开无印豆包相关入口',
    value: APP_DOUBAO_URL,
    action: 'external',
  },
  {
    id: 'mini-program-preview',
    label: '小程序预览',
    description: '查看小程序预览页与扫码演示入口',
    value: APP_MINI_PROGRAM_PREVIEW_URL,
    action: 'external',
  },
];

const ABOUT_VISIBLE_CONTACT_LINKS = ABOUT_CONTACT_LINKS.filter(
  (item) => item.id === 'website' || item.id === 'email',
);

const ABOUT_QR_ITEMS: AboutDialogQrItem[] = [
  {
    id: 'personal',
    title: '个人微信',
    subtitle: '扫码后可一对一沟通',
    hint: '扫码即可添加客服微信。',
    imageSrc: SUPPORT_WECHAT_QR_SRC,
  },
  {
    id: 'group',
    title: '微信群',
    subtitle: '扫码加入用户群或测试群',
    hint: '群聊二维码敬请期待。',
  },
];

const ABOUT_PRODUCT_ITEMS: AboutDialogProductItem[] = [
  {
    id: 'website',
    title: 'AINIVOX 官网',
    description: '查看产品介绍、常见说明和最新服务入口。',
    tag: '官网',
    url: APP_WEBSITE_URL,
  },
  {
    id: 'activate',
    title: '激活与登录',
    description: '打开网页端登录、注册和激活入口。',
    tag: '激活',
    url: APP_ACTIVATION_CENTER_URL,
  },
  {
    id: 'download',
    title: '下载中心',
    description: '手动下载安装包，并查看版本更新说明。',
    tag: '下载',
    url: APP_DOWNLOAD_CENTER_URL,
  },
  {
    id: 'doubao',
    title: '无印豆包',
    description: '打开无印豆包网页端入口。',
    tag: '入口',
    url: APP_DOUBAO_URL,
  },
  {
    id: 'mini-program-preview',
    title: '小程序预览',
    description: '查看小程序预览页与扫码演示入口。',
    tag: '预览',
    url: APP_MINI_PROGRAM_PREVIEW_URL,
  },
];

const FOOTER_HELP_QR_ITEMS: FooterHelpCardQrItem[] = [
  {
    id: 'help-personal',
    title: '客服二维码',
    subtitle: '个人联系入口',
    hint: '扫码即可添加客服微信。',
    imageSrc: SUPPORT_WECHAT_QR_SRC,
  },
  {
    id: 'help-group',
    title: '群二维码',
    subtitle: '用户群 / 测试群',
    hint: '群聊二维码敬请期待。',
  },
];

const createEmptyHeartbeatBootstrap = (): HeartbeatBootstrap => ({
  checkedAt: '',
  serverTime: '',
  online: true,
  requiresOnline: true,
  graceExpiresAt: null,
  graceSecondsRemaining: 0,
  minSupportedVersion: null,
  mandatoryUpdate: false,
  allowCoreFeatures: true,
  notice: '当前未连接在线授权服务，将以本地授权状态为准。',
});

const createEmptyLicenseSummary = (): LicenseSummary => ({
  status: 'inactive',
  licenseTypeLabel: '未授权',
  durationKey: null,
  expiresAt: null,
  permanent: false,
  slotLimit: 0,
  slotLimitLabel: '未开放卡片',
  source: 'local',
  sourceLabel: '本地授权状态',
  stageLabel: '待登录并录入授权码',
  remainingLabel: '未激活',
  activatedAt: null,
  lastValidatedAt: null,
  rollbackDetected: false,
  graceExpiresAt: null,
});

const createEmptyAuthState = (): AuthState => ({
  sessionStatus: 'guest',
  profile: null,
  device: {
    deviceId: 'DEV-UNKNOWN',
    fingerprintHash: 'unknown',
    fingerprintSummary: 'UNKN-OWNS-IGN',
    hostLabel: 'unknown',
    platformLabel: 'Unknown',
    machineName: 'Unknown',
  },
  license: createEmptyLicenseSummary(),
  invite: {
    inviteCode: 'INV-UNKNOWN',
    requestedInviteCode: null,
    invitedCount: 0,
    successfulCount: 0,
    progressLabel: '0/4',
    rewardPreview: [...INVITE_REWARD_PREVIEW],
    slotsUsed: 0,
    maxPermanentSlots: 4,
    eligible: false,
    nextRewardLabel: '+7 天 / 2 张卡片',
    ruleSummary: INVITE_RULE_SUMMARY,
    ruleDetails: [...INVITE_RULE_DETAILS],
    pendingRewardCount: 0,
  },
  inviteRecords: [],
  rewardRecords: [],
  pendingProfileTransfers: [],
  cloudProfileBackups: [],
  currentDeviceCloudProfileBackups: [],
  feedback: {
    pendingCount: 0,
    acknowledgedCount: 0,
    items: [],
  },
  security: {
    secondaryPassword: {
      enabled: false,
      updatedAt: null,
      trustedDeviceId: null,
      trustedFingerprintSummary: null,
      currentDeviceTrusted: true,
      legacyCloudBackupDetected: false,
    },
  },
  bootstrap: createEmptyHeartbeatBootstrap(),
  savedCredentials: {
    identifier: '',
    email: '',
    password: '',
    rememberPassword: false,
  },
  lastAuthAction: null,
});

const createInitialAuthForm = (authState?: Pick<AuthState, 'profile' | 'savedCredentials'> | null): UserCenterAuthForm => {
  const savedCredentials = authState?.savedCredentials;
  return {
    identifier:
      savedCredentials?.identifier ||
      authState?.profile?.loginIdentifierHint ||
      savedCredentials?.email ||
      authState?.profile?.email ||
      '',
    email: savedCredentials?.email || authState?.profile?.email || '',
    password: savedCredentials?.rememberPassword ? savedCredentials.password : '',
    confirmPassword: '',
    verificationCode: '',
    inviteCode: '',
    rememberMe: savedCredentials?.rememberPassword ?? true,
    loginMethod: 'password',
  };
};

const normalizeAuthCredentialValue = (value?: string | null): string =>
  String(value || '').trim().toLowerCase();

const getRememberedAuthIdentifiers = (
  authState?: Pick<AuthState, 'profile' | 'savedCredentials'> | null,
): Set<string> => {
  const identifiers = new Set<string>();
  [
    authState?.savedCredentials?.identifier,
    authState?.savedCredentials?.email,
    authState?.profile?.loginIdentifierHint,
    authState?.profile?.email,
  ].forEach((value) => {
    const normalized = normalizeAuthCredentialValue(value);
    if (normalized) {
      identifiers.add(normalized);
    }
  });
  return identifiers;
};

const createModeAuthForm = (
  mode: AuthFormMode,
  authState?: Pick<AuthState, 'profile' | 'savedCredentials'> | null,
  currentForm?: UserCenterAuthForm | null,
): UserCenterAuthForm => {
  const loginDefaults = createInitialAuthForm(authState);
  if (mode === 'register') {
    return {
      identifier: '',
      email: '',
      password: '',
      confirmPassword: '',
      verificationCode: '',
      inviteCode: '',
      rememberMe: false,
      loginMethod: 'otp',
    };
  }

  if (mode === 'forgot') {
    const nextEmail = currentForm?.email.trim() || currentForm?.identifier.trim() || '';
    return {
      ...loginDefaults,
      identifier: '',
      email: nextEmail,
      password: '',
      confirmPassword: '',
      verificationCode: '',
      inviteCode: '',
      rememberMe: false,
      loginMethod: 'otp',
    };
  }

  const preferredIdentifier =
    currentForm?.identifier.trim() || currentForm?.email.trim() || loginDefaults.identifier;
  const preferredEmail = currentForm?.email.trim() || loginDefaults.email || preferredIdentifier;
  const matchesRemembered = getRememberedAuthIdentifiers(authState).has(
    normalizeAuthCredentialValue(preferredIdentifier),
  );

  return {
    ...loginDefaults,
    identifier: preferredIdentifier,
    email: preferredEmail,
    password: matchesRemembered ? loginDefaults.password : '',
    confirmPassword: '',
    verificationCode: '',
    inviteCode: '',
    rememberMe: matchesRemembered ? loginDefaults.rememberMe : Boolean(currentForm?.rememberMe),
    loginMethod: 'password',
  };
};

const formatCardUsageText = (usedCount: number, slotLimit: number | null): string =>
  slotLimit === null ? `${usedCount} / 无限` : `${usedCount} / ${Math.max(0, slotLimit)}`;

const formatCardLimitText = (slotLimit: number | null): string =>
  slotLimit === null ? '无限' : `${Math.max(0, slotLimit)} 张`;

const SYNC_USAGE_LOG_PATTERN = /^已同步\s+(\d+)\s+个 profile 的实时额度。$/;

const formatDisplayLogMessage = (
  message: string,
  displayedProfilesCount: number,
  simulationEnabled: boolean,
): string => {
  if (!simulationEnabled) {
    return message;
  }

  const match = message.match(SYNC_USAGE_LOG_PATTERN);
  if (!match) {
    return message;
  }

  const syncedProfiles = Number.parseInt(match[1], 10) || 0;
  if (displayedProfilesCount <= syncedProfiles) {
    return message;
  }

  return `已刷新 ${displayedProfilesCount} 个 profile 的额度展示，其中 ${syncedProfiles} 个同步到实时额度。`;
};

const normalizeSettingsNumberDraft = (
  draft: string,
  min: number,
  max: number,
  fallback: number,
): number => {
  const parsed = Number.parseInt(draft.trim(), 10);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return Math.max(min, Math.min(max, parsed));
};

const MAX_LOCAL_SIMULATION_CARD_COUNT = 25_000;

const normalizeVisibleVersion = (value?: string | null): string => {
  const normalized = value?.trim() ?? '';
  if (!normalized || normalized === '0.0.0') {
    return APP_FALLBACK_VERSION;
  }

  return normalized;
};

const formatPreciseCountdown = (
  targetAt: string | null,
  nowMs: number,
  options?: {
    permanent?: boolean;
    emptyLabel?: string;
  },
): string => {
  if (options?.permanent) {
    return '永久有效';
  }

  if (!targetAt) {
    return options?.emptyLabel ?? '未记录';
  }

  const targetMs = new Date(targetAt).getTime();
  if (Number.isNaN(targetMs)) {
    return '未记录';
  }

  const diff = targetMs - nowMs;
  if (diff <= 0) {
    return '已到期';
  }

  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return `${days}天 ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const hasUsableWorkspaceLicense = (authState: AuthState): boolean => {
  if (authState.license.permanent) {
    return true;
  }

  if (authState.license.status === 'active') {
    return true;
  }

  if (authState.license.status !== 'grace' || !authState.license.expiresAt) {
    return false;
  }

  const expiresAtMs = new Date(authState.license.expiresAt).getTime();
  return !Number.isNaN(expiresAtMs) && expiresAtMs > Date.now();
};

const isWorkspaceAuthorized = (authState: AuthState): boolean =>
  authState.sessionStatus === 'authenticated' && hasUsableWorkspaceLicense(authState);

const hasAuthenticatedProfile = (
  authState: AuthState | null | undefined,
): authState is AuthState & { profile: UserProfile } =>
  Boolean(authState && authState.sessionStatus === 'authenticated' && authState.profile);

const formatSecondaryPasswordTrustedLabel = (authState: AuthState): string => {
  const securityState = authState.security.secondaryPassword;
  if (!securityState.enabled) {
    return securityState.legacyCloudBackupDetected
      ? '检测到旧版云备份记录，需先启用二次密码后才能继续使用云备份。'
      : '当前设备未启用额外保护。';
  }

  if (securityState.currentDeviceTrusted) {
    return `当前设备已受信，可直接接收卡片与恢复备份。${
      securityState.trustedFingerprintSummary ? ` 可信设备：${securityState.trustedFingerprintSummary}` : ''
    }`;
  }

  return `当前设备尚未通过验证。可信设备：${
    securityState.trustedFingerprintSummary ?? securityState.trustedDeviceId ?? '未记录'
  }`;
};

const mapAuthStateToUserCenterSession = (authState: AuthState): UserCenterSession => ({
  status: authState.sessionStatus,
  displayName: authState.profile?.displayName ?? '未登录',
  email: authState.profile?.email ?? null,
  displayId: authState.profile?.displayId ?? null,
  avatarText: authState.profile?.avatarText ?? 'U',
  hasAlert:
    authState.license.rollbackDetected ||
    authState.bootstrap.mandatoryUpdate ||
    !isWorkspaceAuthorized(authState),
  licenseLabel: authState.license.licenseTypeLabel,
  remainingLabel: authState.license.remainingLabel,
  cardLimitLabel: formatCardLimitText(authState.license.slotLimit),
  expiresAt: authState.license.expiresAt,
  permanent: authState.license.permanent,
  deviceId: authState.device.deviceId,
  fingerprintSummary: authState.device.fingerprintSummary,
  fingerprintHash: authState.device.fingerprintHash,
  inviteCode: authState.invite.requestedInviteCode ?? authState.invite.inviteCode,
  invitedCount: authState.invite.invitedCount,
  inviteProgressLabel: authState.invite.progressLabel,
  bootstrapNotice: authState.bootstrap.notice,
  secondaryPasswordEnabled: authState.security.secondaryPassword.enabled,
  secondaryPasswordTrusted: authState.security.secondaryPassword.currentDeviceTrusted,
  secondaryPasswordTrustedLabel: formatSecondaryPasswordTrustedLabel(authState),
  secondaryPasswordUpdatedAt: authState.security.secondaryPassword.updatedAt,
  secondaryPasswordLegacyCloudBackupDetected:
    authState.security.secondaryPassword.legacyCloudBackupDetected,
  scanBound: authState.profile?.scanBound ?? false,
  needsEmailBinding: authState.profile?.needsEmailBinding ?? false,
});

const resolveLicenseCenterAccountSummary = (
  authState: AuthState,
): { accountLabel: string; accountDetail: string | null } => {
  const displayId = authState.profile?.displayId?.trim() || '';
  const displayName = authState.profile?.displayName?.trim() || '';
  const email = authState.profile?.email?.trim() || '';

  if (displayId) {
    const detail = [displayName, email].filter((item) => item && item !== displayId).join(' / ');
    return {
      accountLabel: displayId,
      accountDetail: detail || null,
    };
  }

  if (email) {
    return {
      accountLabel: email,
      accountDetail: displayName && displayName !== email ? displayName : null,
    };
  }

  if (displayName) {
    return {
      accountLabel: displayName,
      accountDetail: null,
    };
  }

  return {
    accountLabel: '未登录',
    accountDetail: null,
  };
};

const mapAuthStateToLicenseSnapshot = (authState: AuthState): LicenseCenterSnapshot => {
  const accountSummary = resolveLicenseCenterAccountSummary(authState);

  return {
    licenseType: authState.license.licenseTypeLabel,
    expiresAt: authState.license.expiresAt,
    permanent: authState.license.permanent,
    accountLabel: accountSummary.accountLabel,
    accountDetail: accountSummary.accountDetail,
    cardLimitLabel: formatCardLimitText(authState.license.slotLimit),
    sourceLabel: authState.license.sourceLabel,
    stageLabel: authState.license.stageLabel,
    remainingLabel: authState.license.remainingLabel,
    deviceId: authState.device.deviceId,
    fingerprintSummary: authState.device.fingerprintSummary,
    inviteCode: authState.invite.inviteCode,
    requestedInviteCode: authState.invite.requestedInviteCode,
    inviteRule: authState.invite.ruleSummary,
    inviteProgressLabel: authState.invite.progressLabel,
    rewardPreview: authState.invite.rewardPreview,
    inviteSlotsText: `当前奖励卡片 ${authState.invite.slotsUsed}/${authState.invite.maxPermanentSlots}`,
    pendingRewardCount: authState.invite.pendingRewardCount,
    eligibleForInvite: authState.invite.eligible,
    nextRewardLabel: authState.invite.nextRewardLabel,
    inviteRecords: authState.inviteRecords.map((item) => ({
      id: item.id,
      time: item.createdAt,
      target: item.targetLabel,
      result: item.result,
      reward: item.rewardLabel,
    })),
    rewardRecords: authState.rewardRecords.map((item) => ({
      id: item.id,
      time: item.createdAt,
      target: `奖励码 ${item.code}`,
      result: item.result,
      reward: item.rewardLabel,
    })),
    bootstrapNotice: authState.bootstrap.notice,
  };
};

type BusyAction = 'initial' | 'refresh' | 'add' | 'edit' | 'import' | 'switch' | 'delete' | 'root' | null;
type ToastTone = 'success' | 'error' | 'info';
type AuthOtpPurpose = 'login' | 'register' | 'password-reset' | 'secondary-password-reset';

interface ToastState {
  tone: ToastTone;
  message: string;
}

const createInitialAuthOtpCooldowns = (): Record<AuthOtpPurpose, number> => ({
  login: 0,
  register: 0,
  'password-reset': 0,
  'secondary-password-reset': 0,
});

const OTP_DEFAULT_COOLDOWN_SECONDS = 60;

const resolveOtpCooldownSeconds = (deadlineAt: number, nowMs: number): number => {
  if (deadlineAt <= 0) {
    return 0;
  }

  const remainingMs = deadlineAt - nowMs;
  if (remainingMs <= 0) {
    return 0;
  }

  return Math.ceil(remainingMs / 1000);
};

interface AvatarUploadDraft {
  fileName: string;
  fileSize: number;
  sourceWidth: number;
  sourceHeight: number;
  sourceType: string;
  sourceDataUrl: string;
  scale: number;
  offsetX: number;
  offsetY: number;
  minScale: number;
  maxScale: number;
}

interface AvatarRenderMetrics {
  renderWidth: number;
  renderHeight: number;
  maxOffsetX: number;
  maxOffsetY: number;
}

interface QuotaAlertSuggestion {
  activeProfile: ProfileSummary;
  recommendedProfile: ProfileSummary;
  threshold: number;
  breachedLabels: string[];
  warningKey: string;
}

interface CenterNotice {
  id: string;
  message: string;
  title?: string;
  detail?: string;
  variant?: 'default' | 'active-card';
}

interface AutoDisabledPromptState {
  profileId: string;
  promptKey: string;
}

interface PendingAddOverwriteState {
  target: ProfileSummary;
  values: ProfileFormValues;
}

interface PendingLocalImportOverwriteState {
  requestId: number;
  candidates: LocalProfileImportCandidate[];
  duplicateCandidates: LocalProfileImportCandidate[];
}

type SpacePoolImportOverlayStep = 'select' | 'input';

interface SwitchSuccessNotice {
  name: string;
  workspaceName: string;
  activatedAt: string;
  switchMode: SwitchMode;
  codexAutoRestart: boolean;
  traeAutoRestart: boolean;
  codexTraeAutoRestart: boolean;
  restartedDesktopApps: string[];
  openclawSynced: boolean;
  openclawGatewayRestarted: boolean;
  openclawModel: string | null;
  openclawModelLabel: string | null;
  openclawModelChanged: boolean;
  warnings: string[];
}

interface SwitchRuntimeOverlayState {
  runId: string;
  phase: 'running' | 'success' | 'error';
  workspaceName: string;
  visible: boolean;
  message?: string;
}

type CardSwitchInteractionPhase = 'preview' | 'running' | 'success' | 'error';

interface CardSwitchInteractionState {
  profileId: string;
  phase: CardSwitchInteractionPhase;
}

interface CardSwitchSweepState {
  profileId: string;
  token: number;
}

interface ProfileActionOverlayState {
  title: string;
  detail: string;
  busy?: boolean;
  success?: boolean;
}

type StartupCardIntroPhase = 'idle' | 'backface' | 'revealed';

const CARD_SWITCH_SUCCESS_HOLD_MS = 780;
const CARD_SWITCH_SUCCESS_SWEEP_MS = 920;
const CARD_SWITCH_ERROR_RETRY_READY_MS = 1680;
const DEFAULT_CARD_FLIP_VOLUME_PERCENT = 66;
const MIN_CARD_FLIP_VOLUME_PERCENT = 0;
const MAX_CARD_FLIP_VOLUME_PERCENT = 100;

const SWITCH_PROGRESS_STEP_ORDER: Record<SwitchProgressEvent['stepKey'], number> = {
  prepare: 0,
  'switch-auth': 1,
  'desktop-stop': 2,
  'desktop-start': 3,
  'openclaw-sync': 4,
  'openclaw-model': 5,
  'openclaw-gateway': 6,
  'quota-sync': 7,
  complete: 8,
};

const hashStartupCardSeed = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33 + value.charCodeAt(index)) % 997;
  }
  return hash;
};

const getStartupCardIntroTiming = (
  profileId: string,
  introIndex: number,
): { delayMs: number; durationMs: number } => {
  const seed = hashStartupCardSeed(profileId);
  const boundedIndex = Math.min(introIndex, 10);
  return {
    delayMs: 140 + boundedIndex * 108 + (seed % 56),
    durationMs: 1240 + (seed % 280),
  };
};

const getCardSwitchWaveTiming = (
  profileId: string,
  introIndex: number,
): { delayMs: number; durationMs: number } => {
  const seed = hashStartupCardSeed(profileId);
  const boundedIndex = Math.min(introIndex, 10);
  return {
    delayMs: 36 + boundedIndex * 52 + (seed % 26),
    durationMs: 760 + (seed % 140),
  };
};

interface AutoSwitchPromptState {
  suggestion: QuotaAlertSuggestion;
  startedAt: number;
  deadlineAt: number;
}

type SimulationPlanType = 'Free' | 'Plus' | 'Pro 5x' | 'Pro 20x' | 'Team' | 'Business';
type DisplayPlanType = PlanType | SimulationPlanType;
type PlanFilterValue = 'ALL' | DisplayPlanType;
type BatchProfileActionMode = 'notes' | 'enable' | 'disable' | 'delete';
type BatchProfileSelectionFilter = 'all' | 'enabled' | 'disabled';
type WorkspaceSelectionMode = 'send' | 'batch';
type MainWorkspaceView = 'profiles' | 'statistics' | 'api-config';
type AuthCredentialMode = 'oauth' | 'api';
type WorkspaceSelectionBusyAction = 'send' | 'enable' | 'disable' | 'delete' | 'cloud-delete';
type HiddenModeDialogMode = 'unlock' | 'setup' | 'change';
type UiInteractionTestMode = 'idle' | 'switching';
type ApiProviderId =
  | 'openai-compatible'
  | 'anthropic'
  | 'gemini'
  | 'deepseek'
  | 'stepfun'
  | 'moonshot'
  | 'zhipu'
  | 'volcengine-ark'
  | 'openrouter'
  | 'azure-openai'
  | 'dashscope'
  | 'hunyuan'
  | 'minimax'
  | 'siliconflow'
  | 'custom';
type ApiKeyHealthStatus = 'success' | 'failed' | 'untested';

interface ApiProviderOption {
  id: ApiProviderId;
  name: string;
  detail: string;
  baseUrl: string;
  baseUrlPlaceholder: string;
  modelPlaceholder: string;
  modelOptions: string[];
  tag: string;
}

interface ApiKeyEntry {
  id: string;
  maskedValue: string;
  secretValue?: string;
  enabled: boolean;
  primary: boolean;
  status: ApiKeyHealthStatus;
  testedAt: string;
}

const API_PROVIDER_OPTIONS: ApiProviderOption[] = [
  {
    id: 'openai-compatible',
    name: 'OpenAI',
    detail: '领先的通用 AI 模型与能力',
    baseUrl: 'https://api.openai.com/v1',
    baseUrlPlaceholder: '例如：https://api.openai.com/v1',
    modelPlaceholder: '选择或输入模型 ID',
    modelOptions: [
      'gpt-5.5',
      'gpt-5.4',
      'gpt-5.4-mini',
      'gpt-5.3',
      'gpt-5',
      'gpt-5-mini',
      'gpt-4.1',
      'gpt-4o',
      'gpt-4o-mini',
    ],
    tag: '主流',
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    detail: 'Claude 系列模型',
    baseUrl: 'https://api.anthropic.com',
    baseUrlPlaceholder: '例如：https://api.anthropic.com',
    modelPlaceholder: '选择或输入 Claude 模型 ID',
    modelOptions: ['claude-opus-4-7', 'claude-sonnet-4-6', 'claude-haiku-4-5'],
    tag: '主流',
  },
  {
    id: 'gemini',
    name: 'Gemini',
    detail: 'Google 旗下大模型',
    baseUrl: 'https://generativelanguage.googleapis.com/v1beta/openai/',
    baseUrlPlaceholder: '例如：https://generativelanguage.googleapis.com/v1beta/openai/',
    modelPlaceholder: '选择或输入 Gemini 模型 ID',
    modelOptions: [
      'gemini-2.5-pro',
      'gemini-2.5-flash',
      'gemini-2.5-flash-lite',
      'gemini-2.5-flash-live-preview',
      'gemini-3.1-flash-live-preview',
    ],
    tag: '主流',
  },
  {
    id: 'deepseek',
    name: 'DeepSeek',
    detail: 'DeepSeek 模型',
    baseUrl: 'https://api.deepseek.com',
    baseUrlPlaceholder: '例如：https://api.deepseek.com',
    modelPlaceholder: '选择或输入 DeepSeek 模型 ID',
    modelOptions: ['deepseek-v4-flash', 'deepseek-v4-pro', 'deepseek-chat', 'deepseek-reasoner'],
    tag: '兼容',
  },
  {
    id: 'stepfun',
    name: '阶跃星辰',
    detail: 'Step 模型',
    baseUrl: 'https://api.stepfun.com/v1',
    baseUrlPlaceholder: '例如：https://api.stepfun.com/v1',
    modelPlaceholder: '选择或输入 Step 模型 ID',
    modelOptions: ['step-3.5-flash-2603', 'step-3.5-flash', 'step-3', 'step-r1-v-mini', 'step-2-mini'],
    tag: '兼容',
  },
  {
    id: 'moonshot',
    name: '月之暗面 Kimi',
    detail: 'Kimi 模型',
    baseUrl: 'https://api.moonshot.cn/v1',
    baseUrlPlaceholder: '例如：https://api.moonshot.cn/v1',
    modelPlaceholder: '选择或输入 Kimi 模型 ID',
    modelOptions: ['kimi-k2.5', 'kimi-k2-0905-preview', 'kimi-k2-turbo-preview', 'kimi-k2-thinking'],
    tag: '兼容',
  },
  {
    id: 'zhipu',
    name: '智谱 AI',
    detail: 'GLM 模型',
    baseUrl: 'https://open.bigmodel.cn/api/paas/v4/',
    baseUrlPlaceholder: '例如：https://open.bigmodel.cn/api/paas/v4/',
    modelPlaceholder: '选择或输入 GLM 模型 ID',
    modelOptions: ['glm-5.1', 'glm-4.7', 'glm-z1-air', 'glm-4.6v'],
    tag: '兼容',
  },
  {
    id: 'volcengine-ark',
    name: '火山引擎方舟',
    detail: '方舟模型',
    baseUrl: 'https://ark.cn-beijing.volces.com/api/coding/v3',
    baseUrlPlaceholder: '例如：https://ark.cn-beijing.volces.com/api/coding/v3',
    modelPlaceholder: '选择或输入方舟模型 ID',
    modelOptions: ['ark-code-latest', 'doubao-seed-2.0-code', 'doubao-seed-2.0-pro', 'deepseek-v3.2'],
    tag: '兼容',
  },
  {
    id: 'openrouter',
    name: 'OpenRouter',
    detail: '路由聚合',
    baseUrl: 'https://openrouter.ai/api/v1',
    baseUrlPlaceholder: '例如：https://openrouter.ai/api/v1',
    modelPlaceholder: '选择或输入 OpenRouter 模型 ID',
    modelOptions: ['openrouter/auto', 'openai/gpt-5.4', 'anthropic/claude-sonnet-4-6', 'google/gemini-2.5-flash'],
    tag: '兼容',
  },
  {
    id: 'azure-openai',
    name: 'Azure OpenAI',
    detail: 'Azure 部署',
    baseUrl: 'https://YOUR-RESOURCE-NAME.openai.azure.com/openai/v1/',
    baseUrlPlaceholder: '例如：https://YOUR-RESOURCE-NAME.openai.azure.com/openai/v1/',
    modelPlaceholder: '选择模型或填写部署名',
    modelOptions: ['gpt-5.5', 'gpt-5.4', 'gpt-5.3', 'gpt-5', 'gpt-5-mini', 'gpt-4.1', 'gpt-4o'],
    tag: '兼容',
  },
  {
    id: 'dashscope',
    name: '阿里云百炼',
    detail: '通义模型',
    baseUrl: 'https://dashscope.aliyuncs.com/compatible-mode/v1',
    baseUrlPlaceholder: '例如：https://dashscope.aliyuncs.com/compatible-mode/v1',
    modelPlaceholder: '选择或输入通义模型 ID',
    modelOptions: ['qwen3-coder-next', 'qwen3-max', 'qwen3-max-preview', 'qwen-max-latest', 'qwen-plus'],
    tag: '兼容',
  },
  {
    id: 'hunyuan',
    name: '腾讯混元',
    detail: '混元模型',
    baseUrl: 'https://api.hunyuan.cloud.tencent.com/v1',
    baseUrlPlaceholder: '例如：https://api.hunyuan.cloud.tencent.com/v1',
    modelPlaceholder: '选择或输入混元模型 ID',
    modelOptions: ['hunyuan-turbos-latest', 'hunyuan-standard-256K', 'hunyuan-lite', 'hunyuan-vision'],
    tag: '兼容',
  },
  {
    id: 'minimax',
    name: 'MiniMax',
    detail: 'MiniMax 模型',
    baseUrl: 'https://api.minimax.io/v1',
    baseUrlPlaceholder: '例如：https://api.minimax.io/v1',
    modelPlaceholder: '选择或输入 MiniMax 模型 ID',
    modelOptions: ['MiniMax-M2.7', 'MiniMax-M2.7-highspeed', 'MiniMax-M2.5', 'MiniMax-M1'],
    tag: '兼容',
  },
  {
    id: 'siliconflow',
    name: '硅基流动',
    detail: '硅基模型',
    baseUrl: 'https://api.siliconflow.cn/v1',
    baseUrlPlaceholder: '例如：https://api.siliconflow.cn/v1',
    modelPlaceholder: '选择或输入硅基流动模型 ID',
    modelOptions: ['Qwen/Qwen3-Coder-480B-A35B-Instruct', 'deepseek-ai/DeepSeek-V3', 'deepseek-ai/DeepSeek-R1'],
    tag: '兼容',
  },
  {
    id: 'custom',
    name: '自定义供应商',
    detail: '自定义接口',
    baseUrl: '',
    baseUrlPlaceholder: '输入自定义接口地址',
    modelPlaceholder: '输入模型 ID',
    modelOptions: [],
    tag: '兼容',
  },
];

const API_KEY_ENTRIES: ApiKeyEntry[] = [
  {
    id: 'primary',
    maskedValue: 'sk-***************7f3a',
    secretValue: 'sk-demo-openai-primary-7f3a',
    enabled: true,
    primary: true,
    status: 'success',
    testedAt: '2 分钟前',
  },
  {
    id: 'backup',
    maskedValue: 'sk-***************9b2d',
    secretValue: 'sk-demo-openai-backup-9b2d',
    enabled: true,
    primary: false,
    status: 'success',
    testedAt: '8 分钟前',
  },
  {
    id: 'paused',
    maskedValue: 'sk-***************c8e1',
    secretValue: 'sk-demo-openai-paused-c8e1',
    enabled: false,
    primary: false,
    status: 'failed',
    testedAt: '1 小时前',
  },
  {
    id: 'standby',
    maskedValue: 'sk-***************4d7f',
    secretValue: 'sk-demo-openai-standby-4d7f',
    enabled: false,
    primary: false,
    status: 'untested',
    testedAt: '未测试',
  },
];

interface DisplayProfile extends ProfileSummary {
  simulatedPlanType?: SimulationPlanType;
}

type ImportPlaceholderStatus = 'loading' | 'success' | 'error';

interface ImportPlaceholderCardBase {
  id: string;
  profileName: string;
  workspaceName: string | null;
  note: string;
  planType: PlanType;
  status: ImportPlaceholderStatus;
  importedProfileId: string | null;
  revealToken: number;
  loadingPhase: Extract<CardSwitchInteractionPhase, 'preview' | 'running'>;
  loadingTipText: string;
  loadingDetailText: string;
  successDetailText: string;
  errorTipText: string;
  errorMessage: string | null;
}

interface CloudDownloadPlaceholderCardState extends ImportPlaceholderCardBase {
  cloudEntryId: string;
}

interface CurrentAuthImportPlaceholderCardState extends ImportPlaceholderCardBase {}

interface LocalImportPlaceholderCardState extends ImportPlaceholderCardBase {
  importCandidateId: string;
}

type ImportPlaceholderCardState =
  | CloudDownloadPlaceholderCardState
  | CurrentAuthImportPlaceholderCardState
  | LocalImportPlaceholderCardState;

const buildSyncPlaceholderQuota = (): UsageQuota => ({
  percentUsed: 0,
  resetAt: new Date(Date.now() + 5 * 60 * 60 * 1000).toISOString(),
  source: 'manual',
  label: null,
  configured: true,
});

const getImportPlaceholderStorageKey = (placeholder: ImportPlaceholderCardState): string =>
  'cloudEntryId' in placeholder
    ? placeholder.cloudEntryId
    : 'importCandidateId' in placeholder
      ? placeholder.importCandidateId
      : placeholder.id;

const buildImportPlaceholderProfile = (placeholder: ImportPlaceholderCardState): DisplayProfile => ({
  id: `import-placeholder:${placeholder.id}`,
  storageDirName: `import-placeholder-${getImportPlaceholderStorageKey(placeholder)}`,
  name: placeholder.profileName.trim() || 'Personal',
  workspaceName: placeholder.workspaceName?.trim() || null,
  notes: placeholder.note,
  planType: placeholder.planType,
  manuallyDisabled: false,
  authFilePath: `import-placeholder/${getImportPlaceholderStorageKey(placeholder)}/auth.json`,
  weeklyQuota: buildSyncPlaceholderQuota(),
  fiveHourQuota: buildSyncPlaceholderQuota(),
  reviewQuota: buildSyncPlaceholderQuota(),
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  usageUpdatedAt: null,
  usageError: null,
  lastActivatedAt: null,
  authStatus: 'ok',
  probeStatus: undefined,
  probeCheckedAt: null,
  probeMessage: null,
  probeFailureKind: null,
  consecutiveAuthFailures: 0,
  autoDisabled: false,
  autoDisabledAt: null,
  securityBinding: null,
  isActive: false,
});

const buildImportPlaceholderProgressEvent = (
  placeholder: ImportPlaceholderCardState,
): SwitchProgressEvent => {
  const displayTitle =
    placeholder.workspaceName?.trim() || placeholder.profileName.trim() || '空间卡片';

  if (placeholder.status === 'error') {
    return {
      runId: `import-placeholder:${placeholder.id}:error`,
      targetProfileId: `import-placeholder:${placeholder.id}`,
      switchMode: 'codex',
      stepKey: 'complete',
      title: placeholder.errorTipText,
      detail: placeholder.errorMessage?.trim() || '请稍后重试',
      status: 'error',
      timestamp: new Date().toISOString(),
    };
  }

  if (placeholder.status === 'success') {
    return {
      runId: `import-placeholder:${placeholder.id}:success`,
      targetProfileId: `import-placeholder:${placeholder.id}`,
      switchMode: 'codex',
      stepKey: 'complete',
      title: displayTitle,
      detail: placeholder.successDetailText,
      status: 'success',
      timestamp: new Date().toISOString(),
    };
  }

  return {
    runId: `import-placeholder:${placeholder.id}:loading`,
    targetProfileId: `import-placeholder:${placeholder.id}`,
    switchMode: 'codex',
    stepKey: 'complete',
    title: placeholder.loadingTipText,
    detail: placeholder.loadingDetailText,
    status: 'active',
    timestamp: new Date().toISOString(),
  };
};

const buildSyncPlaceholderCloudStatus = (): CurrentDeviceCloudBackupStatus => ({
  state: 'missing',
  uploadedAt: null,
  detail: '当前设备正在接入这张空间卡片。',
});

interface UiInteractionBaselineState {
  state: ProfilesState;
  authState: AuthState;
  userCenterBrandFrameEnabled: boolean;
  warningThresholdDraft: string;
  autoSwitchThresholdDraft: string;
  autoSwitchCountdownDraft: string;
  searchValue: string;
  planFilter: PlanFilterValue;
  forceEnableProbeCards: boolean;
}

interface ProxyReminderState {
  network: NetworkAccessState;
  source: 'startup' | 'manual';
}

interface BatchDeleteConfirmRequest {
  source: 'workspace-selected' | 'workspace-all' | 'batch-dialog';
  profileIds: string[];
  deletedProfiles?: ProfileSummary[];
  successMessage?: string;
  failureMessage?: string;
  resetSelection?: boolean;
  closeBatchDialog?: boolean;
}

type SecondaryPasswordDialogMode =
  | 'set'
  | 'reset-by-email'
  | 'verify-device'
  | 'verify-cloud'
  | 'verify-local-import';

type CloudSyncDialogSource = 'auto' | 'manual';
type CloudSyncDialogStep = 'select' | 'confirm';
type CloudBackupCenterView = 'local' | 'cloud' | 'pending' | 'cloud-only';
type CloudSyncDialogAction =
  | 'overwrite-device-to-cloud'
  | 'download-cloud-to-device'
  | 'merge-local-to-cloud';

type PendingLocalImportOperation = 'add' | 'overwrite' | 'edit';

interface PendingLocalImportAction {
  operation: PendingLocalImportOperation;
  values: ProfileFormValues;
  targetProfileId?: string | null;
}

interface SecondaryPasswordDialogState {
  mode: SecondaryPasswordDialogMode;
  activateAfterImport?: boolean;
  pendingLocalImport?: PendingLocalImportAction;
  pendingCloudBackupProfileId?: string | null;
  pendingCloudSyncDirection?: CloudProfileSyncDirection;
  pendingCloudSyncAction?: CloudSyncDialogAction | null;
  pendingCloudSyncLocalIds?: string[];
  pendingCloudSyncCloudIds?: string[];
  pendingCloudSyncPurgeUnmatchedLocalBeforeImport?: boolean;
  continueCloudRestore?: boolean;
}

interface CloudSyncDialogState {
  source: CloudSyncDialogSource;
  step: CloudSyncDialogStep;
  direction: CloudProfileSyncDirection | null;
  action: CloudSyncDialogAction | null;
  purgeUnmatchedLocalBeforeImport: boolean;
}

type CloudSyncVisualProgressAction =
  | 'backup-refresh'
  | 'overwrite-device-to-cloud'
  | 'merge-local-to-cloud';

interface CloudSyncVisualProgressState {
  action: CloudSyncVisualProgressAction;
  total: number;
  status: 'running' | 'success';
}

interface NewDeviceCloudRestorePromptState {
  cloudEntryIds: string[];
}

interface CloudBackupRowState {
  tone: 'synced' | 'stale' | 'missing';
  label: string;
  detail: string;
}

interface CloudBackupCenterRow {
  entry: CloudProfileBackup;
  localProfile: ProfileSummary | null;
  localSnapshot: LicenseHostRemoteProfileSnapshot | null;
  status: CloudBackupRowState;
}

type OptimisticFeedbackMessageState = 'sending' | 'failed';

function CloudSyncProgressInline({
  count,
  text,
  running,
  compact = false,
  className,
}: {
  count: number;
  text: string;
  running: boolean;
  compact?: boolean;
  className?: string;
}) {
  const safeCount = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
  const countLabel = safeCount > 99 ? '99+' : String(safeCount);
  return (
    <span className={`cloud-sync-progress-inline${compact ? ' is-compact' : ''}${className ? ` ${className}` : ''}`}>
      <span className={`cloud-sync-count-ring${running ? ' is-running' : ''}${compact ? ' is-compact' : ''}`} aria-hidden="true">
        <svg viewBox="0 0 36 36" className="cloud-sync-count-ring__svg">
          <circle className="cloud-sync-count-ring__track" cx="18" cy="18" r="15.5" />
          <circle className="cloud-sync-count-ring__arc" cx="18" cy="18" r="15.5" />
        </svg>
        <span className="cloud-sync-count-ring__value">{countLabel}</span>
      </span>
      <span>{text}</span>
    </span>
  );
}

interface OptimisticFeedbackMessage {
  id: string;
  threadId: string | null;
  body: string;
  createdAt: string;
  state: OptimisticFeedbackMessageState;
  errorMessage: string | null;
}

interface SimulatedProfileSeed {
  id: string;
  storageDirName: string;
  name: string;
  workspaceName: string;
  notes: string;
  planType: PlanType;
  simulatedPlanType: SimulationPlanType;
  authFilePath: string;
  createdAt: string;
  updatedAt: string;
  usageUpdatedAt: string;
  lastActivatedAt: string;
  weeklyBaseResetAtMs: number;
  weeklyCycleSeconds: number;
  fiveHourBaseResetAtMs: number;
  fiveHourCycleSeconds: number;
  reviewBaseResetAtMs: number;
  reviewCycleSeconds: number;
}

const VIEW_MODE =
  typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('view') ??
      (window.location.pathname.startsWith('/root-console') ? 'license-host' : null)
    : null;
const IS_TRAY_CARD_VIEW = VIEW_MODE === 'tray-card';
const IS_FLOATING_WIDGET_VIEW = VIEW_MODE === 'floating-widget';
const IS_LICENSE_HOST_VIEW = VIEW_MODE === 'license-host';
const FLOATING_WIDGET_FEATURE_ENABLED = true;
const MAIN_BROWSER_BRIDGE_URL = 'http://127.0.0.1:5176';
const MAIN_BROWSER_BRIDGE_SETTINGS_POLL_MS = 500;
const MAIN_BROWSER_BRIDGE_UNAVAILABLE_MESSAGE =
  '未检测到本地开发客户端桥接，请先启动桌面客户端后再刷新这个浏览器页。';
const MAIN_BROWSER_BRIDGE_METHOD_CHANNELS: Partial<Record<keyof CodexWorkspaceApi, string>> = {
  getAppState: IPC_CHANNELS.getAppState,
  getInitialAppState: IPC_CHANNELS.getInitialAppState,
  importCurrentAuth: IPC_CHANNELS.importCurrentAuth,
  importProfileTransferPackage: IPC_CHANNELS.importProfileTransferPackage,
  importProfileTransferPackagesBatch: IPC_CHANNELS.importProfileTransferPackagesBatch,
  inspectLocalProfileImports: IPC_CHANNELS.inspectLocalProfileImports,
  inspectManualImportText: IPC_CHANNELS.inspectManualImportText,
  importLocalProfileImportCandidate: IPC_CHANNELS.importLocalProfileImportCandidate,
  getRemoteProfileSnapshots: IPC_CHANNELS.getRemoteProfileSnapshots,
  syncUsage: IPC_CHANNELS.syncUsage,
  getStatisticsDashboard: IPC_CHANNELS.getStatisticsDashboard,
  runPostSwitchDesktopLaunch: IPC_CHANNELS.runPostSwitchDesktopLaunch,
  listProfiles: IPC_CHANNELS.listProfiles,
  refreshProfiles: IPC_CHANNELS.refreshProfiles,
  addProfile: IPC_CHANNELS.addProfile,
  updateProfile: IPC_CHANNELS.updateProfile,
  batchUpdateProfileAvailability: IPC_CHANNELS.batchUpdateProfileAvailability,
  batchUpdateProfileNotes: IPC_CHANNELS.batchUpdateProfileNotes,
  batchDeleteProfiles: IPC_CHANNELS.batchDeleteProfiles,
  unlockAccountBoundProfiles: IPC_CHANNELS.unlockAccountBoundProfiles,
  deleteProfile: IPC_CHANNELS.deleteProfile,
  switchProfile: IPC_CHANNELS.switchProfile,
  chooseAuthFile: IPC_CHANNELS.chooseAuthFile,
  chooseLocalImportFiles: IPC_CHANNELS.chooseLocalImportFiles,
  chooseExecutableFile: IPC_CHANNELS.chooseExecutableFile,
  chooseDirectory: IPC_CHANNELS.chooseDirectory,
  chooseRootDirectory: IPC_CHANNELS.chooseRootDirectory,
  setProfilesRootDir: IPC_CHANNELS.setProfilesRootDir,
  updateSettings: IPC_CHANNELS.updateSettings,
  setTrayHoverCardEnabled: IPC_CHANNELS.setTrayHoverCardEnabled,
  setFloatingWidgetEnabled: IPC_CHANNELS.setFloatingWidgetEnabled,
  openPath: IPC_CHANNELS.openPath,
  openExternal: IPC_CHANNELS.openExternal,
  startWorkspaceOAuth: IPC_CHANNELS.startWorkspaceOAuth,
  getNetworkAccessState: IPC_CHANNELS.getNetworkAccessState,
  getWindowState: IPC_CHANNELS.windowGetState,
  getTrayCardState: IPC_CHANNELS.trayCardGetState,
  getFloatingWidgetState: IPC_CHANNELS.floatingWidgetGetState,
  toggleFloatingWidgetMode: IPC_CHANNELS.floatingWidgetToggleMode,
  floatingWidgetPreviewTransition: IPC_CHANNELS.floatingWidgetPreviewTransition,
  openLicenseHostWindow: IPC_CHANNELS.openLicenseHostWindow,
  getUpdateState: IPC_CHANNELS.updateGetState,
  getUpdateSourcePreference: IPC_CHANNELS.updateGetSourcePreference,
  setUpdateSourcePreference: IPC_CHANNELS.updateSetSourcePreference,
  checkForUpdates: IPC_CHANNELS.updateCheck,
  downloadAndInstallUpdate: IPC_CHANNELS.updateDownloadAndInstall,
  openUpdateNotes: IPC_CHANNELS.updateOpenNotes,
  getAuthState: IPC_CHANNELS.authGetState,
  login: IPC_CHANNELS.authLogin,
  register: IPC_CHANNELS.authRegister,
  logout: IPC_CHANNELS.authLogout,
  requestPasswordReset: IPC_CHANNELS.authRequestPasswordReset,
  createScanTicket: IPC_CHANNELS.authCreateScanTicket,
  getScanTicket: IPC_CHANNELS.authGetScanTicket,
  getScanCodeUrl: IPC_CHANNELS.authGetScanCodeUrl,
  consumeScanTicket: IPC_CHANNELS.authConsumeScanTicket,
  getAccountProfile: IPC_CHANNELS.authGetAccountProfile,
  getLicenseSummary: IPC_CHANNELS.authGetLicenseSummary,
  applyActivationCode: IPC_CHANNELS.authApplyActivationCode,
  requestInviteCode: IPC_CHANNELS.authRequestInviteCode,
  redeemInviteCode: IPC_CHANNELS.authRedeemInviteCode,
  claimInviteReward: IPC_CHANNELS.authClaimInviteReward,
  applyRewardCode: IPC_CHANNELS.authApplyRewardCode,
  listInviteRecords: IPC_CHANNELS.authListInviteRecords,
  submitFeedback: IPC_CHANNELS.authSubmitFeedback,
  markFeedbackConversationRead: IPC_CHANNELS.authMarkFeedbackConversationRead,
  issueUserProfileTransfer: IPC_CHANNELS.authIssueUserProfileTransfer,
  respondToProfileTransfer: IPC_CHANNELS.authRespondProfileTransfer,
  setSecondaryPassword: IPC_CHANNELS.authSetSecondaryPassword,
  verifySecondaryPassword: IPC_CHANNELS.authVerifySecondaryPassword,
  syncCloudProfiles: IPC_CHANNELS.authSyncCloudProfiles,
  heartbeat: IPC_CHANNELS.authHeartbeat,
  getHeartbeatBootstrap: IPC_CHANNELS.authGetHeartbeatBootstrap,
};

let localMainBrowserBridge: CodexWorkspaceApi | null = null;

const isLocalMainBrowserPreview = (): boolean => {
  if (typeof window === 'undefined' || VIEW_MODE !== null) {
    return false;
  }

  const { protocol, hostname } = window.location;
  return protocol === 'http:' && (hostname === '127.0.0.1' || hostname === 'localhost');
};

const buildMainBrowserBridgeUrl = (pathname: string): string =>
  `${MAIN_BROWSER_BRIDGE_URL}${pathname}`;

const requestMainBrowserBridge = async <T,>(pathname: string, init?: RequestInit): Promise<T> => {
  let response: Response;
  try {
    response = await fetch(buildMainBrowserBridgeUrl(pathname), {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
    });
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(MAIN_BROWSER_BRIDGE_UNAVAILABLE_MESSAGE);
    }
    throw error;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const payload = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const message =
      typeof payload === 'string'
        ? payload
        : payload && typeof payload === 'object' && 'message' in payload
          ? String(payload.message)
          : `主界面浏览器桥接请求失败：${response.status}`;
    throw new Error(message);
  }

  return payload as T;
};

const invokeMainBrowserBridge = async <T,>(channel: string, args: unknown[] = []): Promise<T> =>
  requestMainBrowserBridge('/ipc/invoke', {
    method: 'POST',
    body: JSON.stringify({
      channel,
      args,
    }),
  });

const createNoopUnsubscribe = (): (() => void) => () => undefined;

const createPollingBridgeSubscription = <T,>(
  load: () => Promise<T>,
  listener: (value: T) => void,
  intervalMs = 2_000,
): (() => void) => {
  let active = true;
  let timerId: number | null = null;
  let lastSnapshot: string | null = null;

  const poll = async (): Promise<void> => {
    try {
      const nextValue = await load();
      if (!active) {
        return;
      }

      const nextSnapshot = JSON.stringify(nextValue);
      if (nextSnapshot !== lastSnapshot) {
        lastSnapshot = nextSnapshot;
        listener(nextValue);
      }
    } catch {
      // Ignore transient bridge errors and keep polling.
    } finally {
      if (active) {
        timerId = window.setTimeout(() => {
          void poll();
        }, intervalMs);
      }
    }
  };

  void poll();
  return () => {
    active = false;
    if (timerId !== null) {
      window.clearTimeout(timerId);
    }
  };
};

const createBrowserLocalMainBridge = (): CodexWorkspaceApi => {
  if (localMainBrowserBridge) {
    return localMainBrowserBridge;
  }

  const invokeMethods: Partial<CodexWorkspaceApi> = {};
  for (const [methodName, channel] of Object.entries(MAIN_BROWSER_BRIDGE_METHOD_CHANNELS)) {
    if (!channel) {
      continue;
    }

    (invokeMethods as Record<string, (...args: unknown[]) => Promise<unknown>>)[methodName] = (
      ...args: unknown[]
    ) => invokeMainBrowserBridge(channel, args);
  }

  localMainBrowserBridge = {
    ...(invokeMethods as CodexWorkspaceApi),
    onStartupProgress: () => createNoopUnsubscribe(),
    quitApp: () => {
      window.close();
    },
    restartApp: () => {
      window.location.reload();
    },
    restartLicenseHost: async () => {
      window.location.reload();
      return { ok: true as const };
    },
    resolveDroppedFilePaths: (files) =>
      Array.from(files ?? [])
        .map((file) => {
          const runtimeFile = file as File & { path?: string };
          return runtimeFile.path?.trim() || '';
        })
        .filter(Boolean),
    onProfilesStateChange: (listener) =>
      createPollingBridgeSubscription(
        () => invokeMainBrowserBridge<ProfilesState>(IPC_CHANNELS.getAppState),
        listener,
        MAIN_BROWSER_BRIDGE_SETTINGS_POLL_MS,
      ),
    onSwitchProgress: () => createNoopUnsubscribe(),
    minimizeWindow: async () => ({ maximized: false, visible: true }),
    toggleMaximizeWindow: async () => ({ maximized: false, visible: true }),
    closeWindow: async () => {
      window.close();
      return { maximized: false, visible: false };
    },
    hideWindow: async () => ({ maximized: false, visible: true }),
    showWindow: async () => ({ maximized: false, visible: true }),
    onWindowStateChange: (listener) =>
      createPollingBridgeSubscription(
        () => invokeMainBrowserBridge<WindowState>(IPC_CHANNELS.windowGetState),
        listener,
        1_500,
      ),
    onTrayCardStateChange: (listener) =>
      createPollingBridgeSubscription(
        () => invokeMainBrowserBridge<TrayCardState>(IPC_CHANNELS.trayCardGetState),
        listener,
        MAIN_BROWSER_BRIDGE_SETTINGS_POLL_MS,
      ),
    onFloatingWidgetStateChange: (listener) =>
      createPollingBridgeSubscription(
        () => invokeMainBrowserBridge<FloatingWidgetState>(IPC_CHANNELS.floatingWidgetGetState),
        listener,
        MAIN_BROWSER_BRIDGE_SETTINGS_POLL_MS,
      ),
    startFloatingWidgetDrag: () => undefined,
    updateFloatingWidgetDrag: () => undefined,
    endFloatingWidgetDrag: () => undefined,
    onUpdateStateChange: (listener) =>
      createPollingBridgeSubscription(
        () => invokeMainBrowserBridge<UpdateState>(IPC_CHANNELS.updateGetState),
        listener,
        4_000,
      ),
    onAuthStateChange: (listener) =>
      createPollingBridgeSubscription(
        () => invokeMainBrowserBridge<AuthState>(IPC_CHANNELS.authGetState),
        listener,
        1_500,
      ),
  };

  return localMainBrowserBridge;
};

const ensureLocalMainBrowserBridge = (): void => {
  if (typeof window === 'undefined' || !isLocalMainBrowserPreview() || window.codexWorkspace) {
    return;
  }

  (
    window as Window &
      typeof globalThis & {
        codexWorkspace?: CodexWorkspaceApi;
      }
  ).codexWorkspace = createBrowserLocalMainBridge();
};

ensureLocalMainBrowserBridge();

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) {
    return '未记录';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '未记录';
  }

  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

const readRuntimeErrorInfo = (error: unknown, fallbackMessage: string): RuntimeIpcErrorInfo => {
  const rawMessage = error instanceof Error ? error.message : fallbackMessage;
  let normalized = rawMessage.trim();

  while (true) {
    const next = normalized
      .replace(/^Error invoking remote method '[^']+':\s*/i, '')
      .replace(/^Error:\s*/i, '')
      .trim();
    if (next === normalized) {
      break;
    }
    normalized = next;
  }
  if (normalized.startsWith(IPC_ERROR_MESSAGE_PREFIX)) {
    try {
      const payload = JSON.parse(normalized.slice(IPC_ERROR_MESSAGE_PREFIX.length)) as Partial<RuntimeIpcErrorInfo>;
      return {
        message: (payload.message?.trim() || fallbackMessage).trim(),
        status: typeof payload.status === 'number' ? payload.status : null,
        code: typeof payload.code === 'string' && payload.code.trim() ? payload.code.trim() : null,
        details:
          typeof payload.details === 'string' && payload.details.trim()
            ? payload.details.trim()
            : null,
      };
    } catch {
      // Fall back to the normalized message below.
    }
  }

  return {
    message: normalized || fallbackMessage,
    status: null,
    code: null,
    details: null,
  };
};

const isScanIdentityMissingRuntimeError = (runtimeError: RuntimeIpcErrorInfo): boolean =>
  runtimeError.code === 'scan_identity_missing' ||
  runtimeError.message.includes('scan_identity_missing') ||
  runtimeError.details?.includes('openId') === true ||
  runtimeError.details?.includes('userId') === true ||
  runtimeError.details?.includes('key') === true;

const normalizeRuntimeErrorMessage = (error: unknown, fallbackMessage: string): string => {
  const runtimeError = readRuntimeErrorInfo(error, fallbackMessage);
  const normalized = runtimeError.message;
  const missingHandlerMatch = normalized.match(/No handler registered for '([^']+)'/i);
  if (missingHandlerMatch) {
    const channel = missingHandlerMatch[1] ?? 'unknown';
    if (channel === 'app:get-state') {
      return `检测到主界面状态通道缺失：${channel}。这不是“必须先登录”的问题；更像是当前安装包的主界面 IPC 链没有正常接上。`;
    }

    if (channel.startsWith('auth:')) {
      return `检测到授权通道缺失：${channel}。这不是授权码内容错误，而是当前运行进程没有接上对应的授权 IPC。`;
    }

    return `检测到 IPC 通道缺失：${channel}。这更像是当前运行进程与安装包不一致，或安装包本身缺少该通道。`;
  }
  return normalized || fallbackMessage;
};

const hasRecognizedDisabledProbeMessage = (value?: string | null): boolean => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (!normalized) {
    return false;
  }

  return (
    normalized.includes('deactivated_workspace') ||
    normalized.includes('当前空间已停用')
  );
};

interface ProfileAvailabilityOptions {
  ignoreProbeUnavailable?: boolean;
}

const hasProbeFailureUsageError = (value?: string | null): boolean => {
  const normalized = (value || '').toLowerCase();
  return (
    normalized.includes('401') ||
    normalized.includes('403') ||
    normalized.includes('unauthorized') ||
    normalized.includes('forbidden') ||
    normalized.includes('token')
  );
};

const canIgnoreProbeUnavailable = (
  profile: Pick<ProfileSummary, 'authStatus' | 'autoDisabled' | 'usageError' | 'manuallyDisabled'>,
): boolean =>
  !profile.manuallyDisabled &&
  profile.authStatus === 'ok' &&
  (profile.autoDisabled || hasProbeFailureUsageError(profile.usageError));

const isRecognizedDisabledProfile = (profile: ProfileSummary): boolean =>
  profile.authStatus === 'ok' &&
  (profile.autoDisabled === true || hasRecognizedDisabledProbeMessage(profile.probeMessage));

const isBatchEnabledProfile = (profile: ProfileSummary): boolean =>
  profile.authStatus === 'ok' && !profile.manuallyDisabled && !profile.autoDisabled;

const isBatchDisabledProfile = (profile: ProfileSummary): boolean =>
  profile.authStatus === 'ok' && Boolean(profile.manuallyDisabled || profile.autoDisabled);

const filterBatchProfilesBySelection = (
  profiles: ProfileSummary[],
  filter: BatchProfileSelectionFilter,
): ProfileSummary[] =>
  filter === 'enabled'
    ? profiles.filter(isBatchEnabledProfile)
    : filter === 'disabled'
      ? profiles.filter(isBatchDisabledProfile)
      : profiles;

const getBatchSelectionFilterDefault = (
  mode: BatchProfileActionMode,
): BatchProfileSelectionFilter =>
  mode === 'enable' ? 'disabled' : mode === 'disable' ? 'enabled' : 'all';

const hasDesktopClientRuntimeBridge = (): boolean =>
  typeof window !== 'undefined' &&
  Boolean(window.codexWorkspace) &&
  typeof window.codexWorkspace.getAppState === 'function' &&
  typeof window.codexWorkspace.getAuthState === 'function';

const formatFileSize = (sizeBytes: number): string => {
  if (sizeBytes >= 1024 * 1024) {
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)}MB`;
  }

  if (sizeBytes >= 1024) {
    return `${(sizeBytes / 1024).toFixed(1)}KB`;
  }

  return `${sizeBytes}B`;
};

const formatPreciseFileSize = (sizeBytes: number): string => {
  if (sizeBytes >= 1024) {
    return `${formatFileSize(sizeBytes)} · ${sizeBytes.toLocaleString()} B`;
  }

  return formatFileSize(sizeBytes);
};

const getProfileDisplayName = (
  profile?: Pick<ProfileSummary, 'name' | 'workspaceName'> | null,
): string => {
  const primary = profile?.name?.trim();
  if (primary) {
    return primary;
  }

  const fallback = profile?.workspaceName?.trim();
  return fallback || '未识别';
};

const normalizeDisplayNoteText = (note?: string | null): string => {
  const normalized = note?.trim() ?? '';
  if (!normalized || normalized.startsWith('由当前 %USERPROFILE%')) {
    return '';
  }

  return normalized
    .replace(/^由云端备份(?:\s+.+?)?\s*恢复接入。\s*/u, '')
    .replace(/^由主机\s+.+?\s+传送导入。\s*/u, '')
    .replace(/^由远程主机传送导入。\s*/u, '')
    .trim();
};

const getVisibleNoteFromText = (note?: string | null, fallbackText?: string | null): string => {
  const normalizedNote = normalizeDisplayNoteText(note);
  if (normalizedNote) {
    return normalizedNote;
  }

  return fallbackText?.trim() ?? '';
};

const formatDetectedAuthOwnershipNote = (email?: string | null): string => email?.trim() ?? '';

const getCurrentAuthSuggestedBaseName = (summary: AppSummary): string =>
  summary.currentAuthSuggestedName?.trim() ||
  summary.currentAuthSuggestedWorkspaceName?.trim() ||
  'Personal';

const getProfileDisplayIdentity = (
  profile?: Pick<ProfileSummary, 'name' | 'workspaceName'> | null,
): string => getProfileDisplayName(profile).trim().toLowerCase();

const buildUniqueDetectedProfileName = (
  profiles: Array<Pick<ProfileSummary, 'name' | 'workspaceName'>>,
  summary: AppSummary,
): string => {
  const baseName = getCurrentAuthSuggestedBaseName(summary);
  const takenNames = new Set(
    profiles
      .map((profile) => getProfileDisplayIdentity(profile))
      .filter((value): value is string => Boolean(value)),
  );
  const hasConflict = (candidate: string): boolean => takenNames.has(candidate.trim().toLowerCase());

  if (!hasConflict(baseName)) {
    return baseName;
  }

  let index = 2;
  while (hasConflict(`${baseName}${index}`)) {
    index += 1;
  }

  return `${baseName}${index}`;
};

const canAutoImportCurrentAuth = (summary: AppSummary): boolean =>
  Boolean(
    summary.currentAuthExists &&
      !summary.authRecognized &&
      getCurrentAuthSuggestedBaseName(summary).trim() &&
      summary.currentAuthSuggestedPlanType &&
      summary.currentAuthSuggestedPlanType !== 'Unknown' &&
      (
        (summary.currentAuthSuggestedAccountId?.trim() ?? '') ||
        (summary.currentAuthSuggestedEmail?.trim() ?? '')
      ),
  );

const buildCurrentAuthImportPlaceholder = (
  summary: AppSummary,
  source: 'auto' | 'manual',
): CurrentAuthImportPlaceholderCardState => {
  const profileName = getCurrentAuthSuggestedBaseName(summary).trim() || 'Personal';
  const workspaceName =
    summary.currentAuthSuggestedWorkspaceName?.trim() || summary.currentAuthSuggestedName?.trim() || null;
  const accountLabel =
    summary.currentAuthSuggestedEmail?.trim() ||
    summary.currentAuthSuggestedAccountId?.trim() ||
    '';

  return {
    id: `current-auth:${Date.now()}:${summary.currentAuthHash ?? summary.currentAuthModifiedAt ?? 'pending'}`,
    profileName,
    workspaceName,
    note: accountLabel,
    planType: summary.currentAuthSuggestedPlanType ?? 'Unknown',
    status: 'loading',
    importedProfileId: null,
    revealToken: 0,
    loadingPhase: 'running',
    loadingTipText: '正在导入新空间',
    loadingDetailText:
      source === 'auto'
        ? '已检测到新的 OAuth 登录，正在纳入当前空间池。'
        : '正在把当前 auth.json 纳入当前空间池，请稍等。',
    successDetailText: '已纳入当前空间池',
    errorTipText: '导入新空间失败',
    errorMessage: null,
  };
};

const buildLocalImportPlaceholder = (
  candidate: LocalProfileImportCandidate,
  index: number,
): LocalImportPlaceholderCardState => ({
  id: `local-import:${candidate.id}:${Date.now()}:${index}`,
  importCandidateId: candidate.id,
  profileName: candidate.package.sourceProfileName?.trim() || candidate.fileName.trim() || 'Personal',
  workspaceName: candidate.package.sourceWorkspaceName?.trim() || null,
  note: candidate.accountEmail?.trim() || candidate.package.notes?.trim() || '',
  planType: candidate.package.planType,
  status: 'loading',
  importedProfileId: null,
  revealToken: 0,
  loadingPhase: 'preview',
  loadingTipText: '准备导入空间',
  loadingDetailText: candidate.matchedProfileId
    ? `已识别本地已有空间：${candidate.matchedProfileName || candidate.package.sourceProfileName}。`
    : candidate.sourceKind === 'zip-entry'
      ? '已识别压缩包凭证，等待写入空间池。'
      : candidate.sourceKind === 'manual-text'
        ? '已识别手动输入凭证，等待写入空间池。'
        : '已识别本地凭证，等待写入空间池。',
  successDetailText: '已写入当前空间池',
  errorTipText: '导入空间失败',
  errorMessage: null,
});

const getProfileCustomNote = (profile?: Pick<ProfileSummary, 'notes'> | null): string => {
  return normalizeDisplayNoteText(profile?.notes);
};

const getProfileVisibleNote = (
  profile?: Pick<ProfileSummary, 'notes' | 'authDerivedEmail'> | null,
): string => {
  return getVisibleNoteFromText(profile?.notes, profile?.authDerivedEmail);
};

const getCloudBackupVisibleNote = (
  entry?: Pick<CloudProfileBackup, 'note' | 'ownerUserEmail'> | null,
): string => getVisibleNoteFromText(entry?.note, entry?.ownerUserEmail);

const buildCloudBackupDownloadTransferPackage = (
  entry: Pick<CloudProfileBackup, 'note' | 'ownerUserEmail' | 'transferPackage'>,
): CloudProfileBackup['transferPackage'] => {
  const visibleNote = getCloudBackupVisibleNote(entry);
  return {
    ...entry.transferPackage,
    notes: visibleNote || entry.transferPackage.notes,
  };
};

type ProfileSecurityIndicatorTone = 'cloud' | 'success' | 'security' | 'warning';

interface ProfileSecurityIndicator {
  key: string;
  label: string;
  tone: ProfileSecurityIndicatorTone;
}

interface CurrentDeviceCloudBackupStatus {
  state: 'missing' | 'stale' | 'synced';
  uploadedAt: string | null;
  detail: string;
}

const buildCurrentDeviceCloudBackupStatus = (
  profile: Pick<ProfileSummary, 'id' | 'updatedAt'>,
  currentDeviceCloudBackupMap: Map<string, AuthState['currentDeviceCloudProfileBackups'][number]>,
): CurrentDeviceCloudBackupStatus => {
  if (isSimulatedProfileId(profile.id)) {
    return {
      state: 'synced',
      uploadedAt: profile.updatedAt ?? null,
      detail: '工程师模拟卡片使用本地仿真云状态展示，不会写入真实云端。',
    };
  }

  const backup = currentDeviceCloudBackupMap.get(profile.id) ?? null;
  if (!backup) {
    return {
      state: 'missing',
      uploadedAt: null,
      detail: '当前设备还没有这张卡片的云端快照。',
    };
  }

  const localUpdatedAt = profile.updatedAt?.trim() ?? '';
  const backupProfileUpdatedAt = backup.profileUpdatedAt?.trim() ?? '';
  const isStale = Boolean(
    localUpdatedAt &&
      backupProfileUpdatedAt &&
      backupProfileUpdatedAt.localeCompare(localUpdatedAt) < 0,
  );
  if (isStale) {
    return {
      state: 'stale',
      uploadedAt: backup.uploadedAt,
      detail: `云端快照记录于 ${formatDateTime(backup.uploadedAt)}，但本地在 ${formatDateTime(profile.updatedAt)} 后还有更新。`,
    };
  }

  return {
    state: 'synced',
    uploadedAt: backup.uploadedAt,
    detail: `当前设备已在 ${formatDateTime(backup.uploadedAt)} 完成云端备份。`,
  };
};

const getProfileSecurityIndicators = (
  profile?: Pick<ProfileSummary, 'securityBinding' | 'securityImportRequired'> | null,
  currentDeviceId?: string | null,
): ProfileSecurityIndicator[] => {
  const indicators: ProfileSecurityIndicator[] = [];
  if (profile?.securityImportRequired) {
    indicators.push({ key: 'security-import-required', label: '待校验', tone: 'warning' });
  }
  const binding = profile?.securityBinding;
  if (!binding) {
    return indicators;
  }

  if (binding.integrityState === 'tampered') {
    indicators.push({ key: 'device-bound-alert', label: '安全异常', tone: 'warning' });
  }

  if (binding.boundDeviceId === (currentDeviceId ?? '').trim()) {
    return indicators;
  }

  if (binding.importSource === 'cloud-backup') {
    indicators.push({ key: 'cloud-restored', label: '云恢复', tone: 'cloud' });
    indicators.push({ key: 'device-bound', label: '设备绑定', tone: 'security' });
  } else if (binding.importSource === 'transfer') {
    indicators.push({ key: 'cross-device', label: '跨设备传卡', tone: 'cloud' });
    indicators.push({ key: 'device-bound', label: '设备绑定', tone: 'security' });
  }
  return indicators;
};

const getProfileSecurityDetail = (
  profile?: Pick<ProfileSummary, 'securityBinding' | 'securityImportRequired'> | null,
  currentDeviceId?: string | null,
): string => {
  const details: string[] = [];
  if (profile?.securityImportRequired) {
    details.push('外部目录未携带可信安全锁，请先启用并校验二次密码，再通过正式导入、传卡或云恢复接入。');
  }
  const binding = profile?.securityBinding;
  if (!binding) {
    return details.join(' / ');
  }

  const sourceLabel =
    binding.importSource === 'cloud-backup'
      ? binding.senderHostLabel
        ? `来源 ${binding.senderHostLabel} 的云备份`
        : '来源同账号云备份'
      : binding.importSource === 'local'
        ? '当前设备本机绑定'
      : binding.senderHostLabel
        ? `来源 ${binding.senderHostLabel} 的跨设备传卡`
        : '来源跨设备传卡';

  const ownerLabel = binding.ownerUserEmail ? `归属 ${binding.ownerUserEmail}` : '';
  const boundLabel = `绑定 ${binding.boundFingerprintSummary}`;
  const integrityLabel =
    binding.integrityState === 'tampered'
      ? '安全锁校验失败'
      : binding.integrityState === 'legacy'
        ? '安全锁待升级'
        : '';
  if (binding.boundDeviceId === (currentDeviceId ?? '').trim()) {
    if (integrityLabel) {
      details.push(integrityLabel);
    }
    return details.join(' / ');
  }
  details.push(...[sourceLabel, ownerLabel, boundLabel, integrityLabel].filter(Boolean));
  return details.join(' / ');
};

const renderProfileSecurityIndicatorIcon = (tone: ProfileSecurityIndicatorTone): ReactNode => {
  switch (tone) {
    case 'cloud':
      return <Globe size={12} strokeWidth={2.2} />;
    case 'success':
      return <CheckCircle2 size={12} strokeWidth={2.2} />;
    case 'warning':
      return <CircleAlert size={12} strokeWidth={2.2} />;
    default:
      return <ShieldCheck size={12} strokeWidth={2.2} />;
  }
};

const shouldFallbackBatchNoteUpdate = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    message.includes('batchUpdateProfileNotes is not a function') ||
    message.includes('profiles:batch-update-notes')
  );
};

const upsertSwitchProgressEvent = (
  current: SwitchProgressEvent[],
  nextEvent: SwitchProgressEvent,
): SwitchProgressEvent[] => {
  const next = current.filter((item) => item.stepKey !== nextEvent.stepKey);
  next.push(nextEvent);
  next.sort((left, right) => {
    const orderDiff =
      SWITCH_PROGRESS_STEP_ORDER[left.stepKey] - SWITCH_PROGRESS_STEP_ORDER[right.stepKey];
    if (orderDiff !== 0) {
      return orderDiff;
    }

    return left.timestamp.localeCompare(right.timestamp);
  });
  return next;
};

const getSwitchProgressIdleDetail = (switchMode: SwitchMode): string =>
  switchMode === 'openclaw'
    ? '确认后会依次写入当前 auth.json、同步 OpenClaw 认证和模型，并在需要时执行网关联动。'
    : '确认后会先写入当前 auth.json，再按设置联动关闭或重启 Codex / Trae，最后刷新该空间额度。';

const findLatestSwitchProgressEvent = (
  events: SwitchProgressEvent[],
  status?: SwitchProgressEvent['status'],
): SwitchProgressEvent | null => {
  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index];
    if (!event) {
      continue;
    }

    if (!status || event.status === status) {
      return event;
    }
  }

  return null;
};

function SwitchProgressHeaderAside({
  switchMode,
  events,
  busy,
  completed = false,
}: {
  switchMode: SwitchMode;
  events: SwitchProgressEvent[];
  busy: boolean;
  completed?: boolean;
}) {
  const latestActiveEvent = findLatestSwitchProgressEvent(events, 'active');
  const latestEvent = events[events.length - 1] ?? null;
  const currentEvent = latestActiveEvent ?? latestEvent;
  const currentStatus = completed
    ? 'success'
    : currentEvent?.status ?? (busy ? 'active' : 'idle');
  const currentTitle = completed
    ? '切换完成'
    : currentStatus === 'error'
      ? '切换失败'
      : currentEvent?.title ?? (busy ? '正在准备切换' : '等待开始');
  const currentDetail =
    currentEvent?.detail ??
    (completed ? '切换链路已经执行完成，可以继续查看下方结果信息。' : getSwitchProgressIdleDetail(switchMode));
  const iconToneClass =
    currentStatus === 'success'
      ? 'confirm-action-dialog-inline-progress__icon confirm-action-dialog-inline-progress__icon--success'
      : currentStatus === 'warning'
        ? 'confirm-action-dialog-inline-progress__icon confirm-action-dialog-inline-progress__icon--warning'
        : currentStatus === 'error'
          ? 'confirm-action-dialog-inline-progress__icon confirm-action-dialog-inline-progress__icon--error'
          : currentStatus === 'active'
            ? 'confirm-action-dialog-inline-progress__icon confirm-action-dialog-inline-progress__icon--active'
            : 'confirm-action-dialog-inline-progress__icon confirm-action-dialog-inline-progress__icon--idle';

  return (
    <div className="confirm-action-dialog-inline-progress" aria-live="polite">
      <span className={iconToneClass}>
        {currentStatus === 'success' ? (
          <CheckCircle2 className="h-[18px] w-[18px]" />
        ) : currentStatus === 'warning' || currentStatus === 'error' ? (
          <CircleAlert className="h-[18px] w-[18px]" />
        ) : currentStatus === 'active' ? (
          <LoaderCircle className="h-[18px] w-[18px] animate-spin" />
        ) : (
          <ArrowUpCircle className="h-[18px] w-[18px]" />
        )}
      </span>
      <span className="confirm-action-dialog-inline-progress__copy">
        <span className="confirm-action-dialog-inline-progress__title">{currentTitle}</span>
        <span className="confirm-action-dialog-inline-progress__dot">·</span>
        <span className="confirm-action-dialog-inline-progress__detail">{currentDetail}</span>
      </span>
    </div>
  );
}

const getSwitchRuntimeOverlayMessage = (
  overlay: SwitchRuntimeOverlayState,
  event: SwitchProgressEvent | null,
): string => {
  if (overlay.phase === 'success' || overlay.phase === 'error') {
    return overlay.message ?? '';
  }

  if (event && event.status !== 'active') {
    return `正在切换到 ${overlay.workspaceName} · ${event.detail || event.title}`;
  }

  const currentTitle = event?.title ?? '正在准备切换';
  return `正在切换到 ${overlay.workspaceName} · ${currentTitle}，请稍等`;
};

const truncateCardSwitchCopy = (value: string, maxLength = 24): string => {
  const normalized = value.trim();
  if (!normalized) {
    return '';
  }

  return normalized.length > maxLength ? `${normalized.slice(0, maxLength).trim()}…` : normalized;
};

const getCardSwitchProgressDetail = (event: SwitchProgressEvent): string => {
  if (event.status === 'error') {
    return truncateCardSwitchCopy(event.detail, 28) || '请稍后重试';
  }

  switch (event.stepKey) {
    case 'prepare':
      return '正在校验认证文件';
    case 'switch-auth':
      return event.status === 'success' ? '认证写入完成' : '正在写入当前认证';
    case 'desktop-stop':
      return event.status === 'active'
        ? '正在关闭桌面端'
        : truncateCardSwitchCopy(event.detail, 28) || '桌面端关闭完成';
    case 'desktop-start':
      return event.status === 'active'
        ? '正在联动桌面端'
        : truncateCardSwitchCopy(event.detail, 28) || '桌面端联动完成';
    case 'openclaw-sync':
      return '正在同步 OpenClaw 认证';
    case 'openclaw-model':
      return '正在写入 OpenClaw 模型';
    case 'openclaw-gateway':
      return '正在联动 OpenClaw 网关';
    case 'quota-sync':
      return event.status === 'success' ? '空间额度同步完成' : '正在刷新空间额度';
    case 'complete':
      return event.status === 'success' ? '新空间已经生效' : truncateCardSwitchCopy(event.detail, 28);
    default:
      return truncateCardSwitchCopy(event.detail, 28);
  }
};

const getCardSwitchCopy = (
  phase: CardSwitchInteractionPhase,
  workspaceName: string,
  event: SwitchProgressEvent | null,
): {
  title: string;
  detail: string;
  showSuccessMark: boolean;
} => {
  switch (phase) {
    case 'preview':
      return {
        title: workspaceName,
        detail: '',
        showSuccessMark: false,
      };
    case 'running':
      return {
        title: event?.title ?? '正在准备切换',
        detail: event ? getCardSwitchProgressDetail(event) : '正在初始化切换链路',
        showSuccessMark: false,
      };
    case 'success':
      return {
        title: workspaceName,
        detail: '切换完成',
        showSuccessMark: true,
      };
    case 'error':
      return {
        title: event?.title ?? '切换失败',
        detail: event ? getCardSwitchProgressDetail(event) : '请稍后重试',
        showSuccessMark: false,
      };
    default:
      return {
        title: workspaceName,
        detail: '',
        showSuccessMark: false,
      };
  }
};

type CardFlipAudioPattern = 'single' | 'wave' | 'accent';

const getCardFlipAudioContextConstructor = (win: Window): typeof AudioContext | undefined =>
  (typeof AudioContext !== 'undefined' ? AudioContext : undefined) ??
  (
    win as Window & {
      webkitAudioContext?: typeof AudioContext;
    }
  ).webkitAudioContext;

const createCardFlipNoiseBuffer = (context: AudioContext): AudioBuffer => {
  const durationSeconds = 0.22;
  const buffer = context.createBuffer(1, Math.ceil(context.sampleRate * durationSeconds), context.sampleRate);
  const data = buffer.getChannelData(0);
  let filtered = 0;
  for (let index = 0; index < data.length; index += 1) {
    const white = Math.random() * 2 - 1;
    filtered = filtered * 0.84 + white * 0.16;
    const progress = index / data.length;
    const envelope = Math.max(0, 1 - progress) ** 1.35;
    data[index] = filtered * envelope;
  }
  return buffer;
};

const schedulePokerCardFlipSound = (
  context: AudioContext,
  noiseBuffer: AudioBuffer,
  startAt: number,
  options: {
    brightness?: number;
    gainScale?: number;
    snapScale?: number;
  } = {},
) => {
  const brightness = options.brightness ?? 1;
  const gainScale = options.gainScale ?? 1;
  const snapScale = options.snapScale ?? 1;

  const rustleSource = context.createBufferSource();
  const rustleHighPass = context.createBiquadFilter();
  const rustleBandPass = context.createBiquadFilter();
  const rustleGain = context.createGain();
  rustleSource.buffer = noiseBuffer;
  rustleHighPass.type = 'highpass';
  rustleHighPass.frequency.setValueAtTime(620 * brightness, startAt);
  rustleBandPass.type = 'bandpass';
  rustleBandPass.frequency.setValueAtTime(1480 * brightness, startAt);
  rustleBandPass.Q.setValueAtTime(0.68, startAt);
  rustleGain.gain.setValueAtTime(0.0001, startAt);
  rustleGain.gain.exponentialRampToValueAtTime(0.015 * gainScale, startAt + 0.012);
  rustleGain.gain.exponentialRampToValueAtTime(0.0042 * gainScale, startAt + 0.052);
  rustleGain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.17);
  rustleSource.connect(rustleHighPass);
  rustleHighPass.connect(rustleBandPass);
  rustleBandPass.connect(rustleGain);
  rustleGain.connect(context.destination);
  rustleSource.start(startAt);
  rustleSource.stop(startAt + 0.18);

  const bodyOscillator = context.createOscillator();
  const bodyGain = context.createGain();
  bodyOscillator.type = 'triangle';
  bodyOscillator.frequency.setValueAtTime(360 * brightness, startAt + 0.016);
  bodyOscillator.frequency.exponentialRampToValueAtTime(182 * brightness, startAt + 0.108);
  bodyGain.gain.setValueAtTime(0.0001, startAt + 0.016);
  bodyGain.gain.exponentialRampToValueAtTime(0.0048 * gainScale, startAt + 0.03);
  bodyGain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.118);
  bodyOscillator.connect(bodyGain);
  bodyGain.connect(context.destination);
  bodyOscillator.start(startAt + 0.016);
  bodyOscillator.stop(startAt + 0.13);

  const snapOscillator = context.createOscillator();
  const snapHighPass = context.createBiquadFilter();
  const snapGain = context.createGain();
  snapOscillator.type = 'square';
  snapOscillator.frequency.setValueAtTime(1220 * snapScale, startAt + 0.058);
  snapOscillator.frequency.exponentialRampToValueAtTime(720 * snapScale, startAt + 0.112);
  snapHighPass.type = 'highpass';
  snapHighPass.frequency.setValueAtTime(1350 * brightness, startAt + 0.058);
  snapGain.gain.setValueAtTime(0.0001, startAt + 0.058);
  snapGain.gain.exponentialRampToValueAtTime(0.0038 * gainScale, startAt + 0.074);
  snapGain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.126);
  snapOscillator.connect(snapHighPass);
  snapHighPass.connect(snapGain);
  snapGain.connect(context.destination);
  snapOscillator.start(startAt + 0.058);
  snapOscillator.stop(startAt + 0.13);
};

const scheduleCardFlipSampleSound = (
  context: AudioContext,
  audioBuffer: AudioBuffer,
  startAt: number,
  options: {
    playbackRate?: number;
    volume?: number;
    highPassHz?: number;
  } = {},
) => {
  const playbackRate = options.playbackRate ?? 1;
  const volume = options.volume ?? 0.28;
  const highPassHz = options.highPassHz ?? 240;
  const clipDurationSeconds = Math.min(0.34, Math.max(0.1, audioBuffer.duration / playbackRate));

  const source = context.createBufferSource();
  const highPass = context.createBiquadFilter();
  const gain = context.createGain();
  source.buffer = audioBuffer;
  source.playbackRate.setValueAtTime(playbackRate, startAt);
  highPass.type = 'highpass';
  highPass.frequency.setValueAtTime(highPassHz, startAt);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.linearRampToValueAtTime(volume, startAt + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + clipDurationSeconds);
  source.connect(highPass);
  highPass.connect(gain);
  gain.connect(context.destination);
  source.start(startAt, 0, clipDurationSeconds);
  source.stop(startAt + clipDurationSeconds + 0.02);
};

const hasQuotaData = (quota: UsageQuota): boolean =>
  Boolean(quota.configured) || quota.source === 'detected' || quota.source === 'live';

const getQuotaSourceText = (quota: UsageQuota): string => {
  if (quota.source === 'live') {
    return '实时同步';
  }

  if (quota.source === 'detected') {
    return '本地缓存';
  }

  if (hasQuotaData(quota)) {
    return '手动维护';
  }

  return '未识别';
};

const getRemainingPercent = (quota: UsageQuota): number =>
  hasQuotaData(quota) ? Math.max(0, 100 - quota.percentUsed) : 0;

const getQuotaTone = (quota: UsageQuota): 'ok' | 'warn' | 'danger' | 'idle' => {
  if (!hasQuotaData(quota)) {
    return 'idle';
  }

  const remaining = getRemainingPercent(quota);

  if (remaining <= 20) {
    return 'danger';
  }

  if (remaining <= 45) {
    return 'warn';
  }

  return 'ok';
};

const getQuotaBarClassName = (quota: UsageQuota): string => {
  switch (getQuotaTone(quota)) {
    case 'danger':
      return 'quota-fill is-danger';
    case 'warn':
      return 'quota-fill is-warn';
    case 'idle':
      return 'quota-fill is-idle';
    default:
      return 'quota-fill is-ok';
  }
};

const getAuthStatusText = (
  profile: ProfileSummary,
  options?: ProfileAvailabilityOptions,
): string => {
  if (profile.securityImportRequired) {
    return '待校验';
  }

  if (options?.ignoreProbeUnavailable && profile.autoDisabled && canIgnoreProbeUnavailable(profile)) {
    return '强制启用';
  }

  if (profile.manuallyDisabled || profile.autoDisabled) {
    return '已停用';
  }

  switch (profile.authStatus) {
    case 'missing':
      return '文件缺失';
    case 'path-error':
      return '路径异常';
    default:
      return '认证正常';
  }
};

const mapPlanLabel = (planType: DisplayPlanType): string => {
  switch (planType) {
    case 'Free':
      return 'FREE';
    case 'Plus':
      return 'PLUS';
    case 'Pro 5x':
      return 'PRO 5X';
    case 'Pro 20x':
      return 'PRO 20X';
    case 'Team':
      return 'TEAM';
    case 'Business':
      return 'BUSSINESS';
    default:
      return 'UNKNOWN';
  }
};

const getPlanBadgeClassName = (planType: DisplayPlanType): string => {
  switch (planType) {
    case 'Team':
      return 'badge--plan-team';
    case 'Plus':
    case 'Pro 5x':
    case 'Pro 20x':
      return 'badge--plan-plus';
    default:
      return '';
  }
};

const getDisplayPlanType = (
  profile: Pick<DisplayProfile, 'planType' | 'simulatedPlanType'>,
): DisplayPlanType => profile.simulatedPlanType ?? profile.planType;

const mapSwitchModeLabel = (mode: SwitchMode): string =>
  mode === 'openclaw' ? 'OpenClaw' : 'Codex / Trae';

const mapExecutableSourceLabel = (source: ExecutablePathSource): string => {
  switch (source) {
    case 'manual':
      return '手动定位';
    case 'running':
      return '自动识别：运行中实例';
    case 'where':
      return '自动识别：where.exe';
    case 'common':
      return '自动识别：常见安装目录';
    default:
      return '未识别';
  }
};

const buildHoverTitle = (...parts: Array<string | null | undefined | false>): string | undefined => {
  const lines = parts
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter((part) => part.length > 0);
  return lines.length > 0 ? lines.join('\n') : undefined;
};

const chooseExecutableFileWithFallback = async (title: string): Promise<FileDialogResult> => {
  const runtimeApi = window.codexWorkspace as {
    chooseExecutableFile?: (dialogTitle?: string) => Promise<FileDialogResult>;
  };

  if (typeof runtimeApi.chooseExecutableFile === 'function') {
    return runtimeApi.chooseExecutableFile(title);
  }

  return new Promise<FileDialogResult>((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.exe';
    input.style.display = 'none';

    const cleanup = () => {
      input.remove();
    };

    input.addEventListener(
      'change',
      () => {
        const file = input.files?.[0] as (File & { path?: string }) | undefined;
        const filePath = typeof file?.path === 'string' ? file.path.trim() : '';
        cleanup();

        if (!file) {
          resolve({ canceled: true });
          return;
        }

        if (filePath) {
          resolve({ canceled: false, filePath });
          return;
        }

        reject(new Error('当前窗口未加载新版桌面文件选择接口，请重启应用后再选择可执行文件。'));
      },
      { once: true },
    );

    input.addEventListener(
      'cancel',
      () => {
        cleanup();
        resolve({ canceled: true });
      },
      { once: true },
    );

    document.body.appendChild(input);
    input.click();
  });
};

const getProfileRoleBadge = (
  profile: ProfileSummary,
): { label: string; className: string } => {
  if (profile.isActive) {
    return { label: '当前空间', className: 'badge--role-current' };
  }

  return { label: '其他空间', className: 'badge--role-saved' };
};

const getProfileAvailability = (
  profile: ProfileSummary,
  options?: ProfileAvailabilityOptions,
): {
  isUnavailable: boolean;
  lockBackFace: boolean;
  badgeLabel: string | null;
  footerText: string | null;
} => {
  const ignoreProbeUnavailable = options?.ignoreProbeUnavailable === true;

  if (profile.securityImportRequired) {
    return {
      isUnavailable: true,
      lockBackFace: true,
      badgeLabel: '待校验',
      footerText: '请重新导入或用当前登录修复',
    };
  }

  if (profile.manuallyDisabled) {
    return {
      isUnavailable: true,
      lockBackFace: true,
      badgeLabel: '已停用',
      footerText: '已手动标记停用',
    };
  }

  if (profile.autoDisabled) {
    if (ignoreProbeUnavailable) {
      return {
        isUnavailable: false,
        lockBackFace: false,
        badgeLabel: null,
        footerText: null,
      };
    }

    return {
      isUnavailable: true,
      lockBackFace: true,
      badgeLabel: '空间停用',
      footerText: '探活确认当前不可用',
    };
  }

  if ((profile.authStatus as string) === 'missing') {
    return {
      isUnavailable: true,
      lockBackFace: false,
      badgeLabel: '文件缺失',
      footerText: 'auth.json 已丢失',
    };
  }

  if ((profile.authStatus as string) === 'path-error') {
    return {
      isUnavailable: true,
      lockBackFace: false,
      badgeLabel: '路径异常',
      footerText: '本地 auth 目录不存在，请重新导入或修正路径',
    };
  }

  if (profile.authStatus === 'missing') {
    return {
      isUnavailable: true,
      lockBackFace: false,
      badgeLabel: '空间停用',
      footerText: 'auth.json 已丢失',
    };
  }

  if (profile.authStatus === 'path-error') {
    return {
      isUnavailable: true,
      lockBackFace: false,
      badgeLabel: '空间停用',
      footerText: '目录路径异常',
    };
  }

  if (hasProbeFailureUsageError(profile.usageError)) {
    if (ignoreProbeUnavailable) {
      return {
        isUnavailable: false,
        lockBackFace: false,
        badgeLabel: null,
        footerText: null,
      };
    }

    return {
      isUnavailable: true,
      lockBackFace: true,
      badgeLabel: '疑似失效',
      footerText: '认证探活失败，请确认当前空间是否仍可用',
    };
  }

  if (false) {
    return {
      isUnavailable: true,
      lockBackFace: true,
      badgeLabel: '绌洪棿鍋滅敤',
      footerText: '璁よ瘉澶辨晥鎴栧凡杩囨湡',
    };
  }

  return {
    isUnavailable: false,
    lockBackFace: false,
    badgeLabel: null,
    footerText: null,
  };
};

const getProfileAuthToneClassName = (
  profile: ProfileSummary,
  options?: ProfileAvailabilityOptions,
): string =>
  options?.ignoreProbeUnavailable && profile.autoDisabled && canIgnoreProbeUnavailable(profile)
    ? 'ok'
    : profile.manuallyDisabled || profile.autoDisabled
      ? 'manualdisabled'
      : profile.authStatus.replace('-', '');

const withFeedbackActionTimeout = async <T,>(
  promise: Promise<T>,
  timeoutMessage: string,
): Promise<T> => {
  let settled = false;
  return await new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      if (!settled) {
        settled = true;
        reject(new Error(timeoutMessage));
      }
    }, FEEDBACK_ACTION_TIMEOUT_MS);

    void promise.then(
      (value) => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
};

const getQuotaCountdownCycleMs = (label: string): number | null => {
  if (label.includes('5小时')) {
    return 5 * 60 * 60 * 1000;
  }
  if (label.includes('周')) {
    return 7 * 24 * 60 * 60 * 1000;
  }
  return null;
};

const resolveCountdownTargetMs = (
  targetAt: string | null,
  nowMs: number,
  cycleMs: number | null,
): number | null => {
  if (!targetAt) {
    return null;
  }

  const targetMs = new Date(targetAt).getTime();
  if (Number.isNaN(targetMs)) {
    return null;
  }

  if (targetMs > nowMs || !cycleMs || cycleMs <= 0) {
    return targetMs;
  }

  const elapsed = nowMs - targetMs;
  return targetMs + (Math.floor(elapsed / cycleMs) + 1) * cycleMs;
};

const formatCountdown = (
  targetAt: string | null,
  nowMs: number,
  cycleMs: number | null = null,
): string | null => {
  const resolvedTargetMs = resolveCountdownTargetMs(targetAt, nowMs, cycleMs);
  if (resolvedTargetMs === null) {
    return null;
  }

  const diff = resolvedTargetMs - nowMs;
  if (diff <= 0) {
    return '即将刷新';
  }

  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return `${days}天 ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const getCountdownTone = (
  targetAt: string | null,
  nowMs: number,
  cycleMs: number | null = null,
): 'ok' | 'warn' | 'danger' | 'idle' => {
  const resolvedTargetMs = resolveCountdownTargetMs(targetAt, nowMs, cycleMs);
  if (resolvedTargetMs === null) {
    return 'idle';
  }

  const diff = resolvedTargetMs - nowMs;
  if (diff <= 60 * 60 * 1000) {
    return 'danger';
  }

  if (diff <= 12 * 60 * 60 * 1000) {
    return 'warn';
  }

  return 'ok';
};

const didQuotaReset = (previous: UsageQuota | undefined, next: UsageQuota): boolean => {
  if (!previous || !hasQuotaData(previous) || !hasQuotaData(next) || !previous.resetAt || !next.resetAt) {
    return false;
  }

  const previousResetAt = new Date(previous.resetAt).getTime();
  const nextResetAt = new Date(next.resetAt).getTime();
  if (Number.isNaN(previousResetAt) || Number.isNaN(nextResetAt) || nextResetAt <= previousResetAt) {
    return false;
  }

  return next.percentUsed < previous.percentUsed && (previous.percentUsed - next.percentUsed >= 15 || next.percentUsed <= 12);
};

const createEmptyState = (): ProfilesState => ({
  profiles: [],
  settings: {
    profilesRootDir: 'C:\\codex-profiles',
    backupBeforeSwitch: true,
    cloudSyncPaused: false,
    launchAtLoginEnabled: true,
    switchMode: 'codex',
    betaModeEnabled: true,
    lowQuotaWarningEnabled: true,
    lowQuotaWarningThreshold: 30,
    codexExecutablePathOverride: null,
    traeExecutablePathOverride: null,
    codexAutoRestart: false,
    traeAutoRestart: false,
    codexTraeAutoRestart: false,
    openclawStateDirOverride: null,
    openclawAutoRestartGateway: false,
    autoSwitchEnabled: false,
    autoSwitchThreshold: 18,
    autoSwitchCountdownSeconds: 30,
    trayHoverCardEnabled: true,
    floatingWidgetEnabled: false,
    floatingWidgetOpacityPercent: 78,
    cardFlipVolumePercent: DEFAULT_CARD_FLIP_VOLUME_PERCENT,
  },
  lastScannedAt: new Date(0).toISOString(),
  logs: [],
  backups: [],
  summary: {
    activeProfileName: null,
    currentAuthExists: false,
    currentAuthModifiedAt: null,
    authRecognized: false,
    currentAuthSuggestedName: null,
    currentAuthSuggestedWorkspaceName: null,
    currentAuthSuggestedPlanType: 'Unknown',
    currentAuthSuggestedAccountId: null,
    currentAuthSuggestedEmail: null,
    profilesCount: 0,
    backupBeforeSwitch: true,
    lastSwitchedAt: null,
    overallRemainingPercent: null,
    currentAuthPath: '',
    launchAtLoginSupported: false,
    codexExecutablePath: null,
    codexExecutablePathSource: 'missing',
    traeExecutablePath: null,
    traeExecutablePathSource: 'missing',
    openclawStateDir: '',
    openclawGatewayCommandPath: null,
    openclawGatewayAvailable: false,
    openclawCurrentModel: null,
    openclawAvailableModels: [],
  },
});

const createEmptyUpdateState = (): UpdateState => ({
  appId: 'com.jerryc0820.auth-api-switcher',
  currentVersion: APP_FALLBACK_VERSION,
  currentReleaseTime: null,
  currentPlatform: 'win',
  channel: 'stable',
  override: null,
  allowPrerelease: false,
  allowEngineerAccess: false,
  testerId: null,
  sourcePreference: 'auto',
  availability: 'idle',
  updateAvailable: false,
  updateKind: null,
  blockedByMandatory: false,
  mandatory: false,
  minSupportedVersion: null,
  lastCheckedAt: null,
  lastDownloadedAt: null,
  sourceName: null,
  sourceUrl: null,
  errorMessage: null,
  manifest: null,
  notesContent: null,
  downloadFilePath: null,
  downloadStage: 'idle',
  downloadStageLabel: null,
  downloadSourceUrl: null,
  downloadProgressPercent: null,
  downloadTransferredBytes: 0,
  downloadTotalBytes: null,
});

const mapUpdateChannelLabel = (channel: UpdateState['channel']): string => {
  switch (channel) {
    case 'beta':
      return '内测';
    case 'canary':
      return '测试';
    default:
      return '正式';
  }
};

const mapUpdateSourceLabel = (sourceName: UpdateState['sourceName']): string => {
  switch (sourceName) {
    case 'github':
      return 'GitHub';
    case 'gitee':
      return 'Gitee';
    case 'custom':
      return '自定义';
    default:
      return '未检查';
  }
};

const mapUpdateAvailabilityLabel = (updateState: UpdateState): string => {
  switch (updateState.availability) {
    case 'checking':
      return '检查中';
    case 'unconfigured':
      return '更新未配置';
    case 'available':
      return '发现新版本';
    case 'downloading':
      return '下载中';
    case 'ready':
      return updateState.currentPlatform === 'win' ? '安装器就绪' : '下载完成';
    case 'required':
      return '必须更新';
    case 'error':
      return '更新失败';
    case 'up-to-date':
      return '已是最新';
    default:
      return '待检查';
  }
};

const getUpdateActionTone = (
  result: UpdateActionResult,
): ToastTone => {
  if (result.state.availability === 'error') {
    return 'error';
  }

  if (
    result.state.availability === 'available' ||
    result.state.availability === 'required' ||
    result.state.availability === 'ready'
  ) {
    return 'success';
  }

  return 'info';
};

const getRemainingPercentOrNull = (quota: UsageQuota): number | null =>
  hasQuotaData(quota) ? Math.max(0, 100 - quota.percentUsed) : null;

const STATISTICS_PLAN_TOKEN_CAPACITY: Record<PlanType, number> = {
  Unknown: 2_000_000,
  Free: 320_000,
  Plus: 5_800_000,
  'Pro 5x': 2_100_000,
  'Pro 20x': 8_400_000,
  Team: 4_500_000,
  Business: 12_000_000,
};

const STATISTICS_TOKEN_SPLIT_INPUT_RATIO = 0.425;
const STATISTICS_TOKEN_SPLIT_OUTPUT_RATIO = 0.575;

const STATISTICS_RANKING_ACCENTS: StatisticsDashboard['ranking'][number]['accent'][] = [
  'green',
  'blue',
  'violet',
  'amber',
  'purple',
  'cyan',
  'slate',
];

const clampStatisticsMetric = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const roundStatisticsMetric = (value: number, fractionDigits = 2): number => {
  const factor = 10 ** fractionDigits;
  return Math.round(value * factor) / factor;
};

const getStatisticsProfileUsagePercent = (profile: ProfileSummary): number => {
  const usagePercents = [profile.weeklyQuota, profile.fiveHourQuota, profile.reviewQuota]
    .filter(hasQuotaData)
    .map((quota) => clampStatisticsMetric(quota.percentUsed, 0, 100));

  if (usagePercents.length === 0) {
    return 0;
  }

  return roundStatisticsMetric(
    usagePercents.reduce((sum, current) => sum + current, 0) / usagePercents.length,
    2,
  );
};

const getStatisticsProfileHealthPercent = (profile: ProfileSummary): number => {
  if (!isStatisticsProfileAvailable(profile)) {
    return 0;
  }

  let healthPercent = 100 - getStatisticsProfileUsagePercent(profile);

  if (profile.weeklyQuota.source === 'live') {
    healthPercent += 3;
  }

  if (profile.fiveHourQuota.source === 'live') {
    healthPercent += 3;
  }

  return clampStatisticsMetric(roundStatisticsMetric(healthPercent, 2), 0, 100);
};

const isStatisticsProfileAvailable = (profile: ProfileSummary): boolean =>
  profile.authStatus === 'ok' &&
  !getProfileAvailability(profile, {
    ignoreProbeUnavailable: false,
  }).isUnavailable;

const getStatisticsProfileStatus = (
  profile: ProfileSummary,
  usagePercent: number,
): Pick<StatisticsDashboard['tableRows'][number], 'statusLabel' | 'statusTone'> => {
  if (!isStatisticsProfileAvailable(profile)) {
    return { statusLabel: '停用', statusTone: 'offline' };
  }

  if (usagePercent >= 70) {
    return { statusLabel: '紧张', statusTone: 'danger' };
  }

  if (usagePercent >= 40) {
    return { statusLabel: '预警', statusTone: 'warning' };
  }

  return { statusLabel: '正常', statusTone: 'healthy' };
};

const getStatisticsProfileActionLabel = (
  profile: ProfileSummary,
  statusTone: StatisticsDashboard['tableRows'][number]['statusTone'],
  healthPercent: number,
): string => {
  if (!isStatisticsProfileAvailable(profile)) {
    return profile.manuallyDisabled || profile.autoDisabled ? '重新启用' : '检查认证';
  }

  if (statusTone === 'healthy') {
    return '查看详情';
  }

  if (statusTone === 'warning') {
    return healthPercent >= 55 ? '优化额度' : '查看详情';
  }

  return '扩容建议';
};

const buildStatisticsCompareLabel = (range: StatisticsRange): string => {
  switch (range) {
    case '7d':
      return '较7天前';
    case '30d':
      return '较30天前';
    default:
      return '较昨日';
  }
};

const buildStatisticsFallbackTrendPoints = (
  range: StatisticsRange,
  estimatedUsedTokens: number,
): StatisticsDashboard['trendPoints'] => {
  const pointTime = new Date();
  return [
    {
      label:
        range === 'today'
          ? pointTime.toLocaleString('zh-CN', {
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })
          : pointTime.toLocaleDateString('zh-CN', {
              month: '2-digit',
              day: '2-digit',
            }),
      shortLabel:
        range === 'today'
          ? pointTime.toLocaleTimeString('zh-CN', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })
          : pointTime.toLocaleDateString('zh-CN', {
              month: '2-digit',
              day: '2-digit',
            }),
      timestamp: pointTime.toISOString(),
      usedTokens: Math.round(estimatedUsedTokens),
      averageTokens: Math.round(estimatedUsedTokens),
    },
  ];
};

const buildLocalStatisticsDashboard = (
  state: ProfilesState,
  range: StatisticsRange,
): StatisticsDashboard => {
  const tableRows = state.profiles
    .map((profile) => {
      const usagePercent = getStatisticsProfileUsagePercent(profile);
      const healthPercent = getStatisticsProfileHealthPercent(profile);
      const tokenCapacity =
        STATISTICS_PLAN_TOKEN_CAPACITY[profile.planType] ?? STATISTICS_PLAN_TOKEN_CAPACITY.Unknown;
      const usedTokens = Math.round(tokenCapacity * (usagePercent / 100));
      const remainingTokens = Math.max(0, tokenCapacity - usedTokens);
      const status = getStatisticsProfileStatus(profile, usagePercent);

      return {
        profileId: profile.id,
        name: getProfileDisplayName(profile),
        planType: profile.planType,
        statusLabel: status.statusLabel,
        statusTone: status.statusTone,
        healthPercent,
        usedTokens,
        remainingTokens,
        usagePercent,
        lastSyncAt: profile.usageUpdatedAt ?? null,
        actionLabel: getStatisticsProfileActionLabel(profile, status.statusTone, healthPercent),
      };
    })
    .sort((left, right) => right.usedTokens - left.usedTokens);

  const totalSpaces = tableRows.length;
  const estimatedUsedTokens = tableRows.reduce((sum, row) => sum + row.usedTokens, 0);
  const estimatedRemainingTokens = tableRows.reduce((sum, row) => sum + row.remainingTokens, 0);
  const estimatedTotalTokens = estimatedUsedTokens + estimatedRemainingTokens;
  const healthySpaces = tableRows.filter((row) => row.statusTone === 'healthy').length;
  const warningSpaces = tableRows.filter((row) => row.statusTone === 'warning').length;
  const abnormalSpaces = tableRows.filter((row) => row.statusTone === 'danger').length;
  const offlineSpaces = tableRows.filter((row) => row.statusTone === 'offline').length;
  const activeSpaces = state.profiles.filter((profile) => profile.authStatus === 'ok').length;
  const disabledSpaces = state.profiles.filter(
    (profile) => profile.manuallyDisabled || profile.autoDisabled || profile.authStatus !== 'ok',
  ).length;
  const availableSpaces = tableRows.filter((row) => row.statusTone !== 'offline').length;
  const averageHealthPercent =
    totalSpaces > 0
      ? roundStatisticsMetric(
          tableRows.reduce((sum, row) => sum + row.healthPercent, 0) / totalSpaces,
          2,
        )
      : 0;
  const ranking = tableRows.slice(0, 8).map((row, index) => ({
    profileId: row.profileId,
    name: row.name,
    usedTokens: row.usedTokens,
    usagePercent: row.usagePercent,
    sharePercent:
      estimatedUsedTokens > 0 ? roundStatisticsMetric((row.usedTokens / estimatedUsedTokens) * 100, 1) : 0,
    accent: STATISTICS_RANKING_ACCENTS[index % STATISTICS_RANKING_ACCENTS.length] ?? 'slate',
  }));
  const trendPoints = buildStatisticsFallbackTrendPoints(range, estimatedUsedTokens);
  const estimatedInputTokens = Math.round(estimatedUsedTokens * STATISTICS_TOKEN_SPLIT_INPUT_RATIO);
  const estimatedOutputTokens = Math.round(estimatedUsedTokens * STATISTICS_TOKEN_SPLIT_OUTPUT_RATIO);

  return {
    range,
    generatedAt: new Date().toISOString(),
    compareLabel: buildStatisticsCompareLabel(range),
    historyBackfilled: true,
    totalSpaces,
    availableSpaces,
    activeSpaces,
    disabledSpaces,
    healthySpaces,
    warningSpaces,
    abnormalSpaces,
    averageHealthPercent,
    estimatedUsedTokens,
    estimatedRemainingTokens,
    estimatedTotalTokens,
    estimatedInputTokens,
    estimatedOutputTokens,
    totalSpacesDeltaPercent: 0,
    availableSpacesDeltaPercent: 0,
    usedTokensDeltaPercent: 0,
    remainingTokensDeltaPercent: 0,
    averageHealthDeltaPercent: 0,
    totalTokensDeltaPercent: 0,
    trendPoints,
    ranking,
    healthDistribution: [
      {
        label: '正常',
        count: healthySpaces,
        sharePercent: totalSpaces > 0 ? roundStatisticsMetric((healthySpaces / totalSpaces) * 100, 1) : 0,
        tone: 'healthy',
      },
      {
        label: '预警',
        count: warningSpaces,
        sharePercent: totalSpaces > 0 ? roundStatisticsMetric((warningSpaces / totalSpaces) * 100, 1) : 0,
        tone: 'warning',
      },
      {
        label: '紧张',
        count: abnormalSpaces,
        sharePercent: totalSpaces > 0 ? roundStatisticsMetric((abnormalSpaces / totalSpaces) * 100, 1) : 0,
        tone: 'danger',
      },
      {
        label: '停用',
        count: offlineSpaces,
        sharePercent: totalSpaces > 0 ? roundStatisticsMetric((offlineSpaces / totalSpaces) * 100, 1) : 0,
        tone: 'offline',
      },
    ],
    tokenDistribution: [
      {
        label: '输入 Token',
        value: estimatedInputTokens,
        sharePercent:
          estimatedUsedTokens > 0
            ? roundStatisticsMetric((estimatedInputTokens / estimatedUsedTokens) * 100, 1)
            : 0,
        tone: 'input',
      },
      {
        label: '输出 Token',
        value: estimatedOutputTokens,
        sharePercent:
          estimatedUsedTokens > 0
            ? roundStatisticsMetric((estimatedOutputTokens / estimatedUsedTokens) * 100, 1)
            : 0,
        tone: 'output',
      },
    ],
    tableRows,
  };
};

const formatCompactQuotaValue = (quota: UsageQuota): string => {
  const remaining = getRemainingPercentOrNull(quota);
  return remaining === null ? '未识别' : `${remaining}%`;
};

const getCompactQuotaValueClassName = (quota: UsageQuota): string => {
  switch (getQuotaTone(quota)) {
    case 'ok':
      return 'compact-quota-value compact-quota-value--ok';
    case 'warn':
      return 'compact-quota-value compact-quota-value--warn';
    case 'danger':
      return 'compact-quota-value compact-quota-value--danger';
    default:
      return 'compact-quota-value';
  }
};

const getCompactOwnerLabel = (note?: string | null, fallbackText?: string | null): string =>
  getVisibleNoteFromText(note, fallbackText) || '未识别';

const getCloudBackupCardTitle = (entry: CloudProfileBackup): string =>
  entry.sourceProfileName.trim() || entry.sourceWorkspaceName?.trim() || '未命名空间';

const getLocalSnapshotCardTitle = (
  snapshot: Pick<LicenseHostRemoteProfileSnapshot, 'name' | 'workspaceName'>,
): string => snapshot.name.trim() || snapshot.workspaceName?.trim() || '未命名空间';

const getDisabledBadgeLabel = (
  options: {
    manuallyDisabled?: boolean;
    autoDisabled?: boolean;
  } | null,
): string | null => {
  if (!options) {
    return null;
  }
  if (options.manuallyDisabled) {
    return '已停用';
  }
  if (options.autoDisabled) {
    return '已被停用';
  }
  return null;
};

const getRecommendationPlanBonus = (planType: PlanType): number => {
  switch (planType) {
    case 'Business':
      return 22;
    case 'Team':
      return 14;
    case 'Pro 5x':
    case 'Pro 20x':
      return 8;
    case 'Plus':
      return 8;
    case 'Free':
      return 0;
    default:
      return 4;
  }
};

const getRecommendationScore = (
  profile: ProfileSummary,
  options?: {
    allowActive?: boolean;
  },
): number => {
  const availability = getProfileAvailability(profile);
  if ((!options?.allowActive && profile.isActive) || profile.authStatus !== 'ok' || availability.isUnavailable) {
    return Number.NEGATIVE_INFINITY;
  }

  const weeklyRemaining = getRemainingPercentOrNull(profile.weeklyQuota) ?? 58;
  const fiveHourRemaining = getRemainingPercentOrNull(profile.fiveHourQuota) ?? 58;
  const liveBonus =
    (profile.weeklyQuota.source === 'live' ? 6 : 0) +
    (profile.fiveHourQuota.source === 'live' ? 6 : 0);
  const recencyBonus = profile.lastActivatedAt ? 3 : 0;

  return (
    weeklyRemaining * 0.55 +
    fiveHourRemaining * 0.45 +
    getRecommendationPlanBonus(profile.planType) +
    liveBonus +
    recencyBonus
  );
};

const getOpenClawModelLabel = (
  modelKey: string | null | undefined,
  options: OpenClawModelOption[],
): string => {
  const normalized = modelKey?.trim() ?? '';
  if (!normalized) {
    return '未选择';
  }

  return options.find((item) => item.key === normalized)?.label ?? normalized;
};

const buildOpenClawSwitchModelOptions = (
  options: OpenClawModelOption[],
  currentModel: string | null | undefined,
): OpenClawModelOption[] => {
  if (options.length > 0) {
    return options;
  }

  const normalizedCurrent = currentModel?.trim() ?? '';
  if (!normalizedCurrent) {
    return [];
  }

  const slashIndex = normalizedCurrent.indexOf('/');
  return [
    {
      key: normalizedCurrent,
      label: getOpenClawModelLabel(normalizedCurrent, options),
      provider: slashIndex >= 0 ? normalizedCurrent.slice(0, slashIndex) : 'custom',
      modelId: slashIndex >= 0 ? normalizedCurrent.slice(slashIndex + 1) : normalizedCurrent,
    },
  ];
};

const LOCAL_SIMULATION_PREFIXES = [
  'a',
  'b',
  'c',
  'd',
  'e',
  'f',
  'g',
  'h',
  'j',
  'k',
  'm',
  'n',
  'p',
  'q',
  'r',
  's',
  't',
  'u',
  'v',
  'w',
  'x',
  'y',
  'z',
] as const;
const LOCAL_SIMULATION_PLAN_TYPES: SimulationPlanType[] = [
  'Free',
  'Plus',
  'Pro 5x',
  'Pro 20x',
  'Team',
  'Business',
];
const DISPLAY_PLAN_FILTER_ORDER: DisplayPlanType[] = [
  'Free',
  'Plus',
  'Pro 5x',
  'Pro 20x',
  'Team',
  'Business',
  'Unknown',
];
const LOCAL_SIMULATION_PLAN_TYPE_MAP: Record<SimulationPlanType, PlanType> = {
  Free: 'Free',
  Plus: 'Plus',
  'Pro 5x': 'Pro 5x',
  'Pro 20x': 'Pro 20x',
  Team: 'Team',
  Business: 'Business',
};
const LOCAL_HIDDEN_MODE_PASSWORD_STORAGE_KEY = 'codex-workspace.hidden-mode-password';
const LOCAL_SPECIAL_FEATURE_SETTINGS_STORAGE_KEY = 'codex-workspace.special-feature-settings';
const LOCAL_AVATAR_STORAGE_PREFIX = 'codex-workspace.avatar';
const LOCAL_USER_CENTER_BRAND_FRAME_STORAGE_KEY = 'codex-workspace.user-center-brand-frame';
const AVATAR_OUTPUT_SIZE = 192;
const MAX_AVATAR_FILE_SIZE = 5 * 1024 * 1024;
const DEFAULT_LOCAL_SIMULATION_SETTINGS = {
  enabled: false,
  cardCount: 12,
  planTypes: LOCAL_SIMULATION_PLAN_TYPES,
} as const;

const isSimulationPlanType = (value: string): value is SimulationPlanType =>
  LOCAL_SIMULATION_PLAN_TYPES.includes(value as SimulationPlanType);

const loadHiddenModePassword = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return window.localStorage.getItem(LOCAL_HIDDEN_MODE_PASSWORD_STORAGE_KEY)?.trim() ?? '';
  } catch {
    return '';
  }
};

const persistHiddenModePassword = (value: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const normalized = value.trim();
    if (normalized) {
      window.localStorage.setItem(LOCAL_HIDDEN_MODE_PASSWORD_STORAGE_KEY, normalized);
    } else {
      window.localStorage.removeItem(LOCAL_HIDDEN_MODE_PASSWORD_STORAGE_KEY);
    }
  } catch {
    // Ignore local-only storage failures and keep the UI functional.
  }
};

const getAvatarStorageKey = (profile: Pick<UserProfile, 'id' | 'email'> | null | undefined): string | null => {
  if (!profile) {
    return null;
  }

  const idPart = profile.id?.trim();
  if (idPart) {
    return `${LOCAL_AVATAR_STORAGE_PREFIX}:${idPart}`;
  }

  const emailPart = profile.email?.trim().toLowerCase();
  return emailPart ? `${LOCAL_AVATAR_STORAGE_PREFIX}:${emailPart}` : null;
};

const loadStoredAvatar = (profile: Pick<UserProfile, 'id' | 'email'> | null | undefined): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const storageKey = getAvatarStorageKey(profile);
  if (!storageKey) {
    return null;
  }

  try {
    return window.localStorage.getItem(storageKey);
  } catch {
    return null;
  }
};

const normalizeEmailForComparison = (value?: string | null): string =>
  value?.trim().toLowerCase() ?? '';

const isSimulatedProfileId = (profileId?: string | null): boolean =>
  (profileId ?? '').startsWith('sim:');

const isProfileBoundToDifferentAccountForUser = (
  profile: Pick<ProfileSummary, 'securityBinding'>,
  currentAuthProfile: AuthState['profile'] | null | undefined,
  currentDeviceId?: string | null,
): boolean => {
  const binding = profile.securityBinding;
  if (!binding || !currentAuthProfile) {
    return false;
  }

  const normalizedCurrentDeviceId = (currentDeviceId ?? '').trim();
  if (
    binding.importSource === 'local' &&
    binding.boundDeviceId.trim() === normalizedCurrentDeviceId &&
    binding.ownerBindingSource !== 'software-account'
  ) {
    return false;
  }

  const currentUserId = currentAuthProfile.id?.trim() || '';
  const currentEmail = normalizeEmailForComparison(currentAuthProfile.email);
  const ownerUserId = binding.ownerUserId?.trim() || '';
  const ownerEmail = normalizeEmailForComparison(binding.ownerUserEmail);

  // Local auth profile ids are renderer-local identifiers, while security bindings may
  // come from cloud-side account ids. Prefer email matching when available so current
  // account cards are not hidden just because the identifier domains differ.
  if (ownerEmail && currentEmail) {
    return ownerEmail !== currentEmail;
  }
  if (!ownerEmail && !currentEmail && ownerUserId && currentUserId) {
    return ownerUserId !== currentUserId;
  }
  return false;
};

const doesProfileNeedUnlockForAccount = (
  profile: Pick<ProfileSummary, 'securityBinding' | 'securityImportRequired'>,
  currentAuthProfile: AuthState['profile'] | null | undefined,
  currentDeviceId?: string | null,
): boolean => {
  if (!currentAuthProfile) {
    return false;
  }
  if (isProfileBoundToDifferentAccountForUser(profile, currentAuthProfile, currentDeviceId)) {
    return false;
  }
  if (profile.securityImportRequired) {
    return true;
  }

  const binding = profile.securityBinding;
  if (!binding) {
    return false;
  }

  const ownerUserId = binding.ownerUserId?.trim() || '';
  const ownerEmail = normalizeEmailForComparison(binding.ownerUserEmail);
  const currentUserId = currentAuthProfile.id?.trim() || '';
  const currentEmail = normalizeEmailForComparison(currentAuthProfile.email);
  const ownerMatches =
    (ownerEmail && currentEmail && ownerEmail === currentEmail) ||
    (!ownerEmail && !currentEmail && ownerUserId && currentUserId && ownerUserId === currentUserId);
  if (!ownerMatches) {
    return false;
  }

  return Boolean(binding.boundDeviceId && binding.boundDeviceId !== (currentDeviceId ?? '').trim());
};

const buildAuthContentMatchKey = (value: string): string => {
  let hashA = 0x811c9dc5;
  let hashB = 0x27d4eb2d;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    hashA ^= code;
    hashA = Math.imul(hashA, 0x01000193);
    hashB = Math.imul(hashB ^ code, 0x85ebca6b);
  }
  return `${value.length}:${(hashA >>> 0).toString(36)}:${(hashB >>> 0).toString(36)}`;
};

const buildProfileSnapshotMatchKeys = (
  snapshot:
    | Pick<LicenseHostRemoteProfileSnapshot, 'name' | 'workspaceName' | 'notes' | 'planType' | 'transferPackage'>
    | Pick<CloudProfileBackup, 'sourceProfileName' | 'sourceWorkspaceName' | 'note' | 'sourcePlanType' | 'transferPackage'>,
): string[] => {
  const keys: string[] = [];
  const authJsonContent = snapshot.transferPackage?.authJsonContent?.trim() ?? '';
  if (authJsonContent) {
    keys.push(`auth:${buildAuthContentMatchKey(authJsonContent)}`);
  }

  const profileName = 'name' in snapshot ? snapshot.name.trim() : snapshot.sourceProfileName.trim();
  const workspaceName =
    'workspaceName' in snapshot
      ? snapshot.workspaceName?.trim() ?? ''
      : snapshot.sourceWorkspaceName?.trim() ?? '';
  const notes =
    'notes' in snapshot ? snapshot.notes.trim() : snapshot.note?.trim() ?? '';
  const planType =
    'planType' in snapshot ? snapshot.planType.trim() : snapshot.sourcePlanType.trim();
  if (profileName || workspaceName || notes) {
    keys.push(`meta:${profileName}::${workspaceName}::${notes}::${planType}`);
  }

  return keys;
};

const convertCloudBackupToRemoteSnapshot = (
  entry: CloudProfileBackup,
): LicenseHostRemoteProfileSnapshot => ({
  id: entry.id,
  name: entry.sourceProfileName,
  workspaceName: entry.sourceWorkspaceName,
  notes: entry.note ?? '',
  planType: entry.sourcePlanType,
  manuallyDisabled: entry.transferPackage.manuallyDisabled,
  isActive: false,
  authStatus: 'ok',
  overallRemainingPercent:
    [
      entry.transferPackage.weeklyQuota,
      entry.transferPackage.fiveHourQuota,
      entry.transferPackage.reviewQuota,
    ]
      .map((quota) => {
        if (!quota.configured && quota.source === 'unknown') {
          return null;
        }
        return Math.max(0, 100 - quota.percentUsed);
      })
      .filter((value): value is number => value !== null)
      .sort((left, right) => left - right)[0] ?? null,
  weeklyQuota: entry.transferPackage.weeklyQuota,
  fiveHourQuota: entry.transferPackage.fiveHourQuota,
  reviewQuota: entry.transferPackage.reviewQuota,
  createdAt: entry.transferPackage.exportedAt,
  updatedAt: entry.updatedAt,
  usageUpdatedAt: null,
  usageError: null,
  lastActivatedAt: null,
  securityBinding: null,
  transferPackage: entry.transferPackage,
});

const normalizeFsPathForComparison = (value?: string | null): string => {
  let normalized = (value ?? '').trim().replace(/\//g, '\\');
  while (normalized.length > 3 && normalized.endsWith('\\')) {
    normalized = normalized.slice(0, -1);
  }
  return normalized.toLowerCase();
};

const isFsPathInsideRoot = (filePath?: string | null, rootDir?: string | null): boolean => {
  const normalizedPath = normalizeFsPathForComparison(filePath);
  const normalizedRoot = normalizeFsPathForComparison(rootDir);
  if (!normalizedPath || !normalizedRoot) {
    return false;
  }
  return normalizedPath === normalizedRoot || normalizedPath.startsWith(`${normalizedRoot}\\`);
};

const persistStoredAvatar = (
  profile: Pick<UserProfile, 'id' | 'email'> | null | undefined,
  avatarDataUrl: string,
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const storageKey = getAvatarStorageKey(profile);
  if (!storageKey) {
    return;
  }

  window.localStorage.setItem(storageKey, avatarDataUrl);
};

const loadUserCenterBrandFrameEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.localStorage.getItem(LOCAL_USER_CENTER_BRAND_FRAME_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const persistUserCenterBrandFrameEnabled = (enabled: boolean): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(LOCAL_USER_CENTER_BRAND_FRAME_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Ignore local-only preference write failures and keep the UI functional.
  }
};

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('读取头像文件失败。'));
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('头像文件格式不受支持。'));
    };
    reader.readAsDataURL(file);
  });

const loadImageElement = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('头像图片加载失败。'));
    image.src = src;
  });

const buildAvatarRenderMetrics = (
  sourceWidth: number,
  sourceHeight: number,
  scale: number,
): AvatarRenderMetrics => {
  const fitScale = Math.min(AVATAR_OUTPUT_SIZE / sourceWidth, AVATAR_OUTPUT_SIZE / sourceHeight);
  const renderWidth = Math.max(1, Math.round(sourceWidth * fitScale * scale));
  const renderHeight = Math.max(1, Math.round(sourceHeight * fitScale * scale));
  return {
    renderWidth,
    renderHeight,
    maxOffsetX: Math.max(0, Math.round(Math.abs(renderWidth - AVATAR_OUTPUT_SIZE) / 2)),
    maxOffsetY: Math.max(0, Math.round(Math.abs(renderHeight - AVATAR_OUTPUT_SIZE) / 2)),
  };
};

const clampAvatarUploadDraft = (draft: AvatarUploadDraft): AvatarUploadDraft => {
  const nextScale = clampNumber(draft.scale, draft.minScale, draft.maxScale);
  const metrics = buildAvatarRenderMetrics(draft.sourceWidth, draft.sourceHeight, nextScale);
  return {
    ...draft,
    scale: nextScale,
    offsetX: clampNumber(draft.offsetX, -metrics.maxOffsetX, metrics.maxOffsetX),
    offsetY: clampNumber(draft.offsetY, -metrics.maxOffsetY, metrics.maxOffsetY),
  };
};

const buildAvatarDataUrl = async (draft: AvatarUploadDraft): Promise<string> => {
  const image = await loadImageElement(draft.sourceDataUrl);
  const normalizedDraft = clampAvatarUploadDraft(draft);
  const metrics = buildAvatarRenderMetrics(
    normalizedDraft.sourceWidth,
    normalizedDraft.sourceHeight,
    normalizedDraft.scale,
  );
  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_OUTPUT_SIZE;
  canvas.height = AVATAR_OUTPUT_SIZE;
  const context = canvas.getContext('2d');
  if (!context) {
    return normalizedDraft.sourceDataUrl;
  }

  context.clearRect(0, 0, AVATAR_OUTPUT_SIZE, AVATAR_OUTPUT_SIZE);
  const drawX = Math.round((AVATAR_OUTPUT_SIZE - metrics.renderWidth) / 2 + normalizedDraft.offsetX);
  const drawY = Math.round((AVATAR_OUTPUT_SIZE - metrics.renderHeight) / 2 + normalizedDraft.offsetY);
  context.drawImage(
    image,
    drawX,
    drawY,
    metrics.renderWidth,
    metrics.renderHeight,
  );
  return canvas.toDataURL('image/png');
};

const prepareAvatarUploadDraft = async (file: File): Promise<AvatarUploadDraft> => {
  if (!file.type.startsWith('image/')) {
    throw new Error('请选择图片格式的头像文件。');
  }

  if (file.size > MAX_AVATAR_FILE_SIZE) {
    throw new Error('头像图片不能超过 5MB。');
  }

  const sourceDataUrl = await readFileAsDataUrl(file);
  const image = await loadImageElement(sourceDataUrl);
  return {
    fileName: file.name,
    fileSize: file.size,
    sourceWidth: image.naturalWidth,
    sourceHeight: image.naturalHeight,
    sourceType: file.type || 'image/*',
    sourceDataUrl,
    scale: 1,
    offsetX: 0,
    offsetY: 0,
    minScale: 1,
    maxScale: 3,
  };
};

const formatAvatarFileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${bytes} B`;
};

const loadLocalSimulationSettings = (): {
  enabled: boolean;
  cardCount: number;
  planTypes: SimulationPlanType[];
} => {
  if (typeof window === 'undefined') {
    return {
      enabled: DEFAULT_LOCAL_SIMULATION_SETTINGS.enabled,
      cardCount: DEFAULT_LOCAL_SIMULATION_SETTINGS.cardCount,
      planTypes: [...DEFAULT_LOCAL_SIMULATION_SETTINGS.planTypes],
    };
  }

  try {
    const raw = window.localStorage.getItem(LOCAL_SPECIAL_FEATURE_SETTINGS_STORAGE_KEY);
    if (!raw) {
      return {
        enabled: DEFAULT_LOCAL_SIMULATION_SETTINGS.enabled,
        cardCount: DEFAULT_LOCAL_SIMULATION_SETTINGS.cardCount,
        planTypes: [...DEFAULT_LOCAL_SIMULATION_SETTINGS.planTypes],
      };
    }

    const parsed = JSON.parse(raw) as Partial<{
      enabled: boolean;
      cardCount: number;
      planTypes: string[];
    }>;
    const planTypes = Array.isArray(parsed.planTypes)
      ? LOCAL_SIMULATION_PLAN_TYPES.filter((planType) => parsed.planTypes?.some((item) => item === planType))
      : [...DEFAULT_LOCAL_SIMULATION_SETTINGS.planTypes];

    return {
      enabled: parsed.enabled === true,
      cardCount: clampSimulationCardCount(parsed.cardCount ?? DEFAULT_LOCAL_SIMULATION_SETTINGS.cardCount),
      planTypes:
        planTypes.length > 0 || Array.isArray(parsed.planTypes)
          ? planTypes.filter(isSimulationPlanType)
          : [...DEFAULT_LOCAL_SIMULATION_SETTINGS.planTypes],
    };
  } catch {
    return {
      enabled: DEFAULT_LOCAL_SIMULATION_SETTINGS.enabled,
      cardCount: DEFAULT_LOCAL_SIMULATION_SETTINGS.cardCount,
      planTypes: [...DEFAULT_LOCAL_SIMULATION_SETTINGS.planTypes],
    };
  }
};

const persistLocalSimulationSettings = (settings: {
  enabled: boolean;
  cardCount: number;
  planTypes: SimulationPlanType[];
}): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      LOCAL_SPECIAL_FEATURE_SETTINGS_STORAGE_KEY,
      JSON.stringify({
        enabled: settings.enabled,
        cardCount: clampSimulationCardCount(settings.cardCount),
        planTypes: LOCAL_SIMULATION_PLAN_TYPES.filter((planType) => settings.planTypes.includes(planType)),
      }),
    );
  } catch {
    // Ignore local-only storage failures and keep the UI functional.
  }
};

const toggleSimulationPlanType = (
  current: SimulationPlanType[],
  target: SimulationPlanType,
): SimulationPlanType[] => {
  const nextSet = new Set(current);
  if (nextSet.has(target)) {
    nextSet.delete(target);
  } else {
    nextSet.add(target);
  }

  return LOCAL_SIMULATION_PLAN_TYPES.filter((planType) => nextSet.has(planType));
};

const clampSimulationCardCount = (value: number): number =>
  Math.max(1, Math.min(MAX_LOCAL_SIMULATION_CARD_COUNT, Math.round(value)));

const clampNumber = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const getRandomInt = (min: number, max: number): number =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const randomFutureIso = (minSeconds: number, maxSeconds: number): string =>
  new Date(Date.now() + getRandomInt(minSeconds, maxSeconds) * 1000).toISOString();

const randomPastIso = (minSeconds: number, maxSeconds: number): string =>
  new Date(Date.now() - getRandomInt(minSeconds, maxSeconds) * 1000).toISOString();

const getRollingResetAt = (
  baseResetAtMs: number,
  cycleSeconds: number,
  nowMs: number,
): string => {
  const cycleMs = cycleSeconds * 1000;
  if (baseResetAtMs > nowMs) {
    return new Date(baseResetAtMs).toISOString();
  }

  const cyclesPassed = Math.floor((nowMs - baseResetAtMs) / cycleMs) + 1;
  return new Date(baseResetAtMs + cyclesPassed * cycleMs).toISOString();
};

const buildSimulatedQuota = (
  baseResetAtMs: number,
  cycleSeconds: number,
  nowMs: number,
): UsageQuota => ({
  percentUsed: 0,
  resetAt: getRollingResetAt(baseResetAtMs, cycleSeconds, nowMs),
  source: 'live',
  label: null,
  configured: true,
});

const buildSimulatedProfileSeeds = (
  count: number,
  selectedPlanTypes: SimulationPlanType[],
): SimulatedProfileSeed[] => {
  if (selectedPlanTypes.length === 0) {
    return [];
  }

  return Array.from({ length: clampSimulationCardCount(count) }, (_, index) => {
    const prefix = LOCAL_SIMULATION_PREFIXES[index % LOCAL_SIMULATION_PREFIXES.length];
    const suffix = String((index * 17 + getRandomInt(11, 96)) % 100).padStart(2, '0');
    const name = `${prefix}${suffix}`;
    const simulatedPlanType = selectedPlanTypes[index % selectedPlanTypes.length];
    const lastActivatedAt = randomPastIso(15 * 60, 8 * 24 * 60 * 60);
    const updatedAt = randomPastIso(5 * 60, 5 * 24 * 60 * 60);
    const usageUpdatedAt = randomPastIso(60, 3 * 60 * 60);
    const createdAt = randomPastIso(20 * 24 * 60 * 60, 120 * 24 * 60 * 60);

    return {
      id: `sim:${name}:${index}`,
      storageDirName: `sim-${name}-${index + 1}`,
      name,
      workspaceName: name,
      notes: '',
      planType: LOCAL_SIMULATION_PLAN_TYPE_MAP[simulatedPlanType],
      simulatedPlanType,
      authFilePath: `C:\\simulated\\${name}\\auth.json`,
      createdAt,
      updatedAt,
      usageUpdatedAt,
      lastActivatedAt,
      weeklyBaseResetAtMs: new Date(randomFutureIso(30 * 60, 7 * 24 * 60 * 60)).getTime(),
      weeklyCycleSeconds: 7 * 24 * 60 * 60,
      fiveHourBaseResetAtMs: new Date(randomFutureIso(8 * 60, 5 * 60 * 60)).getTime(),
      fiveHourCycleSeconds: 5 * 60 * 60,
      reviewBaseResetAtMs: new Date(randomFutureIso(2 * 60 * 60, 6 * 24 * 60 * 60)).getTime(),
      reviewCycleSeconds: 7 * 24 * 60 * 60,
    };
  });
};

const materializeSimulatedProfiles = (
  seeds: SimulatedProfileSeed[],
  nowMs: number,
): DisplayProfile[] =>
  seeds.map((seed) => ({
    id: seed.id,
    storageDirName: seed.storageDirName,
    name: seed.name,
    workspaceName: seed.workspaceName,
    notes: seed.notes,
    planType: seed.planType,
    manuallyDisabled: false,
    simulatedPlanType: seed.simulatedPlanType,
    authFilePath: seed.authFilePath,
    weeklyQuota: buildSimulatedQuota(seed.weeklyBaseResetAtMs, seed.weeklyCycleSeconds, nowMs),
    fiveHourQuota: buildSimulatedQuota(seed.fiveHourBaseResetAtMs, seed.fiveHourCycleSeconds, nowMs),
    reviewQuota: buildSimulatedQuota(seed.reviewBaseResetAtMs, seed.reviewCycleSeconds, nowMs),
    createdAt: seed.createdAt,
    updatedAt: seed.updatedAt,
    usageUpdatedAt: seed.usageUpdatedAt,
    usageError: null,
    lastActivatedAt: seed.lastActivatedAt,
    authStatus: 'ok',
    isActive: false,
  }));

const isSimulatedProfile = (profile: Pick<ProfileSummary, 'id'>): boolean =>
  isSimulatedProfileId(profile.id);

const getQuotaAlertSuggestion = (
  profiles: ProfileSummary[],
  threshold: number,
  enabled: boolean,
): QuotaAlertSuggestion | null => {
  if (!enabled) {
    return null;
  }

  const activeProfile = profiles.find((profile) => profile.isActive);
  if (!activeProfile) {
    return null;
  }

  const weeklyRemaining = getRemainingPercentOrNull(activeProfile.weeklyQuota);
  const fiveHourRemaining = getRemainingPercentOrNull(activeProfile.fiveHourQuota);
  const breachedLabels = [
    weeklyRemaining !== null && weeklyRemaining <= threshold ? '周额度' : null,
    fiveHourRemaining !== null && fiveHourRemaining <= threshold ? '5 小时额度' : null,
  ].filter((value): value is string => Boolean(value));

  if (breachedLabels.length === 0) {
    return null;
  }

  const candidateProfiles = [...profiles]
    .filter((profile) => profile.id !== activeProfile.id)
    .filter((profile) => Number.isFinite(getRecommendationScore(profile)))
    .sort((left, right) => getRecommendationScore(right) - getRecommendationScore(left));
  const strictRecommendedProfile =
    candidateProfiles.find((profile) => {
      const weeklyRemaining = getRemainingPercentOrNull(profile.weeklyQuota);
      const fiveHourRemaining = getRemainingPercentOrNull(profile.fiveHourQuota);

      if (
        breachedLabels.includes('周额度') &&
        weeklyRemaining !== null &&
        weeklyRemaining <= threshold
      ) {
        return false;
      }

      if (
        breachedLabels.includes('5 小时额度') &&
        fiveHourRemaining !== null &&
        fiveHourRemaining <= threshold
      ) {
        return false;
      }

      return true;
    }) ?? null;
  const fallbackRecommendedProfile = candidateProfiles[0] ?? null;
  const activeScore = getRecommendationScore(activeProfile, { allowActive: true });
  const recommendedProfile =
    strictRecommendedProfile ??
    (fallbackRecommendedProfile && getRecommendationScore(fallbackRecommendedProfile) > activeScore
      ? fallbackRecommendedProfile
      : null);

  if (!recommendedProfile || !Number.isFinite(getRecommendationScore(recommendedProfile))) {
    return null;
  }

  return {
    activeProfile,
    recommendedProfile,
    threshold,
    breachedLabels,
    warningKey: [
      activeProfile.id,
      recommendedProfile.id,
      threshold,
      breachedLabels.join('|'),
      weeklyRemaining ?? 'na',
      fiveHourRemaining ?? 'na',
    ].join(':'),
  };
};

export default function App() {
  if (IS_TRAY_CARD_VIEW) {
    return <TrayHoverCardApp />;
  }

  if (IS_FLOATING_WIDGET_VIEW) {
    return <FloatingWidgetApp />;
  }

  if (IS_LICENSE_HOST_VIEW) {
    return <LicenseHostView />;
  }

  if (!hasDesktopClientRuntimeBridge()) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          padding: '32px',
          background:
            'radial-gradient(circle at top, rgba(39, 72, 132, 0.42), transparent 42%), #060912',
          color: '#eef4ff',
        }}
      >
        <section
          style={{
            width: 'min(640px, calc(100vw - 48px))',
            borderRadius: '28px',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            background: 'rgba(10, 16, 29, 0.86)',
            boxShadow: '0 28px 80px rgba(2, 8, 18, 0.48)',
            backdropFilter: 'blur(24px)',
            padding: '28px 30px',
          }}
        >
          <p
            style={{
              margin: '0 0 10px',
              fontSize: '12px',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: '#8fb1ff',
            }}
          >
            Desktop Guard
          </p>
          <h1 style={{ margin: '0 0 12px', fontSize: '32px', lineHeight: 1.15 }}>
            当前打开的是开发网页，不是真实客户端
          </h1>
          <p style={{ margin: '0 0 12px', lineHeight: 1.7, color: 'rgba(234, 242, 255, 0.78)' }}>
            这个网页不会接入 Electron 本地桥，所以会显示成空白未授权，也不会把你本机已有的登录状态、授权状态和
            profiles 数据读出来。
          </p>
          <p style={{ margin: '0 0 12px', lineHeight: 1.7, color: 'rgba(234, 242, 255, 0.78)' }}>
            真实数据仍然保存在本机。请关闭当前浏览器页，改用项目根目录里的 <code>start-app.bat</code>{' '}
            打开桌面客户端；主机端请改用 <code>start-license-host.bat</code>。
          </p>
          <p style={{ margin: 0, lineHeight: 1.7, color: 'rgba(234, 242, 255, 0.62)' }}>
            如果你看到地址栏是 <code>http://127.0.0.1:5173</code>，那只是本地渲染调试页，不是桌面客户端窗口。
          </p>
        </section>
      </div>
    );
  }

  const isLocalSimulationAvailable = import.meta.env.DEV;
  const initialLocalSimulationSettings = loadLocalSimulationSettings();
  const [state, setState] = useState<ProfilesState>(createEmptyState);
  const [mainView, setMainView] = useState<MainWorkspaceView>('profiles');
  const [authCredentialMode, setAuthCredentialMode] = useState<AuthCredentialMode>('oauth');
  const [oauthModeConfirmOpen, setOauthModeConfirmOpen] = useState(false);
  const [restartAfterOauthSwitch, setRestartAfterOauthSwitch] = useState(false);
  const [statisticsRange, setStatisticsRange] = useState<StatisticsRange>('today');
  const [statisticsMode, setStatisticsMode] = useState<SwitchMode>('codex');
  const [statisticsDashboard, setStatisticsDashboard] = useState<StatisticsDashboard | null>(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [statisticsError, setStatisticsError] = useState<string | null>(null);
  const [statisticsRefreshSource, setStatisticsRefreshSource] =
    useState<StatisticsRefreshSource | null>(null);
  const [busyAction, setBusyAction] = useState<BusyAction>('initial');
  const [profileAvailabilityBusy, setProfileAvailabilityBusy] = useState(false);
  const [batchActionBusy, setBatchActionBusy] = useState(false);
  const [recognizedDisabledActionBusy, setRecognizedDisabledActionBusy] = useState<
    'enable' | 'disable' | null
  >(null);
  const [updateBusyAction, setUpdateBusyAction] = useState<'check' | 'install' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [updateState, setUpdateState] = useState<UpdateState>(createEmptyUpdateState);
  const [authState, setAuthState] = useState<AuthState>(createEmptyAuthState);
  const [authBusyAction, setAuthBusyAction] = useState<
    | 'login'
    | 'register'
    | 'forgot'
    | 'logout'
    | 'feedback'
    | 'activation'
    | 'reward'
    | 'request-invite'
    | 'claim-reward'
    | null
  >(null);
  const [authMode, setAuthMode] = useState<AuthFormMode>('login');
  const [authEntryMode, setAuthEntryMode] = useState<AuthGateEntryMode>('manual');
  const [authForm, setAuthForm] = useState<UserCenterAuthForm>(() => createInitialAuthForm(authState));
  const [authOtpBusyPurpose, setAuthOtpBusyPurpose] = useState<AuthOtpPurpose | null>(null);
  const [authOtpCooldowns, setAuthOtpCooldowns] = useState<Record<AuthOtpPurpose, number>>(
    () => createInitialAuthOtpCooldowns(),
  );
  const [authOtpNowMs, setAuthOtpNowMs] = useState(() => Date.now());
  const [authGateActivationCode, setAuthGateActivationCode] = useState('');
  const [authGateRewardCode, setAuthGateRewardCode] = useState('');
  const [pendingProfileTransferPrompt, setPendingProfileTransferPrompt] = useState<
    AuthState['pendingProfileTransfers'][number] | null
  >(null);
  const [pendingProfileTransferBusy, setPendingProfileTransferBusy] = useState(false);
  const [pendingTransferSelectionIds, setPendingTransferSelectionIds] = useState<string[]>([]);
  const [pendingCloudBackupPrompt, setPendingCloudBackupPrompt] = useState<
    AuthState['cloudProfileBackups'][number] | null
  >(null);
  const [newDeviceCloudRestorePrompt, setNewDeviceCloudRestorePrompt] =
    useState<NewDeviceCloudRestorePromptState | null>(null);
  const [pendingCloudBackupBusy, setPendingCloudBackupBusy] = useState(false);
  const [dismissedCloudBackupIds, setDismissedCloudBackupIds] = useState<string[]>([]);
  const [, setCloudBackupAutoPromptConsumed] = useState(false);
  const [cloudSyncDialog, setCloudSyncDialog] = useState<CloudSyncDialogState | null>(null);
  const [localRemoteProfileSnapshots, setLocalRemoteProfileSnapshots] = useState<
    LicenseHostRemoteProfileSnapshot[]
  >([]);
  const [, setLocalRemoteProfileSnapshotsBusy] = useState(false);
  const [cloudSyncSelectedLocalIds, setCloudSyncSelectedLocalIds] = useState<string[]>([]);
  const [cloudSyncSelectedCloudIds, setCloudSyncSelectedCloudIds] = useState<string[]>([]);
  const [cloudBackupCenterOpen, setCloudBackupCenterOpen] = useState(false);
  const [cloudBackupCenterView, setCloudBackupCenterView] = useState<CloudBackupCenterView>('cloud');
  const [cloudBackupSyncBusyProfileId, setCloudBackupSyncBusyProfileId] = useState<string | null>(null);
  const [cloudSyncVisualProgress, setCloudSyncVisualProgress] = useState<CloudSyncVisualProgressState | null>(null);
  const [cloudDownloadPlaceholders, setCloudDownloadPlaceholders] = useState<
    CloudDownloadPlaceholderCardState[]
  >([]);
  const [localImportPlaceholders, setLocalImportPlaceholders] = useState<LocalImportPlaceholderCardState[]>(
    [],
  );
  const [currentAuthImportPlaceholder, setCurrentAuthImportPlaceholder] =
    useState<CurrentAuthImportPlaceholderCardState | null>(null);
  const [spacePoolDragActive, setSpacePoolDragActive] = useState(false);
  const [spacePoolImportOverlayStep, setSpacePoolImportOverlayStep] =
    useState<SpacePoolImportOverlayStep | null>(null);
  const [spacePoolImportOverlayBounds, setSpacePoolImportOverlayBounds] = useState<CSSProperties | null>(
    null,
  );
  const [manualImportTextDraft, setManualImportTextDraft] = useState('');
  const [manualImportTextBusy, setManualImportTextBusy] = useState(false);
  const [manualImportTextError, setManualImportTextError] = useState<string | null>(null);
  const [legacyCloudBackupReminderOpen, setLegacyCloudBackupReminderOpen] = useState(false);
  const legacyCloudBackupReminderShownRef = useRef<Set<string>>(new Set());
  const cloudSyncAutoPromptKeyRef = useRef<string | null>(null);
  const cloudBackupAutoSyncKeyRef = useRef<string | null>(null);
  const cloudDownloadPlaceholderCleanupTimerRef = useRef<number | null>(null);
  const localImportPlaceholderCleanupTimerRef = useRef<number | null>(null);
  const currentAuthImportPlaceholderCleanupTimerRef = useRef<number | null>(null);
  const cloudSyncVisualProgressTimerRef = useRef<number | null>(null);
  const spacePoolDragDepthRef = useRef(0);
  const shellMainRef = useRef<HTMLElement | null>(null);
  const accountUnlockPromptKeyRef = useRef<string | null>(null);
  const cloudSyncAutomationSettledUntilRef = useRef(0);
  const [secondaryPasswordDialog, setSecondaryPasswordDialog] =
    useState<SecondaryPasswordDialogState | null>(null);
  const [secondaryPasswordCurrentDraft, setSecondaryPasswordCurrentDraft] = useState('');
  const [secondaryPasswordNextDraft, setSecondaryPasswordNextDraft] = useState('');
  const [secondaryPasswordConfirmDraft, setSecondaryPasswordConfirmDraft] = useState('');
  const [secondaryPasswordVerifyDraft, setSecondaryPasswordVerifyDraft] = useState('');
  const [secondaryPasswordEmailOtpDraft, setSecondaryPasswordEmailOtpDraft] = useState('');
  const [secondaryPasswordDialogBusy, setSecondaryPasswordDialogBusy] = useState(false);
  const [secondaryPasswordDialogError, setSecondaryPasswordDialogError] = useState<string | null>(
    null,
  );
  const [feedbackCenterOpen, setFeedbackCenterOpen] = useState(false);
  const [feedbackDraft, setFeedbackDraft] = useState('');
  const [legacyFeedbackReadFallback, setLegacyFeedbackReadFallback] = useState<
    Record<string, string>
  >({});
  const [optimisticFeedbackMessages, setOptimisticFeedbackMessages] = useState<
    OptimisticFeedbackMessage[]
  >([]);
  const feedbackNotificationAudioContextRef = useRef<AudioContext | null>(null);
  const feedbackObservedHostReplyIdsRef = useRef<Set<string>>(new Set());
  const feedbackObservedHostReplyInitializedRef = useRef(false);
  const feedbackSeenHostReplyIdsRef = useRef<Set<string>>(new Set());
  const feedbackHostReplyInitializedRef = useRef(false);
  const feedbackReadSyncingThreadIdRef = useRef<string | null>(null);
  const [feedbackHeaderUnreadVisible, setFeedbackHeaderUnreadVisible] = useState(false);
  const feedbackMessageListRef = useRef<HTMLDivElement | null>(null);
  const [authGateNowMs, setAuthGateNowMs] = useState(() => Date.now());
  const [authGateStandaloneMode, setAuthGateStandaloneMode] = useState<'preview' | 'interactive' | null>(null);
  const [scanTicket, setScanTicket] = useState<ScanTicket | null>(null);
  const [scanCodeUrl, setScanCodeUrl] = useState<string | null>(null);
  const [scanCodeChannel, setScanCodeChannel] = useState<ScanChannel | null>(null);
  const [scanChannel, setScanChannel] = useState<ScanChannel>('mini_program');
  const [scanBusy, setScanBusy] = useState<'create' | 'consume' | null>(null);
  const [scanFlowState, setScanFlowState] = useState<ScanFlowState>('waiting_scan');
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanPendingBinding, setScanPendingBinding] = useState<PendingScanBindingContext | null>(null);
  const [scanRescanConfirmOpen, setScanRescanConfirmOpen] = useState(false);
  const [emailBindingReminderOpen, setEmailBindingReminderOpen] = useState(false);
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const mandatoryUpdateLockActive = updateState.blockedByMandatory;

  useEffect(() => {
    if (!spacePoolImportOverlayStep && !spacePoolDragActive) {
      setSpacePoolImportOverlayBounds(null);
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const updateOverlayBounds = () => {
      const nextHost = shellMainRef.current;
      if (!nextHost) {
        return;
      }

      const rect = nextHost.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return;
      }

      setSpacePoolImportOverlayBounds({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    };

    updateOverlayBounds();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' && shellMainRef.current
        ? new ResizeObserver(() => {
            updateOverlayBounds();
          })
        : null;

    if (resizeObserver && shellMainRef.current) {
      resizeObserver.observe(shellMainRef.current);
    }

    window.addEventListener('resize', updateOverlayBounds);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateOverlayBounds);
    };
  }, [spacePoolDragActive, spacePoolImportOverlayStep]);
  const [userCenterOpen, setUserCenterOpen] = useState(false);
  const [licenseCenterOpen, setLicenseCenterOpen] = useState(false);
  const [footerHelpOpen, setFooterHelpOpen] = useState(false);
  const [authGateQrPreviewState, setAuthGateQrPreviewState] = useState<QrPreviewState | null>(null);
  const scanConsumedTicketIdRef = useRef<string | null>(null);
  const pendingTransferIdsRef = useRef<string[]>([]);
  const emailBindingReminderProfileIdRef = useRef<string | null>(null);
  const emailBindingReminderNextAtRef = useRef<number | null>(null);
  const [avatarImageSrc, setAvatarImageSrc] = useState<string | null>(null);
  const [userCenterBrandFrameEnabled, setUserCenterBrandFrameEnabled] = useState(() =>
    loadUserCenterBrandFrameEnabled(),
  );
  const [codexAutoRestartDraft, setCodexAutoRestartDraft] = useState<boolean | null>(null);
  const [traeAutoRestartDraft, setTraeAutoRestartDraft] = useState<boolean | null>(null);
  const [trayHoverCardEnabledDraft, setTrayHoverCardEnabledDraft] = useState<boolean | null>(null);
  const [floatingWidgetEnabledDraft, setFloatingWidgetEnabledDraft] = useState<boolean | null>(null);
  const [floatingWidgetOpacityDraft, setFloatingWidgetOpacityDraft] = useState<number | null>(null);
  const [cardFlipVolumeDraft, setCardFlipVolumeDraft] = useState<number | null>(null);
  const [avatarUploadOpen, setAvatarUploadOpen] = useState(false);
  const [avatarUploadDraft, setAvatarUploadDraft] = useState<AvatarUploadDraft | null>(null);
  const [avatarAdjustOpen, setAvatarAdjustOpen] = useState(false);
  const [avatarAdjustDraft, setAvatarAdjustDraft] = useState<AvatarUploadDraft | null>(null);
  const [avatarUploadBusy, setAvatarUploadBusy] = useState(false);
  const [avatarUploadDragging, setAvatarUploadDragging] = useState(false);
  const [avatarUploadError, setAvatarUploadError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [planFilter, setPlanFilter] = useState<PlanFilterValue>('ALL');
  const [forceEnableProbeCards, setForceEnableProbeCards] = useState(false);
  const [menuProfileId, setMenuProfileId] = useState<string | null>(null);
  const [switchTarget, setSwitchTarget] = useState<ProfileSummary | null>(null);
  const [switchOpenClawModel, setSwitchOpenClawModel] = useState('');
  const [switchProgressRunId, setSwitchProgressRunId] = useState<string | null>(null);
  const [switchProgressEvents, setSwitchProgressEvents] = useState<SwitchProgressEvent[]>([]);
  const [switchSuccessNotice, setSwitchSuccessNotice] = useState<SwitchSuccessNotice | null>(null);
  const [switchRuntimeOverlay, setSwitchRuntimeOverlay] = useState<SwitchRuntimeOverlayState | null>(null);
  const [cardSwitchInteraction, setCardSwitchInteraction] = useState<CardSwitchInteractionState | null>(
    null,
  );
  const [cardSwitchWaveToken, setCardSwitchWaveToken] = useState(0);
  const [cardSwitchSweepState, setCardSwitchSweepState] = useState<CardSwitchSweepState | null>(null);
  const [quotaAlertTarget, setQuotaAlertTarget] = useState<QuotaAlertSuggestion | null>(null);
  const [dismissedQuotaAlertKey, setDismissedQuotaAlertKey] = useState<string | null>(null);
  const [autoSwitchPrompt, setAutoSwitchPrompt] = useState<AutoSwitchPromptState | null>(null);
  const [autoDisabledPrompt, setAutoDisabledPrompt] = useState<AutoDisabledPromptState | null>(null);
  const [queuedAutoDisabledPrompt, setQueuedAutoDisabledPrompt] =
    useState<AutoDisabledPromptState | null>(null);
  const [dismissedAutoSwitchKey, setDismissedAutoSwitchKey] = useState<string | null>(null);
  const [autoSwitchThresholdDraft, setAutoSwitchThresholdDraft] = useState('18');
  const [autoSwitchCountdownDraft, setAutoSwitchCountdownDraft] = useState('30');
  const [centerNotice, setCenterNotice] = useState<CenterNotice | null>(null);
  const [recentlyResetKeys, setRecentlyResetKeys] = useState<Record<string, boolean>>({});
  const [editTarget, setEditTarget] = useState<ProfileSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProfileSummary | null>(null);
  const [removeFilesOnDelete, setRemoveFilesOnDelete] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [issueTransferDialogOpen, setIssueTransferDialogOpen] = useState(false);
  const [issueTransferTargetAccount, setIssueTransferTargetAccount] = useState('');
  const [issueTransferNote, setIssueTransferNote] = useState('');
  const [issueTransferSelectedProfileIds, setIssueTransferSelectedProfileIds] = useState<string[]>([]);
  const [issueTransferBusy, setIssueTransferBusy] = useState(false);
  const [workspaceSelectionMode, setWorkspaceSelectionMode] = useState<WorkspaceSelectionMode | null>(
    null,
  );
  const [workspaceSelectionIds, setWorkspaceSelectionIds] = useState<string[]>([]);
  const [workspaceSendTargetAccount, setWorkspaceSendTargetAccount] = useState('');
  const [workspaceSendNote, setWorkspaceSendNote] = useState('');
  const [workspaceSelectionBusyAction, setWorkspaceSelectionBusyAction] =
    useState<WorkspaceSelectionBusyAction | null>(null);
  const [batchDeleteConfirmRequest, setBatchDeleteConfirmRequest] =
    useState<BatchDeleteConfirmRequest | null>(null);
  const [batchDeleteReturnToDialog, setBatchDeleteReturnToDialog] = useState(false);
  const [cloudDeleteSyncPrompt, setCloudDeleteSyncPrompt] = useState<{
    source: 'local-delete' | 'cloud-clear';
    deletedProfileIds: string[];
    deletedProfileNames: string[];
    remainingSnapshotIds: string[];
    remainingSnapshots: LicenseHostRemoteProfileSnapshot[];
  } | null>(null);
  const [quotaAlertSnoozeMenuOpen, setQuotaAlertSnoozeMenuOpen] = useState(false);
  const [quotaAlertSnoozeOption, setQuotaAlertSnoozeOption] = useState<'none' | '5m' | '15m' | '30m'>(
    'none',
  );
  const [quotaAlertSnoozeUntil, setQuotaAlertSnoozeUntil] = useState<number | null>(null);
  const [workspaceActionOverlayById, setWorkspaceActionOverlayById] = useState<
    Record<string, ProfileActionOverlayState | null>
  >({});
  const [workspaceRemovingProfileIds, setWorkspaceRemovingProfileIds] = useState<string[]>([]);
  const [addDialogInitialValues, setAddDialogInitialValues] =
    useState<Partial<ProfileFormValues> | null>(null);
  const [addDialogDiscoveryHint, setAddDialogDiscoveryHint] = useState(false);
  const [pendingAddOverwrite, setPendingAddOverwrite] =
    useState<PendingAddOverwriteState | null>(null);
  const [pendingLocalImportOverwrite, setPendingLocalImportOverwrite] =
    useState<PendingLocalImportOverwriteState | null>(null);

  const resetSpacePoolImportOverlay = () => {
    setSpacePoolImportOverlayStep(null);
    setManualImportTextDraft('');
    setManualImportTextError(null);
  };

  const closeSpacePoolImportOverlay = () => {
    if (manualImportTextBusy) {
      return;
    }

    resetSpacePoolImportOverlay();
  };

  const openDeleteConfirmDialog = (target: ProfileSummary) => {
    setRemoveFilesOnDelete(false);
    setDeleteTarget(target);
  };

  const openBatchDeleteConfirmDialog = (request: BatchDeleteConfirmRequest) => {
    if (typeof window === 'undefined') {
      setBatchDeleteConfirmRequest(request);
      return;
    }

    if (request.source === 'batch-dialog') {
      window.setTimeout(() => {
        setBatchDeleteConfirmRequest(request);
      }, 0);
      return;
    }

    setBatchDeleteConfirmRequest(request);
  };
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [batchNoteDialogOpen, setBatchNoteDialogOpen] = useState(false);
  const [batchActionMode, setBatchActionMode] = useState<BatchProfileActionMode>('notes');
  const [batchSelectionFilter, setBatchSelectionFilter] =
    useState<BatchProfileSelectionFilter>('all');
  const [batchNoteDraft, setBatchNoteDraft] = useState('');
  const [batchNoteSelectedProfileIds, setBatchNoteSelectedProfileIds] = useState<string[]>([]);
  const [unrecognizedAuthPrompted, setUnrecognizedAuthPrompted] = useState(false);
  const autoImportCurrentAuthKeyRef = useRef<string | null>(null);
  const [warningThresholdDraft, setWarningThresholdDraft] = useState('30');
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);
  const [windowState, setWindowState] = useState<WindowState>(() => ({
    maximized: false,
    visible: !hasDesktopClientRuntimeBridge(),
  }));
  const [viewportSize, setViewportSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  }));
  const [localSimulationEnabled, setLocalSimulationEnabled] = useState(
    initialLocalSimulationSettings.enabled,
  );
  const [localSimulationCardCount, setLocalSimulationCardCount] = useState(
    initialLocalSimulationSettings.cardCount,
  );
  const [localSimulationPlanTypes, setLocalSimulationPlanTypes] = useState<SimulationPlanType[]>(
    initialLocalSimulationSettings.planTypes,
  );
  const [simulationNowMs, setSimulationNowMs] = useState(() => Date.now());
  const [hiddenModeUnlocked, setHiddenModeUnlocked] = useState(false);
  const [hiddenModeStoredPassword, setHiddenModeStoredPassword] = useState(() =>
    isLocalSimulationAvailable ? loadHiddenModePassword() : '',
  );
  const [hiddenModeDialogOpen, setHiddenModeDialogOpen] = useState(false);
  const [hiddenModeDialogMode, setHiddenModeDialogMode] =
    useState<HiddenModeDialogMode>('unlock');
  const [hiddenModePasswordDraft, setHiddenModePasswordDraft] = useState('');
  const [hiddenModePasswordConfirmDraft, setHiddenModePasswordConfirmDraft] = useState('');
  const [hiddenModeDialogError, setHiddenModeDialogError] = useState<string | null>(null);
  const [uiInteractionTestEnabled, setUiInteractionTestEnabled] = useState(false);
  const [uiInteractionTestMode, setUiInteractionTestMode] = useState<UiInteractionTestMode>('idle');
  const [workspaceOAuthMonitor, setWorkspaceOAuthMonitor] = useState<WorkspaceOAuthLaunchResult | null>(
    null,
  );
  const [networkAccessState, setNetworkAccessState] = useState<NetworkAccessState | null>(null);
  const [startupProgressEvent, setStartupProgressEvent] = useState<StartupProgressEvent | null>(null);
  const [proxyReminder, setProxyReminder] = useState<ProxyReminderState | null>(null);
  const startupHandoffFromSplashRef = useRef(hasStartupHandoffFromSplash());
  const startupLoadingStartedAtRef = useRef<number | null>(
    hasDesktopClientRuntimeBridge() ? null : Date.now(),
  );
  const startupCardIntroPendingRef = useRef(false);
  const startupCardIntroPlayedRef = useRef(false);
  const startupCardIntroRevealTimerRef = useRef<number | null>(null);
  const startupCardIntroAudioContextRef = useRef<AudioContext | null>(null);
  const uiInteractionTestEnabledRef = useRef(false);
  const uiInteractionBaselineRef = useRef<UiInteractionBaselineState | null>(null);
  const uiInteractionRunTokenRef = useRef(0);
  const startupProxyCheckTriggeredRef = useRef(false);
  const cardFlipAudioArrayBufferRef = useRef<ArrayBuffer | null>(null);
  const cardFlipAudioArrayBufferPromiseRef = useRef<Promise<ArrayBuffer | null> | null>(null);
  const cardFlipAudioBufferRef = useRef<AudioBuffer | null>(null);
  const cardFlipNoiseBufferRef = useRef<AudioBuffer | null>(null);
  const previousProfilesRef = useRef<ProfileSummary[]>([]);
  const nextProfilesStateRequestIdRef = useRef(0);
  const appliedProfilesStateRequestIdRef = useRef(0);
  const statisticsRequestIdRef = useRef(0);
  const switchProgressRunIdRef = useRef<string | null>(null);
  const cardSwitchVisualTimerRef = useRef<number | null>(null);
  const cardSwitchSweepTimerRef = useRef<number | null>(null);
  const postSwitchDesktopLaunchTimerRef = useRef<number | null>(null);
  const codexAutoRestartRequestIdRef = useRef(0);
  const traeAutoRestartRequestIdRef = useRef(0);
  const trayHoverCardEnabledRequestIdRef = useRef(0);
  const floatingWidgetEnabledRequestIdRef = useRef(0);
  const floatingWidgetOpacityRequestIdRef = useRef(0);
  const floatingWidgetOpacityCommitTimerRef = useRef<number | null>(null);
  const cardFlipVolumeRequestIdRef = useRef(0);
  const cardFlipVolumeCommitTimerRef = useRef<number | null>(null);
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const [startupLoadingVisible, setStartupLoadingVisible] = useState(true);
  const [startupBackdropVisible, setStartupBackdropVisible] = useState(
    () => startupHandoffFromSplashRef.current,
  );
  const [startupLoadingHintVisible, setStartupLoadingHintVisible] = useState(false);
  const [startupCardIntroPhase, setStartupCardIntroPhase] =
    useState<StartupCardIntroPhase>('idle');
  const startupLoadingHintText = useMemo(() => {
    if (!startupLoadingHintVisible) {
      return null;
    }

    if (networkAccessState?.status === 'needs-proxy') {
      return '当前代理未连通，请先检查网络后再试。';
    }

    if (networkAccessState?.status === 'network-error') {
      return '当前代理或网络异常，请先检查连接。';
    }

    if (
      networkAccessState?.status === 'healthy' &&
      (startupProgressEvent?.phase === 'ready' || busyAction !== 'initial')
    ) {
      return '网络检查正常，正在进入，请稍后。';
    }

    if (startupProgressEvent?.detail) {
      return startupProgressEvent.detail;
    }

    if (busyAction === 'initial') {
      return '正在读取本机空间与授权状态。';
    }

    return '正在整理本机数据，准备进入界面。';
  }, [busyAction, networkAccessState, startupLoadingHintVisible, startupProgressEvent]);
  const proxyInlineAlertText = useMemo(() => {
    if (networkAccessState?.status === 'needs-proxy') {
      return '代理异常，建议开 TUN + 全局后刷新恢复';
    }

    if (networkAccessState?.status === 'network-error') {
      return '网络异常，建议检查代理后刷新恢复';
    }

    return null;
  }, [networkAccessState]);
  const clearCloudDownloadPlaceholderCleanupTimer = () => {
    if (cloudDownloadPlaceholderCleanupTimerRef.current !== null) {
      window.clearTimeout(cloudDownloadPlaceholderCleanupTimerRef.current);
      cloudDownloadPlaceholderCleanupTimerRef.current = null;
    }
  };
  const clearLocalImportPlaceholderCleanupTimer = () => {
    if (localImportPlaceholderCleanupTimerRef.current !== null) {
      window.clearTimeout(localImportPlaceholderCleanupTimerRef.current);
      localImportPlaceholderCleanupTimerRef.current = null;
    }
  };
  const clearCurrentAuthImportPlaceholderCleanupTimer = () => {
    if (currentAuthImportPlaceholderCleanupTimerRef.current !== null) {
      window.clearTimeout(currentAuthImportPlaceholderCleanupTimerRef.current);
      currentAuthImportPlaceholderCleanupTimerRef.current = null;
    }
  };
  const scheduleCloudDownloadPlaceholderCleanup = (delayMs: number) => {
    clearCloudDownloadPlaceholderCleanupTimer();
    cloudDownloadPlaceholderCleanupTimerRef.current = window.setTimeout(() => {
      setCloudDownloadPlaceholders([]);
      cloudDownloadPlaceholderCleanupTimerRef.current = null;
    }, delayMs);
  };
  const scheduleLocalImportPlaceholderCleanup = (delayMs: number) => {
    clearLocalImportPlaceholderCleanupTimer();
    localImportPlaceholderCleanupTimerRef.current = window.setTimeout(() => {
      setLocalImportPlaceholders([]);
      localImportPlaceholderCleanupTimerRef.current = null;
    }, delayMs);
  };
  const scheduleCurrentAuthImportPlaceholderCleanup = (delayMs: number) => {
    clearCurrentAuthImportPlaceholderCleanupTimer();
    currentAuthImportPlaceholderCleanupTimerRef.current = window.setTimeout(() => {
      setCurrentAuthImportPlaceholder(null);
      currentAuthImportPlaceholderCleanupTimerRef.current = null;
    }, delayMs);
  };
  const effectiveCodexAutoRestart =
    codexAutoRestartDraft ?? state.settings.codexAutoRestart;
  const effectiveTraeAutoRestart =
    traeAutoRestartDraft ?? state.settings.traeAutoRestart;
  const effectiveCodexTraeAutoRestart = effectiveCodexAutoRestart || effectiveTraeAutoRestart;
  const persistedCodexAutoRestart = state.settings.codexAutoRestart;
  const persistedTraeAutoRestart = state.settings.traeAutoRestart;
  const quotaAlertSnoozed =
    quotaAlertSnoozeUntil !== null && quotaAlertSnoozeUntil > authGateNowMs;
  const quotaAlertSnoozeLabel =
    QUOTA_ALERT_SNOOZE_OPTIONS.find((option) => option.key === quotaAlertSnoozeOption)?.label ?? '无';
  const effectiveTrayHoverCardEnabled =
    trayHoverCardEnabledDraft ?? state.settings.trayHoverCardEnabled;
  const effectiveFloatingWidgetEnabled =
    floatingWidgetEnabledDraft ?? state.settings.floatingWidgetEnabled;
  const effectiveFloatingWidgetOpacityPercent =
    floatingWidgetOpacityDraft ?? state.settings.floatingWidgetOpacityPercent;
  const effectiveCardFlipVolumePercent =
    cardFlipVolumeDraft ?? state.settings.cardFlipVolumePercent;
  const effectiveCardFlipVolumeScale = Math.max(0, Math.min(2, effectiveCardFlipVolumePercent / 50));
  const openClawSwitchModelOptions = useMemo(
    () =>
      buildOpenClawSwitchModelOptions(
        state.summary.openclawAvailableModels,
        state.summary.openclawCurrentModel,
      ),
    [state.summary.openclawAvailableModels, state.summary.openclawCurrentModel],
  );
  const suggestedAddInitialValues = useMemo<Partial<ProfileFormValues>>(() => {
    const shouldUseDetectedAuth = state.summary.currentAuthExists && !state.summary.authRecognized;
    const detectedName = shouldUseDetectedAuth ? buildUniqueDetectedProfileName(state.profiles, state.summary) : '';
    const detectedWorkspaceName = shouldUseDetectedAuth
      ? state.summary.currentAuthSuggestedWorkspaceName ?? getCurrentAuthSuggestedBaseName(state.summary)
      : '';
    const detectedNotes = shouldUseDetectedAuth
      ? formatDetectedAuthOwnershipNote(state.summary.currentAuthSuggestedEmail)
      : '';

    return {
      authSourcePath: shouldUseDetectedAuth ? state.summary.currentAuthPath : '',
      name: detectedName,
      workspaceName: detectedWorkspaceName,
      notes: detectedNotes,
      planType: shouldUseDetectedAuth ? state.summary.currentAuthSuggestedPlanType ?? 'Unknown' : 'Unknown',
    };
  }, [
    state.profiles,
    state.summary.authRecognized,
    state.summary.currentAuthExists,
    state.summary.currentAuthPath,
    state.summary.currentAuthSuggestedAccountId,
    state.summary.currentAuthSuggestedEmail,
    state.summary.currentAuthSuggestedName,
    state.summary.currentAuthSuggestedPlanType,
    state.summary.currentAuthSuggestedWorkspaceName,
  ]);
  const currentAuthAutoImportReady = useMemo(
    () => canAutoImportCurrentAuth(state.summary),
    [
      state.summary.authRecognized,
      state.summary.currentAuthExists,
      state.summary.currentAuthSuggestedAccountId,
      state.summary.currentAuthSuggestedEmail,
      state.summary.currentAuthSuggestedName,
      state.summary.currentAuthSuggestedPlanType,
      state.summary.currentAuthSuggestedWorkspaceName,
    ],
  );
  const currentAuthAutoImportKey = useMemo(() => {
    if (!currentAuthAutoImportReady) {
      return null;
    }

    return [
      state.summary.currentAuthHash ?? '',
      state.summary.currentAuthModifiedAt ?? '',
      state.summary.currentAuthSuggestedName ?? '',
      state.summary.currentAuthSuggestedPlanType ?? 'Unknown',
      state.summary.currentAuthSuggestedAccountId ?? '',
      state.summary.currentAuthSuggestedEmail ?? '',
    ].join('::');
  }, [
    currentAuthAutoImportReady,
    state.summary.currentAuthHash,
    state.summary.currentAuthModifiedAt,
    state.summary.currentAuthSuggestedAccountId,
    state.summary.currentAuthSuggestedEmail,
    state.summary.currentAuthSuggestedName,
    state.summary.currentAuthSuggestedPlanType,
  ]);

  const latestSwitchProgressEvent = useMemo(() => {
    if (switchProgressEvents.length === 0) {
      return null;
    }

    return switchProgressEvents[switchProgressEvents.length - 1] ?? null;
  }, [switchProgressEvents]);

  const switchRuntimeOverlayMessage = useMemo(() => {
    if (!switchRuntimeOverlay) {
      return '';
    }

    const currentEvent =
      findLatestSwitchProgressEvent(switchProgressEvents, 'active') ?? latestSwitchProgressEvent;
    return getSwitchRuntimeOverlayMessage(switchRuntimeOverlay, currentEvent);
  }, [latestSwitchProgressEvent, switchProgressEvents, switchRuntimeOverlay]);

  const switchRuntimeOverlayBusy = useMemo(() => {
    if (!switchRuntimeOverlay || switchRuntimeOverlay.phase !== 'running') {
      return false;
    }

    const currentEvent =
      findLatestSwitchProgressEvent(switchProgressEvents, 'active') ?? latestSwitchProgressEvent;
    return (currentEvent?.status ?? 'active') === 'active';
  }, [latestSwitchProgressEvent, switchProgressEvents, switchRuntimeOverlay]);

  const currentCardSwitchEvent = useMemo(() => {
    if (!cardSwitchInteraction) {
      return null;
    }

    if (cardSwitchInteraction.phase === 'running') {
      return findLatestSwitchProgressEvent(switchProgressEvents, 'active') ?? latestSwitchProgressEvent;
    }

    if (cardSwitchInteraction.phase === 'error') {
      return findLatestSwitchProgressEvent(switchProgressEvents, 'error') ?? latestSwitchProgressEvent;
    }

    return latestSwitchProgressEvent;
  }, [cardSwitchInteraction, latestSwitchProgressEvent, switchProgressEvents]);

  const clearCardSwitchFeedbackTimers = () => {
    if (cardSwitchVisualTimerRef.current !== null) {
      window.clearTimeout(cardSwitchVisualTimerRef.current);
      cardSwitchVisualTimerRef.current = null;
    }

    if (cardSwitchSweepTimerRef.current !== null) {
      window.clearTimeout(cardSwitchSweepTimerRef.current);
      cardSwitchSweepTimerRef.current = null;
    }
  };

  const clearCardSwitchFeedback = () => {
    clearCardSwitchFeedbackTimers();
    setCardSwitchInteraction(null);
    setCardSwitchSweepState(null);
  };

  const clearPostSwitchDesktopLaunchTimer = () => {
    if (postSwitchDesktopLaunchTimerRef.current !== null) {
      window.clearTimeout(postSwitchDesktopLaunchTimerRef.current);
      postSwitchDesktopLaunchTimerRef.current = null;
    }
  };

  const runDeferredDesktopLaunch = async (
    targetProfile: Pick<ProfileSummary, 'id' | 'name' | 'workspaceName'>,
    desktopAppKinds: ManagedDesktopAppKind[],
    options?: {
      successToast?: boolean;
    },
  ): Promise<PostSwitchDesktopLaunchResult | null> => {
    if (!desktopAppKinds.length || typeof window.codexWorkspace.runPostSwitchDesktopLaunch !== 'function') {
      return null;
    }

    try {
      const rawResult = await window.codexWorkspace.runPostSwitchDesktopLaunch({
        profileId: targetProfile.id,
        desktopAppKinds,
      });
      const result: PostSwitchDesktopLaunchResult = {
        restartedDesktopApps: Array.isArray(rawResult?.restartedDesktopApps)
          ? rawResult.restartedDesktopApps
          : [],
        warnings: Array.isArray(rawResult?.warnings) ? rawResult.warnings : [],
      };

      if (result.restartedDesktopApps.length > 0 && options?.successToast) {
        setToast({
          tone: 'success',
          message: `${getProfileDisplayName(targetProfile)} 切换完成，已在后台启动 ${result.restartedDesktopApps.join(' / ')}。`,
        });
      } else if (result.warnings.length > 0) {
        setToast({
          tone: 'info',
          message: result.warnings[0] ?? '卡片切换已完成，但后台桌面应用暂未确认启动。',
        });
      }

      return result;
    } catch (error) {
      setToast({
        tone: 'error',
        message: normalizeRuntimeErrorMessage(error, '卡片切换已完成，但后台启动桌面应用失败。'),
      });
      return null;
    }
  };

  const scheduleDeferredDesktopLaunch = (
    targetProfile: Pick<ProfileSummary, 'id' | 'name' | 'workspaceName'>,
    desktopAppKinds: ManagedDesktopAppKind[],
  ) => {
    if (!desktopAppKinds.length) {
      return;
    }

    clearPostSwitchDesktopLaunchTimer();
    postSwitchDesktopLaunchTimerRef.current = window.setTimeout(() => {
      postSwitchDesktopLaunchTimerRef.current = null;
      void runDeferredDesktopLaunch(targetProfile, desktopAppKinds, { successToast: true });
    }, CARD_SWITCH_SUCCESS_HOLD_MS + CARD_SWITCH_SUCCESS_SWEEP_MS);
  };

  const getCardFlipNoiseBuffer = (context: AudioContext): AudioBuffer => {
    const cachedBuffer = cardFlipNoiseBufferRef.current;
    if (cachedBuffer && cachedBuffer.sampleRate === context.sampleRate) {
      return cachedBuffer;
    }

    const nextBuffer = createCardFlipNoiseBuffer(context);
    cardFlipNoiseBufferRef.current = nextBuffer;
    return nextBuffer;
  };

  const loadCardFlipAudioBytes = (): Promise<ArrayBuffer | null> => {
    if (cardFlipAudioArrayBufferRef.current) {
      return Promise.resolve(cardFlipAudioArrayBufferRef.current);
    }

    if (cardFlipAudioArrayBufferPromiseRef.current) {
      return cardFlipAudioArrayBufferPromiseRef.current;
    }

    const nextPromise = fetch(cardFlipFlickSoundSrc)
      .then(async (response) => {
        if (!response.ok) {
          return null;
        }

        const bytes = await response.arrayBuffer();
        cardFlipAudioArrayBufferRef.current = bytes;
        return bytes;
      })
      .catch(() => null)
      .finally(() => {
        cardFlipAudioArrayBufferPromiseRef.current = null;
      });

    cardFlipAudioArrayBufferPromiseRef.current = nextPromise;
    return nextPromise;
  };

  const loadCardFlipAudioBuffer = async (context: AudioContext): Promise<AudioBuffer | null> => {
    if (cardFlipAudioBufferRef.current) {
      return cardFlipAudioBufferRef.current;
    }

    const audioBytes = await loadCardFlipAudioBytes();
    if (!audioBytes) {
      return null;
    }

    try {
      const decodedBuffer = await context.decodeAudioData(audioBytes.slice(0));
      cardFlipAudioBufferRef.current = decodedBuffer;
      return decodedBuffer;
    } catch {
      return null;
    }
  };

  const ensureCardFlipAudioContext = async (): Promise<AudioContext | null> => {
    if (typeof window === 'undefined') {
      return null;
    }

    const AudioContextConstructor = getCardFlipAudioContextConstructor(window);
    if (!AudioContextConstructor) {
      return null;
    }

    try {
      const audioContext =
        startupCardIntroAudioContextRef.current ?? new AudioContextConstructor();
      startupCardIntroAudioContextRef.current = audioContext;
      if (audioContext.state === 'suspended') {
        try {
          await audioContext.resume();
        } catch {
          // Keep ornamental audio optional if autoplay resume is blocked.
        }
      }
      return audioContext.state === 'closed' ? null : audioContext;
    } catch {
      return null;
    }
  };

  const playCardFlipAudio = (pattern: CardFlipAudioPattern = 'single') => {
    if (effectiveCardFlipVolumeScale <= 0) {
      return;
    }

    void ensureCardFlipAudioContext().then((audioContext) => {
      if (!audioContext) {
        return;
      }

      const sampleVolumeScale = effectiveCardFlipVolumeScale;
      const noiseGainScale = effectiveCardFlipVolumeScale;

      void loadCardFlipAudioBuffer(audioContext).then((audioBuffer) => {
        const baseTime = audioContext.currentTime + 0.018;

        if (audioBuffer) {
          if (pattern === 'wave') {
            [
              { offsetSeconds: 0, playbackRate: 1.08, volume: 0.42 },
              { offsetSeconds: 0.078, playbackRate: 1.02, volume: 0.36 },
              { offsetSeconds: 0.154, playbackRate: 0.96, volume: 0.3 },
            ].forEach(({ offsetSeconds, playbackRate, volume }) => {
              scheduleCardFlipSampleSound(audioContext, audioBuffer, baseTime + offsetSeconds, {
                playbackRate,
                volume: volume * sampleVolumeScale,
                highPassHz: 280,
              });
            });
            return;
          }

          if (pattern === 'accent') {
            scheduleCardFlipSampleSound(audioContext, audioBuffer, baseTime, {
              playbackRate: 1.14,
              volume: 0.56 * sampleVolumeScale,
              highPassHz: 300,
            });
            scheduleCardFlipSampleSound(audioContext, audioBuffer, baseTime + 0.094, {
              playbackRate: 0.94,
              volume: 0.3 * sampleVolumeScale,
              highPassHz: 260,
            });
            return;
          }

          scheduleCardFlipSampleSound(audioContext, audioBuffer, baseTime, {
            playbackRate: 1.02,
            volume: 0.48 * sampleVolumeScale,
            highPassHz: 260,
          });
          return;
        }

        const noiseBuffer = getCardFlipNoiseBuffer(audioContext);
        if (pattern === 'wave') {
          [0, 0.072, 0.148].forEach((offsetSeconds, index) => {
            schedulePokerCardFlipSound(audioContext, noiseBuffer, baseTime + offsetSeconds, {
              brightness: 1.04 - index * 0.07,
              gainScale: (1.08 - index * 0.1) * noiseGainScale,
              snapScale: 1.02 - index * 0.04,
            });
          });
          return;
        }

        if (pattern === 'accent') {
          schedulePokerCardFlipSound(audioContext, noiseBuffer, baseTime, {
            brightness: 1.08,
            gainScale: 1.2 * noiseGainScale,
            snapScale: 1.08,
          });
          schedulePokerCardFlipSound(audioContext, noiseBuffer, baseTime + 0.092, {
            brightness: 0.92,
            gainScale: 0.76 * noiseGainScale,
            snapScale: 0.92,
          });
          return;
        }

        schedulePokerCardFlipSound(audioContext, noiseBuffer, baseTime, {
          brightness: 1,
          gainScale: 1.14 * noiseGainScale,
          snapScale: 1,
        });
      });
    });
  };

  const openAddDialog = (discoveryHint = false) => {
    if (hasReachedProfileSlotLimit) {
      setToast({ tone: 'error', message: profileSlotLimitReachedMessage });
      return;
    }

    setAddDialogInitialValues(suggestedAddInitialValues);
    setAddDialogDiscoveryHint(discoveryHint);
    setAddDialogOpen(true);
    setMenuProfileId(null);
  };

  const closeAddDialog = () => {
    setAddDialogOpen(false);
    setAddDialogInitialValues(null);
    setAddDialogDiscoveryHint(false);
    setPendingAddOverwrite(null);
  };

  const closeBatchNoteDialog = () => {
    setBatchNoteDialogOpen(false);
    setBatchActionMode('notes');
    setBatchSelectionFilter('all');
    setBatchNoteDraft('');
    setBatchNoteSelectedProfileIds([]);
  };

  const resetWorkspaceSelectionState = () => {
    setWorkspaceSelectionMode(null);
    setWorkspaceSelectionIds([]);
    setWorkspaceSendTargetAccount('');
    setWorkspaceSendNote('');
    setWorkspaceSelectionBusyAction(null);
    setBatchDeleteConfirmRequest(null);
    setWorkspaceActionOverlayById({});
  };

  const playSpacePoolEntryAnimation = (profiles: ProfileSummary[]): void => {
    if (startupCardIntroRevealTimerRef.current !== null) {
      window.clearTimeout(startupCardIntroRevealTimerRef.current);
      startupCardIntroRevealTimerRef.current = null;
    }

    if (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setStartupCardIntroPhase('revealed');
      return;
    }

    setStartupCardIntroPhase('backface');
    const introProfiles = profiles.slice(0, 8);
    startupCardIntroRevealTimerRef.current = window.setTimeout(() => {
      startupCardIntroRevealTimerRef.current = null;
      setStartupCardIntroPhase('revealed');

      if (effectiveCardFlipVolumeScale <= 0) {
        return;
      }

      void ensureCardFlipAudioContext().then((audioContext) => {
        if (!audioContext) {
          return;
        }

        const sampleVolumeScale = effectiveCardFlipVolumeScale;
        const noiseGainScale = effectiveCardFlipVolumeScale;

        void loadCardFlipAudioBuffer(audioContext).then((audioBuffer) => {
          const baseTime = audioContext.currentTime + 0.04;

          if (audioBuffer) {
            for (const [index, profile] of introProfiles.entries()) {
              const { delayMs, durationMs } = getStartupCardIntroTiming(profile.id, index);
              const startAt = baseTime + delayMs / 1000;
              const playbackRate = Math.min(1.22, Math.max(0.92, 560 / durationMs));
              const volume = Math.max(0.28, 0.46 - index * 0.026);
              scheduleCardFlipSampleSound(audioContext, audioBuffer, startAt, {
                playbackRate,
                volume: volume * sampleVolumeScale,
                highPassHz: 260,
              });
            }
            return;
          }

          const noiseBuffer = getCardFlipNoiseBuffer(audioContext);
          for (const [index, profile] of introProfiles.entries()) {
            const { delayMs } = getStartupCardIntroTiming(profile.id, index);
            const startAt = baseTime + delayMs / 1000;
            schedulePokerCardFlipSound(audioContext, noiseBuffer, startAt, {
              brightness: 1.06 - index * 0.035,
              gainScale: (1.12 - index * 0.03) * noiseGainScale,
              snapScale: 1.04 - index * 0.02,
            });
          }
        });
      });
    }, 70);
  };

  const closeStatisticsView = () => {
    setAuthCredentialMode('oauth');
    setMainView('profiles');
    playSpacePoolEntryAnimation(filteredProfiles);
  };

  const openApiConfigurationView = () => {
    setMenuProfileId(null);
    clearCardSwitchFeedback();
    resetWorkspaceSelectionState();
    setSwitchSuccessNotice(null);
    setAuthCredentialMode('api');
    setMainView('api-config');
    window.requestAnimationFrame(() => {
      shellMainRef.current?.scrollTo({ top: 0 });
      shellMainRef.current?.closest('.shell-workspace')?.scrollTo({ top: 0 });
    });
  };

  const confirmSwitchToOauthMode = () => {
    setOauthModeConfirmOpen(false);
    setAuthCredentialMode('oauth');
    setMainView('profiles');
    setToast({
      tone: 'info',
      message: restartAfterOauthSwitch ? '已切换到 OAuth 模式，正在重启应用。' : '已切换到 OAuth 模式。',
    });
    if (restartAfterOauthSwitch) {
      window.codexWorkspace.restartApp();
    }
  };

  const toggleAuthCredentialMode = () => {
    if (authCredentialMode === 'api') {
      setOauthModeConfirmOpen(true);
      return;
    }

    openApiConfigurationView();
  };

  const toggleStatisticsView = () => {
    setMenuProfileId(null);
    if (mainView === 'statistics') {
      closeStatisticsView();
      return;
    }

    clearCardSwitchFeedback();
    resetWorkspaceSelectionState();
    setSwitchSuccessNotice(null);
    setAuthCredentialMode('oauth');
    setStatisticsMode(state.settings.switchMode);
    setMainView('statistics');
  };

  const enterWorkspaceSelectionMode = (
    mode: WorkspaceSelectionMode,
    profileIds?: string[],
  ) => {
    const nextIds = Array.from(new Set((profileIds ?? []).map((item) => item.trim()).filter(Boolean)));
    setMenuProfileId(null);
    setWorkspaceSelectionMode(mode);
    setWorkspaceSelectionIds(nextIds);
    setWorkspaceSelectionBusyAction(null);
    setBatchDeleteConfirmRequest(null);
    if (mode === 'send') {
      setWorkspaceSendTargetAccount('');
      setWorkspaceSendNote('');
    }
  };

  const openBatchNoteDialog = () => {
    setAuthCredentialMode('oauth');
    setMainView('profiles');
    if (workspaceSelectionMode === 'batch') {
      resetWorkspaceSelectionState();
      return;
    }

    if (batchNoteEligibleProfiles.length === 0) {
      setToast({ tone: 'error', message: '当前筛选结果里没有可批量处理的 profile。' });
      return;
    }

    enterWorkspaceSelectionMode('batch');
  };

  const autoDisabledPromptBlocked =
    busyAction !== null ||
    addDialogOpen ||
    editDialogOpen ||
    batchNoteDialogOpen ||
    workspaceSelectionMode !== null ||
    Boolean(batchDeleteConfirmRequest) ||
    Boolean(cloudDeleteSyncPrompt) ||
    Boolean(pendingAddOverwrite) ||
    Boolean(switchTarget) ||
    Boolean(deleteTarget) ||
    Boolean(pendingProfileTransferPrompt) ||
    Boolean(autoDisabledPrompt) ||
    Boolean(quotaAlertTarget) ||
    Boolean(autoSwitchPrompt) ||
    Boolean(switchSuccessNotice) ||
    Boolean(switchRuntimeOverlay?.visible) ||
    aboutDialogOpen ||
    updateDialogOpen ||
    footerHelpOpen ||
    licenseCenterOpen ||
    userCenterOpen ||
    mandatoryUpdateLockActive ||
    Boolean(authGateQrPreviewState) ||
    avatarUploadOpen;

  useEffect(() => {
    const runtimeApi = window.codexWorkspace as Partial<typeof window.codexWorkspace>;
    if (typeof runtimeApi.onStartupProgress !== 'function') {
      return;
    }

    return runtimeApi.onStartupProgress((nextEvent) => {
      setStartupProgressEvent(nextEvent);
    });
  }, []);

  useEffect(() => {
    void loadState('initial');
  }, []);

  const getStartupLoadingVisibleStartedAt = (): number => {
    if (startupLoadingStartedAtRef.current === null) {
      startupLoadingStartedAtRef.current = Date.now();
    }

    return startupLoadingStartedAtRef.current;
  };

  useEffect(() => {
    if (!startupLoadingVisible || !windowState.visible || busyAction === 'initial') {
      return;
    }

    const remaining = Math.max(
      0,
      getStartupLoadingVisibleStartedAt() + STARTUP_LOADING_MIN_DURATION_MS - Date.now(),
    );
    const timer = window.setTimeout(() => {
      if (
        typeof window !== 'undefined' &&
        (!window.matchMedia || !window.matchMedia('(prefers-reduced-motion: reduce)').matches) &&
        !startupCardIntroPlayedRef.current
      ) {
        setStartupCardIntroPhase('backface');
      }
      setStartupLoadingVisible(false);
    }, remaining);

    return () => window.clearTimeout(timer);
  }, [busyAction, startupLoadingVisible, windowState.visible]);

  useEffect(() => {
    if (!startupLoadingVisible) {
      setStartupBackdropVisible(false);
      return;
    }

    if (!windowState.visible) {
      if (!startupHandoffFromSplashRef.current) {
        setStartupBackdropVisible(false);
      }
      return;
    }

    if (startupHandoffFromSplashRef.current) {
      setStartupBackdropVisible(true);
      return;
    }

    setStartupBackdropVisible(false);
    const timer = window.setTimeout(() => {
      setStartupBackdropVisible(true);
    }, STARTUP_ICON_ONLY_DURATION_MS);

    return () => window.clearTimeout(timer);
  }, [startupLoadingVisible, windowState.visible]);

  useEffect(() => {
    if (!startupLoadingVisible) {
      setStartupBackdropVisible(false);
      setStartupLoadingHintVisible(false);
      setStartupProgressEvent(null);
      return;
    }

    if (!windowState.visible) {
      return;
    }

    const remaining = Math.max(
      0,
      getStartupLoadingVisibleStartedAt() + STARTUP_LOADING_HINT_DELAY_MS - Date.now(),
    );
    const timer = window.setTimeout(() => {
      setStartupLoadingHintVisible(true);
    }, remaining);

    return () => window.clearTimeout(timer);
  }, [startupLoadingVisible, windowState.visible]);

  const runStartupNetworkProbe = async () => {
    if (startupProxyCheckTriggeredRef.current) {
      return;
    }

    startupProxyCheckTriggeredRef.current = true;
    try {
      const nextState = await window.codexWorkspace.getNetworkAccessState();
      setNetworkAccessState(nextState);
      if (nextState.status === 'needs-proxy' || nextState.status === 'network-error') {
        setProxyReminder({
          network: nextState,
          source: 'startup',
        });
      }
    } catch {
      // Ignore startup proxy probe failures.
    }
  };

  useEffect(() => {
    if (!startupLoadingVisible || !windowState.visible) {
      return;
    }

    const timer = window.setTimeout(() => {
      void runStartupNetworkProbe();
    }, STARTUP_LOADING_NETWORK_PROBE_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [startupLoadingVisible, windowState.visible]);

  useEffect(() => {
    if (!toast) {
      return;
    }

    const timer = window.setTimeout(() => {
      setToast(null);
    }, 2200);

    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    return () => {
      if (floatingWidgetOpacityCommitTimerRef.current !== null) {
        window.clearTimeout(floatingWidgetOpacityCommitTimerRef.current);
        floatingWidgetOpacityCommitTimerRef.current = null;
      }
      if (cardFlipVolumeCommitTimerRef.current !== null) {
        window.clearTimeout(cardFlipVolumeCommitTimerRef.current);
        cardFlipVolumeCommitTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setCodexAutoRestartDraft((current) =>
      current !== null && current === persistedCodexAutoRestart ? null : current,
    );
  }, [persistedCodexAutoRestart]);

  useEffect(() => {
    setTraeAutoRestartDraft((current) =>
      current !== null && current === persistedTraeAutoRestart ? null : current,
    );
  }, [persistedTraeAutoRestart]);

  useEffect(() => {
    setTrayHoverCardEnabledDraft((current) =>
      current !== null && current === state.settings.trayHoverCardEnabled ? null : current,
    );
  }, [state.settings.trayHoverCardEnabled]);

  useEffect(() => {
    setFloatingWidgetEnabledDraft((current) =>
      current !== null && current === state.settings.floatingWidgetEnabled ? null : current,
    );
  }, [state.settings.floatingWidgetEnabled]);

  useEffect(() => {
    setFloatingWidgetOpacityDraft((current) =>
      current !== null && current === state.settings.floatingWidgetOpacityPercent ? null : current,
    );
  }, [state.settings.floatingWidgetOpacityPercent]);

  useEffect(() => {
    setCardFlipVolumeDraft((current) =>
      current !== null && current === state.settings.cardFlipVolumePercent ? null : current,
    );
  }, [state.settings.cardFlipVolumePercent]);

  useEffect(() => {
    if (!switchTarget || state.settings.switchMode !== 'openclaw') {
      setSwitchOpenClawModel('');
      return;
    }

    const currentModel = state.summary.openclawCurrentModel;
    const currentModelExists = openClawSwitchModelOptions.some((option) => option.key === currentModel);
    const nextModel = currentModel && currentModelExists ? currentModel : '';

    setSwitchOpenClawModel(nextModel);
  }, [
    switchTarget?.id,
    state.settings.switchMode,
    state.summary.openclawCurrentModel,
    openClawSwitchModelOptions,
  ]);

  useEffect(() => {
    switchProgressRunIdRef.current = switchProgressRunId;
  }, [switchProgressRunId]);

  useEffect(() => {
    if (
      !switchRuntimeOverlay ||
      !switchRuntimeOverlay.visible ||
      switchRuntimeOverlay.phase === 'running'
    ) {
      return;
    }

    const fadeDelay = switchRuntimeOverlay.phase === 'success' ? 1350 : 1500;
    const hideTimer = window.setTimeout(() => {
      setSwitchRuntimeOverlay((current) => (current ? { ...current, visible: false } : current));
    }, fadeDelay);
    const clearTimer = window.setTimeout(() => {
      setSwitchRuntimeOverlay(null);
      setSwitchSuccessNotice(null);
      setSwitchProgressEvents([]);
    }, fadeDelay + 340);

    return () => {
      window.clearTimeout(hideTimer);
      window.clearTimeout(clearTimer);
    };
  }, [switchRuntimeOverlay]);

  useEffect(
    () => () => {
      clearCardSwitchFeedbackTimers();
      clearPostSwitchDesktopLaunchTimer();
    },
    [],
  );

  useEffect(() => {
    if (busyAction === 'refresh') {
      return;
    }

    if (busyAction === 'initial' && state.profiles.length === 0 && !state.lastScannedAt) {
      return;
    }

    void loadStatisticsDashboard(statisticsRange, {
      source: 'background',
      modeOverride: statisticsMode,
    });
  }, [
    busyAction,
    state.lastScannedAt,
    state.profiles.length,
    state.summary.activeProfileName,
    state.summary.overallRemainingPercent,
  ]);

  useEffect(() => {
    const subscribeSwitchProgress = window.codexWorkspace?.onSwitchProgress;
    if (typeof subscribeSwitchProgress !== 'function') {
      return;
    }

    return subscribeSwitchProgress((event) => {
      if (!event.runId || event.runId !== switchProgressRunIdRef.current) {
        return;
      }

      setSwitchProgressEvents((current) => upsertSwitchProgressEvent(current, event));
    });
  }, []);

  useEffect(() => {
    const runtimeApi = window.codexWorkspace as Partial<typeof window.codexWorkspace>;
    let active = true;

    if (typeof runtimeApi.getUpdateState === 'function') {
      runtimeApi
        .getUpdateState()
        .then((nextState) => {
          if (active) {
            setUpdateState(nextState);
          }
        })
        .catch(() => {
          // Keep the current shell usable even if update preload wiring is stale.
        });
    }

    if (typeof runtimeApi.onUpdateStateChange !== 'function') {
      return () => {
        active = false;
      };
    }

    const unsubscribe = runtimeApi.onUpdateStateChange((nextState) => {
      if (active) {
        setUpdateState(nextState);
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncViewportSize = () => {
      const nextWidth = window.innerWidth;
      const nextHeight = window.innerHeight;
      setViewportSize((current) =>
        current.width === nextWidth && current.height === nextHeight
          ? current
          : {
              width: nextWidth,
              height: nextHeight,
            },
      );
    };

    syncViewportSize();
    window.addEventListener('resize', syncViewportSize);
    return () => {
      window.removeEventListener('resize', syncViewportSize);
    };
  }, []);

  useEffect(() => {
    if (updateState.blockedByMandatory) {
      setUpdateDialogOpen(true);
    }
  }, [updateState.blockedByMandatory]);

  useEffect(() => {
    let active = true;

    authClient
      .getState()
      .then((nextState) => {
        if (active) {
          setAuthState(nextState);
        }
      })
      .catch(() => {
        // Keep the current shell usable even if auth preload wiring is stale.
      });

    const unsubscribe = authClient.onStateChange((nextState) => {
      if (active) {
        setAuthState(nextState);
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    let disposed = false;
    const heartbeatIntervalMs =
      feedbackCenterOpen || optimisticFeedbackMessages.length > 0
        ? 4_000
        : authState.sessionStatus === 'authenticated'
          ? 5_000
          : 20_000;

    const runHeartbeat = async () => {
      try {
        const result = await authClient.heartbeat();
        if (!disposed) {
          setAuthState(result.state);
        }
      } catch {
        // Ignore transient auth heartbeat errors and keep the shell interactive.
      }
    };

    void runHeartbeat();
    const timer = window.setInterval(() => {
      void runHeartbeat();
    }, heartbeatIntervalMs);

    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, [authState.sessionStatus, feedbackCenterOpen, optimisticFeedbackMessages.length]);

  useEffect(() => {
    if (authState.sessionStatus !== 'authenticated') {
      return;
    }

    let disposed = false;
    const runHeartbeatOnce = async () => {
      try {
        const result = await authClient.heartbeat();
        if (!disposed) {
          setAuthState(result.state);
        }
      } catch {
        // Ignore focus-triggered heartbeat errors and keep the shell interactive.
      }
    };

    const handleFocus = () => {
      void runHeartbeatOnce();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        void runHeartbeatOnce();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      disposed = true;
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [authState.sessionStatus]);

  useEffect(() => {
    if (!authState.bootstrap.mandatoryUpdate) {
      return;
    }

    setCenterNotice({
      id: 'auth-min-supported-version',
      message: `授权服务要求最低版本 ${authState.bootstrap.minSupportedVersion ?? '未知'}，请先完成更新。`,
    });
    setUpdateDialogOpen(true);
  }, [authState.bootstrap.mandatoryUpdate, authState.bootstrap.minSupportedVersion]);

  useEffect(() => {
    if (pendingProfileTransferBusy) {
      return;
    }

    const nextTransfer = authState.pendingProfileTransfers[0] ?? null;
    if (!nextTransfer) {
      setPendingProfileTransferPrompt(null);
      return;
    }

    setPendingProfileTransferPrompt((current) =>
      current && current.id === nextTransfer.id ? current : nextTransfer,
    );
  }, [authState.pendingProfileTransfers, pendingProfileTransferBusy]);

  useEffect(() => {
    const pendingIds = authState.pendingProfileTransfers.map((entry) => entry.id);
    setPendingTransferSelectionIds((current) => {
      if (pendingIds.length === 0) {
        return [];
      }

      const next = current.filter((id) => pendingIds.includes(id));
      return next.length > 0 ? next : pendingIds;
    });
  }, [authState.pendingProfileTransfers]);

  useEffect(() => {
    const previous = new Set(pendingTransferIdsRef.current);
    const next = authState.pendingProfileTransfers.map((entry) => entry.id);
    const arrived = authState.pendingProfileTransfers.filter((entry) => !previous.has(entry.id));

    if (arrived.length > 0) {
      setCenterNotice({
        id: `incoming-transfer-${Date.now()}`,
        message:
          arrived.length === 1
            ? `收到来自 ${arrived[0].senderHostLabel ?? '远端'} 的空间卡片`
            : `收到 ${arrived.length} 张新的空间卡片`,
      });
    }

    pendingTransferIdsRef.current = next;
  }, [authState.pendingProfileTransfers]);

  useEffect(() => {
    setDismissedCloudBackupIds([]);
    setCloudBackupAutoPromptConsumed(false);
    resetCloudSyncDialogState();
    setPendingCloudBackupPrompt(null);
    setNewDeviceCloudRestorePrompt(null);
    setPendingTransferSelectionIds([]);
    setLocalRemoteProfileSnapshots([]);
    accountUnlockPromptKeyRef.current = null;
    cloudSyncAutoPromptKeyRef.current = null;
    cloudBackupAutoSyncKeyRef.current = null;
  }, [authState.profile?.id]);

  useEffect(() => {
    if (IS_LOCAL_FAST_IMPORT_BUILD || authState.sessionStatus !== 'authenticated' || !authState.profile) {
      setLegacyCloudBackupReminderOpen(false);
      return;
    }

    if (
      authState.security.secondaryPassword.enabled ||
      !authState.security.secondaryPassword.legacyCloudBackupDetected ||
      legacyCloudBackupReminderShownRef.current.has(authState.profile.id) ||
      secondaryPasswordDialog !== null
    ) {
      return;
    }

    legacyCloudBackupReminderShownRef.current.add(authState.profile.id);
    setLegacyCloudBackupReminderOpen(true);
  }, [
    authState.profile,
    authState.security.secondaryPassword.enabled,
    authState.security.secondaryPassword.legacyCloudBackupDetected,
    authState.sessionStatus,
    secondaryPasswordDialog,
  ]);

  useEffect(() => {
    setOptimisticFeedbackMessages([]);
    feedbackObservedHostReplyIdsRef.current = new Set();
    feedbackObservedHostReplyInitializedRef.current = false;
    feedbackSeenHostReplyIdsRef.current = new Set();
    feedbackHostReplyInitializedRef.current = false;
    setFeedbackHeaderUnreadVisible(false);
  }, [authState.profile?.id]);

  useEffect(() => {
    const runtimeApi = window.codexWorkspace as Partial<typeof window.codexWorkspace>;
    let active = true;

    if (typeof runtimeApi.getWindowState === 'function') {
      runtimeApi
        .getWindowState()
        .then((nextState) => {
          if (active) {
            setWindowState(nextState);
          }
        })
        .catch(() => {
          if (active) {
            setWindowState((current) => ({ ...current, visible: true }));
          }
          // Ignore stale preload state and keep the main UI usable.
        });
    }

    if (typeof runtimeApi.onWindowStateChange !== 'function') {
      return () => {
        active = false;
      };
    }

    const unsubscribe = runtimeApi.onWindowStateChange((nextState) => {
      if (active) {
        setWindowState(nextState);
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!centerNotice) {
      return;
    }

    const timer = window.setTimeout(() => {
      setCenterNotice(null);
    }, centerNotice.variant === 'active-card' ? 2600 : 2200);

    return () => window.clearTimeout(timer);
  }, [centerNotice]);

  useEffect(() => {
    setWarningThresholdDraft(String(state.settings.lowQuotaWarningThreshold));
  }, [state.settings.lowQuotaWarningThreshold]);

  useEffect(() => {
    setAutoSwitchThresholdDraft(String(state.settings.autoSwitchThreshold));
  }, [state.settings.autoSwitchThreshold]);

  useEffect(() => {
    setAutoSwitchCountdownDraft(String(state.settings.autoSwitchCountdownSeconds));
  }, [state.settings.autoSwitchCountdownSeconds]);

  useEffect(() => {
    const previousProfiles = previousProfilesRef.current;
    previousProfilesRef.current = state.profiles;

    if (previousProfiles.length === 0) {
      return;
    }

    const previousById = new Map(previousProfiles.map((profile) => [profile.id, profile]));
    const newlyAutoDisabledProfile =
      state.profiles.find((profile) => {
        if (!profile.autoDisabled) {
          return false;
        }

        const previous = previousById.get(profile.id);
        if (!previous?.autoDisabled) {
          return true;
        }

        return previous.autoDisabledAt !== profile.autoDisabledAt && Boolean(profile.autoDisabledAt);
      }) ?? null;
    const resetEvents: Array<{ key: string; message: string }> = [];

    for (const profile of state.profiles) {
      const previous = previousById.get(profile.id);
      if (!previous) {
        continue;
      }

      if (didQuotaReset(previous.weeklyQuota, profile.weeklyQuota)) {
        resetEvents.push({
          key: `${profile.id}:weekly`,
          message: `${getProfileDisplayName(profile)} 空间周额度已重置`,
        });
      }

      if (didQuotaReset(previous.fiveHourQuota, profile.fiveHourQuota)) {
        resetEvents.push({
          key: `${profile.id}:five-hour`,
          message: `${getProfileDisplayName(profile)} 空间 5 小时额度已重置`,
        });
      }
    }

    if (newlyAutoDisabledProfile) {
      const nextPrompt = {
        profileId: newlyAutoDisabledProfile.id,
        promptKey: `${newlyAutoDisabledProfile.id}:${
          newlyAutoDisabledProfile.autoDisabledAt ?? newlyAutoDisabledProfile.probeCheckedAt ?? 'auto-disabled'
        }`,
      };

      if (autoDisabledPromptBlocked) {
        setQueuedAutoDisabledPrompt((current) =>
          current?.promptKey === nextPrompt.promptKey ? current : nextPrompt,
        );
      } else {
        setAutoDisabledPrompt((current) =>
          current?.promptKey === nextPrompt.promptKey ? current : nextPrompt,
        );
        setQueuedAutoDisabledPrompt(null);
        setQuotaAlertTarget(null);
        setAutoSwitchPrompt(null);
      }
    }

    if (resetEvents.length > 0) {
      setRecentlyResetKeys((current) => {
        const next = { ...current };
        for (const event of resetEvents) {
          next[event.key] = true;
        }
        return next;
      });

      setCenterNotice({
        id: `${Date.now()}`,
        message: resetEvents[0].message,
      });

      window.setTimeout(() => {
        setRecentlyResetKeys((current) => {
          const next = { ...current };
          for (const event of resetEvents) {
            delete next[event.key];
          }
          return next;
        });
      }, 1800);
    }
  }, [autoDisabledPromptBlocked, state.profiles]);

  useEffect(() => {
    if (!autoDisabledPrompt) {
      return;
    }

    const currentProfile =
      state.profiles.find((profile) => profile.id === autoDisabledPrompt.profileId) ?? null;
    if (currentProfile?.autoDisabled) {
      return;
    }

    setAutoDisabledPrompt(null);
  }, [autoDisabledPrompt, state.profiles]);

  useEffect(() => {
    if (!queuedAutoDisabledPrompt) {
      return;
    }

    const queuedProfile =
      state.profiles.find((profile) => profile.id === queuedAutoDisabledPrompt.profileId) ?? null;
    if (!queuedProfile?.autoDisabled) {
      setQueuedAutoDisabledPrompt(null);
      return;
    }

    if (autoDisabledPrompt || autoDisabledPromptBlocked) {
      return;
    }

    setAutoDisabledPrompt(queuedAutoDisabledPrompt);
    setQueuedAutoDisabledPrompt(null);
    setQuotaAlertTarget(null);
    setAutoSwitchPrompt(null);
  }, [autoDisabledPrompt, autoDisabledPromptBlocked, queuedAutoDisabledPrompt, state.profiles]);

  useEffect(() => {
    if (state.summary.authRecognized) {
      setUnrecognizedAuthPrompted(false);
      autoImportCurrentAuthKeyRef.current = null;
      return;
    }

    if (
      unrecognizedAuthPrompted ||
      currentAuthAutoImportReady ||
      authGateVisible ||
      busyAction !== null ||
      addDialogOpen ||
      editDialogOpen ||
      (authState.license.slotLimit !== null &&
        state.profiles.length >= Math.max(0, authState.license.slotLimit)) ||
      !state.summary.currentAuthExists ||
      state.summary.authRecognized
    ) {
      return;
    }

    setUnrecognizedAuthPrompted(true);
    setAddDialogInitialValues(suggestedAddInitialValues);
    setAddDialogDiscoveryHint(true);
    setAddDialogOpen(true);
    setToast({ tone: 'info', message: '检测到新的登录空间，请确认名称后保存。' });
  }, [
    addDialogOpen,
    authState.license.slotLimit,
    busyAction,
    currentAuthAutoImportReady,
    editDialogOpen,
    suggestedAddInitialValues,
    state.profiles.length,
    state.summary.authRecognized,
    state.summary.currentAuthExists,
    unrecognizedAuthPrompted,
  ]);

  useEffect(() => {
    if (!currentAuthAutoImportKey) {
      autoImportCurrentAuthKeyRef.current = null;
      return;
    }

    if (
      autoImportCurrentAuthKeyRef.current === currentAuthAutoImportKey ||
      authGateVisible ||
      busyAction !== null ||
      addDialogOpen ||
      editDialogOpen ||
      (authState.license.slotLimit !== null &&
        state.profiles.length >= Math.max(0, authState.license.slotLimit))
    ) {
      return;
    }

    autoImportCurrentAuthKeyRef.current = currentAuthAutoImportKey;
    const requestId = beginProfilesStateRequest();
    const placeholder = buildCurrentAuthImportPlaceholder(state.summary, 'auto');
    clearCurrentAuthImportPlaceholderCleanupTimer();
    setCurrentAuthImportPlaceholder(placeholder);
    setBusyAction('add');

    window.codexWorkspace
      .importCurrentAuth()
      .then((result) => {
        commitProfilesState(requestId, result.state);
        setAddDialogOpen(false);
        setAddDialogInitialValues(null);
        setAddDialogDiscoveryHint(false);
        setUnrecognizedAuthPrompted(false);
        setCurrentAuthImportPlaceholder((current) =>
          current?.id === placeholder.id
            ? {
                ...current,
                status: 'success',
                importedProfileId: result.importedProfileId ?? null,
                revealToken: Date.now(),
                successDetailText: result.backgroundSyncPending
                  ? '已纳入当前空间池，后台同步中'
                  : '已纳入当前空间池',
                errorMessage: null,
              }
            : current,
        );
        scheduleCurrentAuthImportPlaceholderCleanup(result.importedProfileId ? 1_800 : 1_400);
        setToast({ tone: 'success', message: result.message });
      })
      .catch((error) => {
        const message = normalizeRuntimeErrorMessage(error, '自动加入当前登录空间失败。');
        setErrorMessage(message);
        setCurrentAuthImportPlaceholder((current) =>
          current?.id === placeholder.id
            ? {
                ...current,
                status: 'error',
                errorMessage: message,
              }
            : current,
        );
        scheduleCurrentAuthImportPlaceholderCleanup(4200);
        setToast({ tone: 'error', message });
      })
      .finally(() => {
        setBusyAction(null);
      });
  }, [
    addDialogOpen,
    authState,
    authState.license.slotLimit,
    busyAction,
    currentAuthAutoImportKey,
    editDialogOpen,
    state.profiles.length,
  ]);

  const hasHiddenModePassword = hiddenModeStoredPassword.trim().length > 0;
  const effectiveProfileSlotLimit =
    authState.license.slotLimit === null ? null : Math.max(0, authState.license.slotLimit);
  const hasReachedProfileSlotLimit =
    effectiveProfileSlotLimit !== null && state.profiles.length >= effectiveProfileSlotLimit;
  const profileSlotLimitReachedMessage =
    effectiveProfileSlotLimit === null
      ? '当前授权未限制卡片数量。'
      : `当前授权最多只开放 ${effectiveProfileSlotLimit} 张卡片，第 ${
          effectiveProfileSlotLimit + 1
        } 张开始不允许再添加。`;
  const canAccessEngineerFeatures =
    isLocalSimulationAvailable || updateState.allowEngineerAccess;
  const oauthQuickDockScale = useMemo(() => {
    if (windowState.maximized) {
      return OAUTH_QUICK_DOCK_MAX_SCALE;
    }

    const viewportWidth =
      viewportSize.width ||
      (typeof window !== 'undefined' ? window.innerWidth : OAUTH_QUICK_DOCK_BASE_VIEWPORT.width);
    const viewportHeight =
      viewportSize.height ||
      (typeof window !== 'undefined' ? window.innerHeight : OAUTH_QUICK_DOCK_BASE_VIEWPORT.height);
    const widthScale = viewportWidth / OAUTH_QUICK_DOCK_BASE_VIEWPORT.width;
    const heightScale = viewportHeight / OAUTH_QUICK_DOCK_BASE_VIEWPORT.height;
    return Number(
      clampNumber(
        Math.min(widthScale, heightScale),
        OAUTH_QUICK_DOCK_MIN_SCALE,
        OAUTH_QUICK_DOCK_MAX_SCALE,
      ).toFixed(3),
    );
  }, [viewportSize.height, viewportSize.width, windowState.maximized]);
  const visibleRealProfiles = useMemo(
    () =>
      authState.sessionStatus === 'authenticated' && authState.profile
        ? state.profiles.filter(
            (profile) =>
              !isProfileBoundToDifferentAccountForUser(
                profile,
                authState.profile,
                authState.device.deviceId,
              ),
          )
        : state.profiles,
    [authState.device.deviceId, authState.profile, authState.sessionStatus, state.profiles],
  );
  const hiddenForeignProfileCount = Math.max(0, state.profiles.length - visibleRealProfiles.length);
  const simulatedProfileSeeds = useMemo(
    () =>
      canAccessEngineerFeatures && localSimulationEnabled
        ? buildSimulatedProfileSeeds(localSimulationCardCount, localSimulationPlanTypes)
        : [],
    [
      canAccessEngineerFeatures,
      localSimulationCardCount,
      localSimulationEnabled,
      localSimulationPlanTypes,
    ],
  );
  const simulatedProfiles = useMemo<DisplayProfile[]>(
    () => materializeSimulatedProfiles(simulatedProfileSeeds, simulationNowMs),
    [simulatedProfileSeeds, simulationNowMs],
  );
  const displayProfiles = useMemo<DisplayProfile[]>(
    () => {
      const availableProfiles: ProfileSummary[] = [];
      const unavailableProfiles: ProfileSummary[] = [];
      const manuallyDisabledProfiles: ProfileSummary[] = [];

      for (const profile of visibleRealProfiles) {
        if (profile.manuallyDisabled) {
          manuallyDisabledProfiles.push(profile);
          continue;
        }

        if (
          getProfileAvailability(profile, {
            ignoreProbeUnavailable: forceEnableProbeCards,
          }).isUnavailable
        ) {
          unavailableProfiles.push(profile);
          continue;
        }

        availableProfiles.push(profile);
      }

      const combinedProfiles = [
        ...availableProfiles,
        ...simulatedProfiles,
        ...unavailableProfiles,
        ...manuallyDisabledProfiles,
      ];
      return effectiveProfileSlotLimit === null
        ? combinedProfiles
        : combinedProfiles.slice(0, effectiveProfileSlotLimit);
    },
    [effectiveProfileSlotLimit, forceEnableProbeCards, simulatedProfiles, visibleRealProfiles],
  );
  const forceEnableProbeProfileCount = useMemo(
    () => visibleRealProfiles.filter((profile) => canIgnoreProbeUnavailable(profile)).length,
    [visibleRealProfiles],
  );
  const displayLogEntries = useMemo(
    () =>
      state.logs.map((item) => ({
        ...item,
        displayMessage: formatDisplayLogMessage(
          item.message,
          displayProfiles.length,
          canAccessEngineerFeatures && localSimulationEnabled,
        ),
      })),
    [canAccessEngineerFeatures, displayProfiles.length, localSimulationEnabled, state.logs],
  );
  const availablePlanFilters = useMemo(() => DISPLAY_PLAN_FILTER_ORDER, []);

  useEffect(() => {
    if (planFilter === 'ALL') {
      return;
    }

    if (!availablePlanFilters.includes(planFilter as DisplayPlanType)) {
      setPlanFilter('ALL');
    }
  }, [availablePlanFilters, planFilter]);

  const filteredProfiles = useMemo(() => {
    const keyword = searchValue.trim().toLowerCase();

    return displayProfiles.filter((profile) => {
      const matchesPlan = planFilter === 'ALL' || getDisplayPlanType(profile) === planFilter;
      const haystack =
        `${profile.name} ${profile.workspaceName ?? ''} ${profile.notes} ${profile.storageDirName}`.toLowerCase();
      const matchesKeyword = !keyword || haystack.includes(keyword);
      return matchesPlan && matchesKeyword;
    });
  }, [displayProfiles, planFilter, searchValue]);
  const cloudDownloadResolvedProfileIds = useMemo(
    () =>
      cloudDownloadPlaceholders
        .map((placeholder) => placeholder.importedProfileId?.trim() ?? '')
        .filter(Boolean),
    [cloudDownloadPlaceholders],
  );
  const cloudDownloadResolvedProfileIdSet = useMemo(
    () => new Set(cloudDownloadResolvedProfileIds),
    [cloudDownloadResolvedProfileIds],
  );
  const workspaceRemovingProfileIdSet = useMemo(
    () => new Set(workspaceRemovingProfileIds),
    [workspaceRemovingProfileIds],
  );
  const renderedProfiles = useMemo(
    () =>
      filteredProfiles.filter(
        (profile) =>
          !cloudDownloadResolvedProfileIdSet.has(profile.id) &&
          !workspaceRemovingProfileIdSet.has(profile.id),
      ),
    [cloudDownloadResolvedProfileIdSet, filteredProfiles, workspaceRemovingProfileIdSet],
  );
  const workspaceSelectionEligibleProfiles = useMemo(
    () =>
      filteredProfiles.filter(
        (profile): profile is ProfileSummary =>
          !isSimulatedProfile(profile) && !workspaceRemovingProfileIdSet.has(profile.id),
      ),
    [filteredProfiles, workspaceRemovingProfileIdSet],
  );
  const workspaceSelectionEligibleIdSet = useMemo(
    () => new Set(workspaceSelectionEligibleProfiles.map((profile) => profile.id)),
    [workspaceSelectionEligibleProfiles],
  );
  const workspaceSelectionIdSet = useMemo(
    () => new Set(workspaceSelectionIds.map((item) => item.trim()).filter(Boolean)),
    [workspaceSelectionIds],
  );
  const workspaceSelectedProfiles = useMemo(
    () =>
      workspaceSelectionEligibleProfiles.filter((profile) => workspaceSelectionIdSet.has(profile.id)),
    [workspaceSelectionEligibleProfiles, workspaceSelectionIdSet],
  );
  useEffect(
    () => () => {
      if (cloudDownloadPlaceholderCleanupTimerRef.current !== null) {
        window.clearTimeout(cloudDownloadPlaceholderCleanupTimerRef.current);
        cloudDownloadPlaceholderCleanupTimerRef.current = null;
      }
      if (localImportPlaceholderCleanupTimerRef.current !== null) {
        window.clearTimeout(localImportPlaceholderCleanupTimerRef.current);
        localImportPlaceholderCleanupTimerRef.current = null;
      }
      if (currentAuthImportPlaceholderCleanupTimerRef.current !== null) {
        window.clearTimeout(currentAuthImportPlaceholderCleanupTimerRef.current);
        currentAuthImportPlaceholderCleanupTimerRef.current = null;
      }
      if (cloudSyncVisualProgressTimerRef.current !== null) {
        window.clearTimeout(cloudSyncVisualProgressTimerRef.current);
        cloudSyncVisualProgressTimerRef.current = null;
      }
    },
    [],
  );
  const hasOnlyHiddenForeignProfiles =
    filteredProfiles.length === 0 &&
    visibleRealProfiles.length === 0 &&
    hiddenForeignProfileCount > 0 &&
    authState.sessionStatus === 'authenticated';
  const currentAccountDisplayLabel =
    authState.profile?.email?.trim() || authState.profile?.displayName?.trim() || '当前账号';
  const cloudSyncPaused = state.settings.cloudSyncPaused === true;
  const currentDeviceCloudBackupMap = useMemo(
    () =>
      new Map(
        authState.currentDeviceCloudProfileBackups.map((entry) => [entry.profileId, entry] as const),
      ),
    [authState.currentDeviceCloudProfileBackups],
  );
  const autoCloudBackupPendingProfileIds = useMemo(
    () =>
      visibleRealProfiles
        .filter((profile) => {
          const backupStatus = buildCurrentDeviceCloudBackupStatus(profile, currentDeviceCloudBackupMap);
          return backupStatus.state === 'missing' || backupStatus.state === 'stale';
        })
        .map((profile) => profile.id),
    [currentDeviceCloudBackupMap, visibleRealProfiles],
  );
  const canAutoSyncCurrentDeviceCloudBackup =
    !IS_LOCAL_FAST_IMPORT_BUILD &&
    authState.sessionStatus === 'authenticated' &&
    Boolean(authState.profile) &&
    authState.security.secondaryPassword.enabled &&
    authState.security.secondaryPassword.currentDeviceTrusted !== false &&
    !cloudSyncPaused;
  const profilesById = useMemo(
    () => new Map(state.profiles.map((profile) => [profile.id, profile] as const)),
    [state.profiles],
  );
  useEffect(() => {
    if (!workspaceSelectionMode) {
      return;
    }

    setWorkspaceSelectionIds((current) =>
      current.filter((profileId) => workspaceSelectionEligibleIdSet.has(profileId)),
    );
  }, [workspaceSelectionEligibleIdSet, workspaceSelectionMode]);
  useEffect(() => {
    if (!menuProfileId && !quotaAlertSnoozeMenuOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (menuProfileId && !target?.closest('.card-menu-wrap')) {
        setMenuProfileId(null);
      }
      if (quotaAlertSnoozeMenuOpen && !target?.closest('.quota-alert-snooze')) {
        setQuotaAlertSnoozeMenuOpen(false);
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, [menuProfileId, quotaAlertSnoozeMenuOpen]);
  const currentAuthImportResolvedProfile = useMemo(
    () =>
      currentAuthImportPlaceholder?.importedProfileId
        ? (profilesById.get(currentAuthImportPlaceholder.importedProfileId) as DisplayProfile | undefined)
        : undefined,
    [currentAuthImportPlaceholder, profilesById],
  );
  const currentAuthImportRevealToken =
    currentAuthImportResolvedProfile &&
    currentAuthImportPlaceholder?.importedProfileId === currentAuthImportResolvedProfile.id
      ? currentAuthImportPlaceholder.revealToken
      : 0;
  const leadingPlaceholderCount =
    localImportPlaceholders.length +
    cloudDownloadPlaceholders.length +
    (currentAuthImportPlaceholder && !currentAuthImportResolvedProfile ? 1 : 0);
  const cloudCoverageProfiles = useMemo(
    () => [...visibleRealProfiles, ...simulatedProfiles],
    [simulatedProfiles, visibleRealProfiles],
  );
  const localCloudBackupRows = useMemo(
    () =>
      cloudCoverageProfiles.map((profile) => ({
        profile,
        backupStatus: buildCurrentDeviceCloudBackupStatus(profile, currentDeviceCloudBackupMap),
      })),
    [cloudCoverageProfiles, currentDeviceCloudBackupMap],
  );
  const cloudBackupSummary = useMemo(() => {
    const summary = {
      totalCount: localCloudBackupRows.length,
      syncedCount: 0,
      staleCount: 0,
      missingCount: 0,
    };
    for (const row of localCloudBackupRows) {
      if (row.backupStatus.state === 'synced') {
        summary.syncedCount += 1;
      } else if (row.backupStatus.state === 'stale') {
        summary.staleCount += 1;
      } else {
        summary.missingCount += 1;
      }
    }
    return {
      ...summary,
      pendingCount: summary.staleCount + summary.missingCount,
    };
  }, [localCloudBackupRows]);
  const activeCloudSyncVisualProgress =
    cloudSyncVisualProgress?.status === 'running' ? cloudSyncVisualProgress : null;
  const backupRefreshVisualProgress =
    activeCloudSyncVisualProgress?.action === 'backup-refresh' ? activeCloudSyncVisualProgress : null;
  const deviceToCloudVisualProgress =
    activeCloudSyncVisualProgress && activeCloudSyncVisualProgress.action !== 'backup-refresh'
      ? activeCloudSyncVisualProgress
      : null;
  const visibleRealProfileIdSet = useMemo(
    () => new Set(visibleRealProfiles.map((profile) => profile.id)),
    [visibleRealProfiles],
  );
  const visibleRealProfileById = useMemo(
    () => new Map(visibleRealProfiles.map((profile) => [profile.id, profile] as const)),
    [visibleRealProfiles],
  );
  const availableLocalRemoteSnapshots = useMemo(
    () =>
      localRemoteProfileSnapshots.filter((snapshot) => visibleRealProfileIdSet.has(snapshot.id)),
    [localRemoteProfileSnapshots, visibleRealProfileIdSet],
  );
  const localSnapshotById = useMemo(
    () => new Map(availableLocalRemoteSnapshots.map((snapshot) => [snapshot.id, snapshot] as const)),
    [availableLocalRemoteSnapshots],
  );
  const localSnapshotMatchMap = useMemo(() => {
    const matchMap = new Map<string, LicenseHostRemoteProfileSnapshot>();
    for (const snapshot of availableLocalRemoteSnapshots) {
      for (const key of buildProfileSnapshotMatchKeys(snapshot)) {
        if (!matchMap.has(key)) {
          matchMap.set(key, snapshot);
        }
      }
    }
    return matchMap;
  }, [availableLocalRemoteSnapshots]);
  const cloudEntryMatchMap = useMemo(() => {
    const matchMap = new Map<string, CloudProfileBackup>();
    for (const entry of authState.cloudProfileBackups) {
      for (const key of buildProfileSnapshotMatchKeys(entry)) {
        if (!matchMap.has(key)) {
          matchMap.set(key, entry);
        }
      }
    }
    return matchMap;
  }, [authState.cloudProfileBackups]);
  const cloudBackupCenterRows = useMemo<CloudBackupCenterRow[]>(
    () =>
      authState.cloudProfileBackups.map((entry) => {
        const matchedLocalSnapshot =
          buildProfileSnapshotMatchKeys(entry)
            .map((key) => localSnapshotMatchMap.get(key) ?? null)
            .find((snapshot): snapshot is LicenseHostRemoteProfileSnapshot => Boolean(snapshot)) ??
          null;
        const matchedLocalProfile = matchedLocalSnapshot
          ? visibleRealProfileById.get(matchedLocalSnapshot.id) ?? null
          : null;
        const localUpdatedAt = matchedLocalProfile?.updatedAt ?? matchedLocalSnapshot?.updatedAt ?? '';
        const cloudUpdatedAt = entry.updatedAt.trim();

        let status: CloudBackupRowState;
        if (!matchedLocalSnapshot) {
          status = {
            tone: 'missing',
            label: '仅云端',
            detail: '本地没有找到对应卡片，下载覆盖时会直接新增到当前卡片池。',
          };
        } else if (localUpdatedAt && cloudUpdatedAt && localUpdatedAt.localeCompare(cloudUpdatedAt) > 0) {
          status = {
            tone: 'stale',
            label: '待更新',
            detail: `本地在 ${formatDateTime(localUpdatedAt)} 后还有改动，可把本地版本更新到云端。`,
          };
        } else {
          status = {
            tone: 'synced',
            label: '已同步',
            detail: `云端最近版本时间为 ${formatDateTime(entry.updatedAt)}，当前本地已有对应卡片。`,
          };
        }

        return {
          entry,
          localProfile: matchedLocalProfile,
          localSnapshot: matchedLocalSnapshot,
          status,
        };
      }),
    [authState.cloudProfileBackups, localSnapshotMatchMap, visibleRealProfileById],
  );
  const cloudBackupVisibleLocalRows = useMemo(
    () =>
      visibleRealProfiles.map((profile) => {
        const backupStatus = buildCurrentDeviceCloudBackupStatus(profile, currentDeviceCloudBackupMap);
        const localSnapshot = localSnapshotById.get(profile.id) ?? null;
        const cloudEntry =
          localSnapshot
            ? buildProfileSnapshotMatchKeys(localSnapshot)
                .map((key) => cloudEntryMatchMap.get(key) ?? null)
                .find((entry): entry is CloudProfileBackup => Boolean(entry)) ?? null
            : null;

        return {
          profile,
          backupStatus,
          localSnapshot,
          cloudEntry,
        };
      }),
    [cloudEntryMatchMap, currentDeviceCloudBackupMap, localSnapshotById, visibleRealProfiles],
  );
  const cloudBackupPendingRows = useMemo(
    () => cloudBackupVisibleLocalRows.filter((row) => row.backupStatus.state !== 'synced'),
    [cloudBackupVisibleLocalRows],
  );
  const cloudBackupCloudOnlyRows = useMemo(
    () => cloudBackupCenterRows.filter((row) => row.status.tone === 'missing'),
    [cloudBackupCenterRows],
  );
  const cloudBackupCenterLocalDisplayRows =
    cloudBackupCenterView === 'pending' ? cloudBackupPendingRows : cloudBackupVisibleLocalRows;
  const cloudBackupCenterCloudDisplayRows =
    cloudBackupCenterView === 'cloud-only' ? cloudBackupCloudOnlyRows : cloudBackupCenterRows;
  const sameAccountLockedProfiles = useMemo(
    () =>
      visibleRealProfiles.filter((profile) =>
        doesProfileNeedUnlockForAccount(profile, authState.profile, authState.device.deviceId),
      ),
    [authState.device.deviceId, authState.profile, visibleRealProfiles],
  );
  const availableCloudBackupEntries = useMemo(
    () =>
      authState.cloudProfileBackups.filter(
        (entry) => !dismissedCloudBackupIds.includes(entry.id),
      ),
    [authState.cloudProfileBackups, dismissedCloudBackupIds],
  );
  const newDeviceCloudRestoreEntries = useMemo(() => {
    if (!newDeviceCloudRestorePrompt) {
      return [];
    }
    const selectedIds = new Set(
      newDeviceCloudRestorePrompt.cloudEntryIds.map((item) => item.trim()).filter(Boolean),
    );
    if (selectedIds.size === 0) {
      return [];
    }
    return authState.cloudProfileBackups.filter((entry) => selectedIds.has(entry.id));
  }, [authState.cloudProfileBackups, newDeviceCloudRestorePrompt]);
  const issueTransferEligibleSnapshots = useMemo(
    () =>
      availableLocalRemoteSnapshots.filter(
        (snapshot) => snapshot.securityImportRequired !== true && snapshot.authStatus === 'ok',
      ),
    [availableLocalRemoteSnapshots],
  );
  const selectedCloudSyncEntries = useMemo(
    () => {
      const selectedIds = new Set(cloudSyncSelectedCloudIds.map((item) => item.trim()).filter(Boolean));
      if (selectedIds.size === 0) {
        return [];
      }
      return authState.cloudProfileBackups.filter((entry) => selectedIds.has(entry.id));
    },
    [authState.cloudProfileBackups, availableCloudBackupEntries, cloudSyncSelectedCloudIds],
  );
  const selectedCloudSyncLocalSnapshots = useMemo(
    () => {
      const selectedIds = new Set(cloudSyncSelectedLocalIds.map((item) => item.trim()).filter(Boolean));
      if (selectedIds.size === 0) {
        return [];
      }
      return availableLocalRemoteSnapshots.filter((snapshot) => selectedIds.has(snapshot.id));
    },
    [availableLocalRemoteSnapshots, cloudSyncSelectedLocalIds],
  );
  const selectedCloudSyncLocalCompareRows = useMemo(
    () =>
      selectedCloudSyncLocalSnapshots.map((snapshot) => ({
        localSnapshot: snapshot,
        localProfile: visibleRealProfileById.get(snapshot.id) ?? null,
        cloudEntry:
          buildProfileSnapshotMatchKeys(snapshot)
            .map((key) => cloudEntryMatchMap.get(key) ?? null)
            .find((entry): entry is CloudProfileBackup => Boolean(entry)) ?? null,
      })),
    [cloudEntryMatchMap, selectedCloudSyncLocalSnapshots, visibleRealProfileById],
  );

  useEffect(() => {
    if (!cloudBackupCenterOpen && cloudSyncDialog === null && !issueTransferDialogOpen) {
      return;
    }

    void refreshLocalRemoteProfileSnapshots();
  }, [
    authState.profile?.id,
    cloudBackupCenterOpen,
    cloudSyncDialog,
    issueTransferDialogOpen,
    state.lastScannedAt,
  ]);

  useEffect(() => {
    if (!cloudSyncDialog) {
      return;
    }

    const localIds = availableLocalRemoteSnapshots.map((snapshot) => snapshot.id);
    const cloudIds = availableCloudBackupEntries.map((entry) => entry.id);
    setCloudSyncSelectedLocalIds((current) => {
      return current.filter((id) => localIds.includes(id));
    });
    setCloudSyncSelectedCloudIds((current) => {
      return current.filter((id) => cloudIds.includes(id));
    });
  }, [availableCloudBackupEntries, availableLocalRemoteSnapshots, cloudSyncDialog]);

  useEffect(() => {
    if (!issueTransferDialogOpen) {
      return;
    }

    const availableIds = issueTransferEligibleSnapshots.map((snapshot) => snapshot.id);
    setIssueTransferSelectedProfileIds((current) => {
      return current.filter((id) => availableIds.includes(id));
    });
  }, [issueTransferDialogOpen, issueTransferEligibleSnapshots]);

  useEffect(() => {
    if (!canAutoSyncCurrentDeviceCloudBackup) {
      cloudBackupAutoSyncKeyRef.current = null;
      return;
    }
    if (Date.now() < cloudSyncAutomationSettledUntilRef.current) {
      cloudBackupAutoSyncKeyRef.current = null;
      return;
    }

    if (
      pendingCloudBackupBusy ||
      pendingProfileTransferBusy ||
      pendingProfileTransferPrompt ||
      cloudSyncDialog !== null ||
      newDeviceCloudRestorePrompt !== null ||
      secondaryPasswordDialog !== null ||
      legacyCloudBackupReminderOpen
    ) {
      return;
    }

    if (autoCloudBackupPendingProfileIds.length === 0) {
      cloudBackupAutoSyncKeyRef.current = null;
      return;
    }

    const reconcileKey = `${authState.profile?.id ?? ''}|${authState.device.deviceId}|${visibleRealProfiles
      .filter((profile) => autoCloudBackupPendingProfileIds.includes(profile.id))
      .map((profile) => `${profile.id}:${profile.updatedAt}`)
      .sort()
      .join('|')}`;
    if (cloudBackupAutoSyncKeyRef.current === reconcileKey) {
      return;
    }

    cloudBackupAutoSyncKeyRef.current = reconcileKey;
    void handleSyncCurrentDeviceProfilesToCloud(
      true,
      autoCloudBackupPendingProfileIds,
      undefined,
      undefined,
      { silentSuccess: true },
    );
  }, [
    authState.device.deviceId,
    authState.profile,
    autoCloudBackupPendingProfileIds,
    canAutoSyncCurrentDeviceCloudBackup,
    cloudSyncDialog,
    legacyCloudBackupReminderOpen,
    newDeviceCloudRestorePrompt,
    pendingCloudBackupBusy,
    pendingProfileTransferBusy,
    pendingProfileTransferPrompt,
    secondaryPasswordDialog,
    visibleRealProfiles,
  ]);

  useEffect(() => {
    if (IS_LOCAL_FAST_IMPORT_BUILD) {
      cloudSyncAutoPromptKeyRef.current = null;
      setCloudSyncDialog(null);
      setNewDeviceCloudRestorePrompt(null);
      return;
    }

    if (authState.sessionStatus !== 'authenticated' || !authState.profile) {
      cloudSyncAutoPromptKeyRef.current = null;
      return;
    }

    if (cloudSyncPaused) {
      cloudSyncAutoPromptKeyRef.current = null;
      return;
    }
    if (Date.now() < cloudSyncAutomationSettledUntilRef.current) {
      cloudSyncAutoPromptKeyRef.current = null;
      return;
    }

    if (
      pendingCloudBackupBusy ||
      pendingProfileTransferBusy ||
      pendingProfileTransferPrompt ||
      cloudSyncDialog !== null ||
      newDeviceCloudRestorePrompt !== null ||
      secondaryPasswordDialog !== null ||
      legacyCloudBackupReminderOpen
    ) {
      return;
    }

    if (authState.lastAuthAction !== 'login' && authState.lastAuthAction !== 'register') {
      return;
    }

    const foreignProfileIds = getProfilesBoundToDifferentAccount(authState.profile)
      .map((profile) => profile.id)
      .sort();
    const availableCloudBackups = authState.cloudProfileBackups.filter(
      (entry) => entry.status === 'available' && !dismissedCloudBackupIds.includes(entry.id),
    );
    const availableBackupIds = availableCloudBackups.map((entry) => entry.id).sort();
    const localVisibleCount = visibleRealProfiles.length;
    const cloudVisibleCount = authState.cloudProfileBackups.length;
    const shouldPromptNewDeviceRestore =
      foreignProfileIds.length === 0 && availableBackupIds.length > 0 && localVisibleCount === 0;
    const shouldPromptSync =
      foreignProfileIds.length > 0 ||
      shouldPromptNewDeviceRestore ||
      (!canAutoSyncCurrentDeviceCloudBackup && localVisibleCount !== cloudVisibleCount);

    if (!shouldPromptSync) {
      return;
    }

    const reconcileKey = `${authState.profile.id}|${authState.device.deviceId}|${authState.lastAuthAction}|${foreignProfileIds.join(',')}|${availableBackupIds.join(',')}|${localVisibleCount}|${cloudVisibleCount}`;
    if (cloudSyncAutoPromptKeyRef.current === reconcileKey) {
      return;
    }

    cloudSyncAutoPromptKeyRef.current = reconcileKey;
    setCloudBackupCenterOpen(false);
    if (shouldPromptNewDeviceRestore) {
      setNewDeviceCloudRestorePrompt({
        cloudEntryIds: availableBackupIds,
      });
      return;
    }
    setCloudSyncDialog({
      source: 'auto',
      step: 'select',
      direction: null,
      action: null,
      purgeUnmatchedLocalBeforeImport: false,
    });
  }, [
    authState.cloudProfileBackups,
    authState.device.deviceId,
    authState.lastAuthAction,
    authState.profile,
    authState.sessionStatus,
    cloudSyncPaused,
    cloudSyncDialog,
    dismissedCloudBackupIds,
    legacyCloudBackupReminderOpen,
    newDeviceCloudRestorePrompt,
    pendingCloudBackupBusy,
    pendingProfileTransferBusy,
    pendingProfileTransferPrompt,
    secondaryPasswordDialog,
    visibleRealProfiles,
    canAutoSyncCurrentDeviceCloudBackup,
  ]);

  useEffect(() => {
    if (IS_LOCAL_FAST_IMPORT_BUILD) {
      accountUnlockPromptKeyRef.current = null;
      return;
    }

    if (
      authState.sessionStatus !== 'authenticated' ||
      !authState.profile ||
      !authState.security.secondaryPassword.enabled ||
      authState.security.secondaryPassword.currentDeviceTrusted ||
      sameAccountLockedProfiles.length === 0
    ) {
      if (sameAccountLockedProfiles.length === 0) {
        accountUnlockPromptKeyRef.current = null;
      }
      return;
    }

    if (
      pendingCloudBackupBusy ||
      pendingProfileTransferBusy ||
      pendingProfileTransferPrompt ||
      cloudSyncDialog !== null ||
      newDeviceCloudRestorePrompt !== null ||
      secondaryPasswordDialog !== null ||
      legacyCloudBackupReminderOpen
    ) {
      return;
    }

    const promptKey = `${authState.profile.id}|${authState.device.deviceId}|${sameAccountLockedProfiles
      .map((profile) => profile.id)
      .sort()
      .join(',')}`;
    if (accountUnlockPromptKeyRef.current === promptKey) {
      return;
    }

    accountUnlockPromptKeyRef.current = promptKey;
    openSecondaryPasswordDialog({ mode: 'verify-device' });
  }, [
    authState.device.deviceId,
    authState.profile,
    authState.security.secondaryPassword.currentDeviceTrusted,
    authState.security.secondaryPassword.enabled,
    authState.sessionStatus,
    cloudSyncDialog,
    legacyCloudBackupReminderOpen,
    newDeviceCloudRestorePrompt,
    pendingCloudBackupBusy,
    pendingProfileTransferBusy,
    pendingProfileTransferPrompt,
    sameAccountLockedProfiles,
    secondaryPasswordDialog,
  ]);

  useEffect(() => {
    if (!(canAccessEngineerFeatures && localSimulationEnabled)) {
      return;
    }

    const timer = window.setInterval(() => {
      setSimulationNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [canAccessEngineerFeatures, localSimulationEnabled]);

  useEffect(() => {
    if (!canAccessEngineerFeatures) {
      return;
    }

    persistHiddenModePassword(hiddenModeStoredPassword);
  }, [canAccessEngineerFeatures, hiddenModeStoredPassword]);

  useEffect(() => {
    if (!canAccessEngineerFeatures) {
      return;
    }

    persistLocalSimulationSettings({
      enabled: localSimulationEnabled,
      cardCount: localSimulationCardCount,
      planTypes: localSimulationPlanTypes,
    });
  }, [
    canAccessEngineerFeatures,
    localSimulationCardCount,
    localSimulationEnabled,
    localSimulationPlanTypes,
  ]);

  const feedbackThreads = useMemo(
    () =>
      [...authState.feedback.items].sort((left, right) => {
        const leftTime = Date.parse(left.latestMessageAt || left.createdAt || '');
        const rightTime = Date.parse(right.latestMessageAt || right.createdAt || '');
        return (Number.isNaN(rightTime) ? 0 : rightTime) - (Number.isNaN(leftTime) ? 0 : leftTime);
      }),
    [authState.feedback.items],
  );
  const activeFeedbackThread = feedbackThreads[0] ?? null;
  const latestFeedbackHostReplyIds = useMemo(
    () =>
      authState.feedback.items.reduce<string[]>((ids, item) => {
        for (let index = item.messages.length - 1; index >= 0; index -= 1) {
          const message = item.messages[index];
          if (message.sender === 'host') {
            ids.push(message.id);
            break;
          }
        }
        return ids;
      }, []),
    [authState.feedback.items],
  );
  const latestFeedbackHostReplies = useMemo(
    () =>
      authState.feedback.items.reduce<
        Array<{
          id: string;
          body: string;
          threadId: string;
        }>
      >((items, entry) => {
        for (let index = entry.messages.length - 1; index >= 0; index -= 1) {
          const message = entry.messages[index];
          if (message.sender === 'host') {
            items.push({
              id: message.id,
              body: message.body,
              threadId: entry.id,
            });
            break;
          }
        }
        return items;
      }, []),
    [authState.feedback.items],
  );
  const latestFeedbackUserMessageId = useMemo(() => {
    if (!activeFeedbackThread) {
      return null;
    }
    for (let index = activeFeedbackThread.messages.length - 1; index >= 0; index -= 1) {
      const message = activeFeedbackThread.messages[index];
      if (message.sender === 'user') {
        return message.id;
      }
    }
    return null;
  }, [activeFeedbackThread]);
  const latestFeedbackHostMessageId = useMemo(() => {
    if (!activeFeedbackThread) {
      return null;
    }
    for (let index = activeFeedbackThread.messages.length - 1; index >= 0; index -= 1) {
      const message = activeFeedbackThread.messages[index];
      if (message.sender === 'host') {
        return message.id;
      }
    }
    return null;
  }, [activeFeedbackThread]);
  const visibleFeedbackMessages = useMemo(() => {
    const threadStatus = activeFeedbackThread?.status ?? null;
    const latestHostFallbackRead =
      activeFeedbackThread && latestFeedbackHostMessageId
        ? legacyFeedbackReadFallback[activeFeedbackThread.id] === latestFeedbackHostMessageId
        : false;
    const wasHostMessageReadByUser = (messageCreatedAt: string): boolean => {
      const userReadAt = activeFeedbackThread?.userReadAt;
      if (!userReadAt) {
        return false;
      }

      const userReadAtMs = Date.parse(userReadAt);
      const messageCreatedAtMs = Date.parse(messageCreatedAt);
      if (Number.isNaN(userReadAtMs) || Number.isNaN(messageCreatedAtMs)) {
        return true;
      }

      return userReadAtMs >= messageCreatedAtMs;
    };
    const serverMessages = activeFeedbackThread?.messages.map((message) => ({
      id: message.id,
      sender: message.sender,
      body: message.body,
      createdAt: message.createdAt,
      deliveryState:
        message.sender === 'user'
          ? message.id === latestFeedbackUserMessageId
            ? threadStatus === 'pending'
              ? 'unread'
              : 'read'
            : 'sent'
          : message.id === latestFeedbackHostMessageId
            ? wasHostMessageReadByUser(message.createdAt) || latestHostFallbackRead
              ? 'read'
              : 'unread'
            : 'sent',
      errorMessage: null,
    })) ?? [];

    const optimisticMessages = optimisticFeedbackMessages
      .filter((message) =>
        activeFeedbackThread ? message.threadId === activeFeedbackThread.id : message.threadId === null,
      )
      .map((message) => ({
        id: message.id,
        sender: 'user' as const,
        body: message.body,
        createdAt: message.createdAt,
        deliveryState: message.state,
        errorMessage: message.errorMessage,
      }));

    return [...serverMessages, ...optimisticMessages].sort((left, right) => {
      const leftTime = Date.parse(left.createdAt);
      const rightTime = Date.parse(right.createdAt);
      if (Number.isNaN(leftTime) || Number.isNaN(rightTime) || leftTime === rightTime) {
        return left.id.localeCompare(right.id);
      }
      return leftTime - rightTime;
    });
  }, [
    activeFeedbackThread,
    legacyFeedbackReadFallback,
    latestFeedbackHostMessageId,
    latestFeedbackUserMessageId,
    optimisticFeedbackMessages,
  ]);
  const latestVisibleFeedbackMessageId =
    visibleFeedbackMessages[visibleFeedbackMessages.length - 1]?.id ?? null;
  const feedbackComposerIsNewThread = feedbackThreads.length === 0;
  const feedbackConversationStatusLabel = feedbackComposerIsNewThread
    ? null
    : activeFeedbackThread?.status === 'acknowledged'
      ? '客服已回复'
      : activeFeedbackThread?.status === 'read'
        ? '客服已读'
        : '客服未读';

  useEffect(() => {
    const nextSeenHostReplyIds = new Set(latestFeedbackHostReplyIds);

    if (feedbackCenterOpen) {
      feedbackSeenHostReplyIdsRef.current = nextSeenHostReplyIds;
      if (feedbackHeaderUnreadVisible) {
        setFeedbackHeaderUnreadVisible(false);
      }
      if (!feedbackHostReplyInitializedRef.current) {
        feedbackHostReplyInitializedRef.current = true;
      }
      return;
    }

    if (!feedbackHostReplyInitializedRef.current) {
      feedbackSeenHostReplyIdsRef.current = nextSeenHostReplyIds;
      feedbackHostReplyInitializedRef.current = true;
      return;
    }

    if (
      latestFeedbackHostReplyIds.some((messageId) => !feedbackSeenHostReplyIdsRef.current.has(messageId))
    ) {
      setFeedbackHeaderUnreadVisible(true);
    }
  }, [feedbackCenterOpen, feedbackHeaderUnreadVisible, latestFeedbackHostReplyIds]);

  useEffect(() => {
    const nextObservedHostReplyIds = new Set(latestFeedbackHostReplyIds);
    if (!feedbackObservedHostReplyInitializedRef.current) {
      feedbackObservedHostReplyIdsRef.current = nextObservedHostReplyIds;
      feedbackObservedHostReplyInitializedRef.current = true;
      return;
    }

    const incomingReplies = latestFeedbackHostReplies.filter(
      (reply) => !feedbackObservedHostReplyIdsRef.current.has(reply.id),
    );
    if (incomingReplies.length === 0) {
      return;
    }

    feedbackObservedHostReplyIdsRef.current = nextObservedHostReplyIds;

    incomingReplies.forEach((_reply, index) => {
      window.setTimeout(() => {
        void playMessageDropletTone(feedbackNotificationAudioContextRef, window);
      }, index * 140);
    });

    if (!document.hasFocus()) {
      incomingReplies.forEach((reply) => {
        void showDesktopNotification({
          title: `${FEEDBACK_SERVICE_DISPLAY_NAME}有新回复`,
          body: reply.body,
          tag: `feedback-reply-${reply.id}`,
          onClick: async () => {
            await window.codexWorkspace.showWindow();
            setFeedbackCenterOpen(true);
          },
        });
      });
    }
  }, [latestFeedbackHostReplyIds, latestFeedbackHostReplies]);

  useEffect(() => {
    if (
      !feedbackCenterOpen ||
      !activeFeedbackThread ||
      !latestFeedbackHostMessageId ||
      Boolean(activeFeedbackThread.userReadAt)
    ) {
      if (feedbackReadSyncingThreadIdRef.current === activeFeedbackThread?.id) {
        feedbackReadSyncingThreadIdRef.current = null;
      }
      return;
    }

    if (feedbackReadSyncingThreadIdRef.current === activeFeedbackThread.id) {
      return;
    }

    feedbackReadSyncingThreadIdRef.current = activeFeedbackThread.id;
    void withFeedbackActionTimeout(
      authClient.markFeedbackConversationRead({ feedbackId: activeFeedbackThread.id }),
      '同步客服消息状态超时。',
    )
      .then((result) => {
        setLegacyFeedbackReadFallback((current) => ({
          ...current,
          [activeFeedbackThread.id]: latestFeedbackHostMessageId,
        }));
        setAuthState(result.state);
      })
      .catch((error) => {
        feedbackReadSyncingThreadIdRef.current = null;
        const errorMessage = normalizeRuntimeErrorMessage(error, '同步客服消息状态失败。');
        const isLegacyCloudMissingFeedbackReadRoute =
          /\/api\/feedback\/read/i.test(errorMessage) &&
          /未找到路由|404/i.test(errorMessage);
        if (isLegacyCloudMissingFeedbackReadRoute) {
          setLegacyFeedbackReadFallback((current) => ({
            ...current,
            [activeFeedbackThread.id]: latestFeedbackHostMessageId,
          }));
          return;
        }
        setToast({
          tone: 'error',
          message: errorMessage,
        });
      });
  }, [activeFeedbackThread, feedbackCenterOpen, latestFeedbackHostMessageId]);

  useEffect(() => {
    if (!feedbackCenterOpen) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      const listElement = feedbackMessageListRef.current;
      if (listElement) {
        listElement.scrollTop = listElement.scrollHeight;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [feedbackCenterOpen, latestVisibleFeedbackMessageId]);

  useEffect(() => {
    return () => {
      if (startupCardIntroRevealTimerRef.current !== null) {
        window.clearTimeout(startupCardIntroRevealTimerRef.current);
        startupCardIntroRevealTimerRef.current = null;
      }
      const context = startupCardIntroAudioContextRef.current;
      startupCardIntroAudioContextRef.current = null;
      if (context) {
        void context.close().catch(() => {
          // Ignore audio teardown failures during unmount.
        });
      }
      const feedbackContext = feedbackNotificationAudioContextRef.current;
      feedbackNotificationAudioContextRef.current = null;
      if (feedbackContext) {
        void feedbackContext.close().catch(() => {
          // Ignore notification-audio teardown failures during unmount.
        });
      }
    };
  }, []);

  useEffect(() => {
    void loadCardFlipAudioBytes();
  }, []);

  useEffect(() => {
    if (!startupLoadingVisible) {
      startupCardIntroPendingRef.current = true;
    }
  }, [startupLoadingVisible]);

  useEffect(() => {
    if (
      startupLoadingVisible ||
      !startupCardIntroPendingRef.current ||
      startupCardIntroPlayedRef.current ||
      filteredProfiles.length === 0
    ) {
      return;
    }

    startupCardIntroPendingRef.current = false;
    startupCardIntroPlayedRef.current = true;
    playSpacePoolEntryAnimation(filteredProfiles);
  }, [filteredProfiles, startupLoadingVisible, effectiveCardFlipVolumeScale]);

  useEffect(() => {
    if (
      startupLoadingVisible ||
      startupCardIntroPhase !== 'revealed' ||
      startupProxyCheckTriggeredRef.current
    ) {
      return;
    }

    void runStartupNetworkProbe();
  }, [startupCardIntroPhase, startupLoadingVisible]);

  const batchNoteEligibleProfiles = useMemo(
    () => filteredProfiles.filter((profile): profile is ProfileSummary => !isSimulatedProfile(profile)),
    [filteredProfiles],
  );
  const batchFilteredEligibleProfiles = useMemo(
    () => filterBatchProfilesBySelection(batchNoteEligibleProfiles, batchSelectionFilter),
    [batchNoteEligibleProfiles, batchSelectionFilter],
  );
  const batchNoteDefaultProfileIds = useMemo(
    () =>
      batchNoteEligibleProfiles
        .filter((profile) => !getProfileCustomNote(profile))
        .map((profile) => profile.id),
    [batchNoteEligibleProfiles],
  );
  const batchEnableEligibleProfileIds = useMemo(
    () =>
      batchNoteEligibleProfiles
        .filter((profile) => profile.authStatus === 'ok' && (profile.manuallyDisabled || profile.autoDisabled))
        .map((profile) => profile.id),
    [batchNoteEligibleProfiles],
  );
  const batchDisableEligibleProfileIds = useMemo(
    () =>
      batchNoteEligibleProfiles
        .filter(
          (profile) => profile.authStatus === 'ok' && !profile.manuallyDisabled && !profile.autoDisabled,
        )
        .map((profile) => profile.id),
    [batchNoteEligibleProfiles],
  );
  const recognizedDisabledProfileIds = useMemo(
    () => state.profiles.filter(isRecognizedDisabledProfile).map((profile) => profile.id),
    [state.profiles],
  );
  const getBatchDefaultProfileIds = (
    mode: BatchProfileActionMode,
    filter: BatchProfileSelectionFilter = batchSelectionFilter,
  ): string[] => {
    const visibleIds = new Set(filterBatchProfilesBySelection(batchNoteEligibleProfiles, filter).map((profile) => profile.id));
    const baseIds =
      mode === 'notes'
        ? batchNoteDefaultProfileIds
        : mode === 'enable'
          ? batchEnableEligibleProfileIds
          : mode === 'disable'
            ? batchDisableEligibleProfileIds
            : [];

    return baseIds.filter((profileId) => visibleIds.has(profileId));
  };
  useEffect(() => {
    if (!batchNoteDialogOpen) {
      return;
    }

    const visibleIds = new Set(batchFilteredEligibleProfiles.map((profile) => profile.id));
    setBatchNoteSelectedProfileIds((current) =>
      current.filter((profileId) => visibleIds.has(profileId)),
    );
  }, [batchFilteredEligibleProfiles, batchNoteDialogOpen]);
  const quotaAlertSuggestion = useMemo(
    () =>
      mandatoryUpdateLockActive
        ? null
        : getQuotaAlertSuggestion(
            state.profiles,
            state.settings.lowQuotaWarningThreshold,
            state.settings.lowQuotaWarningEnabled &&
              !(state.settings.switchMode === 'openclaw' && state.settings.autoSwitchEnabled),
          ),
    [
      mandatoryUpdateLockActive,
      state.profiles,
      state.settings.autoSwitchEnabled,
      state.settings.lowQuotaWarningEnabled,
      state.settings.lowQuotaWarningThreshold,
      state.settings.switchMode,
    ],
  );
  const autoSwitchSuggestion = useMemo(
    () =>
      mandatoryUpdateLockActive
        ? null
        : state.settings.switchMode === 'openclaw'
        ? getQuotaAlertSuggestion(
            state.profiles,
            state.settings.autoSwitchThreshold,
            state.settings.autoSwitchEnabled,
          )
        : null,
    [
      mandatoryUpdateLockActive,
      state.profiles,
      state.settings.autoSwitchEnabled,
      state.settings.autoSwitchThreshold,
      state.settings.switchMode,
    ],
  );
  const recommendedProfileId = mandatoryUpdateLockActive
    ? null
    :
    autoSwitchSuggestion?.recommendedProfile.id ?? quotaAlertSuggestion?.recommendedProfile.id ?? null;
  const switchPromptBlocked =
    authState.sessionStatus !== 'authenticated' ||
    !isWorkspaceAuthorized(authState) ||
    authGateStandaloneMode !== null;
  const quotaPromptBlocked = switchPromptBlocked || startupLoadingVisible;

  useEffect(() => {
    if (!mandatoryUpdateLockActive) {
      return;
    }

    setQuotaAlertTarget(null);
    setAutoSwitchPrompt(null);
    setAutoDisabledPrompt(null);
    setQueuedAutoDisabledPrompt(null);
  }, [mandatoryUpdateLockActive]);

  useEffect(() => {
    if (!switchPromptBlocked) {
      return;
    }

    setQuotaAlertTarget(null);
    setAutoSwitchPrompt(null);
    setAutoDisabledPrompt(null);
    setQueuedAutoDisabledPrompt(null);
  }, [switchPromptBlocked]);

  useEffect(() => {
    if (quotaPromptBlocked || !quotaAlertSuggestion) {
      setQuotaAlertTarget(null);
      if (!quotaAlertSuggestion) {
        setDismissedQuotaAlertKey(null);
      }
      return;
    }

    if (
      quotaAlertTarget?.warningKey === quotaAlertSuggestion.warningKey ||
      dismissedQuotaAlertKey === quotaAlertSuggestion.warningKey ||
      quotaAlertSnoozed ||
      busyAction !== null ||
      addDialogOpen ||
      editDialogOpen ||
      workspaceSelectionMode !== null ||
      Boolean(batchDeleteConfirmRequest) ||
      Boolean(cloudDeleteSyncPrompt) ||
      Boolean(autoDisabledPrompt) ||
      Boolean(autoSwitchPrompt) ||
      Boolean(switchTarget) ||
      Boolean(deleteTarget)
    ) {
      return;
    }

    setQuotaAlertTarget(quotaAlertSuggestion);
  }, [
    addDialogOpen,
    autoDisabledPrompt,
    autoSwitchPrompt,
    batchDeleteConfirmRequest,
    busyAction,
    cloudDeleteSyncPrompt,
    deleteTarget,
    dismissedQuotaAlertKey,
    editDialogOpen,
    quotaAlertSnoozed,
    quotaAlertSuggestion,
    quotaAlertTarget,
    quotaPromptBlocked,
    switchTarget,
    workspaceSelectionMode,
  ]);

  useEffect(() => {
    if (!autoSwitchPrompt) {
      return;
    }

    const tick = window.setInterval(() => {
      if (Date.now() >= autoSwitchPrompt.deadlineAt) {
        window.clearInterval(tick);
        setDismissedAutoSwitchKey(autoSwitchPrompt.suggestion.warningKey);
        void executeSwitchProfile(autoSwitchPrompt.suggestion.recommendedProfile, 'auto');
      } else {
        setAutoSwitchPrompt((current) => (current ? { ...current } : current));
      }
    }, 1000);

    return () => window.clearInterval(tick);
  }, [autoSwitchPrompt]);

  useEffect(() => {
    if (
      quotaPromptBlocked ||
      !autoSwitchSuggestion ||
      !state.settings.autoSwitchEnabled ||
      state.settings.switchMode !== 'openclaw'
    ) {
      setAutoSwitchPrompt(null);
      if (!autoSwitchSuggestion || !state.settings.autoSwitchEnabled || state.settings.switchMode !== 'openclaw') {
        setDismissedAutoSwitchKey(null);
      }
      return;
    }

    if (
      autoSwitchPrompt?.suggestion.warningKey === autoSwitchSuggestion.warningKey ||
      dismissedAutoSwitchKey === autoSwitchSuggestion.warningKey ||
      busyAction !== null ||
      addDialogOpen ||
      editDialogOpen ||
      Boolean(autoDisabledPrompt) ||
      Boolean(switchTarget) ||
      Boolean(deleteTarget) ||
      Boolean(switchSuccessNotice)
    ) {
      return;
    }

    setAutoSwitchPrompt({
      suggestion: autoSwitchSuggestion,
      startedAt: Date.now(),
      deadlineAt: Date.now() + state.settings.autoSwitchCountdownSeconds * 1000,
    });
    setQuotaAlertTarget(null);
  }, [
    addDialogOpen,
    autoDisabledPrompt,
    autoSwitchPrompt,
    autoSwitchSuggestion,
    busyAction,
    deleteTarget,
    dismissedAutoSwitchKey,
    editDialogOpen,
    state.settings.autoSwitchCountdownSeconds,
    state.settings.autoSwitchEnabled,
    state.settings.switchMode,
    quotaPromptBlocked,
    switchSuccessNotice,
    switchTarget,
  ]);

  const latestBackup = state.backups[0] ?? null;
  const activeProfile =
    state.profiles.find((profile) => profile.isActive) ??
    state.profiles[0] ??
    null;
  const isCodexMode = state.settings.switchMode === 'codex';
  const isOpenClawMode = state.settings.switchMode === 'openclaw';
  const hasPendingCurrentAuthImport = state.summary.currentAuthExists && !state.summary.authRecognized;
  const autoDisabledPromptProfile = autoDisabledPrompt
    ? state.profiles.find((profile) => profile.id === autoDisabledPrompt.profileId) ?? null
    : null;
  const autoSwitchSecondsLeft = autoSwitchPrompt
    ? Math.max(0, Math.ceil((autoSwitchPrompt.deadlineAt - Date.now()) / 1000))
    : 0;
  const updateStatusLabel = mapUpdateAvailabilityLabel(updateState);
  const updateSourceLabel = mapUpdateSourceLabel(updateState.sourceName);
  const updateChannelLabel = mapUpdateChannelLabel(updateState.channel);
  const updateEntitlementChannel =
    updateState.override && updateState.override.channel !== 'stable'
      ? updateState.override.channel
      : null;
  const hasPrereleaseEntitlement = Boolean(
    updateEntitlementChannel && updateState.override?.allowPrerelease,
  );
  const updateEntitlementLabel = updateEntitlementChannel
    ? mapUpdateChannelLabel(updateEntitlementChannel)
    : '内测';
  const updateUserBadgeLabel =
    updateState.allowEngineerAccess
      ? '高级内测用户'
      : updateState.channel === 'beta'
      ? '内测用户'
      : updateState.channel === 'canary'
        ? '测试用户'
        : null;
  const displayedCurrentVersion = useMemo(
    () => normalizeVisibleVersion(updateState.currentVersion),
    [updateState.currentVersion],
  );
  const versionWithChannelLabel = updateUserBadgeLabel
    ? `v${displayedCurrentVersion} / ${updateUserBadgeLabel}`
    : `v${displayedCurrentVersion}`;
  const canStartDownloadedInstaller =
    updateState.currentPlatform === 'win' && Boolean(updateState.downloadFilePath);
  const updatePrimaryActionLabel =
    canStartDownloadedInstaller
      ? '开始安装更新'
      : updateState.availability === 'downloading'
      ? updateState.downloadProgressPercent !== null
        ? `下载中 ${updateState.downloadProgressPercent}%`
        : '下载中...'
      : updateState.currentPlatform === 'win'
        ? '下载安装更新'
        : '下载更新包';
  const updateBadgeVisible =
    updateState.updateAvailable ||
    updateState.blockedByMandatory ||
    updateState.downloadStage === 'failed';
  const updateStatusDisplayLabel =
    updateState.availability === 'downloading'
      ? updateState.downloadProgressPercent !== null
        ? `下载中 ${updateState.downloadProgressPercent}%`
        : updateState.downloadStageLabel ?? updateStatusLabel
      : updateStatusLabel;
  const updateSummaryContent =
    updateState.notesContent ||
    updateState.manifest?.notes ||
    updateState.errorMessage ||
    '当前还没有拿到更新说明。';
  const updateGuardHint =
    updateState.currentPlatform === 'win'
      ? '直接点“下载安装更新”即可完成这一轮升级；如果下载失败，先重新检查更新，再联系客服。'
      : '直接点“下载更新包”即可完成这一轮升级；如果下载失败，先重新检查更新，再联系客服。';
  const platformTags = useMemo(
    () => [
      updateState.currentPlatform === 'win'
        ? 'Windows'
        : updateState.currentPlatform === 'mac'
          ? 'macOS'
          : 'Unknown',
    ],
    [updateState.currentPlatform],
  );
  const userCenterSession = useMemo(
    () => mapAuthStateToUserCenterSession(authState),
    [authState],
  );
  const beginProfilesStateRequest = (): number => {
    const nextRequestId = nextProfilesStateRequestIdRef.current + 1;
    nextProfilesStateRequestIdRef.current = nextRequestId;
    return nextRequestId;
  };

  const beginStatisticsRequest = (): number => {
    const nextRequestId = statisticsRequestIdRef.current + 1;
    statisticsRequestIdRef.current = nextRequestId;
    return nextRequestId;
  };

  const commitProfilesState = (requestId: number, nextState: ProfilesState): void => {
    if (requestId < appliedProfilesStateRequestIdRef.current) {
      return;
    }

    if (uiInteractionTestEnabledRef.current) {
      return;
    }

    appliedProfilesStateRequestIdRef.current = requestId;
    setState(nextState);
  };

  const commitAuthoritativeProfilesState = (nextState: ProfilesState): void => {
    const nextRequestId =
      Math.max(nextProfilesStateRequestIdRef.current, appliedProfilesStateRequestIdRef.current) + 1;
    nextProfilesStateRequestIdRef.current = nextRequestId;
    appliedProfilesStateRequestIdRef.current = nextRequestId;
    setState(nextState);
  };

  const loadStatisticsDashboard = async (
    range: StatisticsRange,
    options?: {
      silent?: boolean;
      modeOverride?: SwitchMode;
      source?: StatisticsRefreshSource;
    },
  ) => {
    if (typeof window.codexWorkspace.getStatisticsDashboard !== 'function') {
      return;
    }

    const requestId = beginStatisticsRequest();
    const isBackgroundRefresh =
      options?.silent === true || (options?.silent !== false && options?.source === 'background');
    if (!isBackgroundRefresh) {
      setStatisticsLoading(true);
    }
    setStatisticsError(null);

    try {
      const nextDashboard = await window.codexWorkspace.getStatisticsDashboard(
        range,
        options?.modeOverride ?? statisticsMode,
      );
      if (requestId !== statisticsRequestIdRef.current) {
        return;
      }

      setStatisticsRefreshSource(options?.source ?? 'manual');
      setStatisticsDashboard(nextDashboard);
    } catch (error) {
      if (requestId !== statisticsRequestIdRef.current) {
        return;
      }

      const message = normalizeRuntimeErrorMessage(error, '读取统计中心失败。');
      if (message.includes('app:get-statistics-dashboard') || message.toLowerCase().includes('ipc')) {
        setStatisticsDashboard(null);
        setStatisticsError('当前运行进程与界面版本不一致，请重启到最新安装包后再查看统计中心。');
        return;
      }
      setStatisticsError(message);
    } finally {
      if (requestId === statisticsRequestIdRef.current && !isBackgroundRefresh) {
        setStatisticsLoading(false);
      }
    }
  };

  const fallbackStatisticsDashboard = useMemo(
    () => buildLocalStatisticsDashboard(state, statisticsRange),
    [state, statisticsRange],
  );

  const resolvedStatisticsDashboard = useMemo(() => {
    if (
      statisticsDashboard &&
      statisticsDashboard.range === statisticsRange &&
      (
        fallbackStatisticsDashboard.totalSpaces === 0 ||
        (statisticsDashboard.totalSpaces > 0 && statisticsDashboard.tableRows.length > 0)
      )
    ) {
      return statisticsDashboard;
    }

    return fallbackStatisticsDashboard;
  }, [fallbackStatisticsDashboard, statisticsDashboard, statisticsRange]);

  const statisticsProfileMetaById = useMemo(
    () =>
      Object.fromEntries(
        state.profiles.map((profile) => [
          profile.id,
          {
            accountLabel:
              getVisibleNoteFromText(
                profile.notes,
                profile.authDerivedEmail ?? profile.securityBinding?.ownerUserEmail,
              ) || '未填写备注邮箱',
            accountEmail:
              profile.authDerivedEmail?.trim() ||
              profile.securityBinding?.ownerUserEmail?.trim() ||
              null,
          },
        ]),
      ),
    [state.profiles],
  );

  const statisticsContextMeta = useMemo(
    () => ({
      softwareAccount: authState.profile?.email?.trim() || '未登录',
      nickname: authState.profile?.displayName?.trim() || '未登录',
      currentDevice:
        [authState.device.machineName, authState.device.platformLabel]
          .filter((value) => Boolean(value?.trim()))
          .join(' · ') ||
        authState.device.hostLabel ||
        '未识别设备',
      deviceIdentity:
        authState.device.hostLabel?.trim() ||
        authState.device.deviceId?.trim() ||
        authState.device.fingerprintSummary?.trim() ||
        '未识别',
    }),
    [authState.device, authState.profile],
  );

  useEffect(() => {
    uiInteractionTestEnabledRef.current = uiInteractionTestEnabled;
  }, [uiInteractionTestEnabled]);

  useEffect(() => {
    const runtimeApi = window.codexWorkspace as Partial<typeof window.codexWorkspace>;
    if (typeof runtimeApi.onProfilesStateChange !== 'function') {
      return;
    }

    return runtimeApi.onProfilesStateChange((nextState) => {
      const requestId = beginProfilesStateRequest();
      commitProfilesState(requestId, nextState);
    });
  }, []);

  const avatarPreviewSrc = avatarUploadDraft?.sourceDataUrl ?? avatarImageSrc;
  const avatarPreviewMetrics = useMemo(
    () =>
      avatarUploadDraft
        ? buildAvatarRenderMetrics(
            avatarUploadDraft.sourceWidth,
            avatarUploadDraft.sourceHeight,
            avatarUploadDraft.scale,
          )
        : null,
    [avatarUploadDraft],
  );
  const avatarPreviewStyle =
    avatarUploadDraft && avatarPreviewMetrics
      ? {
          width: `${avatarPreviewMetrics.renderWidth}px`,
          height: `${avatarPreviewMetrics.renderHeight}px`,
          transform: `translate(calc(-50% + ${avatarUploadDraft.offsetX}px), calc(-50% + ${avatarUploadDraft.offsetY}px))`,
        }
      : undefined;
  const avatarAdjustPreviewSrc = avatarAdjustDraft?.sourceDataUrl ?? avatarPreviewSrc;
  const avatarAdjustMetrics = useMemo(
    () =>
      avatarAdjustDraft
        ? buildAvatarRenderMetrics(
            avatarAdjustDraft.sourceWidth,
            avatarAdjustDraft.sourceHeight,
            avatarAdjustDraft.scale,
          )
        : null,
    [avatarAdjustDraft],
  );
  const avatarAdjustStyle =
    avatarAdjustDraft && avatarAdjustMetrics
      ? {
          width: `${avatarAdjustMetrics.renderWidth}px`,
          height: `${avatarAdjustMetrics.renderHeight}px`,
          transform: `translate(calc(-50% + ${avatarAdjustDraft.offsetX}px), calc(-50% + ${avatarAdjustDraft.offsetY}px))`,
        }
      : undefined;
  const avatarPreviewTitle = avatarUploadDraft
    ? '待确认预览'
    : avatarImageSrc
      ? '当前头像'
      : '等待上传';
  const licenseCenterSnapshot = useMemo(
    () => mapAuthStateToLicenseSnapshot(authState),
    [authState],
  );
  const authGateVisible = useMemo(
    () => !isWorkspaceAuthorized(authState),
    [authState],
  );
  const authGateWorkflowVisible = authGateVisible || authGateStandaloneMode !== null;
  const authGatePreviewMode = authGateStandaloneMode === 'preview' && !authGateVisible;
  const authGateInteractiveMode =
    authGateStandaloneMode === 'interactive' && !authGateVisible;
  const authGateRequiresAccount = useMemo(
    () =>
      !authGatePreviewMode &&
      authState.sessionStatus !== 'authenticated' &&
      hasUsableWorkspaceLicense(authState),
    [authGatePreviewMode, authState],
  );
  const authGateRequiresRelogin = authGateRequiresAccount && authState.lastAuthAction === 'logout';
  const authGateShowsAuthenticatedSession =
    authState.sessionStatus === 'authenticated' &&
    !authGatePreviewMode &&
    !authGateInteractiveMode &&
    authEntryMode === 'manual';
  const authCenterOverlayVisible = authGateWorkflowVisible || licenseCenterOpen;
  const activeQrPreviewItem =
    authGateQrPreviewState?.items.find((item) => item.id === authGateQrPreviewState.activeId) ??
    authGateQrPreviewState?.items[0] ??
    null;
  useEffect(() => {
    if (authGateVisible && authGateStandaloneMode === 'preview') {
      setAuthGateStandaloneMode(null);
    }
  }, [authGateVisible, authGateStandaloneMode]);
  const authCenterUrgentAlert = useMemo(() => {
    if (!authGateVisible || authGatePreviewMode) {
      return false;
    }

    if (authState.license.permanent || authState.license.status === 'active') {
      return false;
    }

    if (!authState.license.expiresAt) {
      return false;
    }

    const expiresAtMs = new Date(authState.license.expiresAt).getTime();
    return Number.isNaN(expiresAtMs) || expiresAtMs <= authGateNowMs;
  }, [
    authGateNowMs,
    authGatePreviewMode,
    authGateVisible,
    authState.license.expiresAt,
    authState.license.permanent,
    authState.license.status,
  ]);
  const scanEntryLabel = authState.sessionStatus === 'authenticated' ? '扫码绑定' : '扫码登录';
  const scanChannelLabel =
    scanChannel === 'official_account'
      ? authState.sessionStatus === 'authenticated'
        ? '公众号扫码绑定'
        : '公众号二维码登录'
      : scanEntryLabel;
  const authGateOverlayNotice = useMemo(() => {
    if (authGatePreviewMode) {
      return '工程师预览模式下不会拉取真实二维码，也不会提交真实登录请求。';
    }
    if (scanPendingBinding && authEntryMode === 'manual') {
      return authMode === 'register'
        ? '先完成账号注册；注册成功后请回到登录步骤，再继续本次扫码绑定。'
        : '该扫码尚未完成账号绑定，请先登录；登录成功后会自动回到扫码界面继续绑定。';
    }
    if (authEntryMode === 'scan' && authState.sessionStatus === 'authenticated') {
      return '扫码确认后会自动绑定到当前账号。';
    }
    if (authGateRequiresRelogin) {
      return '当前设备已有授权，请重新登录后继续使用。';
    }
    if (authGateRequiresAccount) {
      return '当前设备已有授权，登录成功后即可继续进入主界面。';
    }
    if (authCenterUrgentAlert) {
      return '当前授权已到期，请先打开授权中心补录授权码。';
    }
    return authState.bootstrap.notice;
  }, [
    authCenterUrgentAlert,
    authEntryMode,
    authGatePreviewMode,
    authGateRequiresAccount,
    authGateRequiresRelogin,
    authMode,
    authState.sessionStatus,
    authState.bootstrap.notice,
    scanPendingBinding,
  ]);
  const authGateManualNotice = useMemo(() => {
    if (!scanPendingBinding || authEntryMode !== 'manual') {
      return null;
    }

    return authMode === 'register'
      ? '先完成账号注册；注册成功后请回到登录步骤，再继续本次扫码绑定。'
      : '该扫码尚未完成账号绑定，请先登录；登录成功后会自动回到扫码界面继续处理。';
  }, [authEntryMode, authMode, scanPendingBinding]);
  const scanStatusTitle = useMemo(() => {
    if (authGatePreviewMode) {
      return `${scanChannelLabel}预览`;
    }
    if (scanFlowState === 'pending_manual_bind') {
      return '请先登录后完成绑定';
    }
    if (scanFlowState === 'resume_binding') {
      return scanBusy === 'consume' ? '正在完成绑定' : '正在恢复绑定';
    }
    if (scanFlowState === 'binding_success') {
      return '绑定成功';
    }
    if (scanError) {
      return authState.sessionStatus === 'authenticated' ? '请重新扫码绑定' : '请重新扫码';
    }
    if (scanBusy === 'create') {
      return '正在生成二维码';
    }
    if (scanBusy === 'consume') {
      return authState.sessionStatus === 'authenticated' ? '正在完成绑定' : '正在确认登录';
    }
    switch (scanTicket?.status) {
      case 'scanned':
        return '已扫码';
      case 'confirmed':
        return authState.sessionStatus === 'authenticated' || scanPendingBinding ? '正在确认绑定' : '登录中';
      case 'expired':
        return '二维码已过期';
      case 'cancelled':
        return '已取消登录';
      case 'timeout':
        return '等待扫码超时';
      default:
        return scanChannelLabel;
    }
  }, [
    authGatePreviewMode,
    authState.sessionStatus,
    scanBusy,
    scanChannelLabel,
    scanEntryLabel,
    scanError,
    scanFlowState,
    scanPendingBinding,
    scanTicket?.status,
  ]);
  const scanStatusHint = useMemo(() => {
    const waitingHint =
      scanChannel === 'official_account'
        ? authState.sessionStatus === 'authenticated'
          ? '请使用微信扫描公众号二维码，并在公众号会话中完成当前账号绑定。'
          : '请使用微信扫描公众号二维码，并在公众号会话中完成登录确认。'
        : authState.sessionStatus === 'authenticated'
          ? '微信扫码绑定当前账号'
          : '微信扫码登录';
    if (authGatePreviewMode) {
      return scanChannel === 'official_account'
        ? '这里只展示公众号扫码样式，不会请求真实二维码。'
        : '这里只展示预览样式，不会请求真实扫码票据。';
    }
    if (scanFlowState === 'pending_manual_bind') {
      return '该扫码尚未完成账号绑定，请先登录，登录成功后会自动回到扫码界面继续处理。';
    }
    if (scanFlowState === 'resume_binding') {
      return scanBusy === 'consume'
        ? '正在验证当前扫码是否可以绑定到你的账号。'
        : '登录成功，正在恢复本次扫码绑定。';
    }
    if (scanFlowState === 'binding_success') {
      return authState.sessionStatus === 'authenticated'
        ? '扫码身份已绑定到当前账号。'
        : '扫码登录成功，正在进入当前界面。';
    }
    if (scanError) {
      return scanError;
    }
    if (scanBusy === 'create') {
      return '请稍后';
    }
    if (scanBusy === 'consume') {
      return authState.sessionStatus === 'authenticated' ? '正在验证绑定状态。' : '正在验证登录状态。';
    }
    switch (scanTicket?.status) {
      case 'scanned':
        return scanChannel === 'official_account' ? '已收到公众号扫码事件，正在等待完成确认。' : '请在手机上确认登录';
      case 'confirmed':
        return authState.sessionStatus === 'authenticated' || scanPendingBinding
          ? '正在确认当前扫码是否可以绑定到你的账号。'
          : '正在进入当前界面。';
      case 'cancelled':
        return '手机上已取消本次登录，请重新扫码。';
      case 'expired':
      case 'timeout':
        return '请刷新二维码后重新扫码。';
      default:
        return waitingHint;
    }
  }, [
    authGatePreviewMode,
    authState.sessionStatus,
    scanBusy,
    scanChannel,
    scanError,
    scanFlowState,
    scanPendingBinding,
    scanTicket?.status,
  ]);
  const authGateCountdownText = useMemo(() => {
    if (authState.license.permanent) {
      return '永久有效';
    }

    return formatPreciseCountdown(authState.license.expiresAt, authGateNowMs, {
      emptyLabel:
        authState.license.status === 'inactive'
          ? '未授权'
          : authState.license.remainingLabel || '未记录',
    });
  }, [
    authGateNowMs,
    authState.license.expiresAt,
    authState.license.permanent,
    authState.license.remainingLabel,
    authState.license.status,
  ]);
  const cardUsageText = useMemo(
    () => formatCardUsageText(displayProfiles.length, authState.license.slotLimit),
    [authState.license.slotLimit, displayProfiles.length],
  );
  const cardLimitText = useMemo(
    () => formatCardLimitText(authState.license.slotLimit),
    [authState.license.slotLimit],
  );
  const inviteRulesTooltip = (authState.invite.ruleDetails ?? []).join('\n');
  const headerLicenseText = useMemo(() => {
    if (authState.license.permanent) {
      return '永久';
    }

    if (!authState.license.expiresAt) {
      return authState.license.status === 'inactive' ? '未授权' : authState.license.remainingLabel;
    }

    return formatPreciseCountdown(authState.license.expiresAt, authGateNowMs, {
      permanent: authState.license.permanent,
    });
  }, [
    authGateNowMs,
    authState.license.expiresAt,
    authState.license.permanent,
    authState.license.remainingLabel,
    authState.license.status,
  ]);
  const headerLicenseStacked = useMemo(
    () => Boolean(authState.license.expiresAt && !authState.license.permanent),
    [authState.license.expiresAt, authState.license.permanent],
  );
  const headerLicenseTone = useMemo(() => {
    if (authState.license.permanent) {
      return 'permanent';
    }

    if (!authState.license.expiresAt) {
      return authState.license.status === 'inactive' ? 'danger' : 'idle';
    }

    return getCountdownTone(authState.license.expiresAt, authGateNowMs);
  }, [authGateNowMs, authState.license.expiresAt, authState.license.permanent, authState.license.status]);
  const currentAuthOtpPurpose: AuthOtpPurpose | null =
    authMode === 'register'
      ? 'register'
      : authMode === 'forgot'
        ? 'password-reset'
        : authForm.loginMethod === 'otp'
          ? 'login'
          : null;
  const currentAuthOtpCooldownSeconds = currentAuthOtpPurpose
    ? resolveOtpCooldownSeconds(authOtpCooldowns[currentAuthOtpPurpose], authOtpNowMs)
    : 0;
  const currentAuthOtpBusy =
    currentAuthOtpPurpose !== null && authOtpBusyPurpose === currentAuthOtpPurpose;
  const secondaryPasswordResetOtpCooldownSeconds = resolveOtpCooldownSeconds(
    authOtpCooldowns['secondary-password-reset'],
    authOtpNowMs,
  );
  const secondaryPasswordResetOtpBusy = authOtpBusyPurpose === 'secondary-password-reset';
  useEffect(() => {
    setAuthForm((current) => {
      if (authMode === 'forgot') {
        return current;
      }

      const nextIdentifier =
        authState.savedCredentials.identifier ||
        authState.profile?.loginIdentifierHint ||
        authState.savedCredentials.email ||
        authState.profile?.email ||
        '';
      const nextEmail = authState.savedCredentials.email || authState.profile?.email || '';
      const nextPassword = authState.savedCredentials.rememberPassword
        ? authState.savedCredentials.password
        : '';
      const nextRememberMe = authState.savedCredentials.rememberPassword;
      if (
        nextIdentifier === current.identifier &&
        nextEmail === current.email &&
        nextPassword === current.password &&
        nextRememberMe === current.rememberMe
      ) {
        return current;
      }

      return {
        ...current,
        identifier: nextIdentifier,
        email: nextEmail,
        password: nextPassword,
        rememberMe: nextRememberMe,
      };
    });
  }, [
    authMode,
    authState.profile?.email,
    authState.profile?.loginIdentifierHint,
    authState.savedCredentials.email,
    authState.savedCredentials.identifier,
    authState.savedCredentials.password,
    authState.savedCredentials.rememberPassword,
  ]);

  useEffect(() => {
    setAvatarImageSrc(loadStoredAvatar(authState.profile));
  }, [authState.profile?.id, authState.profile?.email]);

  useEffect(() => {
    if (authMode === 'forgot') {
      setAuthForm((current) => ({
        ...current,
        password: '',
        confirmPassword: '',
      }));
    }
  }, [authMode]);

  useEffect(() => {
    if (Object.values(authOtpCooldowns).every((deadlineAt) => deadlineAt <= Date.now())) {
      return;
    }

    const syncNow = () => {
      setAuthOtpNowMs(Date.now());
    };

    syncNow();
    const timer = window.setInterval(syncNow, 1000);
    window.addEventListener('focus', syncNow);
    document.addEventListener('visibilitychange', syncNow);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', syncNow);
      document.removeEventListener('visibilitychange', syncNow);
    };
  }, [authOtpCooldowns]);

  useEffect(() => {
    setAuthGateNowMs(Date.now());

    if (authState.license.permanent && !authCenterOverlayVisible) {
      return;
    }

    const timer = window.setInterval(() => {
      setAuthGateNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [authCenterOverlayVisible, authState.license.permanent]);

  useEffect(() => {
    if (!authGateVisible) {
      return;
    }

    clearCurrentAuthImportPlaceholderCleanupTimer();
    setCurrentAuthImportPlaceholder(null);
    setUserCenterOpen(false);
    setLicenseCenterOpen(false);
  }, [authGateVisible]);

  useEffect(() => {
    if (!authGateWorkflowVisible || authEntryMode !== 'scan' || authGatePreviewMode) {
      return;
    }

    if (scanError) {
      return;
    }

    const status = scanTicket?.status ?? null;
    if (!scanTicket || status === 'expired' || status === 'cancelled' || status === 'timeout') {
      void handleRetryScanLogin();
    }
  }, [authEntryMode, authGatePreviewMode, authGateWorkflowVisible, scanError, scanTicket]);

  useEffect(() => {
    if (!authGateWorkflowVisible || authEntryMode !== 'scan' || authGatePreviewMode || !scanTicket) {
      return;
    }

    const now = Date.now();
    const expiresAtMs = scanTicket.expiresAt ? Date.parse(scanTicket.expiresAt) : Number.NaN;
    if (Number.isFinite(expiresAtMs) && expiresAtMs <= now) {
      setScanTicket((current) =>
        current?.ticketId === scanTicket.ticketId ? { ...current, status: 'expired' } : current,
      );
      return;
    }

    if (scanTicket.status === 'scanned') {
      if (scanFlowState === 'waiting_scan') {
        setScanFlowState('waiting_confirm');
      }
      const scannedAtMs = scanTicket.scannedAt ? Date.parse(scanTicket.scannedAt) : Number.NaN;
      if (Number.isFinite(scannedAtMs) && now - scannedAtMs >= 60_000) {
        setScanTicket((current) =>
          current?.ticketId === scanTicket.ticketId ? { ...current, status: 'timeout' } : current,
        );
        return;
      }
    }

    if (scanTicket.status === 'confirmed') {
      if (scanFlowState === 'waiting_scan') {
        setScanFlowState('waiting_confirm');
      }
      if (scanError || (scanFlowState !== 'waiting_confirm' && scanFlowState !== 'resume_binding')) {
        return;
      }
      if (scanConsumedTicketIdRef.current === scanTicket.ticketId) {
        return;
      }
      scanConsumedTicketIdRef.current = scanTicket.ticketId;
      void handleConsumeScanLogin(scanTicket.ticketId);
      return;
    }

    if (
      scanTicket.status === 'expired' ||
      scanTicket.status === 'cancelled' ||
      scanTicket.status === 'timeout'
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      void handleRefreshScanTicket(scanTicket.ticketId);
    }, scanTicket.status === 'scanned' ? 1_000 : 2_000);

    return () => window.clearTimeout(timer);
  }, [
    authEntryMode,
    authGatePreviewMode,
    authGateWorkflowVisible,
    scanError,
    scanFlowState,
    scanTicket,
  ]);

  useEffect(() => {
    const profile = authState.profile;
    if (!profile || !profile.needsEmailBinding) {
      emailBindingReminderProfileIdRef.current = null;
      emailBindingReminderNextAtRef.current = null;
      setEmailBindingReminderOpen(false);
      return;
    }

    if (emailBindingReminderProfileIdRef.current !== profile.id) {
      emailBindingReminderProfileIdRef.current = profile.id;
      emailBindingReminderNextAtRef.current = Date.now() + EMAIL_BINDING_REMINDER_INTERVAL_MS;
    }

    const nextAt = emailBindingReminderNextAtRef.current ?? Date.now() + EMAIL_BINDING_REMINDER_INTERVAL_MS;
    const delay = Math.max(0, nextAt - Date.now());
    const timer = window.setTimeout(() => {
      setEmailBindingReminderOpen(true);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [authState.profile]);

  useEffect(() => {
    if (!authGateVisible) {
      return;
    }

    if (!addDialogOpen && !pendingAddOverwrite) {
      return;
    }

    // Fresh machines can detect auth.json before the auth gate is cleared.
    // Keep discovery flows fully suppressed until login/activation finishes.
    setAddDialogOpen(false);
    setAddDialogInitialValues(null);
    setAddDialogDiscoveryHint(false);
    setPendingAddOverwrite(null);
  }, [addDialogOpen, authGateVisible, pendingAddOverwrite]);

  useEffect(() => {
    if (!workspaceOAuthMonitor?.started) {
      return;
    }

    let disposed = false;
    let attempts = 0;
    let timerId: number | null = null;

    const poll = async () => {
      if (disposed) {
        return;
      }

      attempts += 1;
      try {
        const requestId = beginProfilesStateRequest();
        const nextState =
          typeof window.codexWorkspace.getInitialAppState === 'function'
            ? await window.codexWorkspace.getInitialAppState()
            : await window.codexWorkspace.getAppState();
        if (disposed) {
          return;
        }

        const modifiedAt = nextState.summary.currentAuthModifiedAt;
        const authHash = nextState.summary.currentAuthHash ?? null;
        const changedByModifiedAt =
          Boolean(modifiedAt) && modifiedAt !== workspaceOAuthMonitor.baselineModifiedAt;
        const changedByHash =
          Boolean(authHash) && authHash !== (workspaceOAuthMonitor.baselineAuthHash ?? null);
        const changed = changedByModifiedAt || changedByHash;
        const readyToImport = changed && nextState.summary.currentAuthExists;
        const autoImportReady = canAutoImportCurrentAuth(nextState.summary);

        if (readyToImport) {
          commitProfilesState(requestId, nextState);
          setWorkspaceOAuthMonitor(null);
          if (nextState.summary.authRecognized) {
            setCenterNotice({
              id: `workspace-oauth-${Date.now()}`,
              message: '新的 OAuth 登录状态已写入当前空间。',
            });
          } else if (autoImportReady) {
            setToast({
              tone: 'info',
              message: '检测到新的 auth.json，正在自动加入空间池。',
            });
          } else {
            setToast({
              tone: 'info',
              message: '检测到新的 auth.json，请先确认是否加入空间池。',
            });
          }
          return;
        }

        if (!uiInteractionTestEnabledRef.current) {
          commitProfilesState(requestId, nextState);
        }
      } catch {
        // Keep polling until timeout.
      }

      const nextDelayMs = attempts <= 12 ? 500 : attempts <= 22 ? 1_000 : null;
      if (!disposed && nextDelayMs !== null) {
        timerId = window.setTimeout(() => {
          void poll();
        }, nextDelayMs);
        return;
      }

      if (!disposed) {
        setWorkspaceOAuthMonitor(null);
        setToast({
          tone: 'info',
          message: 'OAuth 登录页已经打开，但暂时还没检测到新的 auth.json；登录完成后也可以手动点“扫描当前 auth.json”。',
        });
      }
    };

    void poll();
    return () => {
      disposed = true;
      if (timerId !== null) {
        window.clearTimeout(timerId);
      }
    };
  }, [workspaceOAuthMonitor]);

  const applyUiInteractionSettingsPatch = (patch: Partial<ProfilesState['settings']>) => {
    setState((current) => ({
      ...current,
      settings: {
        ...current.settings,
        ...patch,
      },
    }));
  };

  const clearUiInteractionTransientState = () => {
    setSwitchProgressRunId(null);
    switchProgressRunIdRef.current = null;
    setSwitchProgressEvents([]);
    setSwitchRuntimeOverlay(null);
    setSwitchSuccessNotice(null);
    setCardSwitchInteraction(null);
    setCardSwitchSweepState(null);
    setSwitchTarget(null);
    setMenuProfileId(null);
    setPendingAddOverwrite(null);
    setAddDialogOpen(false);
    setEditDialogOpen(false);
    setDeleteTarget(null);
    setBatchNoteDialogOpen(false);
    setBatchNoteDraft('');
    setUiInteractionTestMode('idle');
  };

  const triggerFloatingWidgetPreviewTransition = async (
    input: FloatingWidgetPreviewTransitionInput,
  ) => {
    try {
      await window.codexWorkspace.floatingWidgetPreviewTransition(input);
    } catch {
      // Ignore preview-only animation failures so UI tests and normal switches keep running.
    }
  };

  const enableUiInteractionTest = () => {
    if (uiInteractionTestEnabled) {
      return;
    }

    uiInteractionBaselineRef.current = {
      state: JSON.parse(JSON.stringify(state)) as ProfilesState,
      authState: JSON.parse(JSON.stringify(authState)) as AuthState,
      userCenterBrandFrameEnabled,
      warningThresholdDraft,
      autoSwitchThresholdDraft,
      autoSwitchCountdownDraft,
      searchValue,
      planFilter,
      forceEnableProbeCards,
    };
    uiInteractionRunTokenRef.current += 1;
    void triggerFloatingWidgetPreviewTransition({ phase: 'reset' });
    setUiInteractionTestEnabled(true);
    setUiInteractionTestMode('idle');
    setToast({
      tone: 'success',
      message: '已启用 UI 交互测试。当前轮的切换与设置改动只做前端演示，关闭后会恢复到启用前。',
    });
  };

  const disableUiInteractionTest = () => {
    if (!uiInteractionTestEnabled) {
      return;
    }

    uiInteractionRunTokenRef.current += 1;
    void triggerFloatingWidgetPreviewTransition({ phase: 'reset' });
    setUiInteractionTestEnabled(false);
    const baseline = uiInteractionBaselineRef.current;
    clearUiInteractionTransientState();
    if (baseline) {
      setState(baseline.state);
      setAuthState(baseline.authState);
      setUserCenterBrandFrameEnabled(baseline.userCenterBrandFrameEnabled);
      setWarningThresholdDraft(baseline.warningThresholdDraft);
      setAutoSwitchThresholdDraft(baseline.autoSwitchThresholdDraft);
      setAutoSwitchCountdownDraft(baseline.autoSwitchCountdownDraft);
      setSearchValue(baseline.searchValue);
      setPlanFilter(baseline.planFilter);
      setForceEnableProbeCards(baseline.forceEnableProbeCards);
    }
    setTrayHoverCardEnabledDraft(null);
    setFloatingWidgetEnabledDraft(null);
    setFloatingWidgetOpacityDraft(null);
    setCardFlipVolumeDraft(null);
    setCodexAutoRestartDraft(null);
    setTraeAutoRestartDraft(null);
    uiInteractionBaselineRef.current = null;
    setToast({
      tone: 'info',
      message: '已关闭 UI 交互测试，所有模拟切换和开关都回到启用前状态。',
    });
  };

  const buildUiInteractionSwitchState = (
    current: ProfilesState,
    targetProfile: ProfileSummary,
    activatedAt: string,
  ): ProfilesState => {
    const nextProfiles = current.profiles.map((profile) => ({
      ...profile,
      isActive: profile.id === targetProfile.id,
      lastActivatedAt: profile.id === targetProfile.id ? activatedAt : profile.lastActivatedAt,
    }));
    const targetQuotas = [targetProfile.weeklyQuota, targetProfile.fiveHourQuota, targetProfile.reviewQuota]
      .map((quota) => 100 - quota.percentUsed)
      .filter((value) => Number.isFinite(value));

    return {
      ...current,
      profiles: nextProfiles,
      summary: {
        ...current.summary,
        activeProfileName: targetProfile.name,
        authRecognized: true,
        currentAuthSuggestedName: targetProfile.name,
        currentAuthSuggestedWorkspaceName: targetProfile.workspaceName ?? targetProfile.name,
        currentAuthSuggestedPlanType: targetProfile.planType,
        lastSwitchedAt: activatedAt,
        overallRemainingPercent:
          targetQuotas.length > 0
            ? Math.max(0, Math.min(...targetQuotas))
            : current.summary.overallRemainingPercent,
      },
    };
  };

  const waitForUiInteractionStep = (delayMs: number, token: number): Promise<boolean> =>
    new Promise((resolve) => {
      window.setTimeout(() => {
        resolve(token === uiInteractionRunTokenRef.current && uiInteractionTestEnabledRef.current);
      }, delayMs);
    });

  function handleSimulationOnlyAction(message: string) {
    setToast({ tone: 'info', message });
    setMenuProfileId(null);
  }

  function resetHiddenModeDialogState() {
    setHiddenModePasswordDraft('');
    setHiddenModePasswordConfirmDraft('');
    setHiddenModeDialogError(null);
  }

  function openHiddenModeDialog(mode: HiddenModeDialogMode) {
    setHiddenModeDialogMode(mode);
    resetHiddenModeDialogState();
    setHiddenModeDialogOpen(true);
  }

  function handleRequestHiddenModeAccess() {
    openHiddenModeDialog(hasHiddenModePassword ? 'unlock' : 'setup');
  }

  function handleLockHiddenMode() {
    setHiddenModeUnlocked(false);
    setToast({ tone: 'info', message: '工程师模式已重新隐藏。' });
  }

  function handleHiddenModeDialogOpenChange(open: boolean) {
    setHiddenModeDialogOpen(open);
    if (!open) {
      resetHiddenModeDialogState();
    }
  }

  function handleSubmitHiddenModeDialog() {
    const nextPassword = hiddenModePasswordDraft.trim();

    if (hiddenModeDialogMode === 'unlock') {
      if (!hasHiddenModePassword) {
        setHiddenModeDialogMode('setup');
        setHiddenModeDialogError('当前还没有设置密码，请先创建工程师模式密码。');
        return;
      }

      if (!nextPassword) {
        setHiddenModeDialogError('请输入工程师模式密码。');
        return;
      }

      if (nextPassword !== hiddenModeStoredPassword) {
        setHiddenModeDialogError('密码不正确，请重新输入。');
        return;
      }

      setHiddenModeUnlocked(true);
      setHiddenModeDialogOpen(false);
      resetHiddenModeDialogState();
      setToast({ tone: 'success', message: '工程师模式已解锁。' });
      return;
    }

    if (nextPassword.length < 4) {
      setHiddenModeDialogError('密码至少需要 4 位。');
      return;
    }

    if (nextPassword !== hiddenModePasswordConfirmDraft.trim()) {
      setHiddenModeDialogError('两次输入的密码不一致。');
      return;
    }

    setHiddenModeStoredPassword(nextPassword);
    setHiddenModeUnlocked(true);
    setHiddenModeDialogOpen(false);
    resetHiddenModeDialogState();
    setToast({
      tone: 'success',
      message:
        hiddenModeDialogMode === 'change'
          ? '工程师模式密码已更新。'
          : '工程师模式密码已设置，并已解锁。',
    });
  }

  async function loadState(nextBusyAction: BusyAction) {
    const requestId = beginProfilesStateRequest();
    try {
      setBusyAction(nextBusyAction);
      setErrorMessage(null);
      const nextState =
        nextBusyAction === 'initial' && typeof window.codexWorkspace.getInitialAppState === 'function'
          ? await window.codexWorkspace.getInitialAppState()
          : await window.codexWorkspace.getAppState();
      commitProfilesState(requestId, nextState);
    } catch (error) {
      const message = normalizeRuntimeErrorMessage(error, '读取应用状态失败。');
      setErrorMessage(message);
      if (nextBusyAction !== 'initial') {
        setToast({ tone: 'error', message });
      }
    } finally {
      setBusyAction(null);
    }
  }

  async function refreshState() {
    const requestId = beginProfilesStateRequest();
    try {
      setBusyAction('refresh');
      const nextState = await window.codexWorkspace.refreshProfiles();
      commitProfilesState(requestId, nextState);
      clearCardSwitchFeedback();
      setMenuProfileId(null);
      setSwitchProgressEvents([]);
      playCardFlipAudio('wave');
      setCardSwitchWaveToken((current) => current + 1);
      setToast({ tone: 'info', message: '已刷新 profiles 目录，并尝试同步实时额度。' });
    } catch (error) {
      const message = error instanceof Error ? error.message : '刷新失败。';
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    } finally {
      setBusyAction(null);
    }
  }

  async function syncUsage(silent = false) {
    const requestId = beginProfilesStateRequest();
    try {
      if (!silent) {
        setBusyAction('refresh');
      }

      const result = await window.codexWorkspace.syncUsage();
      commitProfilesState(requestId, result.state);
      if (!silent) {
        setToast({ tone: 'success', message: result.message });
      }
    } catch (error) {
      if (silent) {
        return;
      }

      const message = error instanceof Error ? error.message : '同步实时额度失败。';
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    } finally {
      if (!silent) {
        setBusyAction(null);
      }
    }
  }

  async function handleImportCurrentAuth() {
    if (uiInteractionTestEnabled) {
      setToast({
        tone: 'info',
        message: 'UI 交互测试中不会真正导入 auth.json。请先关闭测试模式，再执行真实导入。',
      });
      return;
    }

    if (hasReachedProfileSlotLimit) {
      setToast({ tone: 'error', message: profileSlotLimitReachedMessage });
      return;
    }

    const requestId = beginProfilesStateRequest();
    const placeholder = buildCurrentAuthImportPlaceholder(state.summary, 'manual');
    try {
      clearCurrentAuthImportPlaceholderCleanupTimer();
      setCurrentAuthImportPlaceholder(placeholder);
      setBusyAction('add');
      const result = await window.codexWorkspace.importCurrentAuth();
      commitProfilesState(requestId, result.state);
      setCurrentAuthImportPlaceholder((current) =>
        current?.id === placeholder.id
          ? {
              ...current,
              status: 'success',
              importedProfileId: result.importedProfileId ?? null,
              revealToken: Date.now(),
              successDetailText: result.backgroundSyncPending
                ? '已纳入当前空间池，后台同步中'
                : '已纳入当前空间池',
              errorMessage: null,
            }
          : current,
      );
      scheduleCurrentAuthImportPlaceholderCleanup(result.importedProfileId ? 1_800 : 1_400);
      setToast({ tone: 'success', message: result.message });
    } catch (error) {
      const message = normalizeRuntimeErrorMessage(error, '扫描当前 auth.json 失败。');
      setErrorMessage(message);
      setCurrentAuthImportPlaceholder((current) =>
        current?.id === placeholder.id
          ? {
              ...current,
              status: 'error',
              errorMessage: message,
            }
          : current,
      );
      scheduleCurrentAuthImportPlaceholderCleanup(4200);
      setToast({ tone: 'error', message });
    } finally {
      setBusyAction(null);
    }
  }

  async function handlePickAuthFile() {
    const result = await window.codexWorkspace.chooseAuthFile();
    return result.canceled ? null : result.filePath ?? null;
  }

  async function handlePickLocalImportFiles() {
    const result = await window.codexWorkspace.chooseLocalImportFiles();
    if (result.canceled || result.filePaths.length === 0) {
      return;
    }

    resetSpacePoolImportOverlay();
    await handleImportLocalProfileFiles(result.filePaths);
  }

  const hasDraggedFiles = (dataTransfer: DataTransfer | null): boolean =>
    Boolean(dataTransfer && Array.from(dataTransfer.types).includes('Files'));

  const normalizeDroppedTextPath = (value: string): string | null => {
    const trimmedValue = value.trim();
    if (!trimmedValue || trimmedValue.startsWith('#')) {
      return null;
    }

    if (/^[A-Za-z]:[\\/]/.test(trimmedValue) || trimmedValue.startsWith('\\\\')) {
      return trimmedValue;
    }

    if (!/^file:/i.test(trimmedValue)) {
      return null;
    }

    try {
      const parsedUrl = new URL(trimmedValue);
      if (parsedUrl.protocol !== 'file:') {
        return null;
      }

      let normalizedPath = decodeURIComponent(parsedUrl.pathname);
      if (/^\/[A-Za-z]:\//.test(normalizedPath)) {
        normalizedPath = normalizedPath.slice(1);
      }
      return normalizedPath.replace(/\//g, '\\');
    } catch {
      return null;
    }
  };

  const getDraggedFilePaths = (dataTransfer: DataTransfer | null): string[] => {
    if (!dataTransfer) {
      return [];
    }

    const droppedFiles = Array.from(dataTransfer.files);
    const resolveDroppedFilePaths = window.codexWorkspace?.resolveDroppedFilePaths;
    if (typeof resolveDroppedFilePaths === 'function') {
      const resolvedPaths = resolveDroppedFilePaths(droppedFiles)
        .map((item) => item.trim())
        .filter(Boolean);
      if (resolvedPaths.length > 0) {
        return resolvedPaths;
      }
    }

    return droppedFiles
      .map((file) => {
        const runtimeFile = file as File & { path?: string };
        return runtimeFile.path?.trim() || '';
      })
      .filter(Boolean)
      .concat(
        ['text/uri-list', 'text/plain']
          .flatMap((format) => (dataTransfer.getData(format) || '').split(/\r?\n+/))
          .map((item) => normalizeDroppedTextPath(item))
          .filter((item): item is string => Boolean(item)),
      )
      .filter((item, index, items) => items.indexOf(item) === index);
  };

  const ensureLocalImportReady = (): boolean => {
    if (uiInteractionTestEnabled) {
      setToast({
        tone: 'info',
        message: 'UI 交互测试中不会真正导入空间卡片。请先关闭测试模式，再执行真实导入。',
      });
      return false;
    }

    if (busyAction === 'import') {
      setToast({ tone: 'info', message: '当前还有导入任务，请先等待这一批完成。' });
      return false;
    }

    if (pendingLocalImportOverwrite) {
      setToast({ tone: 'info', message: '当前还有一批导入等待确认是否覆盖，请先处理这批。' });
      return false;
    }

    if (hasReachedProfileSlotLimit) {
      setToast({ tone: 'error', message: profileSlotLimitReachedMessage });
      return false;
    }

    return true;
  };

  const queueLocalProfileImportCandidates = (input: {
    requestId: number;
    candidates: LocalProfileImportCandidate[];
    message: string;
  }): boolean => {
    if (input.candidates.length === 0) {
      setToast({ tone: 'error', message: input.message });
      return false;
    }

    clearLocalImportPlaceholderCleanupTimer();
    setLocalImportPlaceholders(
      input.candidates.map((candidate, index) => buildLocalImportPlaceholder(candidate, index)),
    );
    setToast({ tone: 'info', message: input.message });

    const duplicateCandidates = input.candidates.filter((candidate) => Boolean(candidate.matchedProfileId));
    if (duplicateCandidates.length > 0) {
      setPendingLocalImportOverwrite({
        requestId: input.requestId,
        candidates: input.candidates,
        duplicateCandidates,
      });
      setToast({
        tone: 'info',
        message: `检测到 ${duplicateCandidates.length} 张本地已有空间卡片，请确认是否覆盖；取消则终止本次导入。`,
      });
      return false;
    }

    return true;
  };

  async function runLocalProfileImportBatch(input: {
    requestId: number;
    candidates: LocalProfileImportCandidate[];
    overwriteExisting: boolean;
  }) {
    const failedEntries: Array<{ fileName: string; message: string }> = [];
    let successCount = 0;
    const duplicateCount = input.candidates.filter((candidate) => Boolean(candidate.matchedProfileId)).length;

    try {
      setBusyAction('import');

      for (let index = 0; index < input.candidates.length; index += 1) {
        const candidate = input.candidates[index];
        const progressPercent = Math.round((index / input.candidates.length) * 100);

        setLocalImportPlaceholders((current) =>
          current.map((placeholder) =>
            placeholder.importCandidateId === candidate.id
              ? {
                  ...placeholder,
                  loadingPhase: 'running',
                  loadingTipText: '正在导入空间',
                  loadingDetailText: `${candidate.fileName} · ${progressPercent}%`,
                }
              : placeholder,
          ),
        );

        try {
          const result = await window.codexWorkspace.importLocalProfileImportCandidate(candidate, {
            overwriteExisting: input.overwriteExisting && Boolean(candidate.matchedProfileId),
          });
          commitProfilesState(input.requestId, result.state);
          successCount += 1;
          const completedPercent = Math.round(((index + 1) / input.candidates.length) * 100);
          const replacedExisting =
            Boolean(candidate.matchedProfileId) && input.overwriteExisting === true;
          const reusedExisting =
            Boolean(candidate.matchedProfileId) && input.overwriteExisting !== true;

          setLocalImportPlaceholders((current) => {
            if (reusedExisting) {
              return current.filter((placeholder) => placeholder.importCandidateId !== candidate.id);
            }

            return current.map((placeholder) =>
              placeholder.importCandidateId === candidate.id
                ? {
                    ...placeholder,
                    status: 'success',
                    importedProfileId: result.importedProfileId ?? null,
                    revealToken: Date.now(),
                    loadingTipText: '导入完成',
                    loadingDetailText: `${completedPercent}%`,
                    successDetailText: replacedExisting ? '已覆盖本地空间' : '已写入当前空间池',
                    errorMessage: null,
                  }
                : placeholder,
            );
          });
        } catch (error) {
          const message = normalizeRuntimeErrorMessage(error, `导入 ${candidate.fileName} 失败。`);
          failedEntries.push({ fileName: candidate.fileName, message });
          setLocalImportPlaceholders((current) =>
            current.map((placeholder) =>
              placeholder.importCandidateId === candidate.id
                ? {
                    ...placeholder,
                    status: 'error',
                    errorMessage: message,
                  }
                : placeholder,
            ),
          );
        }
      }

      const failureCount = failedEntries.length;
      const summaryMessage =
        failureCount === 0
          ? input.candidates.length === 1
            ? duplicateCount > 0
              ? input.overwriteExisting
                ? '本地已有空间已覆盖更新，可以直接切换使用。'
                : '本地已有空间已直接复用，可以直接切换使用。'
              : '文件已导入空间池，可以直接切换使用。'
            : duplicateCount > 0
              ? input.overwriteExisting
                ? `已完成 ${input.candidates.length} 张空间卡片导入，其中 ${duplicateCount} 张已覆盖本地空间。`
                : `已完成 ${input.candidates.length} 张空间卡片导入，其中 ${duplicateCount} 张已复用本地空间。`
              : `已完成 ${input.candidates.length} 张空间卡片导入。`
          : successCount > 0
            ? `导入完成：成功 ${successCount} 张，失败 ${failureCount} 张。`
            : `导入失败：共 ${failureCount} 张凭证未成功写入空间池。`;
      scheduleLocalImportPlaceholderCleanup(failureCount > 0 ? 4200 : 1800);
      setToast({
        tone: failureCount === 0 ? 'success' : successCount > 0 ? 'info' : 'error',
        message: summaryMessage,
      });
    } catch (error) {
      const message = normalizeRuntimeErrorMessage(error, '拖拽导入文件失败。');
      setErrorMessage(message);
      setLocalImportPlaceholders((current) =>
        current.map((placeholder) =>
          placeholder.status === 'loading'
            ? {
                ...placeholder,
                status: 'error',
                errorMessage: message,
              }
            : placeholder,
        ),
      );
      scheduleLocalImportPlaceholderCleanup(4200);
      setToast({ tone: 'error', message });
    } finally {
      setBusyAction(null);
    }
  }

  async function handleImportLocalProfileFiles(filePaths: string[]) {
    const normalizedFilePaths = Array.from(
      new Set(filePaths.map((item) => item.trim()).filter(Boolean)),
    );
    if (normalizedFilePaths.length === 0) {
      return;
    }

    if (!ensureLocalImportReady()) {
      return;
    }

    const requestId = beginProfilesStateRequest();
    let inspectCandidates: LocalProfileImportCandidate[] = [];

    try {
      setBusyAction('import');
      const inspectResult = await window.codexWorkspace.inspectLocalProfileImports(normalizedFilePaths);
      inspectCandidates = inspectResult.candidates;
      const shouldContinue = queueLocalProfileImportCandidates({
        requestId,
        candidates: inspectResult.candidates,
        message: inspectResult.message,
      });
      if (!shouldContinue) {
        return;
      }
    } catch (error) {
      const message = normalizeRuntimeErrorMessage(error, '拖拽导入文件失败。');
      setErrorMessage(message);
      if (inspectCandidates.length > 0) {
        setLocalImportPlaceholders((current) =>
          current.map((placeholder) =>
            placeholder.status === 'loading'
              ? {
                  ...placeholder,
                  status: 'error',
                  errorMessage: message,
                }
              : placeholder,
          ),
        );
        scheduleLocalImportPlaceholderCleanup(4200);
      }
      setToast({ tone: 'error', message });
    } finally {
      setBusyAction(null);
    }

    if (inspectCandidates.length > 0) {
      await runLocalProfileImportBatch({
        requestId,
        candidates: inspectCandidates,
        overwriteExisting: false,
      });
    }
  }

  async function handleImportManualText() {
    const normalizedText = manualImportTextDraft.trim();
    if (!normalizedText) {
      setManualImportTextError('请输入凭证内容。');
      return;
    }

    if (!ensureLocalImportReady()) {
      return;
    }

    const requestId = beginProfilesStateRequest();
    let candidate: LocalProfileImportCandidate | null = null;

    try {
      setManualImportTextBusy(true);
      setManualImportTextError(null);
      setBusyAction('import');
      const inspectResult = await window.codexWorkspace.inspectManualImportText(normalizedText);
      candidate = inspectResult.candidate;
      if (!candidate) {
        setManualImportTextError(inspectResult.message);
        setToast({ tone: 'error', message: inspectResult.message });
        return;
      }

      resetSpacePoolImportOverlay();
      const shouldContinue = queueLocalProfileImportCandidates({
        requestId,
        candidates: [candidate],
        message: inspectResult.message,
      });
      if (!shouldContinue) {
        return;
      }
    } catch (error) {
      const message = normalizeRuntimeErrorMessage(error, '手动输入导入失败。');
      setManualImportTextError(message);
      setErrorMessage(message);
      setToast({ tone: 'error', message });
      return;
    } finally {
      setManualImportTextBusy(false);
      setBusyAction(null);
    }

    if (candidate) {
      await runLocalProfileImportBatch({
        requestId,
        candidates: [candidate],
        overwriteExisting: false,
      });
    }
  }

  async function resolvePendingLocalImportOverwrite(overwriteExisting: boolean) {
    if (!pendingLocalImportOverwrite) {
      return;
    }

    const nextBatch = pendingLocalImportOverwrite;
    setPendingLocalImportOverwrite(null);

    if (!overwriteExisting) {
      clearLocalImportPlaceholderCleanupTimer();
      setLocalImportPlaceholders([]);
      setToast({ tone: 'info', message: '已取消本次导入。' });
      return;
    }

    await runLocalProfileImportBatch({
      requestId: nextBatch.requestId,
      candidates: nextBatch.candidates,
      overwriteExisting,
    });
  }

  const handleSpacePoolDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (!hasDraggedFiles(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    spacePoolDragDepthRef.current += 1;
    setSpacePoolDragActive(true);
  };

  const handleSpacePoolDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!hasDraggedFiles(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    if (!spacePoolDragActive) {
      setSpacePoolDragActive(true);
    }
  };

  const handleSpacePoolDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!hasDraggedFiles(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    spacePoolDragDepthRef.current = Math.max(0, spacePoolDragDepthRef.current - 1);
    if (spacePoolDragDepthRef.current === 0) {
      setSpacePoolDragActive(false);
    }
  };

  const handleSpacePoolDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!hasDraggedFiles(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const droppedFilePaths = getDraggedFilePaths(event.dataTransfer);
    spacePoolDragDepthRef.current = 0;
    setSpacePoolDragActive(false);
    if (droppedFilePaths.length === 0) {
      setToast({
        tone: 'error',
        message: '没有读取到拖入文件路径，请直接拖入本地 .json 或 .zip 文件。',
      });
      return;
    }
    resetSpacePoolImportOverlay();
    void handleImportLocalProfileFiles(droppedFilePaths);
  };

  const findProfileByAuthSourcePath = (
    authSourcePath: string,
    excludedProfileId?: string | null,
  ): ProfileSummary | null => {
    const normalizedSourcePath = normalizeFsPathForComparison(authSourcePath);
    if (!normalizedSourcePath) {
      return null;
    }

    return (
      state.profiles.find((profile) => {
        if (excludedProfileId && profile.id === excludedProfileId) {
          return false;
        }
        return normalizeFsPathForComparison(profile.authFilePath) === normalizedSourcePath;
      }) ?? null
    );
  };

  const getCurrentAuthenticatedEmail = (): string =>
    normalizeEmailForComparison(authState.profile?.email);

  const isProfileBoundToDifferentAccount = (
    profile: Pick<ProfileSummary, 'securityBinding'>,
    currentAuthProfile: AuthState['profile'] | null | undefined = authState.profile,
  ): boolean =>
    isProfileBoundToDifferentAccountForUser(
      profile,
      currentAuthProfile,
      authState.device.deviceId,
    );

  const getProfilesBoundToDifferentAccount = (
    currentAuthProfile: AuthState['profile'] | null | undefined = authState.profile,
  ): ProfileSummary[] => state.profiles.filter((profile) => isProfileBoundToDifferentAccount(profile, currentAuthProfile));

  const getProfilesEligibleForCloudSync = (
    currentAuthProfile: AuthState['profile'] | null | undefined = authState.profile,
  ): ProfileSummary[] =>
    state.profiles.filter((profile) => !isProfileBoundToDifferentAccount(profile, currentAuthProfile));

  const resetCloudSyncDialogState = () => {
    setCloudSyncSelectedLocalIds([]);
    setCloudSyncSelectedCloudIds([]);
    setCloudSyncDialog(null);
  };

  const clearCloudSyncVisualProgress = () => {
    if (cloudSyncVisualProgressTimerRef.current !== null) {
      window.clearTimeout(cloudSyncVisualProgressTimerRef.current);
      cloudSyncVisualProgressTimerRef.current = null;
    }
    setCloudSyncVisualProgress(null);
  };

  const startCloudSyncVisualProgress = (action: CloudSyncVisualProgressAction, total: number) => {
    if (cloudSyncVisualProgressTimerRef.current !== null) {
      window.clearTimeout(cloudSyncVisualProgressTimerRef.current);
      cloudSyncVisualProgressTimerRef.current = null;
    }
    setCloudSyncVisualProgress({
      action,
      total: Math.max(0, total),
      status: 'running',
    });
  };

  const finishCloudSyncVisualProgress = (action: CloudSyncVisualProgressAction, total: number) => {
    if (cloudSyncVisualProgressTimerRef.current !== null) {
      window.clearTimeout(cloudSyncVisualProgressTimerRef.current);
      cloudSyncVisualProgressTimerRef.current = null;
    }
    setCloudSyncVisualProgress({
      action,
      total: Math.max(0, total),
      status: 'success',
    });
    cloudSyncVisualProgressTimerRef.current = window.setTimeout(() => {
      setCloudSyncVisualProgress((current) => (current?.status === 'success' ? null : current));
      cloudSyncVisualProgressTimerRef.current = null;
    }, 1200);
  };

  async function refreshLocalRemoteProfileSnapshots() {
    try {
      setLocalRemoteProfileSnapshotsBusy(true);
      const snapshots = await window.codexWorkspace.getRemoteProfileSnapshots();
      setLocalRemoteProfileSnapshots(snapshots);
    } catch (error) {
      setToast({
        tone: 'error',
        message: normalizeRuntimeErrorMessage(error, '读取本地卡片快照失败。'),
      });
    } finally {
      setLocalRemoteProfileSnapshotsBusy(false);
    }
  }

  const openIssueTransferDialog = (profileIds?: string[]) => {
    setMainView('profiles');
    enterWorkspaceSelectionMode('send', profileIds);
  };

  const toggleSelectionId = (
    setter: Dispatch<SetStateAction<string[]>>,
    targetId: string,
  ) => {
    setter((current) =>
      current.includes(targetId)
        ? current.filter((item) => item !== targetId)
        : [...current, targetId],
    );
  };

  const setWorkspaceProfileOverlay = (
    profileIds: string[],
    overlay: ProfileActionOverlayState | null,
  ) => {
    if (profileIds.length === 0) {
      return;
    }
    setWorkspaceActionOverlayById((current) => {
      const next = { ...current };
      for (const profileId of profileIds) {
        if (overlay) {
          next[profileId] = overlay;
        } else {
          delete next[profileId];
        }
      }
      return next;
    });
  };

  const waitForWorkspaceCardStep = (delayMs: number) =>
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, delayMs);
    });

  const animateWorkspaceActionSuccess = async (
    profileIds: string[],
    detail: string,
    options?: { keepVisible?: boolean },
  ) => {
    const orderedIds = [...profileIds];
    for (const [index, profileId] of orderedIds.entries()) {
      setWorkspaceActionOverlayById((current) => ({
        ...current,
        [profileId]: {
          title: '处理完成',
          detail,
          success: true,
        },
      }));
      if (index < orderedIds.length - 1) {
        await waitForWorkspaceCardStep(110);
      }
    }

    if (options?.keepVisible) {
      return;
    }

    await waitForWorkspaceCardStep(220);
    setWorkspaceProfileOverlay(orderedIds, null);
  };

  const openClawSyncDialog = async (
    source: CloudSyncDialogSource,
    options?: {
      step?: CloudSyncDialogStep;
      direction?: CloudProfileSyncDirection | null;
      action?: CloudSyncDialogAction | null;
      selectedLocalIds?: string[];
      selectedCloudIds?: string[];
      purgeUnmatchedLocalBeforeImport?: boolean;
    },
  ) => {
    const resolvedAuthState = await ensureAuthenticatedCloudActionState(
      authState,
      '请先登录在线账号后再执行云同步。',
    );
    if (!resolvedAuthState) {
      return;
    }

    if (visibleRealProfiles.length === 0 && resolvedAuthState.cloudProfileBackups.length === 0) {
      setToast({ tone: 'info', message: '当前设备和云端都没有可同步的卡片。' });
      return;
    }

    setCloudSyncSelectedLocalIds(
      Array.from(
        new Set(
          (options?.selectedLocalIds ?? availableLocalRemoteSnapshots.map((snapshot) => snapshot.id))
            .map((item) => item.trim())
            .filter(Boolean),
        ),
      ),
    );
    setCloudSyncSelectedCloudIds(
      Array.from(
        new Set(
          (options?.selectedCloudIds ?? availableCloudBackupEntries.map((entry) => entry.id))
            .map((item) => item.trim())
            .filter(Boolean),
        ),
      ),
    );
    setCloudBackupCenterOpen(false);
    setCloudSyncDialog({
      source,
      step: options?.step ?? 'select',
      direction: options?.direction ?? null,
      action: options?.action ?? null,
      purgeUnmatchedLocalBeforeImport: options?.purgeUnmatchedLocalBeforeImport === true,
    });
  };

  const executeProfileMutation = async (
    action: PendingLocalImportAction,
    skipSecurityCheck = false,
  ) => {
    if (!skipSecurityCheck) {
      const shouldContinue = await ensureLocalImportSecurity(action, true);
      if (!shouldContinue) {
        return;
      }
    }

    const requestId = beginProfilesStateRequest();
    const nextBusyAction = action.operation === 'edit' ? 'edit' : 'add';
    try {
      setBusyAction(nextBusyAction);
      if (action.operation === 'add') {
        if (hasReachedProfileSlotLimit) {
          setToast({ tone: 'error', message: profileSlotLimitReachedMessage });
          return;
        }
        const nextState = await window.codexWorkspace.addProfile(action.values);
        commitProfilesState(requestId, nextState);
        closeAddDialog();
        setToast({ tone: 'success', message: `已导入 profile：${action.values.name}` });
        return;
      }

      const targetProfileId = action.targetProfileId?.trim();
      if (!targetProfileId) {
        setToast({ tone: 'error', message: '未找到需要更新的目标卡片。' });
        return;
      }

      const nextState = await window.codexWorkspace.updateProfile({
        id: targetProfileId,
        name: action.values.name,
        workspaceName: action.values.workspaceName,
        authSourcePath: action.values.authSourcePath || undefined,
        notes: action.values.notes,
        planType: action.values.planType,
        manuallyDisabled: action.values.manuallyDisabled,
        weeklyQuota: action.values.weeklyQuota,
        fiveHourQuota: action.values.fiveHourQuota,
        reviewQuota: action.values.reviewQuota,
      });
      commitProfilesState(requestId, nextState);

      if (action.operation === 'overwrite') {
        closeAddDialog();
        setToast({ tone: 'success', message: `已覆盖同命名空间：${action.values.name}` });
        return;
      }

      setEditDialogOpen(false);
      setEditTarget(null);
      setToast({ tone: 'success', message: `已更新 profile：${action.values.name}` });
    } catch (error) {
      const fallbackMessage =
        action.operation === 'add'
          ? '添加 profile 失败。'
          : action.operation === 'overwrite'
            ? '覆盖同命名空间失败。'
            : '编辑 profile 失败。';
      const message = error instanceof Error ? error.message : fallbackMessage;
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    } finally {
      setBusyAction(null);
      if (action.operation === 'overwrite') {
        setPendingAddOverwrite(null);
      }
    }
  };

  async function ensureLocalImportSecurity(
    action: PendingLocalImportAction,
    skipSecondaryPasswordCheck = false,
  ): Promise<boolean> {
    const sourcePath = action.values.authSourcePath.trim();
    if (!sourcePath || !isFsPathInsideRoot(sourcePath, state.settings.profilesRootDir)) {
      return true;
    }

    const targetProfile =
      action.targetProfileId
        ? state.profiles.find((profile) => profile.id === action.targetProfileId) ?? null
        : null;
    const normalizedSourcePath = normalizeFsPathForComparison(sourcePath);
    const normalizedTargetPath = normalizeFsPathForComparison(targetProfile?.authFilePath);
    const scannedProfile = findProfileByAuthSourcePath(sourcePath, action.targetProfileId);
    const shouldTreatAsExternalImport =
      action.operation !== 'edit' ||
      targetProfile?.securityImportRequired === true ||
      normalizedSourcePath !== normalizedTargetPath;

    if (!shouldTreatAsExternalImport && targetProfile?.securityImportRequired !== true) {
      return true;
    }

    if (authState.sessionStatus !== 'authenticated' || !authState.profile) {
      setAuthMode('login');
      setUserCenterOpen(true);
      setToast({ tone: 'info', message: '请先登录当前账号，再导入本地空间卡片。' });
      return false;
    }

    if (!authState.security.secondaryPassword.enabled) {
      openSecondaryPasswordDialog({
        mode: 'set',
        pendingLocalImport: action,
      });
      return false;
    }

    const ownerEmail = normalizeEmailForComparison(scannedProfile?.securityBinding?.ownerUserEmail);
    const currentEmail = getCurrentAuthenticatedEmail();
    if (ownerEmail && currentEmail && ownerEmail !== currentEmail) {
      setToast({
        tone: 'error',
        message: '这张卡片不属于当前账号。请改用主机远程批准的传卡流程导入。',
      });
      return false;
    }

    if (!skipSecondaryPasswordCheck && shouldRequireSecondaryPassword()) {
      openSecondaryPasswordDialog({
        mode: 'verify-local-import',
        pendingLocalImport: action,
      });
      return false;
    }

    return true;
  }

  async function handleAddProfile(values: ProfileFormValues) {
    if (uiInteractionTestEnabled) {
      setToast({
        tone: 'info',
        message: 'UI 交互测试中不会真正新增 Profile。请先关闭测试模式，再执行真实导入。',
      });
      return;
    }

    const trimmedName = values.name.trim();
    const trimmedWorkspaceName = values.workspaceName.trim();
    const isDetectedCurrentAuthImport =
      addDialogDiscoveryHint &&
      values.authSourcePath.trim() === state.summary.currentAuthPath &&
      state.summary.currentAuthExists;
    const normalizedValues: ProfileFormValues = {
      ...values,
      name: trimmedName,
      workspaceName: trimmedWorkspaceName,
    };
    const normalizedName = trimmedName.toLowerCase();
    const duplicateProfile = normalizedName
      ? state.profiles.find((profile) => profile.name.trim().toLowerCase() === normalizedName) ?? null
      : null;

    if (duplicateProfile && !isDetectedCurrentAuthImport) {
      setPendingAddOverwrite({
        target: duplicateProfile,
        values: normalizedValues,
      });
      return;
    }

    if (hasReachedProfileSlotLimit) {
      setToast({ tone: 'error', message: profileSlotLimitReachedMessage });
      return;
    }

    const shouldContinue = await ensureLocalImportSecurity({
      operation: 'add',
      values: normalizedValues,
    });
    if (!shouldContinue) {
      return;
    }

    await executeProfileMutation(
      {
        operation: 'add',
        values: normalizedValues,
      },
      true,
    );
  }

  async function handleConfirmAddOverwrite() {
    if (!pendingAddOverwrite) {
      return;
    }

    if (uiInteractionTestEnabled) {
      setPendingAddOverwrite(null);
      setToast({
        tone: 'info',
        message: 'UI 交互测试中不会真正覆盖已有 Profile。请先关闭测试模式。',
      });
      return;
    }

    const { target, values } = pendingAddOverwrite;
    const shouldContinue = await ensureLocalImportSecurity({
      operation: 'overwrite',
      values,
      targetProfileId: target.id,
    });
    if (!shouldContinue) {
      return;
    }

    await executeProfileMutation(
      {
        operation: 'overwrite',
        values,
        targetProfileId: target.id,
      },
      true,
    );
  }

  async function handleEditProfile(values: ProfileFormValues) {
    if (!editTarget) {
      return;
    }

    if (uiInteractionTestEnabled) {
      setEditDialogOpen(false);
      setEditTarget(null);
      setToast({
        tone: 'info',
        message: 'UI 交互测试中不会真正写入 Profile 编辑结果。请先关闭测试模式。',
      });
      return;
    }

    const shouldContinue = await ensureLocalImportSecurity({
      operation: 'edit',
      values,
      targetProfileId: editTarget.id,
    });
    if (!shouldContinue) {
      return;
    }

    await executeProfileMutation(
      {
        operation: 'edit',
        values,
        targetProfileId: editTarget.id,
      },
      true,
    );
  }

  async function handleSetProfileAvailability(targetProfile: ProfileSummary, nextEnabled: boolean) {
    if (uiInteractionTestEnabled) {
      setToast({
        tone: 'info',
        message: nextEnabled
          ? 'UI 交互测试中不会真正启用卡片。请先关闭测试模式。'
          : 'UI 交互测试中不会真正停用卡片。请先关闭测试模式。',
      });
      return;
    }

    if (profileAvailabilityBusy) {
      return;
    }

    const requestId = beginProfilesStateRequest();
    const displayName = getProfileDisplayName(targetProfile);
    try {
      setProfileAvailabilityBusy(true);
      const nextState = await window.codexWorkspace.batchUpdateProfileAvailability({
        profileIds: [targetProfile.id],
        manuallyDisabled: !nextEnabled,
        clearAutoDisabled: nextEnabled,
      });
      commitProfilesState(requestId, nextState);
      setToast({
        tone: 'success',
        message: nextEnabled ? `已重新启用 ${displayName}。` : `已手动停用 ${displayName}。`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : nextEnabled ? '启用卡片失败。' : '停用卡片失败。';
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    } finally {
      setProfileAvailabilityBusy(false);
    }
  }

  async function handleApplyRecognizedDisabledProfiles(nextEnabled: boolean) {
    if (!recognizedDisabledProfileIds.length) {
      setToast({ tone: 'info', message: '当前没有已识别为停用的卡片。' });
      return;
    }

    if (uiInteractionTestEnabled) {
      setToast({
        tone: 'info',
        message: nextEnabled
          ? 'UI 交互测试中不会真正一键启用已识别停用卡片。请先关闭测试模式。'
          : 'UI 交互测试中不会真正恢复已识别停用卡片。请先关闭测试模式。',
      });
      return;
    }

    if (
      recognizedDisabledActionBusy !== null ||
      profileAvailabilityBusy ||
      batchActionBusy
    ) {
      return;
    }

    const requestId = beginProfilesStateRequest();
    try {
      setRecognizedDisabledActionBusy(nextEnabled ? 'enable' : 'disable');
      const nextState = await window.codexWorkspace.batchUpdateProfileAvailability({
        profileIds: recognizedDisabledProfileIds,
        manuallyDisabled: !nextEnabled,
        clearAutoDisabled: nextEnabled,
      });
      commitProfilesState(requestId, nextState);
      setToast({
        tone: 'success',
        message: nextEnabled
          ? `已一键启用 ${recognizedDisabledProfileIds.length} 张已识别停用卡片。`
          : `已将 ${recognizedDisabledProfileIds.length} 张已识别停用卡片恢复为停用状态。`,
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : nextEnabled
            ? '一键启用已识别停用卡片失败。'
            : '恢复已识别停用卡片失败。';
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    } finally {
      setRecognizedDisabledActionBusy(null);
    }
  }

  async function handleBatchApplyProfileAction() {
    const profileIds = Array.from(new Set(batchNoteSelectedProfileIds));

    if (!profileIds.length) {
      setToast({ tone: 'error', message: '请先选择至少一个 profile。' });
      return;
    }

    if (uiInteractionTestEnabled) {
      setToast({
        tone: 'info',
        message:
          batchActionMode === 'notes'
            ? 'UI 交互测试中不会真正批量写备注。请先关闭测试模式。'
            : batchActionMode === 'enable'
              ? 'UI 交互测试中不会真正批量启用卡片。请先关闭测试模式。'
              : batchActionMode === 'disable'
                ? 'UI 交互测试中不会真正批量停用卡片。请先关闭测试模式。'
                : 'UI 交互测试中不会真正批量删除卡片。请先关闭测试模式。',
      });
      return;
    }

    if (batchActionBusy) {
      return;
    }

    if (batchActionMode === 'delete') {
      setBatchDeleteReturnToDialog(true);
      setBatchNoteDialogOpen(false);
      openBatchDeleteConfirmDialog({
        source: 'batch-dialog',
        profileIds,
        deletedProfiles: profileIds
          .map((profileId) => profilesById.get(profileId))
          .filter((profile): profile is ProfileSummary => profile !== undefined),
        successMessage: `已批量删除 ${profileIds.length} 张卡片及本地文件。`,
        failureMessage: '批量删除失败。',
        closeBatchDialog: true,
      });
      return;
    }

    const requestId = beginProfilesStateRequest();
    let nextState: ProfilesState | null = null;
    try {
      setBatchActionBusy(true);
      if (batchActionMode === 'notes') {
        const notes = batchNoteDraft.trim();
        if (!notes) {
          setToast({ tone: 'error', message: '请先输入要批量写入的备注。' });
          return;
        }

        try {
          nextState = await window.codexWorkspace.batchUpdateProfileNotes({
            profileIds,
            notes,
          });
        } catch (error) {
          if (!shouldFallbackBatchNoteUpdate(error)) {
            throw error;
          }

          let fallbackState: ProfilesState | null = null;
          for (const profileId of profileIds) {
            fallbackState = await window.codexWorkspace.updateProfile({
              id: profileId,
              notes,
              skipUsageSync: true,
            });
          }

          nextState = fallbackState ?? (await window.codexWorkspace.getAppState());
        }
      } else {
        nextState = await window.codexWorkspace.batchUpdateProfileAvailability({
          profileIds,
          manuallyDisabled: batchActionMode === 'disable',
          clearAutoDisabled: batchActionMode === 'enable',
        });
      }
      commitProfilesState(requestId, nextState);
      closeBatchNoteDialog();
      setToast({
        tone: 'success',
        message:
          batchActionMode === 'notes'
            ? `已批量写入 ${profileIds.length} 张卡片的备注。`
            : batchActionMode === 'enable'
              ? `已批量启用 ${profileIds.length} 张卡片。`
              : batchActionMode === 'disable'
              ? `已批量停用 ${profileIds.length} 张卡片。`
                : `已批量删除 ${profileIds.length} 张卡片及本地文件。`,
      });
    } catch (error) {
      if (nextState) {
        commitProfilesState(requestId, nextState);
      }

      const message =
        error instanceof Error
          ? error.message
          : batchActionMode === 'notes'
            ? '批量备注失败。'
            : batchActionMode === 'enable'
              ? '批量启用失败。'
              : batchActionMode === 'disable'
                ? '批量停用失败。'
                : '批量删除失败。';
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    } finally {
      setBatchActionBusy(false);
    }
  }

  const getWorkspaceSelectedProfileIds = () =>
    Array.from(new Set(workspaceSelectionIds.map((item) => item.trim()).filter(Boolean)));

  const toggleWorkspaceSelectionForProfile = (profileId: string) => {
    toggleSelectionId(setWorkspaceSelectionIds, profileId);
  };

  const handleSelectAllWorkspaceProfiles = () => {
    setWorkspaceSelectionIds(workspaceSelectionEligibleProfiles.map((profile) => profile.id));
  };

  const handleInvertWorkspaceSelection = () => {
    setWorkspaceSelectionIds((current) => {
      const currentSet = new Set(current);
      return workspaceSelectionEligibleProfiles
        .map((profile) => profile.id)
        .filter((profileId) => !currentSet.has(profileId));
    });
  };

  const handleClearWorkspaceSelection = () => {
    setWorkspaceSelectionIds([]);
  };

  const maybePromptCloudDeleteSync = (
    deletedProfileIds: string[],
    deletedProfilesOverride?: ProfileSummary[],
  ) => {
    const deletedIdSet = new Set(deletedProfileIds);
    const deletedProfiles =
      deletedProfilesOverride?.filter((profile) => deletedIdSet.has(profile.id)) ??
      visibleRealProfiles.filter((profile) => deletedIdSet.has(profile.id));
    const cloudBackedProfiles = deletedProfiles.filter((profile) =>
      currentDeviceCloudBackupMap.has(profile.id),
    );

    if (cloudBackedProfiles.length === 0) {
      return;
    }

    setCloudDeleteSyncPrompt({
      source: 'local-delete',
      deletedProfileIds: cloudBackedProfiles.map((profile) => profile.id),
      deletedProfileNames: cloudBackedProfiles.map((profile) => getProfileDisplayName(profile)),
      remainingSnapshotIds: visibleRealProfiles
        .map((profile) => profile.id)
        .filter((profileId) => !deletedIdSet.has(profileId)),
      remainingSnapshots: availableLocalRemoteSnapshots.filter(
        (snapshot) => !deletedIdSet.has(snapshot.id),
      ),
    });
  };

  async function handleWorkspaceSelectionAvailability(nextEnabled: boolean) {
    const profileIds = getWorkspaceSelectedProfileIds();
    if (profileIds.length === 0) {
      setToast({ tone: 'info', message: '请先选择至少一张卡片。' });
      return;
    }

    if (workspaceSelectionBusyAction) {
      return;
    }

    const requestId = beginProfilesStateRequest();
    try {
      setWorkspaceSelectionBusyAction(nextEnabled ? 'enable' : 'disable');
      const nextState = await window.codexWorkspace.batchUpdateProfileAvailability({
        profileIds,
        manuallyDisabled: !nextEnabled,
        clearAutoDisabled: nextEnabled,
      });
      commitProfilesState(requestId, nextState);
      resetWorkspaceSelectionState();
      setToast({
        tone: 'success',
        message: nextEnabled
          ? `已批量启用 ${profileIds.length} 张卡片。`
          : `已批量停用 ${profileIds.length} 张卡片。`,
      });
    } catch (error) {
      const message = normalizeRuntimeErrorMessage(
        error,
        nextEnabled ? '批量启用失败。' : '批量停用失败。',
      );
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    } finally {
      setWorkspaceSelectionBusyAction(null);
    }
  }

  async function handleWorkspaceSelectionDelete(options?: {
    profileIds?: string[];
    deletedProfiles?: ProfileSummary[];
    successMessage?: string;
    failureMessage?: string;
    resetSelection?: boolean;
    closeBatchDialog?: boolean;
  }) {
    const profileIds = options?.profileIds ?? getWorkspaceSelectedProfileIds();
    if (profileIds.length === 0) {
      setToast({
        tone: 'info',
        message:
          options?.profileIds && options.profileIds.length === 0
            ? '当前空间池没有可删除的本地卡片。'
            : '请先选择至少一张卡片。',
      });
      return;
    }

    if (workspaceSelectionBusyAction) {
      return;
    }

    const requestId = beginProfilesStateRequest();
    try {
      setWorkspaceSelectionBusyAction('delete');
      setWorkspaceProfileOverlay(profileIds, {
        title: '正在删除中',
        detail: '正在清理本地卡片',
        busy: true,
      });
      setWorkspaceRemovingProfileIds(profileIds);
      const nextState = await window.codexWorkspace.batchDeleteProfiles({
        profileIds,
        removeFiles: true,
      });
      commitProfilesState(requestId, nextState);
      setWorkspaceRemovingProfileIds([]);
      setWorkspaceProfileOverlay(profileIds, null);
      maybePromptCloudDeleteSync(profileIds, options?.deletedProfiles);
      if (options?.resetSelection !== false) {
        resetWorkspaceSelectionState();
      }
      if (options?.closeBatchDialog) {
        closeBatchNoteDialog();
      }
      setToast({
        tone: 'success',
        message: options?.successMessage ?? `已批量删除 ${profileIds.length} 张卡片及本地文件。`,
      });
    } catch (error) {
      setWorkspaceRemovingProfileIds([]);
      setWorkspaceProfileOverlay(profileIds, null);
      const message = normalizeRuntimeErrorMessage(error, options?.failureMessage ?? '批量删除失败。');
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    } finally {
      setWorkspaceSelectionBusyAction(null);
      setBatchDeleteReturnToDialog(false);
      setBatchDeleteConfirmRequest(null);
    }
  }

  async function handleConfirmCloudDeleteSync() {
    const prompt = cloudDeleteSyncPrompt;
    if (!prompt) {
      return;
    }

    setCloudDeleteSyncPrompt(null);
    setWorkspaceSelectionBusyAction('cloud-delete');
    try {
      await handleSyncCurrentDeviceProfilesToCloud(
        true,
        [],
        prompt.remainingSnapshots,
        `已同步删除云端中的 ${prompt.deletedProfileNames.length} 张卡片。`,
        {
          progressTotal: prompt.remainingSnapshots.length,
          replaceCloudSnapshotSet: true,
        },
      );
    } finally {
      setWorkspaceSelectionBusyAction(null);
    }
  }

  function handleRequestClearSelectedCloudEntries(entryIds: string[] = cloudSyncSelectedCloudIds) {
    const selectedIds = new Set(entryIds.map((item) => item.trim()).filter(Boolean));
    if (selectedIds.size === 0) {
      setToast({ tone: 'info', message: '请先勾选要清空的云端卡片。' });
      return;
    }

    const selectedEntries = authState.cloudProfileBackups.filter((entry) => selectedIds.has(entry.id));
    if (selectedEntries.length === 0) {
      setToast({ tone: 'info', message: '当前没有可清空的云端卡片。' });
      return;
    }

    setCloudSyncDialog(null);
    setCloudDeleteSyncPrompt({
      source: 'cloud-clear',
      deletedProfileIds: selectedEntries.map((entry) => entry.transferPackage.sourceProfileId),
      deletedProfileNames: selectedEntries.map((entry) => getCloudBackupCardTitle(entry)),
      remainingSnapshotIds: [],
      remainingSnapshots: authState.cloudProfileBackups
        .filter((entry) => !selectedIds.has(entry.id))
        .map((entry) => convertCloudBackupToRemoteSnapshot(entry)),
    });
  }

  const applyQuotaAlertSnooze = () => {
    const selectedOption =
      QUOTA_ALERT_SNOOZE_OPTIONS.find((option) => option.key === quotaAlertSnoozeOption) ??
      QUOTA_ALERT_SNOOZE_OPTIONS[0];
    const delayMs =
      selectedOption.minutes > 0
        ? selectedOption.minutes * 60 * 1000
        : DEFAULT_QUOTA_ALERT_DISMISS_MS;
    setDismissedQuotaAlertKey(null);
    setQuotaAlertSnoozeMenuOpen(false);
    setQuotaAlertSnoozeUntil(Date.now() + delayMs);
  };

  const getCurrentOpenClawSwitchModel = () =>
    state.summary.openclawCurrentModel &&
    openClawSwitchModelOptions.some((option) => option.key === state.summary.openclawCurrentModel)
      ? state.summary.openclawCurrentModel
      : null;

  const getEffectiveOpenClawSwitchModel = (requestedModel?: string | null): string | null => {
    const normalizedRequestedModel = requestedModel?.trim() ?? '';
    if (normalizedRequestedModel) {
      return openClawSwitchModelOptions.some((option) => option.key === normalizedRequestedModel)
        ? normalizedRequestedModel
        : null;
    }

    const selectedModel = switchOpenClawModel.trim();
    if (selectedModel && openClawSwitchModelOptions.some((option) => option.key === selectedModel)) {
      return selectedModel;
    }

    return getCurrentOpenClawSwitchModel();
  };

  async function executeSwitchProfile(
    targetProfile: ProfileSummary,
    trigger: 'manual' | 'auto' | 'card' | 'recommended' = 'manual',
    openclawModel?: string | null,
  ) {
    if (mandatoryUpdateLockActive) {
      setToast({ tone: 'error', message: '当前版本必须先完成更新，已暂停所有切换操作。' });
      return;
    }

    const runId = `${Date.now()}-${targetProfile.id}`;
    const targetWorkspaceName = getProfileDisplayName(targetProfile);
    const currentSwitchMode = state.settings.switchMode;
    const isCardTrigger = trigger === 'card';
    const useDeferredDesktopLaunch = trigger !== 'auto';
    const shouldRunUiInteractionOnly = uiInteractionTestEnabled && isSimulatedProfile(targetProfile);
    const effectiveOpenClawModel =
      currentSwitchMode === 'openclaw' ? getEffectiveOpenClawSwitchModel(openclawModel) : null;

    if (shouldRunUiInteractionOnly) {
      const token = uiInteractionRunTokenRef.current + 1;
      uiInteractionRunTokenRef.current = token;
      setUiInteractionTestMode('switching');
      setBusyAction('switch');
      setMenuProfileId(null);
      setSwitchTarget(null);
      setSwitchOpenClawModel('');
      setAutoSwitchPrompt(null);
      setAutoDisabledPrompt(null);
      setQueuedAutoDisabledPrompt(null);
      setSwitchSuccessNotice(null);
      clearCardSwitchFeedback();
      clearPostSwitchDesktopLaunchTimer();
      setSwitchProgressRunId(runId);
      setSwitchProgressEvents([]);
      switchProgressRunIdRef.current = runId;
      if (isCardTrigger) {
        setCardSwitchInteraction({
          profileId: targetProfile.id,
          phase: 'running',
        });
        setSwitchRuntimeOverlay(null);
      } else if (trigger === 'recommended') {
        setSwitchRuntimeOverlay(null);
      } else {
        setSwitchRuntimeOverlay({
          runId,
          phase: 'running',
          workspaceName: targetWorkspaceName,
          visible: true,
        });
      }

      const simulatedSteps: Array<{
        waitMs: number;
        stepKey: SwitchProgressEvent['stepKey'];
        title: string;
        detail: string;
      }> = [
        {
          waitMs: 260,
          stepKey: 'prepare',
          title: '准备切换链路',
          detail: `正在读取 ${targetWorkspaceName} 的演示切换流程。`,
        },
        {
          waitMs: 520,
          stepKey: 'switch-auth',
          title: '模拟写入 auth.json',
          detail: '仅演示卡片旋转、阶段文案和前端反馈，不执行真实写入。',
        },
        {
          waitMs: 620,
          stepKey: 'quota-sync',
          title: '模拟额度同步',
          detail: '继续走完整个前端切换阶段，方便检查动画与提示文案。',
        },
        {
          waitMs: 420,
          stepKey: 'complete',
          title: '演示切换完成',
          detail: '当前只是 UI 交互测试，关闭测试模式后会恢复到启用前。',
        },
      ];

      await triggerFloatingWidgetPreviewTransition({
        phase: 'start',
        targetProfileId: targetProfile.id,
      });

      for (const step of simulatedSteps) {
        const shouldContinue = await waitForUiInteractionStep(step.waitMs, token);
        if (!shouldContinue) {
          void triggerFloatingWidgetPreviewTransition({ phase: 'reset' });
          setBusyAction(null);
          return;
        }

        setSwitchProgressEvents((current) =>
          upsertSwitchProgressEvent(current, {
            runId,
            targetProfileId: targetProfile.id,
            switchMode: currentSwitchMode,
            stepKey: step.stepKey,
            title: step.title,
            detail: step.detail,
            status: step.stepKey === 'complete' ? 'success' : 'active',
            timestamp: new Date().toISOString(),
          }),
        );
      }

      const activatedAt = new Date().toISOString();
      setState((current) => buildUiInteractionSwitchState(current, targetProfile, activatedAt));
      await triggerFloatingWidgetPreviewTransition({
        phase: 'complete',
        targetProfileId: targetProfile.id,
      });
      setSwitchProgressRunId(null);
      switchProgressRunIdRef.current = null;
      if (isCardTrigger) {
        playCardFlipAudio('wave');
        setCardSwitchWaveToken((current) => current + 1);
        setCardSwitchInteraction({
          profileId: targetProfile.id,
          phase: 'success',
        });
        cardSwitchVisualTimerRef.current = window.setTimeout(() => {
          cardSwitchVisualTimerRef.current = null;
          setCardSwitchInteraction((current) =>
            current?.profileId === targetProfile.id && current.phase === 'success' ? null : current,
          );
          const nextSweepToken = Date.now();
          playCardFlipAudio('accent');
          setCardSwitchSweepState({
            profileId: targetProfile.id,
            token: nextSweepToken,
          });
          cardSwitchSweepTimerRef.current = window.setTimeout(() => {
            cardSwitchSweepTimerRef.current = null;
            setCardSwitchSweepState((current) =>
              current?.token === nextSweepToken ? null : current,
            );
            setSwitchProgressEvents([]);
          }, CARD_SWITCH_SUCCESS_SWEEP_MS);
        }, CARD_SWITCH_SUCCESS_HOLD_MS);
      } else if (trigger !== 'recommended') {
        setSwitchSuccessNotice({
          name: targetProfile.name,
          workspaceName: targetWorkspaceName,
          activatedAt,
          switchMode: currentSwitchMode,
          codexAutoRestart: false,
          traeAutoRestart: false,
          codexTraeAutoRestart: false,
          restartedDesktopApps: [],
          openclawSynced: false,
          openclawGatewayRestarted: false,
          openclawModel: effectiveOpenClawModel,
          openclawModelLabel: effectiveOpenClawModel
            ? getOpenClawModelLabel(effectiveOpenClawModel, state.summary.openclawAvailableModels)
            : null,
          openclawModelChanged: false,
          warnings: ['UI 交互测试中，未执行真实 auth 写入与后端联动。'],
        });
        setSwitchRuntimeOverlay({
          runId,
          phase: 'success',
          workspaceName: targetWorkspaceName,
          visible: true,
          message: `已模拟切换到 ${targetWorkspaceName}，当前只是前端演示。`,
        });
      } else {
        setSwitchProgressEvents([]);
      }
      setUiInteractionTestMode('idle');
      setBusyAction(null);
      setToast({
        tone: 'success',
        message: `UI 交互测试：已完整演示 ${targetWorkspaceName} 的切换流程。关闭测试模式后会恢复。`,
      });
      return;
    }

    try {
      setBusyAction('switch');
      setMenuProfileId(null);
      setSwitchTarget(null);
      setSwitchOpenClawModel('');
      setAutoSwitchPrompt(null);
      setAutoDisabledPrompt(null);
      setQueuedAutoDisabledPrompt(null);
      setSwitchSuccessNotice(null);
      clearCardSwitchFeedback();
      setSwitchProgressRunId(runId);
      setSwitchProgressEvents([]);
      switchProgressRunIdRef.current = runId;
      if (isCardTrigger) {
        setCardSwitchInteraction({
          profileId: targetProfile.id,
          phase: 'running',
        });
        setSwitchRuntimeOverlay(null);
      } else if (trigger === 'recommended') {
        setSwitchRuntimeOverlay(null);
      } else {
        setSwitchRuntimeOverlay({
          runId,
          phase: 'running',
          workspaceName: targetWorkspaceName,
          visible: true,
        });
      }
      const result = await window.codexWorkspace.switchProfile({
        profileId: targetProfile.id,
        openclawModel: effectiveOpenClawModel,
        ignoreAutoDisabled: forceEnableProbeCards && targetProfile.autoDisabled === true,
        runId,
      });
      commitAuthoritativeProfilesState(result.state);
      setSwitchProgressRunId(null);
      switchProgressRunIdRef.current = null;
      const pendingDesktopLaunchKinds = Array.isArray(result.pendingDesktopLaunchKinds)
        ? result.pendingDesktopLaunchKinds
        : [];
      let deferredLaunchResult: PostSwitchDesktopLaunchResult | null = null;
      if (useDeferredDesktopLaunch && pendingDesktopLaunchKinds.length > 0) {
        scheduleDeferredDesktopLaunch(targetProfile, pendingDesktopLaunchKinds);
      } else if (pendingDesktopLaunchKinds.length > 0) {
        deferredLaunchResult = await runDeferredDesktopLaunch(
          targetProfile,
          pendingDesktopLaunchKinds,
        );
      }
      const restartedDesktopApps =
        deferredLaunchResult?.restartedDesktopApps ??
        (Array.isArray(result.restartedDesktopApps) ? result.restartedDesktopApps : []);
      const mergedSwitchWarnings = [
        ...(Array.isArray(result.warnings) ? result.warnings : []),
        ...(deferredLaunchResult?.warnings ?? []),
      ];
      if (isCardTrigger) {
        playCardFlipAudio('wave');
        setCardSwitchWaveToken((current) => current + 1);
        setCardSwitchInteraction({
          profileId: targetProfile.id,
          phase: 'success',
        });
        cardSwitchVisualTimerRef.current = window.setTimeout(() => {
          cardSwitchVisualTimerRef.current = null;
          setCardSwitchInteraction((current) =>
            current?.profileId === targetProfile.id && current.phase === 'success' ? null : current,
          );
          const nextSweepToken = Date.now();
          playCardFlipAudio('accent');
          setCardSwitchSweepState({
            profileId: targetProfile.id,
            token: nextSweepToken,
          });
          cardSwitchSweepTimerRef.current = window.setTimeout(() => {
            cardSwitchSweepTimerRef.current = null;
            setCardSwitchSweepState((current) =>
              current?.token === nextSweepToken ? null : current,
            );
            setSwitchProgressEvents([]);
          }, CARD_SWITCH_SUCCESS_SWEEP_MS);
        }, CARD_SWITCH_SUCCESS_HOLD_MS);
      } else if (trigger !== 'recommended') {
        setSwitchSuccessNotice({
          name: targetProfile.name,
          workspaceName: targetWorkspaceName,
          activatedAt: result.activatedAt,
          switchMode: result.switchMode,
          codexAutoRestart: result.codexAutoRestart,
          traeAutoRestart: result.traeAutoRestart,
          codexTraeAutoRestart: result.codexTraeAutoRestart,
          restartedDesktopApps,
          openclawSynced: result.openclawSynced,
          openclawGatewayRestarted: result.openclawGatewayRestarted,
          openclawModel: result.openclawModel,
          openclawModelLabel: result.openclawModel
            ? getOpenClawModelLabel(result.openclawModel, result.state.summary.openclawAvailableModels)
            : null,
          openclawModelChanged: result.openclawModelChanged,
          warnings: mergedSwitchWarnings,
        });
        setSwitchRuntimeOverlay({
          runId,
          phase: 'success',
          workspaceName: targetWorkspaceName,
          visible: true,
          message:
            trigger === 'auto'
              ? `已自动切换到 ${targetWorkspaceName}，您可以继续使用`
              : `已切换到 ${targetWorkspaceName}，您可以继续使用`,
        });
      } else {
        setSwitchProgressEvents([]);
      }
      setToast({
        tone: 'success',
        message:
          trigger === 'auto'
            ? `已自动切换到 ${targetWorkspaceName}。`
            : trigger === 'recommended'
              ? `已切换到 ${targetWorkspaceName}，后台会继续启动已勾选应用。`
            : `已切换到 ${targetWorkspaceName}。请先不要重复点击登录。`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : '切换 profile 失败。';
      setSwitchProgressEvents((current) =>
        upsertSwitchProgressEvent(current, {
          runId,
          targetProfileId: targetProfile.id,
          switchMode: currentSwitchMode,
          stepKey: 'complete',
          title: '切换失败',
          detail: message,
          status: 'error',
          timestamp: new Date().toISOString(),
        }),
      );
      setSwitchProgressRunId(null);
      switchProgressRunIdRef.current = null;
      if (isCardTrigger) {
        setCardSwitchInteraction({
          profileId: targetProfile.id,
          phase: 'error',
        });
        cardSwitchVisualTimerRef.current = window.setTimeout(() => {
          cardSwitchVisualTimerRef.current = null;
          setCardSwitchInteraction((current) =>
            current?.profileId === targetProfile.id && current.phase === 'error'
              ? {
                  profileId: targetProfile.id,
                  phase: 'preview',
                }
              : current,
          );
        }, CARD_SWITCH_ERROR_RETRY_READY_MS);
        setSwitchRuntimeOverlay(null);
      } else {
        setSwitchRuntimeOverlay({
          runId,
          phase: 'error',
          workspaceName: targetWorkspaceName,
          visible: true,
          message: `切换失败 · ${message}`,
        });
      }
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    } finally {
      setBusyAction(null);
    }
  }

  async function handleSwitchProfile() {
    if (!switchTarget) {
      return;
    }

    const currentOpenClawModel = getCurrentOpenClawSwitchModel();

    await executeSwitchProfile(
      switchTarget,
      'manual',
      state.settings.switchMode === 'openclaw'
        ? switchOpenClawModel || currentOpenClawModel
        : null,
    );
  }

  function handleCardSwitchPreview(targetProfile: ProfileSummary) {
    if (busyAction === 'switch') {
      return;
    }

    clearCardSwitchFeedback();
    setSwitchProgressEvents([]);
    playCardFlipAudio('single');
    setCardSwitchInteraction({
      profileId: targetProfile.id,
      phase: 'preview',
    });
  }

  function handleCardSwitchPreviewExit(targetProfile: ProfileSummary) {
    if (cardSwitchInteraction?.profileId !== targetProfile.id || cardSwitchInteraction.phase !== 'preview') {
      return;
    }

    clearCardSwitchFeedbackTimers();
    setSwitchProgressEvents([]);
    setCardSwitchInteraction(null);
    playCardFlipAudio('single');
  }

  function handleCardSwitchEnable(targetProfile: ProfileSummary) {
    clearCardSwitchFeedback();
    setSwitchProgressEvents([]);
    setSwitchTarget(targetProfile);
  }

  function handleActiveCardNotice(targetProfile: ProfileSummary) {
    setCenterNotice({
      id: `active-card-${targetProfile.id}-${Date.now()}`,
      message: `当前正在使用 ${getProfileDisplayName(targetProfile)}`,
      title: `当前正在使用 ${getProfileDisplayName(targetProfile)}`,
      detail: '这个空间已经在生效，无需重复切换。换一张卡片试试看，切换会更顺手。',
      variant: 'active-card',
    });
  }

  function handleCardSwitchDirect(targetProfile: ProfileSummary) {
    if (busyAction === 'switch') {
      return;
    }

    void executeSwitchProfile(
      targetProfile,
      'card',
      state.settings.switchMode === 'openclaw' ? getCurrentOpenClawSwitchModel() : null,
    );
  }

  async function handleDeleteProfile() {
    if (!deleteTarget) {
      return;
    }

    const deletedProfile = deleteTarget;
    const deletedProfileIds = [deletedProfile.id];
    const shouldRemoveFiles = removeFilesOnDelete;

    if (uiInteractionTestEnabled) {
      setDeleteTarget(null);
      setRemoveFilesOnDelete(false);
      setToast({
        tone: 'info',
        message: 'UI 交互测试中不会真正删除 Profile。请先关闭测试模式。',
      });
      return;
    }

    const requestId = beginProfilesStateRequest();
    try {
      setDeleteTarget(null);
      setRemoveFilesOnDelete(false);
      setBusyAction('delete');
      setWorkspaceProfileOverlay(deletedProfileIds, {
        title: '正在删除中',
        detail: shouldRemoveFiles ? '正在清理本地卡片' : '正在移除当前卡片',
        busy: true,
      });
      setWorkspaceRemovingProfileIds((current) =>
        current.includes(deletedProfile.id) ? current : [...current, deletedProfile.id],
      );
      const nextState = await window.codexWorkspace.batchDeleteProfiles({
        profileIds: deletedProfileIds,
        removeFiles: shouldRemoveFiles,
      });
      commitProfilesState(requestId, nextState);
      setWorkspaceRemovingProfileIds((current) => current.filter((profileId) => profileId !== deletedProfile.id));
      setWorkspaceProfileOverlay(deletedProfileIds, null);
      if (currentDeviceCloudBackupMap.has(deletedProfile.id)) {
        setCloudDeleteSyncPrompt({
          source: 'local-delete',
          deletedProfileIds: deletedProfileIds,
          deletedProfileNames: [getProfileDisplayName(deletedProfile)],
          remainingSnapshotIds: visibleRealProfiles
            .map((profile) => profile.id)
            .filter((profileId) => profileId !== deletedProfile.id),
          remainingSnapshots: availableLocalRemoteSnapshots.filter(
            (snapshot) => snapshot.id !== deletedProfile.id,
          ),
        });
      }
      setToast({
        tone: 'success',
        message: `${
          shouldRemoveFiles
            ? `已删除 profile 和文件夹：${deletedProfile.name}`
            : `已删除 profile 元数据：${deletedProfile.name}`
        }`,
      });
    } catch (error) {
      setWorkspaceRemovingProfileIds((current) => current.filter((profileId) => profileId !== deletedProfile.id));
      setWorkspaceProfileOverlay(deletedProfileIds, null);
      const message = error instanceof Error ? error.message : '删除 profile 失败。';
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    } finally {
      setBusyAction(null);
    }
  }

  async function handleChangeRootDirectory() {
    if (uiInteractionTestEnabled) {
      setToast({
        tone: 'info',
        message: 'UI 交互测试中不会真正切换根目录。请先关闭测试模式。',
      });
      return;
    }

    const requestId = beginProfilesStateRequest();
    try {
      setBusyAction('root');
      const picked = await window.codexWorkspace.chooseRootDirectory();
      if (picked.canceled || !picked.filePath) {
        return;
      }

      const nextState = await window.codexWorkspace.setProfilesRootDir(picked.filePath);
      commitProfilesState(requestId, nextState);
      setToast({ tone: 'success', message: `根目录已切换到：${picked.filePath}` });
    } catch (error) {
      const message = error instanceof Error ? error.message : '切换根目录失败。';
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    } finally {
      setBusyAction(null);
    }
  }

  async function handleOpenPath(targetPath: string) {
    const result = await window.codexWorkspace.openPath(targetPath);
    if (!result.ok) {
      setToast({ tone: 'error', message: result.error ?? '打开目录失败。' });
    }
  }

  async function handleOpenExternal(targetUrl: string): Promise<boolean> {
    const result = await window.codexWorkspace.openExternal(targetUrl);
    if (!result.ok) {
      setToast({ tone: 'error', message: result.error ?? '打开外部链接失败。' });
      return false;
    }
    return true;
  }

  async function handleOpenWorkspaceOAuth(providerId: OAuthProviderId = 'codex') {
    try {
      const result = await window.codexWorkspace.startWorkspaceOAuth(providerId);
      if (result.targetUrl) {
        const opened = await handleOpenExternal(result.targetUrl);
        if (!opened) {
          return;
        }
      }
      setWorkspaceOAuthMonitor(result.monitorCurrentAuth ? result : null);
      setToast({
        tone: 'info',
        message: result.message,
      });
    } catch (error) {
      setWorkspaceOAuthMonitor(null);
      setToast({ tone: 'error', message: normalizeRuntimeErrorMessage(error, '启动 OAuth 登录失败。') });
    }
  }

  async function handleQuickOAuthProviderAction(entry: OAuthQuickProviderEntry) {
    await handleOpenWorkspaceOAuth(entry.id);
  }

  async function handleCheckNetworkAccess(source: ProxyReminderState['source'] = 'manual') {
    try {
      const nextState = await window.codexWorkspace.getNetworkAccessState();
      setNetworkAccessState(nextState);
      if (nextState.status === 'needs-proxy' || nextState.status === 'network-error') {
        setProxyReminder({
          network: nextState,
          source,
        });
        return;
      }

      setProxyReminder(null);
      if (source === 'manual') {
        setToast({ tone: 'success', message: nextState.summary });
      }
    } catch (error) {
      const message = normalizeRuntimeErrorMessage(error, '检测当前网络代理状态失败。');
      setToast({ tone: 'error', message });
    }
  }

  async function handleRecoverProxyCards() {
    try {
      const nextState = await window.codexWorkspace.getNetworkAccessState();
      setNetworkAccessState(nextState);
      if (nextState.status === 'needs-proxy' || nextState.status === 'network-error') {
        setProxyReminder({
          network: nextState,
          source: 'manual',
        });
        return;
      }

      setProxyReminder(null);
      await refreshState();
    } catch (error) {
      const message = normalizeRuntimeErrorMessage(error, '检测代理恢复状态失败。');
      setToast({ tone: 'error', message });
    }
  }

  async function handleCopyValue(label: string, value: string) {
    if (!value.trim()) {
      setToast({ tone: 'error', message: `${label} 当前为空。` });
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setToast({ tone: 'success', message: `${label} 已复制成功。` });
    } catch (error) {
      const message = error instanceof Error ? error.message : '复制失败。';
      setToast({ tone: 'error', message });
    }
  }

  async function handleCheckForUpdates() {
    try {
      setUpdateBusyAction('check');
      const result = await window.codexWorkspace.checkForUpdates();
      setUpdateState(result.state);
      if (result.state.updateAvailable || result.state.blockedByMandatory) {
        setUpdateDialogOpen(true);
      }
      if (result.state.availability === 'unconfigured') {
        setUpdateDialogOpen(true);
        setToast(null);
        return;
      }
      setToast({ tone: getUpdateActionTone(result), message: result.message });
    } catch (error) {
      const message = normalizeRuntimeErrorMessage(error, '检查更新失败。');
      setToast({ tone: 'error', message });
    } finally {
      setUpdateBusyAction(null);
    }
  }

  async function handleDownloadAndInstallUpdate() {
    try {
      setUpdateDialogOpen(true);
      setUpdateBusyAction('install');
      const result = await window.codexWorkspace.downloadAndInstallUpdate();
      setUpdateState(result.state);
      setToast({ tone: getUpdateActionTone(result), message: result.message });
    } catch (error) {
      const message = normalizeRuntimeErrorMessage(error, '下载安装更新失败。');
      setToast({ tone: 'error', message });
    } finally {
      setUpdateBusyAction(null);
    }
  }

  async function handleOpenUpdateNotes() {
    const result = await window.codexWorkspace.openUpdateNotes();
    if (!result.ok) {
      setToast({ tone: 'error', message: result.error ?? '打开更新说明失败。' });
    }
  }

  function handleOpenAboutDialog() {
    setFooterHelpOpen(false);
    setUserCenterOpen(false);
    setLicenseCenterOpen(false);
    setUpdateDialogOpen(false);
    setAboutDialogOpen(true);
  }

  function handleOpenUpdateDialog() {
    setFooterHelpOpen(false);
    setAboutDialogOpen(false);
    setUserCenterOpen(false);
    setUpdateDialogOpen(true);
  }

  function handleOpenUpdateSupport() {
    setUpdateDialogOpen(false);
    setAboutDialogOpen(false);
    setUserCenterOpen(false);
    setFooterHelpOpen(true);
  }

  function handleOpenLicenseCenter() {
    setUserCenterOpen(false);
    setFooterHelpOpen(false);
    setAboutDialogOpen(false);
    setUpdateDialogOpen(false);
    setLicenseCenterOpen(true);
  }

  function openQrPreview(
    items: readonly QrPreviewItem[],
    source: QrPreviewSource,
    activeId?: string,
  ) {
    const normalizedItems = items.map((item) => ({
      id: item.id,
      title: item.title,
      subtitle: item.subtitle,
      hint: item.hint,
      imageSrc: item.imageSrc ?? null,
    }));
    const activeItem =
      normalizedItems.find((item) => item.id === activeId) ?? normalizedItems[0] ?? null;

    if (!activeItem) {
      return;
    }

    setAuthGateQrPreviewState({
      source,
      items: normalizedItems,
      activeId: activeItem.id,
    });
  }

  function handleOpenAuthGateQrPreview(item: QrPreviewItem, source: QrPreviewSource = 'auth-gate') {
    openQrPreview([item], source, item.id);
  }

  function handleOpenFeedbackQrPreview(activeId?: string) {
    openQrPreview(FOOTER_HELP_QR_ITEMS, 'feedback', activeId);
  }

  function resetScanWorkflow(options?: { clearPendingBinding?: boolean }) {
    setScanFlowState('waiting_scan');
    setScanError(null);
    scanConsumedTicketIdRef.current = null;
    setScanTicket(null);
    setScanCodeUrl(null);
    setScanCodeChannel(null);
    if (options?.clearPendingBinding) {
      setScanPendingBinding(null);
    }
  }

  function createPendingScanBindingContext(ticketId: string): PendingScanBindingContext {
    return {
      ticketId,
      source: authState.sessionStatus === 'authenticated' ? 'user-center-bind' : 'guest-scan',
      reason: 'identity-missing',
      requiresRescanAfterRegister: false,
      createdAt: new Date().toISOString(),
    };
  }

  function openStandaloneAuthGate(
    mode: 'preview' | 'interactive',
    entryMode: AuthGateEntryMode,
    formMode: AuthFormMode = 'login',
  ) {
    setUserCenterOpen(false);
    setFooterHelpOpen(false);
    setAboutDialogOpen(false);
    setUpdateDialogOpen(false);
    setLicenseCenterOpen(false);
    setAuthEntryMode(entryMode);
    setAuthMode(formMode);
    resetScanWorkflow({ clearPendingBinding: entryMode === 'scan' });
    setAuthGateStandaloneMode(mode);
  }

  function handleAuthEntryModeChange(nextMode: AuthGateEntryMode) {
    if (nextMode === authEntryMode) {
      return;
    }

    if (nextMode === 'scan' && scanPendingBinding && authState.sessionStatus !== 'authenticated') {
      setScanRescanConfirmOpen(true);
      return;
    }

    resetScanWorkflow();
    setAuthEntryMode(nextMode);
  }

  function handleOpenAuthGatePreview() {
    openStandaloneAuthGate('preview', 'manual', 'login');
  }

  function handleOpenScanBinding() {
    openStandaloneAuthGate('interactive', 'scan', 'login');
  }

  function handleScanChannelChange(nextChannel: ScanChannel) {
    if (nextChannel === scanChannel) {
      return;
    }

    setScanChannel(nextChannel);
    resetScanWorkflow();

    if (authGateWorkflowVisible && authEntryMode === 'scan' && !authGatePreviewMode) {
      void handleRetryScanLogin(nextChannel);
    }
  }

  function handleOpenEmailBindingRegistration() {
    setEmailBindingReminderOpen(false);
    emailBindingReminderNextAtRef.current = Date.now() + EMAIL_BINDING_REMINDER_INTERVAL_MS;
    setAuthForm((current) => ({
      ...current,
      identifier: '',
      email: '',
      password: '',
      confirmPassword: '',
      verificationCode: '',
      inviteCode: '',
      rememberMe: false,
    }));
    openStandaloneAuthGate('interactive', 'manual', 'register');
  }

  function handleCloseAuthCenterOverlay() {
    setLicenseCenterOpen(false);
    resetScanWorkflow({ clearPendingBinding: true });
    setAuthGateStandaloneMode(null);
  }

  function handleUserCenterBrandFrameEnabledChange(enabled: boolean) {
    setUserCenterBrandFrameEnabled(enabled);
    if (uiInteractionTestEnabled) {
      setToast({
        tone: 'info',
        message: 'UI 交互测试中：品牌图标玻璃框只改当前前端展示，关闭测试后会恢复。',
      });
      return;
    }
    persistUserCenterBrandFrameEnabled(enabled);
  }

  async function persistFloatingWidgetEnabledChange(enabled: boolean, requestId: number) {
    const stateRequestId = beginProfilesStateRequest();
    try {
      const nextState = await window.codexWorkspace.setFloatingWidgetEnabled(enabled);

      if (requestId !== floatingWidgetEnabledRequestIdRef.current) {
        return;
      }

      commitProfilesState(stateRequestId, nextState);
      setFloatingWidgetEnabledDraft(null);
      setToast({ tone: 'success', message: enabled ? '已开启悬浮窗。' : '已关闭悬浮窗。' });
    } catch (error) {
      if (requestId !== floatingWidgetEnabledRequestIdRef.current) {
        return;
      }

      const message = normalizeRuntimeErrorMessage(
        error,
        enabled ? '开启悬浮窗失败。' : '关闭悬浮窗失败。',
      );
      setFloatingWidgetEnabledDraft(null);
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    }
  }

  async function persistTrayHoverCardEnabledChange(enabled: boolean, requestId: number) {
    const stateRequestId = beginProfilesStateRequest();
    try {
      const nextState = await window.codexWorkspace.setTrayHoverCardEnabled(enabled);

      if (requestId !== trayHoverCardEnabledRequestIdRef.current) {
        return;
      }

      commitProfilesState(stateRequestId, nextState);
      setTrayHoverCardEnabledDraft(null);
      setToast({ tone: 'success', message: enabled ? '已开启托盘悬停卡片。' : '已关闭托盘悬停卡片。' });
    } catch (error) {
      if (requestId !== trayHoverCardEnabledRequestIdRef.current) {
        return;
      }

      const message = normalizeRuntimeErrorMessage(
        error,
        enabled ? '开启托盘悬停卡片失败。' : '关闭托盘悬停卡片失败。',
      );
      setTrayHoverCardEnabledDraft(null);
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    }
  }

  function clearFloatingWidgetOpacityCommitTimer() {
    if (floatingWidgetOpacityCommitTimerRef.current !== null) {
      window.clearTimeout(floatingWidgetOpacityCommitTimerRef.current);
      floatingWidgetOpacityCommitTimerRef.current = null;
    }
  }

  async function persistFloatingWidgetOpacityChange(opacityPercent: number, requestId: number) {
    const stateRequestId = beginProfilesStateRequest();
    try {
      const nextState = await window.codexWorkspace.updateSettings({
        floatingWidgetOpacityPercent: opacityPercent,
      });

      if (requestId !== floatingWidgetOpacityRequestIdRef.current) {
        return;
      }

      commitProfilesState(stateRequestId, nextState);
      setFloatingWidgetOpacityDraft(null);
    } catch (error) {
      if (requestId !== floatingWidgetOpacityRequestIdRef.current) {
        return;
      }

      const message = normalizeRuntimeErrorMessage(error, '更新悬浮窗透明度失败。');
      setFloatingWidgetOpacityDraft(null);
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    }
  }

  function handleFloatingWidgetEnabledChange(enabled: boolean) {
    if (uiInteractionTestEnabled) {
      setFloatingWidgetEnabledDraft(null);
      applyUiInteractionSettingsPatch({ floatingWidgetEnabled: enabled });
      setToast({
        tone: 'info',
        message: enabled
          ? 'UI 交互测试中：已模拟开启悬浮窗。'
          : 'UI 交互测试中：已模拟关闭悬浮窗。',
      });
      return;
    }

    if (floatingWidgetEnabledDraft === null && enabled === state.settings.floatingWidgetEnabled) {
      return;
    }

    if (enabled === effectiveFloatingWidgetEnabled) {
      return;
    }

    setFloatingWidgetEnabledDraft(enabled);
    const requestId = floatingWidgetEnabledRequestIdRef.current + 1;
    floatingWidgetEnabledRequestIdRef.current = requestId;
    void persistFloatingWidgetEnabledChange(enabled, requestId);
  }

  function handleTrayHoverCardEnabledChange(enabled: boolean) {
    if (uiInteractionTestEnabled) {
      setTrayHoverCardEnabledDraft(null);
      applyUiInteractionSettingsPatch({ trayHoverCardEnabled: enabled });
      setToast({
        tone: 'info',
        message: enabled
          ? 'UI 交互测试中：已模拟开启托盘悬停卡片。'
          : 'UI 交互测试中：已模拟关闭托盘悬停卡片。',
      });
      return;
    }

    if (trayHoverCardEnabledDraft === null && enabled === state.settings.trayHoverCardEnabled) {
      return;
    }

    if (enabled === effectiveTrayHoverCardEnabled) {
      return;
    }

    setTrayHoverCardEnabledDraft(enabled);
    const requestId = trayHoverCardEnabledRequestIdRef.current + 1;
    trayHoverCardEnabledRequestIdRef.current = requestId;
    void persistTrayHoverCardEnabledChange(enabled, requestId);
  }

  function handleFloatingWidgetOpacityPercentChange(opacityPercent: number) {
    const normalized = Math.min(95, Math.max(35, Math.round(opacityPercent)));
    if (uiInteractionTestEnabled) {
      setFloatingWidgetOpacityDraft(null);
      applyUiInteractionSettingsPatch({ floatingWidgetOpacityPercent: normalized });
      return;
    }

    if (
      floatingWidgetOpacityDraft === null &&
      normalized === state.settings.floatingWidgetOpacityPercent
    ) {
      return;
    }

    if (normalized === effectiveFloatingWidgetOpacityPercent) {
      return;
    }

    setFloatingWidgetOpacityDraft(normalized);
    clearFloatingWidgetOpacityCommitTimer();
    const requestId = floatingWidgetOpacityRequestIdRef.current + 1;
    floatingWidgetOpacityRequestIdRef.current = requestId;
    floatingWidgetOpacityCommitTimerRef.current = window.setTimeout(() => {
      floatingWidgetOpacityCommitTimerRef.current = null;
      void persistFloatingWidgetOpacityChange(normalized, requestId);
    }, 140);
  }

  function clearCardFlipVolumeCommitTimer() {
    if (cardFlipVolumeCommitTimerRef.current !== null) {
      window.clearTimeout(cardFlipVolumeCommitTimerRef.current);
      cardFlipVolumeCommitTimerRef.current = null;
    }
  }

  async function persistCardFlipVolumeChange(volumePercent: number, requestId: number) {
    const stateRequestId = beginProfilesStateRequest();
    try {
      const nextState = await window.codexWorkspace.updateSettings({
        cardFlipVolumePercent: volumePercent,
      });

      if (requestId !== cardFlipVolumeRequestIdRef.current) {
        return;
      }

      commitProfilesState(stateRequestId, nextState);
      setCardFlipVolumeDraft(null);
    } catch (error) {
      if (requestId !== cardFlipVolumeRequestIdRef.current) {
        return;
      }

      const message = normalizeRuntimeErrorMessage(error, '更新翻转音量失败。');
      setCardFlipVolumeDraft(null);
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    }
  }

  function handleCardFlipVolumePercentChange(volumePercent: number) {
    const normalized = Math.min(
      MAX_CARD_FLIP_VOLUME_PERCENT,
      Math.max(MIN_CARD_FLIP_VOLUME_PERCENT, Math.round(volumePercent)),
    );
    if (uiInteractionTestEnabled) {
      setCardFlipVolumeDraft(null);
      applyUiInteractionSettingsPatch({ cardFlipVolumePercent: normalized });
      return;
    }

    if (
      cardFlipVolumeDraft === null &&
      normalized === state.settings.cardFlipVolumePercent
    ) {
      return;
    }

    if (normalized === effectiveCardFlipVolumePercent) {
      return;
    }

    setCardFlipVolumeDraft(normalized);
    clearCardFlipVolumeCommitTimer();
    const requestId = cardFlipVolumeRequestIdRef.current + 1;
    cardFlipVolumeRequestIdRef.current = requestId;
    cardFlipVolumeCommitTimerRef.current = window.setTimeout(() => {
      cardFlipVolumeCommitTimerRef.current = null;
      void persistCardFlipVolumeChange(normalized, requestId);
    }, 120);
  }

  function resetAvatarUploadState() {
    setAvatarUploadDraft(null);
    setAvatarAdjustDraft(null);
    setAvatarAdjustOpen(false);
    setAvatarUploadBusy(false);
    setAvatarUploadDragging(false);
    setAvatarUploadError(null);
  }

  function handleAvatarUploadOpenChange(open: boolean) {
    if (open) {
      setAvatarUploadOpen(true);
      return;
    }

    setAvatarUploadOpen(false);
    resetAvatarUploadState();
  }

  function handleEditAvatar() {
    if (!authState.profile) {
      setToast({ tone: 'error', message: '请先登录后再设置头像。' });
      return;
    }

    resetAvatarUploadState();
    setAvatarUploadOpen(true);
  }

  function handlePickAvatarFile() {
    avatarInputRef.current?.click();
  }

  function handleAvatarDraftChange(patch: Partial<AvatarUploadDraft>) {
    setAvatarAdjustDraft((current) => (current ? clampAvatarUploadDraft({ ...current, ...patch }) : current));
  }

  function handleResetAvatarDraftPosition() {
    setAvatarAdjustDraft((current) =>
      current
        ? {
            ...current,
            scale: current.minScale,
            offsetX: 0,
            offsetY: 0,
          }
        : current,
    );
  }

  function handleAvatarAdjustOpenChange(open: boolean) {
    if (open) {
      if (avatarUploadDraft) {
        setAvatarAdjustDraft({ ...avatarUploadDraft });
        setAvatarAdjustOpen(true);
      }
      return;
    }

    setAvatarAdjustOpen(false);
    setAvatarAdjustDraft(null);
  }

  function handleConfirmAvatarAdjust() {
    if (!avatarAdjustDraft) {
      return;
    }

    setAvatarUploadDraft(clampAvatarUploadDraft({ ...avatarAdjustDraft }));
    setAvatarAdjustOpen(false);
    setAvatarAdjustDraft(null);
  }

  async function handleAvatarFileSelection(selectedFile: File) {
    if (!selectedFile) {
      return;
    }

    if (!authState.profile) {
      setAvatarUploadError('当前账号未登录，不能保存头像。');
      return;
    }

    try {
      setAvatarUploadBusy(true);
      setAvatarUploadError(null);
      const draft = await prepareAvatarUploadDraft(selectedFile);
      const normalizedDraft = clampAvatarUploadDraft(draft);
      setAvatarUploadDraft(normalizedDraft);
      setAvatarAdjustDraft({ ...normalizedDraft });
      setAvatarAdjustOpen(true);
    } catch (error) {
      setAvatarUploadError(error instanceof Error ? error.message : '头像预处理失败。');
    } finally {
      setAvatarUploadBusy(false);
      setAvatarUploadDragging(false);
    }
  }

  async function handleAvatarFileChange(event: ChangeEvent<HTMLInputElement>) {
    const selectedFile = event.target.files?.[0];
    event.target.value = '';
    if (!selectedFile) {
      return;
    }

    await handleAvatarFileSelection(selectedFile);
  }

  function handleAvatarDragOver(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (!avatarUploadDragging) {
      setAvatarUploadDragging(true);
    }
  }

  function handleAvatarDragLeave(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }

    setAvatarUploadDragging(false);
  }

  function handleAvatarDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    const selectedFile = event.dataTransfer.files?.[0];
    if (!selectedFile) {
      setAvatarUploadDragging(false);
      return;
    }

    void handleAvatarFileSelection(selectedFile);
  }

  async function handleConfirmAvatarUpload() {
    if (!authState.profile) {
      setAvatarUploadError('当前账号未登录，不能保存头像。');
      return;
    }

    if (!avatarUploadDraft) {
      setAvatarUploadError('请先拖入头像或手动选择一张图片。');
      return;
    }

    try {
      setAvatarUploadBusy(true);
      setAvatarUploadError(null);
      const avatarDataUrl = await buildAvatarDataUrl(avatarUploadDraft);
      persistStoredAvatar(authState.profile, avatarDataUrl);
      setAvatarImageSrc(avatarDataUrl);
      setToast({ tone: 'success', message: '头像已更新。' });
      setAvatarUploadOpen(false);
      resetAvatarUploadState();
    } catch (error) {
      setAvatarUploadError(error instanceof Error ? error.message : '头像保存失败。');
    } finally {
      setAvatarUploadBusy(false);
    }
  }

  function handleAuthModeChange(nextMode: AuthFormMode) {
    if (nextMode === authMode) {
      return;
    }

    setAuthMode(nextMode);
    setAuthForm((current) => createModeAuthForm(nextMode, authState, current));
  }

  function handleAuthFormChange(patch: Partial<UserCenterAuthForm>) {
    setAuthForm((current) => {
      const next = {
        ...current,
        ...patch,
      };
      const rememberDisabled =
        Object.prototype.hasOwnProperty.call(patch, 'rememberMe') && patch.rememberMe === false;
      const identityTouched =
        Object.prototype.hasOwnProperty.call(patch, 'identifier') ||
        Object.prototype.hasOwnProperty.call(patch, 'email');

      if (rememberDisabled) {
        next.password = '';
        next.confirmPassword = '';
      } else if (identityTouched) {
        const referenceValue =
          authMode === 'login' ? next.identifier.trim() : (next.email || next.identifier).trim();
        if (!getRememberedAuthIdentifiers(authState).has(normalizeAuthCredentialValue(referenceValue))) {
          next.password = '';
          next.confirmPassword = '';
        }
      }

      if (authMode !== 'register') {
        next.confirmPassword = '';
      }

      return next;
    });
  }

  function isAuthAccountMissingMessage(message: string): boolean {
    return /尚未注册|没有账号|未注册/.test(message);
  }

  function isLikelyEmailIdentifier(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  }

  function switchAuthToRegister(email: string, message: string): boolean {
    if (!isLikelyEmailIdentifier(email)) {
      return false;
    }

    setAuthMode('register');
    setAuthForm((current) => ({
      ...createModeAuthForm('register', authState, current),
      email: email.trim(),
    }));
    setToast({ tone: 'info', message });
    return true;
  }

  async function applyAuthResult(
    action: typeof authBusyAction,
    request: Promise<AuthActionResult>,
    options?: {
      onError?: (message: string) => boolean;
    },
  ) {
    try {
      setAuthBusyAction(action);
      const result = await request;
      setAuthState(result.state);
      setToast({ tone: 'success', message: result.message });
      if (action === 'login' || action === 'register') {
        setUserCenterOpen(false);
        if (action === 'register') {
          setAuthMode('login');
        }
        setAuthForm(createInitialAuthForm(result.state));
        setAuthGateStandaloneMode(null);
      }
      if (action === 'logout') {
        setUserCenterOpen(false);
        setAuthGateStandaloneMode(null);
        setAuthMode('login');
        setAuthEntryMode('manual');
        setAuthForm(createInitialAuthForm(result.state));
      }
      if (action === 'activation' || action === 'reward' || action === 'claim-reward') {
        setLicenseCenterOpen(false);
      }
      if (action !== 'logout' && isWorkspaceAuthorized(result.state)) {
        setUserCenterOpen(false);
        setLicenseCenterOpen(false);
      }
      return result;
    } catch (error) {
      const message = normalizeRuntimeErrorMessage(error, '授权操作失败。');
      if (!options?.onError?.(message)) {
        setToast({ tone: 'error', message });
      }
      return null;
    } finally {
      setAuthBusyAction(null);
    }
  }

  async function handleRefreshScanTicket(ticketId: string) {
    try {
      const nextTicket = await authClient.getScanTicket(ticketId);
      setScanTicket(nextTicket);
      setScanError(null);
      if (nextTicket.status === 'scanned' || nextTicket.status === 'confirmed') {
        setScanFlowState((current) => (current === 'resume_binding' ? 'resume_binding' : 'waiting_confirm'));
      } else {
        setScanFlowState('waiting_scan');
      }
    } catch (error) {
      setScanFlowState('error');
      setScanError(normalizeRuntimeErrorMessage(error, '获取扫码状态失败。'));
    }
  }

  async function handleRetryScanLogin(
    channelOverride: ScanChannel = scanChannel,
    allowOfficialFallback = true,
  ) {
    if (authGatePreviewMode) {
      return;
    }

    try {
      setScanBusy('create');
      setScanFlowState('waiting_scan');
      setScanPendingBinding(null);
      setScanError(null);
      scanConsumedTicketIdRef.current = null;
      setScanTicket(null);
      setScanCodeUrl(null);
      setScanCodeChannel(null);
      const nextTicket = await authClient.createScanTicket({
        sourceDeviceId: authState.device.deviceId,
        channel: channelOverride,
      });
      const nextCodeUrl = await authClient.getScanCodeUrl(nextTicket.ticketId);
      setScanTicket(nextTicket);
      setScanCodeUrl(nextCodeUrl);
      setScanCodeChannel(channelOverride);
      setScanFlowState(
        nextTicket.status === 'scanned' || nextTicket.status === 'confirmed'
          ? 'waiting_confirm'
          : 'waiting_scan',
      );
    } catch (error) {
      setScanTicket(null);
      setScanCodeUrl(null);
      setScanCodeChannel(null);
      setScanFlowState('error');
      const nextMessage = normalizeRuntimeErrorMessage(
        error,
        authState.sessionStatus === 'authenticated' ? '创建扫码绑定失败。' : '创建扫码登录失败。',
      );
      const officialWhitelistBlocked =
        channelOverride === 'official_account' && /invalid ip|whitelist|白名单/i.test(nextMessage);
      const officialApiUnauthorized =
        channelOverride === 'official_account' &&
        /api unauthorized|errcode\s*[:=]?\s*48001|接口未授权|无权限|功能未授权/i.test(nextMessage);
      const officialQrUnavailable = officialWhitelistBlocked || officialApiUnauthorized;
      if (officialQrUnavailable && allowOfficialFallback) {
        setScanChannel('mini_program');
        setToast({
          tone: 'info',
          message: officialWhitelistBlocked
            ? '公众号二维码当前被微信白名单拦截，已自动切换到小程序扫码。'
            : '公众号二维码当前缺少微信接口权限，已自动切换到小程序扫码。',
        });
        await handleRetryScanLogin('mini_program', false);
        return;
      }
      setScanError(
        officialWhitelistBlocked
          ? '公众号二维码当前仍被微信 IP 白名单拦截，暂时无法生成独立公众号二维码。'
          : officialApiUnauthorized
            ? '当前公众号未获得微信带参数二维码接口权限，暂时无法生成独立公众号二维码。'
          : nextMessage,
      );
    } finally {
      setScanBusy(null);
    }
  }

  async function handleConsumeScanLogin(ticketId: string) {
    try {
      setScanBusy('consume');
      setScanFlowState(
        authState.sessionStatus === 'authenticated' || scanPendingBinding ? 'resume_binding' : 'waiting_confirm',
      );
      const result = await authClient.consumeScanTicket({ ticketId });
      setScanFlowState('binding_success');
      setScanPendingBinding(null);
      setScanError(null);
      setAuthState(result.state);
      setAuthForm(createInitialAuthForm(result.state));
      setToast({ tone: 'success', message: result.message });
      setAuthGateStandaloneMode(null);
      if (result.state.profile?.needsEmailBinding) {
        emailBindingReminderNextAtRef.current = Date.now() + EMAIL_BINDING_REMINDER_INTERVAL_MS;
        setEmailBindingReminderOpen(true);
      }
    } catch (error) {
      scanConsumedTicketIdRef.current = null;
      const runtimeError = readRuntimeErrorInfo(error, '处理扫码登录失败。');
      if (isScanIdentityMissingRuntimeError(runtimeError)) {
        if (authState.sessionStatus === 'authenticated') {
          setScanFlowState('error');
          setScanPendingBinding((current) => current ?? createPendingScanBindingContext(ticketId));
          setScanError(
            scanPendingBinding
              ? '当前扫码仍未形成可绑定身份，请重新扫码或先在小程序侧完成账号建立。'
              : '该扫码尚未形成可绑定身份，请先在小程序侧完成账号建立后重新扫码绑定。',
          );
          return;
        }

        setScanPendingBinding(createPendingScanBindingContext(ticketId));
        setScanFlowState('pending_manual_bind');
        setScanError(null);
        setAuthMode('login');
        setAuthEntryMode('manual');
        setAuthGateStandaloneMode('interactive');
        setToast({
          tone: 'info',
          message: '该扫码尚未完成账号绑定，请先登录后完成绑定。',
        });
        return;
      }

      setScanFlowState('error');
      setScanError(runtimeError.message);
    } finally {
      setScanBusy(null);
    }
  }

  async function handleSendAuthOtp() {
    if (!currentAuthOtpPurpose) {
      return;
    }

    const identifier = authForm.identifier.trim();
    const email = authForm.email.trim();
    if (currentAuthOtpPurpose === 'login' && !identifier) {
      setToast({ tone: 'error', message: '请先输入邮箱或 5 位 ID。' });
      return;
    }
    if (currentAuthOtpPurpose !== 'login' && !email) {
      setToast({ tone: 'error', message: '请先输入邮箱。' });
      return;
    }

    try {
      const optimisticDeadlineAt = Date.now() + OTP_DEFAULT_COOLDOWN_SECONDS * 1000;
      setAuthOtpNowMs(Date.now());
      setAuthOtpCooldowns((current) => ({
        ...current,
        [currentAuthOtpPurpose]: Math.max(current[currentAuthOtpPurpose], optimisticDeadlineAt),
      }));
      setAuthOtpBusyPurpose(currentAuthOtpPurpose);
      const result = await authClient.sendEmailOtp({
        purpose: currentAuthOtpPurpose,
        identifier: currentAuthOtpPurpose === 'login' ? identifier : undefined,
        email: currentAuthOtpPurpose === 'login' ? identifier : email,
      });
      const resolvedCooldownSeconds = Math.max(
        OTP_DEFAULT_COOLDOWN_SECONDS,
        result.cooldownSeconds ?? OTP_DEFAULT_COOLDOWN_SECONDS,
      );
      const resolvedDeadlineAt = Date.now() + resolvedCooldownSeconds * 1000;
      setAuthOtpNowMs(Date.now());
      setAuthOtpCooldowns((current) => ({
        ...current,
        [currentAuthOtpPurpose]: Math.max(current[currentAuthOtpPurpose], resolvedDeadlineAt),
      }));
      setToast({ tone: 'success', message: result.message });
    } catch (error) {
      const message = normalizeRuntimeErrorMessage(error, '验证码发送失败。');
      if (
        currentAuthOtpPurpose === 'login' &&
        isAuthAccountMissingMessage(message) &&
        switchAuthToRegister(identifier, '该邮箱尚未注册，已切换到注册。')
      ) {
        return;
      }
      setToast({ tone: 'error', message });
    } finally {
      setAuthOtpBusyPurpose(null);
    }
  }

  async function handlePendingScanRegisterFlow() {
    try {
      setAuthBusyAction('register');
      await authClient.register({
        email: authForm.email,
        password: authForm.password,
        verificationCode: authForm.verificationCode,
        inviteCode: authForm.inviteCode,
        rememberMe: authForm.rememberMe,
      });
      const logoutResult = await window.codexWorkspace.logout();
      setAuthState(logoutResult.state);
      setAuthMode('login');
      setAuthEntryMode('manual');
      setAuthGateStandaloneMode('interactive');
      setScanFlowState('pending_manual_bind');
      setAuthForm({
        ...createInitialAuthForm(logoutResult.state),
        identifier: authForm.email.trim(),
        email: authForm.email.trim(),
        confirmPassword: '',
      });
      setToast({ tone: 'success', message: '注册成功，请登录后完成绑定。' });
    } catch (error) {
      setToast({ tone: 'error', message: normalizeRuntimeErrorMessage(error, '注册失败。') });
    } finally {
      setAuthBusyAction(null);
    }
  }

  async function handlePendingScanLoginFlow() {
    try {
      setAuthBusyAction('login');
      const result = await authClient.login({
        identifier: authForm.identifier,
        email: authForm.identifier,
        password: authForm.password,
        rememberMe: authForm.rememberMe,
        loginMethod: authForm.loginMethod,
        verificationCode: authForm.verificationCode,
      });
      setAuthState(result.state);
      setAuthForm(createInitialAuthForm(result.state));
      setScanError(null);
      setScanFlowState('resume_binding');
      setAuthEntryMode('scan');
      setAuthGateStandaloneMode('interactive');
      setToast({ tone: 'success', message: '登录成功，正在继续完成绑定。' });
    } catch (error) {
      setToast({ tone: 'error', message: normalizeRuntimeErrorMessage(error, '登录失败。') });
    } finally {
      setAuthBusyAction(null);
    }
  }

  async function handleSubmitAuth() {
    if (authMode === 'register') {
      if (!authForm.verificationCode.trim()) {
        setToast({ tone: 'error', message: '请先输入邮箱验证码。' });
        return;
      }
      if (authForm.password !== authForm.confirmPassword) {
        setToast({ tone: 'error', message: '两次输入的密码不一致。' });
        return;
      }
      if (scanPendingBinding) {
        await handlePendingScanRegisterFlow();
        return;
      }
      await applyAuthResult(
        'register',
        authClient.register({
          email: authForm.email,
          password: authForm.password,
          verificationCode: authForm.verificationCode,
          inviteCode: authForm.inviteCode,
          rememberMe: authForm.rememberMe,
        }),
      );
      return;
    }

    if (authMode === 'forgot') {
      if (!authForm.verificationCode.trim()) {
        setToast({ tone: 'error', message: '请先输入邮箱验证码。' });
        return;
      }
      if (!authForm.password.trim()) {
        setToast({ tone: 'error', message: '请输入新密码。' });
        return;
      }
      if (authForm.password !== authForm.confirmPassword) {
        setToast({ tone: 'error', message: '两次输入的新密码不一致。' });
        return;
      }
      const result = await applyAuthResult(
        'forgot',
        authClient.requestPasswordReset({
          email: authForm.email,
          verificationCode: authForm.verificationCode,
          password: authForm.password,
        }),
      );
      if (result) {
        const nextEmail = authForm.email.trim();
        setAuthMode('login');
        setAuthForm((current) => ({
          ...createModeAuthForm('login', result.state, current),
          identifier: nextEmail,
          email: nextEmail,
        }));
      }
      return;
    }

    if (scanPendingBinding) {
      await handlePendingScanLoginFlow();
      return;
    }

    await applyAuthResult(
      'login',
      authClient.login({
        identifier: authForm.identifier,
        email: authForm.identifier,
        password: authForm.password,
        rememberMe: authForm.rememberMe,
        loginMethod: authForm.loginMethod,
        verificationCode: authForm.verificationCode,
      }),
      {
        onError: (message) =>
          isAuthAccountMissingMessage(message) &&
          switchAuthToRegister(authForm.identifier.trim(), '该邮箱尚未注册，已切换到注册。'),
      },
    );
  }

  async function handleLogout() {
    await applyAuthResult('logout', window.codexWorkspace.logout());
  }

  function handleDeferEmailBindingReminder() {
    setEmailBindingReminderOpen(false);
    emailBindingReminderNextAtRef.current = Date.now() + EMAIL_BINDING_REMINDER_INTERVAL_MS;
  }

  async function handleApplyActivationCode(code: string) {
    const trimmed = code.trim();
    if (!trimmed) {
      setToast({ tone: 'error', message: '请先输入授权码。' });
      return;
    }

    const result = await applyAuthResult(
      'activation',
      authClient.applyActivationCode({
        code: trimmed,
      }),
    );
    if (result) {
      setAuthGateActivationCode('');
    }
  }

  async function handleApplyRewardCode(code: string) {
    const trimmed = code.trim();
    if (!trimmed) {
      setToast({ tone: 'error', message: '请先输入奖励码。' });
      return;
    }

    const result = await applyAuthResult(
      'reward',
      authClient.applyRewardCode({
        code: trimmed,
      }),
    );
    if (result) {
      setAuthGateRewardCode('');
    }
  }

  async function handleRequestInviteCode() {
    await applyAuthResult('request-invite', authClient.requestInviteCode());
  }

  async function handleClaimInviteReward() {
    await applyAuthResult('claim-reward', authClient.claimInviteReward());
  }

  async function handleSubmitIssueTransfer() {
    if (authState.sessionStatus !== 'authenticated' || !authState.profile) {
      setToast({ tone: 'error', message: '请先登录在线账号后再发送卡片。' });
      return;
    }

    const targetAccount = workspaceSendTargetAccount.trim();
    const selectedProfileIds = Array.from(
      new Set(workspaceSelectionIds.map((item) => item.trim()).filter(Boolean)),
    );
    if (!targetAccount) {
      setToast({ tone: 'info', message: '请输入目标用户的邮箱或 ID。' });
      return;
    }
    if (selectedProfileIds.length === 0) {
      setToast({ tone: 'info', message: '请先勾选要发送的卡片。' });
      return;
    }

    try {
      setIssueTransferBusy(true);
      setWorkspaceSelectionBusyAction('send');
      setWorkspaceProfileOverlay(selectedProfileIds, {
        title: '正在发送中',
        detail: '正在对接云端',
        busy: true,
      });
      const result = await authClient.issueUserProfileTransfer({
        targetAccount,
        profileIds: selectedProfileIds,
        note: workspaceSendNote.trim() || undefined,
      });
      setAuthState(result.state);
      await animateWorkspaceActionSuccess(selectedProfileIds, '发送成功');
      setIssueTransferDialogOpen(false);
      setIssueTransferTargetAccount('');
      setIssueTransferNote('');
      setIssueTransferSelectedProfileIds([]);
      resetWorkspaceSelectionState();
      setToast({ tone: 'success', message: result.message });
    } catch (error) {
      setWorkspaceProfileOverlay(selectedProfileIds, null);
      setToast({
        tone: 'error',
        message: normalizeRuntimeErrorMessage(error, '发送空间卡片失败。'),
      });
    } finally {
      setIssueTransferBusy(false);
      setWorkspaceSelectionBusyAction(null);
    }
  }

  async function ensureAuthenticatedCloudActionState(
    authSnapshot: AuthState,
    unauthenticatedMessage: string,
  ): Promise<(AuthState & { profile: UserProfile }) | null> {
    if (hasAuthenticatedProfile(authSnapshot)) {
      return authSnapshot;
    }

    try {
      const result = await authClient.heartbeat();
      setAuthState(result.state);
      if (hasAuthenticatedProfile(result.state)) {
        return result.state;
      }
    } catch {
      // Keep the current state unchanged and fall through to the message below.
    }

    if (unauthenticatedMessage.trim()) {
      setToast({ tone: 'error', message: unauthenticatedMessage });
    }
    return null;
  }

  async function executeCloudBackupRefresh(
    profile: Pick<ProfileSummary, 'id' | 'name' | 'workspaceName'> | null | undefined,
    authSnapshot: AuthState = authState,
  ) {
    const resolvedAuthState = await ensureAuthenticatedCloudActionState(
      authSnapshot,
      '请先登录在线账号后再执行云端备份。',
    );
    if (!resolvedAuthState) {
      return;
    }

    const targetLabel = profile ? getProfileDisplayName(profile) : '当前设备空间池';
    try {
      setCloudBackupSyncBusyProfileId(profile?.id ?? '__all__');
      const selectedSnapshotIds = profile
        ? availableLocalRemoteSnapshots
            .filter((snapshot) => snapshot.id === profile.id)
            .map((snapshot) => snapshot.id)
        : availableLocalRemoteSnapshots.map((snapshot) => snapshot.id);
      if (profile && selectedSnapshotIds.length === 0) {
        setToast({
          tone: 'info',
          message: `当前还没读取到 ${targetLabel} 的本地快照，暂时不能刷新到云端。`,
        });
        return;
      }
      const selectedSnapshotsForUpload =
        selectedSnapshotIds.length === 0
          ? availableLocalRemoteSnapshots
          : availableLocalRemoteSnapshots.filter((snapshot) => selectedSnapshotIds.includes(snapshot.id));
      const shouldUseSnapshotOverride =
        selectedSnapshotsForUpload.length > 0 || visibleRealProfiles.length === 0;
      startCloudSyncVisualProgress('backup-refresh', selectedSnapshotIds.length);

      const result = await authClient.syncCloudProfiles({
        direction: 'device-to-cloud',
        selectedSnapshotIds: shouldUseSnapshotOverride ? [] : selectedSnapshotIds,
        profileSnapshotsOverride: shouldUseSnapshotOverride ? selectedSnapshotsForUpload : undefined,
      });
      setAuthState(result.state);
      setDismissedCloudBackupIds([]);
      setCloudBackupAutoPromptConsumed(false);
      finishCloudSyncVisualProgress('backup-refresh', selectedSnapshotIds.length);
      const uploadedProfileIds = new Set(result.state.cloudProfileBackups.map((entry) => entry.transferPackage.sourceProfileId));
      const success = profile === null || profile === undefined ? true : uploadedProfileIds.has(profile.id);
      setToast({
        tone: success ? 'success' : 'info',
        message: success
          ? `${targetLabel} 的云端备份已刷新。`
          : `已触发云端备份刷新，但暂时还没看到 ${targetLabel} 的快照记录。`,
      });
    } catch (error) {
      clearCloudSyncVisualProgress();
      setToast({
        tone: 'error',
        message: normalizeRuntimeErrorMessage(error, '云端备份刷新失败。'),
      });
    } finally {
      setCloudBackupSyncBusyProfileId(null);
    }
  }

  async function handleManualCloudBackup(
    profile?: Pick<ProfileSummary, 'id' | 'name' | 'workspaceName'> | null,
  ) {
    const resolvedAuthState = await ensureAuthenticatedCloudActionState(
      authState,
      '请先登录在线账号后再执行云端备份。',
    );
    if (!resolvedAuthState) {
      return;
    }

    if (!resolvedAuthState.security.secondaryPassword.enabled) {
      openSecondaryPasswordDialog({
        mode: 'set',
        pendingCloudBackupProfileId: profile?.id ?? null,
      });
      return;
    }

    await executeCloudBackupRefresh(profile, resolvedAuthState);
  }

  async function handleToggleCloudSyncPaused() {
    await handleUpdateWarningSettings(
      {
        cloudSyncPaused: !cloudSyncPaused,
      },
      cloudSyncPaused
        ? '已恢复自动云同步；后续会继续按当前设备和云端状态自动处理。'
        : '已暂停自动云同步；当前仅保留手动上传和手动下载。',
    );
  }

  const getSelectedCloudEntries = (
    entryIds: string[] = cloudSyncSelectedCloudIds,
  ): AuthState['cloudProfileBackups'] => {
    const selectedIds = new Set(entryIds.map((item) => item.trim()).filter(Boolean));
    if (selectedIds.size === 0) {
      return [];
    }
    return authState.cloudProfileBackups.filter((entry) => selectedIds.has(entry.id));
  };

  const getSelectedLocalSnapshots = (
    snapshotIds: string[] = cloudSyncSelectedLocalIds,
  ): LicenseHostRemoteProfileSnapshot[] => {
    const selectedIds = new Set(snapshotIds.map((item) => item.trim()).filter(Boolean));
    if (selectedIds.size === 0) {
      return [];
    }
    return availableLocalRemoteSnapshots.filter((snapshot) => selectedIds.has(snapshot.id));
  };

  const getSelectedLocalSnapshotIds = (snapshotIds: string[] = cloudSyncSelectedLocalIds): string[] => {
    const normalized = Array.from(new Set(snapshotIds.map((item) => item.trim()).filter(Boolean)));
    if (normalized.length > 0) {
      return normalized;
    }
    return visibleRealProfiles.map((profile) => profile.id);
  };

  const openClawSyncConfirm = (
    action: CloudSyncDialogAction,
    options?: {
      selectedLocalIds?: string[];
      selectedCloudIds?: string[];
      purgeUnmatchedLocalBeforeImport?: boolean;
    },
  ) => {
    const selectedLocalIds = Array.from(
      new Set((options?.selectedLocalIds ?? cloudSyncSelectedLocalIds).map((item) => item.trim()).filter(Boolean)),
    );
    const selectedCloudIds = Array.from(
      new Set((options?.selectedCloudIds ?? cloudSyncSelectedCloudIds).map((item) => item.trim()).filter(Boolean)),
    );

    if (action === 'overwrite-device-to-cloud' && selectedLocalIds.length === 0 && visibleRealProfiles.length === 0) {
      setToast({ tone: 'info', message: '当前没有可上传到云端的本地卡片。' });
      return;
    }
    if (action === 'overwrite-device-to-cloud' && selectedLocalIds.length === 0 && visibleRealProfiles.length > 0) {
      setToast({ tone: 'info', message: '请先勾选要覆盖到云端的本地卡片。' });
      return;
    }
    if (action === 'download-cloud-to-device' && selectedCloudIds.length === 0) {
      setToast({ tone: 'info', message: '请先勾选要下载覆盖到本地的云端卡片。' });
      return;
    }
    if (action === 'merge-local-to-cloud' && selectedLocalIds.length === 0) {
      setToast({ tone: 'info', message: '请先勾选要更新到云端的本地卡片。' });
      return;
    }

    void openClawSyncDialog('manual', {
      step: 'confirm',
      direction: action === 'download-cloud-to-device' ? 'cloud-to-device' : 'device-to-cloud',
      action,
      selectedLocalIds,
      selectedCloudIds,
      purgeUnmatchedLocalBeforeImport: options?.purgeUnmatchedLocalBeforeImport === true,
    });
  };

  async function handleDownloadCloudEntriesToLocal(
    entries: AuthState['cloudProfileBackups'] = getSelectedCloudEntries(),
    skipSecondaryPasswordCheck = false,
    activateAfterImport = false,
    purgeUnmatchedLocalBeforeImport = cloudSyncDialog?.purgeUnmatchedLocalBeforeImport === true,
  ) {
    const resolvedAuthState = await ensureAuthenticatedCloudActionState(
      authState,
      '请先登录账号后再接入云端卡片。',
    );
    if (!resolvedAuthState) {
      return;
    }

    const cloudEntries = [...entries];
    if (cloudEntries.length === 0) {
      resetCloudSyncDialogState();
      setToast({ tone: 'info', message: '当前没有可下载到本地的云端卡片。' });
      return;
    }

    const selectedCloudMatchKeys = new Set<string>();
    for (const entry of cloudEntries) {
      for (const key of buildProfileSnapshotMatchKeys(entry)) {
        selectedCloudMatchKeys.add(key);
      }
    }
    const selectedCloudIdSet = new Set(cloudEntries.map((entry) => entry.id));
    const selectedCloudIds = cloudEntries.map((entry) => entry.id);
    const isFullCloudOverwriteSelection =
      authState.cloudProfileBackups.length > 0 &&
      authState.cloudProfileBackups.length === selectedCloudIdSet.size &&
      authState.cloudProfileBackups.every((entry) => selectedCloudIdSet.has(entry.id));
    const localProfilesToPurge = purgeUnmatchedLocalBeforeImport
      ? isFullCloudOverwriteSelection
        ? [...visibleRealProfiles]
        : availableLocalRemoteSnapshots
            .filter(
              (snapshot) =>
                !buildProfileSnapshotMatchKeys(snapshot).some((key) => selectedCloudMatchKeys.has(key)),
            )
            .map((snapshot) => visibleRealProfileById.get(snapshot.id) ?? null)
            .filter((profile): profile is ProfileSummary => Boolean(profile))
      : [];

    if (!skipSecondaryPasswordCheck && !resolvedAuthState.security.secondaryPassword.enabled) {
      openSecondaryPasswordDialog({
        mode: 'set',
        activateAfterImport,
        pendingCloudSyncDirection: 'cloud-to-device',
        pendingCloudSyncAction: 'download-cloud-to-device',
        pendingCloudSyncCloudIds: selectedCloudIds,
        pendingCloudSyncPurgeUnmatchedLocalBeforeImport: purgeUnmatchedLocalBeforeImport,
      });
      return;
    }

    if (
      !skipSecondaryPasswordCheck &&
      resolvedAuthState.security.secondaryPassword.enabled &&
      resolvedAuthState.security.secondaryPassword.currentDeviceTrusted === false
    ) {
      openSecondaryPasswordDialog({
        mode: 'verify-cloud',
        activateAfterImport,
        pendingCloudSyncDirection: 'cloud-to-device',
        pendingCloudSyncAction: 'download-cloud-to-device',
        pendingCloudSyncCloudIds: selectedCloudIds,
        pendingCloudSyncPurgeUnmatchedLocalBeforeImport: purgeUnmatchedLocalBeforeImport,
      });
      return;
    }

    const requestId = beginProfilesStateRequest();
    let nextState: ProfilesState | null = null;
    const importedProfileIds: string[] = [];
    const failedCloudEntries: { profileName: string; message: string }[] = [];
    const placeholderCards: CloudDownloadPlaceholderCardState[] = cloudEntries.map((entry, index) => ({
      id: `cloud-download:${entry.id}:${Date.now()}:${index}`,
      cloudEntryId: entry.id,
      profileName: entry.sourceProfileName,
      workspaceName: entry.sourceWorkspaceName,
      note: getCloudBackupVisibleNote(entry),
      planType: entry.sourcePlanType,
      status: 'loading',
      importedProfileId: null,
      revealToken: 0,
      loadingPhase: 'running',
      loadingTipText: '云端快速同步中',
      loadingDetailText: '云端卡片正在快速写入当前设备。',
      successDetailText: '已写入当前设备',
      errorTipText: '云端同步失败',
      errorMessage: null,
    }));

    try {
      setPendingCloudBackupBusy(true);
      clearCloudDownloadPlaceholderCleanupTimer();
      setCloudDownloadPlaceholders(placeholderCards);
      resetCloudSyncDialogState();
      setCloudBackupCenterOpen(false);

      if (localProfilesToPurge.length > 0) {
        nextState = await window.codexWorkspace.batchDeleteProfiles({
          profileIds: localProfilesToPurge.map((profile) => profile.id),
          removeFiles: true,
        });
        commitProfilesState(requestId, nextState);
      }

      const batchImportResult = await window.codexWorkspace.importProfileTransferPackagesBatch(
        cloudEntries.map((entry) => ({
          clientRequestId: entry.id,
          profilePackage: buildCloudBackupDownloadTransferPackage(entry),
          options: {
            senderHostLabel: entry.sourceHostLabel,
            importSource: 'cloud-backup',
            overwriteExisting: true,
            skipUsageSync: true,
            binding: {
              userId: resolvedAuthState.profile.id,
              userEmail: resolvedAuthState.profile.email,
              deviceId: resolvedAuthState.device.deviceId,
              fingerprintHash: resolvedAuthState.device.fingerprintHash,
              fingerprintSummary: resolvedAuthState.device.fingerprintSummary,
            },
          },
        })),
      );
      nextState = batchImportResult.state;
      commitProfilesState(requestId, batchImportResult.state);
      cloudSyncAutomationSettledUntilRef.current = Date.now() + CLOUD_SYNC_AUTOMATION_SETTLE_MS;
      cloudBackupAutoSyncKeyRef.current = null;
      cloudSyncAutoPromptKeyRef.current = null;

      const resultByCloudEntryId = new Map(
        batchImportResult.results.map((result) => [result.clientRequestId ?? '', result]),
      );
      const nextPlaceholders: CloudDownloadPlaceholderCardState[] = placeholderCards.map(
        (placeholder, index) => {
          const importResult = resultByCloudEntryId.get(placeholder.cloudEntryId);
          const sourceEntry = cloudEntries.find((entry) => entry.id === placeholder.cloudEntryId) ?? null;
          if (importResult?.ok) {
            if (importResult.importedProfileId) {
              importedProfileIds.push(importResult.importedProfileId);
          }
          return {
            ...placeholder,
            status: 'success',
            importedProfileId: importResult.importedProfileId ?? null,
            revealToken: Date.now() + index,
            loadingTipText: '云端同步完成',
            loadingDetailText: '已写入当前设备',
            successDetailText: '已写入当前设备',
            errorMessage: null,
          };
        }

        const failureMessage = importResult?.errorMessage?.trim() || '下载云端卡片失败。';
        failedCloudEntries.push({
          profileName:
            sourceEntry?.sourceWorkspaceName?.trim() ||
            sourceEntry?.sourceProfileName ||
            placeholder.workspaceName?.trim() ||
            placeholder.profileName,
          message: failureMessage,
        });
          return {
            ...placeholder,
            status: 'error',
            errorMessage: failureMessage,
          };
        },
      );
      setCloudDownloadPlaceholders(nextPlaceholders);

      setDismissedCloudBackupIds([]);
      setPendingCloudBackupPrompt(null);
      setCloudBackupAutoPromptConsumed(false);

      try {
        const heartbeatResult = await authClient.heartbeat();
        setAuthState(heartbeatResult.state);
      } catch {
        // 本地卡片已更新；在线状态等待下一轮心跳刷新。
      }

      if (activateAfterImport && importedProfileIds.length > 0 && nextState) {
        const importedProfile =
          nextState.profiles.find((profile) => profile.id === importedProfileIds[0]) ?? null;
        if (importedProfile) {
          await executeSwitchProfile(importedProfile, 'card');
        }
      }

      const successCount = importedProfileIds.length;
      const failureCount = failedCloudEntries.length;
      const purgeCount = localProfilesToPurge.length;
      const message =
        failureCount === 0
          ? isFullCloudOverwriteSelection && purgeCount > 0
            ? `已先清空当前空间池的 ${purgeCount} 张本地卡片，再按云端全量覆盖回本地。`
            : cloudEntries.length === 1
              ? purgeCount > 0
                ? `已按所选云端卡片覆盖本地，并清理 ${purgeCount} 张不在本次云端集合内的本地卡片。`
                : '已按所选云端卡片覆盖本地对应项；如果本地没有对应卡片，会直接新增。'
              : purgeCount > 0
                ? `已把 ${cloudEntries.length} 张云端卡片同步到本地，并清理 ${purgeCount} 张不在本次云端集合内的本地卡片。`
                : `已把 ${cloudEntries.length} 张云端卡片下载到本地，并覆盖各自对应项。`
          : successCount > 0
            ? `云端下载已完成：成功 ${successCount} 张，失败 ${failureCount} 张。`
            : `云端下载失败：共 ${failureCount} 张卡片未成功写回本地。`;
      setCenterNotice({
        id: `cloud-download-${resolvedAuthState.profile.id}-${Date.now()}`,
        message,
      });
      setToast({ tone: failureCount === 0 ? 'success' : successCount > 0 ? 'info' : 'error', message });
      scheduleCloudDownloadPlaceholderCleanup(failureCount > 0 ? 4200 : 1800);
    } catch (error) {
      if (nextState) {
        commitProfilesState(requestId, nextState);
      }
      setCloudDownloadPlaceholders((current) =>
        current.map((placeholder) =>
          placeholder.status === 'loading'
            ? {
                ...placeholder,
                status: 'error',
                errorMessage: normalizeRuntimeErrorMessage(error, '下载云端卡片失败。'),
              }
            : placeholder,
        ),
      );
      scheduleCloudDownloadPlaceholderCleanup(4200);
      setToast({
        tone: 'error',
        message: normalizeRuntimeErrorMessage(error, '下载云端卡片失败。'),
      });
    } finally {
      setPendingCloudBackupBusy(false);
    }
  }

  async function handleReconcileAuthenticatedCloudState(
    entries: AuthState['cloudProfileBackups'] = getSelectedCloudEntries(),
    skipSecondaryPasswordCheck = false,
    purgeUnmatchedLocalBeforeImport = cloudSyncDialog?.purgeUnmatchedLocalBeforeImport === true,
  ) {
    await handleDownloadCloudEntriesToLocal(
      entries,
      skipSecondaryPasswordCheck,
      false,
      purgeUnmatchedLocalBeforeImport,
    );
  }

  async function handleSyncCurrentDeviceProfilesToCloud(
    skipSecondaryPasswordCheck = false,
    snapshotIds: string[] = cloudSyncSelectedLocalIds,
    profileSnapshotsOverride?: LicenseHostRemoteProfileSnapshot[],
    successMessageOverride?: string,
    options?: {
      silentSuccess?: boolean;
      progressTotal?: number;
      visualProgressAction?: CloudSyncVisualProgressAction;
      replaceCloudSnapshotSet?: boolean;
    },
  ) {
    const resolvedAuthState = await ensureAuthenticatedCloudActionState(
      authState,
      '请先登录在线账号后再执行云同步。',
    );
    if (!resolvedAuthState) {
      return;
    }

    if (!skipSecondaryPasswordCheck && !resolvedAuthState.security.secondaryPassword.enabled) {
      const selectedSnapshotIds = getSelectedLocalSnapshotIds(snapshotIds);
      openSecondaryPasswordDialog({
        mode: 'set',
        pendingCloudSyncDirection: 'device-to-cloud',
        pendingCloudSyncAction: 'overwrite-device-to-cloud',
        pendingCloudSyncLocalIds: selectedSnapshotIds,
      });
      return;
    }

    if (
      !skipSecondaryPasswordCheck &&
      resolvedAuthState.security.secondaryPassword.enabled &&
      resolvedAuthState.security.secondaryPassword.currentDeviceTrusted === false
    ) {
      const selectedSnapshotIds = getSelectedLocalSnapshotIds(snapshotIds);
      openSecondaryPasswordDialog({
        mode: 'verify-cloud',
        pendingCloudSyncDirection: 'device-to-cloud',
        pendingCloudSyncAction: 'overwrite-device-to-cloud',
        pendingCloudSyncLocalIds: selectedSnapshotIds,
      });
      return;
    }

    try {
      setPendingCloudBackupBusy(true);
      const selectedSnapshotIds = getSelectedLocalSnapshotIds(snapshotIds);
      const selectedSnapshotIdSet = new Set(selectedSnapshotIds);
      const selectedLocalSnapshotsForUpload =
        profileSnapshotsOverride ??
        availableLocalRemoteSnapshots.filter((snapshot) => selectedSnapshotIdSet.has(snapshot.id));
      const shouldUseSnapshotOverride =
        profileSnapshotsOverride !== undefined ||
        selectedLocalSnapshotsForUpload.length === selectedSnapshotIds.length ||
        (selectedSnapshotIds.length === 0 && visibleRealProfiles.length === 0);
      const progressAction = options?.visualProgressAction ?? 'overwrite-device-to-cloud';
      const progressTotal = Math.max(0, options?.progressTotal ?? selectedSnapshotIds.length);
      startCloudSyncVisualProgress(progressAction, progressTotal);
      const result = await authClient.syncCloudProfiles({
        direction: 'device-to-cloud',
        selectedSnapshotIds: shouldUseSnapshotOverride ? [] : selectedSnapshotIds,
        profileSnapshotsOverride: shouldUseSnapshotOverride
          ? selectedLocalSnapshotsForUpload
          : undefined,
        replaceCloudSnapshotSet: options?.replaceCloudSnapshotSet === true,
      });
      setAuthState(result.state);
      setDismissedCloudBackupIds([]);
      setCloudBackupAutoPromptConsumed(false);
      resetCloudSyncDialogState();
      finishCloudSyncVisualProgress(progressAction, progressTotal);
      if (!options?.silentSuccess) {
        setCenterNotice({
          id: `cloud-sync-push-${resolvedAuthState.profile.id}`,
          message: successMessageOverride ?? result.message,
        });
        setToast({ tone: 'success', message: successMessageOverride ?? result.message });
      }
    } catch (error) {
      clearCloudSyncVisualProgress();
      setToast({
        tone: 'error',
        message: normalizeRuntimeErrorMessage(error, '以当前设备覆盖云端失败。'),
      });
    } finally {
      setPendingCloudBackupBusy(false);
    }
  }

  async function handleMergeSelectedLocalProfilesToCloud(
    snapshotIds: string[] = cloudSyncSelectedLocalIds,
    skipSecondaryPasswordCheck = false,
  ) {
    const resolvedAuthState = await ensureAuthenticatedCloudActionState(
      authState,
      '请先登录在线账号后再更新云端卡片。',
    );
    if (!resolvedAuthState) {
      return;
    }

    const selectedLocalSnapshots = getSelectedLocalSnapshots(snapshotIds);
    if (selectedLocalSnapshots.length === 0) {
      setToast({ tone: 'info', message: '当前没有可更新到云端的本地卡片。' });
      return;
    }

    if (!skipSecondaryPasswordCheck && !resolvedAuthState.security.secondaryPassword.enabled) {
      openSecondaryPasswordDialog({
        mode: 'set',
        pendingCloudSyncDirection: 'device-to-cloud',
        pendingCloudSyncAction: 'merge-local-to-cloud',
        pendingCloudSyncLocalIds: selectedLocalSnapshots.map((snapshot) => snapshot.id),
      });
      return;
    }

    if (
      !skipSecondaryPasswordCheck &&
      resolvedAuthState.security.secondaryPassword.enabled &&
      resolvedAuthState.security.secondaryPassword.currentDeviceTrusted === false
    ) {
      openSecondaryPasswordDialog({
        mode: 'verify-cloud',
        pendingCloudSyncDirection: 'device-to-cloud',
        pendingCloudSyncAction: 'merge-local-to-cloud',
        pendingCloudSyncLocalIds: selectedLocalSnapshots.map((snapshot) => snapshot.id),
      });
      return;
    }

    const updatedCount = selectedLocalSnapshots.length;
    const successMessage = `已把 ${updatedCount} 张本地卡片更新到云端。`;
    await handleSyncCurrentDeviceProfilesToCloud(
      skipSecondaryPasswordCheck,
      selectedLocalSnapshots.map((snapshot) => snapshot.id),
      selectedLocalSnapshots,
      successMessage,
      {
        progressTotal: updatedCount,
        visualProgressAction: 'merge-local-to-cloud',
      },
    );
  }

  function handleOpenAvailableCloudBackupPrompt() {
    void openClawSyncDialog('manual');
  }

  function handleConfirmNewDeviceCloudRestore() {
    const entries = [...newDeviceCloudRestoreEntries];
    if (entries.length === 0) {
      setNewDeviceCloudRestorePrompt(null);
      setToast({ tone: 'info', message: '当前没有可同步到本机的云端卡片。' });
      return;
    }

    setNewDeviceCloudRestorePrompt(null);
    setToast({ tone: 'info', message: '云端同步已转到后台继续执行，可先处理其他操作。' });
    void handleDownloadCloudEntriesToLocal(entries, false, false, true);
  }

  function handleConfirmClawSyncDialog() {
    if (!cloudSyncDialog?.action) {
      return;
    }

    const action = cloudSyncDialog.action;
    const selectedLocalIds = [...cloudSyncSelectedLocalIds];
    const selectedCloudEntries = [...selectedCloudSyncEntries];
    const purgeUnmatchedLocalBeforeImport =
      cloudSyncDialog.purgeUnmatchedLocalBeforeImport === true;

    if (action === 'download-cloud-to-device') {
      resetCloudSyncDialogState();
      setToast({ tone: 'info', message: '同步已转到后台继续执行，可先处理其他操作。' });
      void handleDownloadCloudEntriesToLocal(
        selectedCloudEntries,
        false,
        false,
        purgeUnmatchedLocalBeforeImport,
      );
      return;
    }
    if (action === 'merge-local-to-cloud') {
      void handleMergeSelectedLocalProfilesToCloud(selectedLocalIds, false);
      return;
    }
    void handleSyncCurrentDeviceProfilesToCloud(false, selectedLocalIds);
  }

  const shouldRequireSecondaryPassword = (): boolean =>
    authState.sessionStatus === 'authenticated' &&
    authState.security.secondaryPassword.enabled &&
    authState.security.secondaryPassword.currentDeviceTrusted === false;

  const openSecondaryPasswordDialog = (dialog: SecondaryPasswordDialogState) => {
    setSecondaryPasswordCurrentDraft('');
    setSecondaryPasswordNextDraft('');
    setSecondaryPasswordConfirmDraft('');
    setSecondaryPasswordVerifyDraft('');
    setSecondaryPasswordEmailOtpDraft('');
    setSecondaryPasswordDialogError(null);
    setSecondaryPasswordDialog(dialog);
  };

  const closeSecondaryPasswordDialog = (force = false) => {
    if (secondaryPasswordDialogBusy && !force) {
      return;
    }
    setSecondaryPasswordDialog(null);
    setSecondaryPasswordDialogError(null);
    setSecondaryPasswordCurrentDraft('');
    setSecondaryPasswordNextDraft('');
    setSecondaryPasswordConfirmDraft('');
    setSecondaryPasswordVerifyDraft('');
    setSecondaryPasswordEmailOtpDraft('');
  };

  async function handleSendSecondaryPasswordResetOtp() {
    const email = authState.profile?.email?.trim() ?? '';
    if (!email) {
      setSecondaryPasswordDialogError('当前账号尚未绑定邮箱，无法发送验证码。');
      return;
    }

    const previousDeadlineAt = authOtpCooldowns['secondary-password-reset'];
    try {
      const nowMs = Date.now();
      const optimisticDeadlineAt = nowMs + OTP_DEFAULT_COOLDOWN_SECONDS * 1000;
      setAuthOtpNowMs(nowMs);
      setAuthOtpCooldowns((current) => ({
        ...current,
        'secondary-password-reset': Math.max(current['secondary-password-reset'], optimisticDeadlineAt),
      }));
      setAuthOtpBusyPurpose('secondary-password-reset');
      const result = await authClient.sendEmailOtp({
        purpose: 'secondary-password-reset',
        email,
      });
      const resolvedCooldownSeconds = Math.max(
        OTP_DEFAULT_COOLDOWN_SECONDS,
        result.cooldownSeconds ?? OTP_DEFAULT_COOLDOWN_SECONDS,
      );
      const resolvedDeadlineAt = Date.now() + resolvedCooldownSeconds * 1000;
      setAuthOtpNowMs(Date.now());
      setAuthOtpCooldowns((current) => ({
        ...current,
        'secondary-password-reset': Math.max(
          current['secondary-password-reset'],
          resolvedDeadlineAt,
        ),
      }));
      setToast({ tone: 'success', message: result.message });
    } catch (error) {
      const rollbackNowMs = Date.now();
      const rollbackDeadlineAt = previousDeadlineAt > rollbackNowMs ? previousDeadlineAt : 0;
      setAuthOtpNowMs(rollbackNowMs);
      setAuthOtpCooldowns((current) => ({
        ...current,
        'secondary-password-reset': rollbackDeadlineAt,
      }));
      setSecondaryPasswordDialogError(normalizeRuntimeErrorMessage(error, '验证码发送失败。'));
    } finally {
      setAuthOtpBusyPurpose(null);
    }
  }

  async function handleUnlockAccountBoundProfilesAfterVerification(authSnapshot: AuthState) {
    if (authSnapshot.sessionStatus !== 'authenticated' || !authSnapshot.profile) {
      return null;
    }

    const result = await window.codexWorkspace.unlockAccountBoundProfiles({
      userId: authSnapshot.profile.id,
      userEmail: authSnapshot.profile.email,
    });
    if (result.unlockedProfileIds.length > 0) {
      const requestId = beginProfilesStateRequest();
      commitProfilesState(requestId, result.state);
      setCenterNotice({
        id: `account-unlock-${authSnapshot.profile.id}-${Date.now()}`,
        message: result.message,
      });
    }
    return result;
  }

  async function handleConfirmSecondaryPasswordDialog() {
    if (!secondaryPasswordDialog) {
      return;
    }

    try {
      setSecondaryPasswordDialogBusy(true);
      setSecondaryPasswordDialogError(null);

      if (secondaryPasswordDialog.mode === 'set') {
        const nextPassword = secondaryPasswordNextDraft.trim();
        if (nextPassword.length < 4) {
          setSecondaryPasswordDialogError('二次密码至少需要 4 位。');
          return;
        }
        if (nextPassword !== secondaryPasswordConfirmDraft.trim()) {
          setSecondaryPasswordDialogError('两次输入的二次密码不一致。');
          return;
        }

        const result = await authClient.setSecondaryPassword({
          currentPassword: authState.security.secondaryPassword.enabled
            ? secondaryPasswordCurrentDraft.trim()
            : undefined,
          nextPassword,
        });
        setAuthState(result.state);
        const unlockResult = await handleUnlockAccountBoundProfilesAfterVerification(result.state);
        const dialog = secondaryPasswordDialog;
        closeSecondaryPasswordDialog(true);
        setToast({
          tone: 'success',
          message:
            unlockResult?.unlockedProfileIds.length
              ? `${result.message} 已解锁 ${unlockResult.unlockedProfileIds.length} 张同账号卡片。`
              : result.message,
        });

        if (dialog.pendingLocalImport) {
          await executeProfileMutation(dialog.pendingLocalImport, true);
        }

        if (dialog.pendingCloudBackupProfileId !== undefined) {
          const targetProfile =
            dialog.pendingCloudBackupProfileId === null
              ? null
              : state.profiles.find((profile) => profile.id === dialog.pendingCloudBackupProfileId) ??
                null;
          await executeCloudBackupRefresh(targetProfile, result.state);
        }

        if (dialog.pendingCloudSyncDirection === 'device-to-cloud') {
          if (dialog.pendingCloudSyncAction === 'merge-local-to-cloud') {
            await handleMergeSelectedLocalProfilesToCloud(
              dialog.pendingCloudSyncLocalIds ?? cloudSyncSelectedLocalIds,
              true,
            );
            return;
          }
          await handleSyncCurrentDeviceProfilesToCloud(
            true,
            dialog.pendingCloudSyncLocalIds ?? cloudSyncSelectedLocalIds,
          );
          return;
        }
        if (dialog.pendingCloudSyncDirection === 'cloud-to-device') {
          await handleReconcileAuthenticatedCloudState(
            getSelectedCloudEntries(dialog.pendingCloudSyncCloudIds),
            true,
            dialog.pendingCloudSyncPurgeUnmatchedLocalBeforeImport === true,
          );
          return;
        }

        if (dialog.continueCloudRestore) {
          await handleRestoreCloudProfileBackup(dialog.activateAfterImport === true, true);
        }
        return;
      }

      if (secondaryPasswordDialog.mode === 'reset-by-email') {
        const verificationCode = secondaryPasswordEmailOtpDraft.trim();
        const nextPassword = secondaryPasswordNextDraft.trim();
        if (!verificationCode) {
          setSecondaryPasswordDialogError('请输入邮箱验证码。');
          return;
        }
        if (nextPassword.length < 4) {
          setSecondaryPasswordDialogError('二次密码至少需要 4 位。');
          return;
        }
        if (nextPassword !== secondaryPasswordConfirmDraft.trim()) {
          setSecondaryPasswordDialogError('两次输入的二次密码不一致。');
          return;
        }

        const result = await authClient.resetSecondaryPasswordByEmail({
          verificationCode,
          nextPassword,
        });
        setAuthState(result.state);
        const unlockResult = await handleUnlockAccountBoundProfilesAfterVerification(result.state);
        closeSecondaryPasswordDialog(true);
        setToast({
          tone: 'success',
          message:
            unlockResult?.unlockedProfileIds.length
              ? `${result.message} 已解锁 ${unlockResult.unlockedProfileIds.length} 张同账号卡片。`
              : result.message,
        });
        return;
      }

      const password = secondaryPasswordVerifyDraft.trim();
      if (!password) {
        setSecondaryPasswordDialogError('请输入二次密码。');
        return;
      }

      const result = await authClient.verifySecondaryPassword({ password });
      setAuthState(result.state);
      const unlockResult = await handleUnlockAccountBoundProfilesAfterVerification(result.state);
      const dialog = secondaryPasswordDialog;
      closeSecondaryPasswordDialog(true);
      setToast({
        tone: 'success',
        message:
          unlockResult?.unlockedProfileIds.length
            ? `${result.message} 已解锁 ${unlockResult.unlockedProfileIds.length} 张同账号卡片。`
            : result.message,
      });

      if (dialog.mode === 'verify-cloud') {
        if (dialog.pendingCloudSyncDirection === 'device-to-cloud') {
          if (dialog.pendingCloudSyncAction === 'merge-local-to-cloud') {
            await handleMergeSelectedLocalProfilesToCloud(
              dialog.pendingCloudSyncLocalIds ?? cloudSyncSelectedLocalIds,
              true,
            );
            return;
          }
          await handleSyncCurrentDeviceProfilesToCloud(
            true,
            dialog.pendingCloudSyncLocalIds ?? cloudSyncSelectedLocalIds,
          );
          return;
        }
        if (dialog.pendingCloudSyncDirection === 'cloud-to-device') {
          await handleReconcileAuthenticatedCloudState(
            getSelectedCloudEntries(dialog.pendingCloudSyncCloudIds),
            true,
            dialog.pendingCloudSyncPurgeUnmatchedLocalBeforeImport === true,
          );
          return;
        }
        if (dialog.continueCloudRestore) {
          await handleRestoreCloudProfileBackup(dialog.activateAfterImport === true, true);
        }
        return;
      }

      if (dialog.mode === 'verify-local-import' && dialog.pendingLocalImport) {
        await executeProfileMutation(dialog.pendingLocalImport, true);
      }
    } catch (error) {
      setSecondaryPasswordDialogError(
        normalizeRuntimeErrorMessage(error, '二次密码处理失败。'),
      );
    } finally {
      setSecondaryPasswordDialogBusy(false);
    }
  }

  async function handleRespondPendingProfileTransfer(
    action: 'accept' | 'decline',
    activateAfterImport = false,
    transferIds: string[] = pendingTransferSelectionIds,
  ) {
    if (!pendingProfileTransferPrompt && authState.pendingProfileTransfers.length === 0) {
      return;
    }

    const normalizedTransferIds = Array.from(
      new Set(
        transferIds.map((item) => item.trim()).filter(Boolean),
      ),
    );
    if (normalizedTransferIds.length === 0) {
      setToast({
        tone: 'info',
        message: action === 'accept' ? '请先勾选要接收的空间卡片。' : '当前没有待处理的传卡记录。',
      });
      return;
    }

    const selectedTransferEntries = authState.pendingProfileTransfers.filter((entry) =>
      normalizedTransferIds.includes(entry.id),
    );
    const senderHostLabel = selectedTransferEntries[0]?.senderHostLabel ?? '远端';
    const placeholderCards: CloudDownloadPlaceholderCardState[] =
      action === 'accept'
        ? selectedTransferEntries.map((entry, index) => ({
            id: `profile-transfer:${entry.id}:${Date.now()}:${index}`,
            cloudEntryId: entry.id,
            profileName: entry.sourceProfileName,
            workspaceName: entry.sourceWorkspaceName,
            note: entry.note?.trim() || '未附带备注',
            planType: entry.sourcePlanType,
            status: 'loading',
            importedProfileId: null,
            revealToken: 0,
            loadingPhase: 'preview',
            loadingTipText: '空间卡片接收中',
            loadingDetailText: '收到的空间卡片正在写入当前设备，请稍等片刻。',
            successDetailText: '已写入当前设备',
            errorTipText: '空间卡片接收失败',
            errorMessage: null,
          }))
        : [];

    const requestId = beginProfilesStateRequest();
    let nextProfilesState: ProfilesState | null = null;
    try {
      setPendingProfileTransferBusy(true);
      if (action === 'accept') {
        clearCloudDownloadPlaceholderCleanupTimer();
        setCloudDownloadPlaceholders(placeholderCards);
      }
      setPendingProfileTransferPrompt(null);
      const importedProfileIds: string[] = [];
      const failedTransferEntries: { profileName: string; message: string }[] = [];
      let lastResult: Awaited<ReturnType<typeof window.codexWorkspace.respondToProfileTransfer>> | null = null;
      for (const transferId of normalizedTransferIds) {
        try {
          const result = await window.codexWorkspace.respondToProfileTransfer({
            transferId,
            action,
          });
          lastResult = result;
          setAuthState(result.state);
          if (action === 'accept' && result.importedProfileId) {
            importedProfileIds.push(result.importedProfileId);
            setCloudDownloadPlaceholders((current) =>
              current.map((placeholder) =>
                placeholder.cloudEntryId === transferId
                  ? {
                      ...placeholder,
                      status: 'success',
                      importedProfileId: result.importedProfileId,
                      revealToken: Date.now(),
                      errorMessage: null,
                    }
                  : placeholder,
              ),
            );
          }
        } catch (error) {
          const failureMessage = normalizeRuntimeErrorMessage(error, '接收空间卡片失败。');
          const failedEntry = selectedTransferEntries.find((entry) => entry.id === transferId);
          failedTransferEntries.push({
            profileName:
              failedEntry?.sourceWorkspaceName?.trim() ||
              failedEntry?.sourceProfileName ||
              '空间卡片',
            message: failureMessage,
          });
          if (action === 'accept') {
            setCloudDownloadPlaceholders((current) =>
              current.map((placeholder) =>
                placeholder.cloudEntryId === transferId
                  ? {
                      ...placeholder,
                      status: 'error',
                      errorMessage: failureMessage,
                    }
                  : placeholder,
              ),
            );
          }
        }
      }

      if (action === 'accept' && importedProfileIds.length > 0) {
        nextProfilesState = await window.codexWorkspace.getAppState();
        commitProfilesState(requestId, nextProfilesState);
        const importedProfile =
          nextProfilesState.profiles.find((profile) => profile.id === importedProfileIds[0]) ?? null;
        setCenterNotice({
          id: `profile-transfer-${Date.now()}`,
          message:
            normalizedTransferIds.length === 1
              ? `已收到来自 ${senderHostLabel} 的空间卡片。`
              : `已批量接收 ${importedProfileIds.length} 张空间卡片。`,
        });
        if (activateAfterImport && importedProfile) {
          await executeSwitchProfile(importedProfile, 'card');
        }
      }

      if (action === 'accept') {
        const failureCount = failedTransferEntries.length;
        scheduleCloudDownloadPlaceholderCleanup(failureCount > 0 ? 4200 : 1800);
      }

      const successCount = importedProfileIds.length;
      const failureCount =
        action === 'accept'
          ? Math.max(0, placeholderCards.length - importedProfileIds.length)
          : 0;
      setToast({
        tone:
          action === 'accept'
            ? failureCount === 0
              ? 'success'
              : successCount > 0
                ? 'info'
                : 'error'
            : 'success',
        message:
          action === 'accept'
            ? failureCount === 0
              ? normalizedTransferIds.length === 1
                ? lastResult?.message ?? '空间卡片已处理。'
                : `已处理 ${normalizedTransferIds.length} 条传卡记录，其中成功导入 ${successCount} 张。`
              : successCount > 0
                ? `空间卡片接收完成：成功 ${successCount} 张，失败 ${failureCount} 张。`
                : `空间卡片接收失败：共 ${failureCount} 张卡片未成功导入。`
            : normalizedTransferIds.length === 1
              ? lastResult?.message ?? '空间卡片已处理。'
              : `已拒绝 ${normalizedTransferIds.length} 条传卡记录。`,
      });
    } catch (error) {
      if (action === 'accept') {
        setCloudDownloadPlaceholders((current) =>
          current.map((placeholder) =>
            placeholder.status === 'loading'
              ? {
                  ...placeholder,
                  status: 'error',
                  errorMessage: normalizeRuntimeErrorMessage(error, '接收空间卡片失败。'),
                }
              : placeholder,
          ),
        );
        scheduleCloudDownloadPlaceholderCleanup(4200);
      }
      if (nextProfilesState) {
        commitProfilesState(requestId, nextProfilesState);
      }
      const message = normalizeRuntimeErrorMessage(error, '处理空间卡片失败。');
      setToast({ tone: 'error', message });
    } finally {
      setPendingProfileTransferBusy(false);
    }
  }

  async function handleRestoreCloudProfileBackup(
    activateAfterImport = false,
    skipSecondaryPasswordCheck = false,
  ) {
    if (!pendingCloudBackupPrompt) {
      return;
    }

    const prompt = pendingCloudBackupPrompt;

    if (authState.sessionStatus !== 'authenticated' || !authState.profile) {
      setToast({ tone: 'error', message: '请先登录账号后再恢复云端备份。' });
      return;
    }

    if (!skipSecondaryPasswordCheck && !authState.security.secondaryPassword.enabled) {
      openSecondaryPasswordDialog({
        mode: 'set',
        activateAfterImport,
        continueCloudRestore: true,
      });
      return;
    }

    if (!skipSecondaryPasswordCheck && shouldRequireSecondaryPassword()) {
      openSecondaryPasswordDialog({
        mode: 'verify-cloud',
        activateAfterImport,
      });
      return;
    }

    const requestId = beginProfilesStateRequest();
    try {
      setPendingCloudBackupBusy(true);
      const result = await window.codexWorkspace.importProfileTransferPackage(
        prompt.transferPackage,
        {
          senderHostLabel: prompt.sourceHostLabel,
          importSource: 'cloud-backup',
          overwriteExisting: true,
          skipUsageSync: true,
          binding: {
            userId: authState.profile.id,
            userEmail: authState.profile.email,
            deviceId: authState.device.deviceId,
            fingerprintHash: authState.device.fingerprintHash,
            fingerprintSummary: authState.device.fingerprintSummary,
          },
        },
      );
      commitProfilesState(requestId, result.state);
      setDismissedCloudBackupIds((current) =>
        current.includes(prompt.id)
          ? current
          : [...current, prompt.id],
      );
      setPendingCloudBackupPrompt(null);

      const importedProfile =
        result.importedProfileId
          ? result.state.profiles.find((profile) => profile.id === result.importedProfileId) ?? null
          : null;

      try {
        const heartbeatResult = await authClient.heartbeat();
        setAuthState(heartbeatResult.state);
      } catch {
        // 本地恢复已完成；如果在线心跳暂时失败，保留当前导入结果并等待下一轮心跳刷新。
      }

      setCenterNotice({
        id: `cloud-backup-${prompt.id}`,
        message: `已接入云端备份空间：${prompt.sourceWorkspaceName ?? prompt.sourceProfileName}`,
      });
      if (activateAfterImport && importedProfile) {
        await executeSwitchProfile(importedProfile, 'card');
      }
      setToast({ tone: 'success', message: result.message });
    } catch (error) {
      setToast({
        tone: 'error',
        message: normalizeRuntimeErrorMessage(error, '恢复云端空间失败。'),
      });
    } finally {
      setPendingCloudBackupBusy(false);
    }
  }

  async function handleSubmitFeedback() {
    if (authState.sessionStatus !== 'authenticated') {
      setAuthMode('login');
      setUserCenterOpen(true);
      setToast({ tone: 'info', message: '请先登录账号后再发送反馈。' });
      return;
    }

    const message = feedbackDraft.trim();
    if (!message) {
      setToast({ tone: 'error', message: '请先输入反馈内容。' });
      return;
    }

    const targetFeedbackId = activeFeedbackThread?.id;
    const optimisticId = `feedback-local-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
    setFeedbackDraft('');
    setOptimisticFeedbackMessages((current) => [
      ...current,
      {
        id: optimisticId,
        threadId: targetFeedbackId ?? null,
        body: message,
        createdAt: new Date().toISOString(),
        state: 'sending',
        errorMessage: null,
      },
    ]);

    try {
      setAuthBusyAction('feedback');
      const result = await withFeedbackActionTimeout(
        authClient.submitFeedback({
          message,
          feedbackId: targetFeedbackId,
          avatarText: userCenterSession.avatarText,
          avatarImageDataUrl: avatarImageSrc ?? undefined,
        }),
        '发送客服消息超时。',
      );
      setAuthState(result.state);
      setOptimisticFeedbackMessages((current) => current.filter((item) => item.id !== optimisticId));
    } catch (error) {
      const errorMessage = normalizeRuntimeErrorMessage(error, '反馈发送失败。');
      setOptimisticFeedbackMessages((current) =>
        current.map((item) =>
          item.id === optimisticId
            ? {
                ...item,
                state: 'failed',
                errorMessage,
              }
            : item,
        ),
      );
      setToast({ tone: 'error', message: errorMessage });
    } finally {
      setAuthBusyAction(null);
    }
  }

  function handleFeedbackTextareaKeyDown(event: ReactKeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }

    event.preventDefault();
    if (authBusyAction === 'feedback') {
      return;
    }
    void handleSubmitFeedback();
  }

  async function handleUpdateWarningSettings(
    patch: Partial<ProfilesState['settings']>,
    successMessage?: string,
  ) {
    if (uiInteractionTestEnabled) {
      applyUiInteractionSettingsPatch(patch);
      if (patch.lowQuotaWarningThreshold !== undefined) {
        setWarningThresholdDraft(String(patch.lowQuotaWarningThreshold));
      }
      if (patch.autoSwitchThreshold !== undefined) {
        setAutoSwitchThresholdDraft(String(patch.autoSwitchThreshold));
      }
      if (patch.autoSwitchCountdownSeconds !== undefined) {
        setAutoSwitchCountdownDraft(String(patch.autoSwitchCountdownSeconds));
      }
      setToast({
        tone: 'info',
        message:
          successMessage ??
          'UI 交互测试中：这次设置改动只更新前端展示，关闭测试后会恢复到启用前。',
      });
      return;
    }

    const requestId = beginProfilesStateRequest();
    try {
      const nextState = await window.codexWorkspace.updateSettings({
        backupBeforeSwitch: patch.backupBeforeSwitch,
        cloudSyncPaused: patch.cloudSyncPaused,
        launchAtLoginEnabled: patch.launchAtLoginEnabled,
        switchMode: patch.switchMode,
        betaModeEnabled: patch.betaModeEnabled,
        lowQuotaWarningEnabled: patch.lowQuotaWarningEnabled,
        lowQuotaWarningThreshold: patch.lowQuotaWarningThreshold,
        codexExecutablePathOverride: patch.codexExecutablePathOverride,
        traeExecutablePathOverride: patch.traeExecutablePathOverride,
        codexAutoRestart: patch.codexAutoRestart,
        traeAutoRestart: patch.traeAutoRestart,
        codexTraeAutoRestart: patch.codexTraeAutoRestart,
        openclawStateDirOverride: patch.openclawStateDirOverride,
        openclawAutoRestartGateway: patch.openclawAutoRestartGateway,
        autoSwitchEnabled: patch.autoSwitchEnabled,
        autoSwitchThreshold: patch.autoSwitchThreshold,
        autoSwitchCountdownSeconds: patch.autoSwitchCountdownSeconds,
        floatingWidgetEnabled: patch.floatingWidgetEnabled,
        floatingWidgetOpacityPercent: patch.floatingWidgetOpacityPercent,
        cardFlipVolumePercent: patch.cardFlipVolumePercent,
      });
      commitProfilesState(requestId, nextState);
      if (successMessage) {
        setToast({ tone: 'success', message: successMessage });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '更新设置失败。';
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    }
  }

  async function persistManagedAutoRestartChange(
    kind: 'codex' | 'trae',
    enabled: boolean,
    requestId: number,
  ) {
    const stateRequestId = beginProfilesStateRequest();
    try {
      const nextState = await window.codexWorkspace.updateSettings({
        codexAutoRestart: kind === 'codex' ? enabled : undefined,
        traeAutoRestart: kind === 'trae' ? enabled : undefined,
      });

      const currentRequestId =
        kind === 'codex' ? codexAutoRestartRequestIdRef.current : traeAutoRestartRequestIdRef.current;
      if (requestId !== currentRequestId) {
        return;
      }

      commitProfilesState(stateRequestId, nextState);
      if (kind === 'codex') {
        setCodexAutoRestartDraft(null);
      } else {
        setTraeAutoRestartDraft(null);
      }
      setToast({
        tone: 'success',
        message: enabled
          ? `已启用切换后自动启动 ${kind === 'codex' ? 'Codex' : 'Trae'}。`
          : `已关闭切换后自动启动 ${kind === 'codex' ? 'Codex' : 'Trae'}。`,
      });
    } catch (error) {
      const currentRequestId =
        kind === 'codex' ? codexAutoRestartRequestIdRef.current : traeAutoRestartRequestIdRef.current;
      if (requestId !== currentRequestId) {
        return;
      }

      const message = normalizeRuntimeErrorMessage(
        error,
        `更新 ${kind === 'codex' ? 'Codex' : 'Trae'} 自动启动失败。`,
      );
      if (kind === 'codex') {
        setCodexAutoRestartDraft(null);
      } else {
        setTraeAutoRestartDraft(null);
      }
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    }
  }

  function handleManagedAutoRestartChange(kind: 'codex' | 'trae', enabled: boolean) {
    const effectiveEnabled = kind === 'codex' ? effectiveCodexAutoRestart : effectiveTraeAutoRestart;
    const requestIdRef =
      kind === 'codex' ? codexAutoRestartRequestIdRef : traeAutoRestartRequestIdRef;

    if (uiInteractionTestEnabled) {
      if (kind === 'codex') {
        setCodexAutoRestartDraft(null);
      } else {
        setTraeAutoRestartDraft(null);
      }
      applyUiInteractionSettingsPatch({
        codexAutoRestart: kind === 'codex' ? enabled : undefined,
        traeAutoRestart: kind === 'trae' ? enabled : undefined,
      });
      setToast({
        tone: 'info',
        message: enabled
          ? `UI 交互测试中：已模拟开启 ${kind === 'codex' ? 'Codex' : 'Trae'} 自动启动。`
          : `UI 交互测试中：已模拟关闭 ${kind === 'codex' ? 'Codex' : 'Trae'} 自动启动。`,
      });
      return;
    }

    if (enabled === effectiveEnabled) {
      return;
    }

    if (kind === 'codex') {
      setCodexAutoRestartDraft(enabled);
    } else {
      setTraeAutoRestartDraft(enabled);
    }
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    void persistManagedAutoRestartChange(kind, enabled, requestId);
  }

  async function handleCommitWarningThreshold() {
    const normalized = normalizeSettingsNumberDraft(
      warningThresholdDraft,
      5,
      95,
      state.settings.lowQuotaWarningThreshold,
    );
    setWarningThresholdDraft(String(normalized));
    if (normalized === state.settings.lowQuotaWarningThreshold) {
      return;
    }

    await handleUpdateWarningSettings(
      { lowQuotaWarningThreshold: normalized },
      `额度预警阈值已更新为 ${normalized}%`,
    );
  }

  async function handleCommitAutoSwitchThreshold() {
    const normalized = normalizeSettingsNumberDraft(
      autoSwitchThresholdDraft,
      5,
      95,
      state.settings.autoSwitchThreshold,
    );
    setAutoSwitchThresholdDraft(String(normalized));
    if (normalized === state.settings.autoSwitchThreshold) {
      return;
    }

    await handleUpdateWarningSettings(
      { autoSwitchThreshold: normalized },
      `自动切换阈值已更新为 ${normalized}%`,
    );
  }

  async function handleCommitAutoSwitchCountdown() {
    const normalized = normalizeSettingsNumberDraft(
      autoSwitchCountdownDraft,
      5,
      180,
      state.settings.autoSwitchCountdownSeconds,
    );
    setAutoSwitchCountdownDraft(String(normalized));
    if (normalized === state.settings.autoSwitchCountdownSeconds) {
      return;
    }

    await handleUpdateWarningSettings(
      { autoSwitchCountdownSeconds: normalized },
      `自动切换倒计时已更新为 ${normalized} 秒`,
    );
  }

  async function handlePickOpenClawDirectory() {
    try {
      const picked = await window.codexWorkspace.chooseDirectory();
      if (picked.canceled || !picked.filePath) {
        return;
      }

      await handleUpdateWarningSettings(
        { openclawStateDirOverride: picked.filePath },
        `已更新 OpenClaw 状态目录：${picked.filePath}`,
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : '选择 OpenClaw 目录失败。';
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    }
  }

  async function handlePickManagedAppExecutable(target: 'codex' | 'trae') {
    try {
      const picked = await chooseExecutableFileWithFallback(
        target === 'codex' ? '选择 Codex 可执行文件' : '选择 Trae 可执行文件',
      );
      if (picked.canceled || !picked.filePath) {
        return;
      }

      await handleUpdateWarningSettings(
        target === 'codex'
          ? { codexExecutablePathOverride: picked.filePath }
          : { traeExecutablePathOverride: picked.filePath },
        `${target === 'codex' ? 'Codex' : 'Trae'} 路径已更新：${picked.filePath}`,
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : `选择 ${target === 'codex' ? 'Codex' : 'Trae'} 可执行文件失败。`;
      setErrorMessage(message);
      setToast({ tone: 'error', message });
    }
  }

  async function handleWindowMinimize() {
    const runtimeApi = window.codexWorkspace as Partial<typeof window.codexWorkspace>;
    if (typeof runtimeApi.minimizeWindow !== 'function') {
      return;
    }

    try {
      const nextState = await runtimeApi.minimizeWindow();
      setWindowState(nextState);
    } catch {
      // Ignore desktop shell failures and keep the renderer responsive.
    }
  }

  async function handleWindowToggleMaximize() {
    const runtimeApi = window.codexWorkspace as Partial<typeof window.codexWorkspace>;
    if (typeof runtimeApi.toggleMaximizeWindow !== 'function') {
      return;
    }

    try {
      const nextState = await runtimeApi.toggleMaximizeWindow();
      setWindowState(nextState);
    } catch {
      // Ignore desktop shell failures and keep the renderer responsive.
    }
  }

  async function handleWindowHide() {
    const runtimeApi = window.codexWorkspace as Partial<typeof window.codexWorkspace>;
    if (typeof runtimeApi.hideWindow !== 'function') {
      return;
    }

    try {
      const nextState = await runtimeApi.hideWindow();
      setWindowState(nextState);
    } catch {
      // Ignore desktop shell failures and keep the renderer responsive.
    }
  }

  function handleQuitApp() {
    const runtimeApi = window.codexWorkspace as Partial<typeof window.codexWorkspace>;
    if (typeof runtimeApi.quitApp !== 'function') {
      return;
    }

    try {
      runtimeApi.quitApp();
    } catch {
      // Ignore desktop shell failures and keep the renderer responsive.
    }
  }

  const packagedOverlayBlurFallbackActive =
    authCenterOverlayVisible ||
    userCenterOpen ||
    footerHelpOpen ||
    aboutDialogOpen ||
    updateDialogOpen ||
    licenseCenterOpen ||
    Boolean(switchRuntimeOverlay?.visible) ||
    Boolean(authGateQrPreviewState);
  const showStartupLoadingOverlay = startupLoadingVisible && !mandatoryUpdateLockActive;
  const mandatoryUpdateGuard =
    updateState.blockedByMandatory && typeof document !== 'undefined'
      ? createPortal(
          <div className="update-guard" role="alertdialog" aria-live="assertive" aria-modal="true">
            <div
              className="update-guard__panel"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <div className="update-guard__eyebrow">必须更新后继续</div>
              <h3 className="update-guard__title">
                发现必须安装的新版本
              </h3>
              <p className="update-guard__copy">
                当前版本 {displayedCurrentVersion}
                {updateState.manifest ? `，最新版本 ${updateState.manifest.version}` : ''}
                {updateState.minSupportedVersion
                  ? `，最低支持版本 ${updateState.minSupportedVersion}`
                  : ''}
                。请先完成更新，再继续使用核心切换功能。
              </p>
              {updateState.errorMessage ? (
                <div className="update-guard__error">{updateState.errorMessage}</div>
              ) : null}
              <div className="update-guard__meta">
                <span>渠道：{updateChannelLabel}</span>
                <span>来源：{updateSourceLabel}</span>
                <span>发布时间：{formatDateTime(updateState.manifest?.releaseTime)}</span>
              </div>
              <div className="update-guard__content">
                <section className="update-guard__section">
                  <div className="update-guard__section-label">更新摘要</div>
                  <pre className="update-guard__notes">{updateSummaryContent}</pre>
                </section>
                <section className="update-guard__section">
                  <div className="update-guard__section-label">处理提示</div>
                  <p className="update-guard__hint">{updateGuardHint}</p>
                </section>
              </div>
              <div className="update-guard__actions">
                <button
                  type="button"
                  className="sidebar-button"
                  onClick={() => void handleDownloadAndInstallUpdate()}
                >
                  {updateBusyAction === 'install' ? (
                    <LoaderCircle size={15} strokeWidth={1.8} className="spin" />
                  ) : null}
                  {updatePrimaryActionLabel}
                </button>
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  onClick={() => {
                    window.codexWorkspace.restartApp();
                  }}
                >
                  重启应用
                </button>
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  onClick={() => {
                    window.codexWorkspace.quitApp();
                  }}
                >
                  关闭应用
                </button>
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  onClick={() => void handleCheckForUpdates()}
                >
                  检查更新
                </button>
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  onClick={() => void handleOpenExternal(APP_DOWNLOAD_CENTER_URL)}
                >
                  官网下载安装
                </button>
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  onClick={() => void handleOpenExternal(APP_WEBSITE_URL)}
                >
                  打开官网
                </button>
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  onClick={() => void handleOpenExternal(APP_ACTIVATION_CENTER_URL)}
                >
                  登录与激活
                </button>
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  onClick={handleOpenUpdateSupport}
                >
                  联系客服
                </button>
                {updateState.manifest?.notesUrl ? (
                  <button
                    type="button"
                    className="sidebar-button sidebar-button--secondary"
                    onClick={() => void handleOpenUpdateNotes()}
                  >
                    查看完整说明
                  </button>
                ) : null}
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;
  const switchRuntimeOverlayPortal =
    switchRuntimeOverlay && typeof document !== 'undefined'
      ? createPortal(
          <div
            className={`switch-runtime-overlay is-${switchRuntimeOverlay.phase}${
              switchRuntimeOverlay.visible ? ' is-visible' : ' is-hiding'
            }`}
            aria-live="polite"
            aria-busy={switchRuntimeOverlayBusy}
          >
            <div className="switch-runtime-overlay__backdrop" />
            <div className="switch-runtime-overlay__panel" role="status">
              <div
                className={`switch-runtime-overlay__brand${
                  switchRuntimeOverlay.phase === 'success'
                    ? ' is-success'
                    : switchRuntimeOverlay.phase === 'error'
                      ? ' is-error'
                      : ''
                }`}
                aria-hidden="true"
              >
                {switchRuntimeOverlay.phase === 'success' ? (
                  <CheckCircle2 size={34} strokeWidth={2} />
                ) : switchRuntimeOverlay.phase === 'error' ? (
                  <CircleAlert size={34} strokeWidth={2} />
                ) : switchRuntimeOverlayBusy ? (
                  <OpenAIKnotIcon className="is-spinning" />
                ) : (
                  <OpenAIKnotIcon />
                )}
              </div>
              <p className="switch-runtime-overlay__status">{switchRuntimeOverlayMessage}</p>
            </div>
          </div>,
          document.body,
        )
      : null;
  const spacePoolImportOverlayPortal =
    (spacePoolImportOverlayStep || spacePoolDragActive) &&
    spacePoolImportOverlayBounds &&
    typeof document !== 'undefined'
      ? createPortal(
          <>
            {spacePoolImportOverlayStep ? (
              <div
                className="space-pool-import-overlay"
                role="dialog"
                aria-modal="true"
                aria-label={spacePoolImportOverlayStep === 'input' ? '手动输入导入' : '导入空间卡片'}
                style={spacePoolImportOverlayBounds}
                onClick={closeSpacePoolImportOverlay}
              >
                <div className="space-pool-import-overlay__shell">
                  <div
                    className="space-pool-import-overlay__header"
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    {spacePoolImportOverlayStep === 'input' ? (
                      <button
                        type="button"
                        className="space-pool-import-overlay__back"
                        onClick={() => {
                          if (manualImportTextBusy) {
                            return;
                          }
                          setManualImportTextError(null);
                          setSpacePoolImportOverlayStep('select');
                        }}
                        disabled={manualImportTextBusy}
                      >
                        <ArrowLeft size={16} strokeWidth={1.9} />
                        返回
                      </button>
                    ) : (
                      <div className="space-pool-import-overlay__title-group">
                        <strong>导入空间</strong>
                      </div>
                    )}
                    <button
                      type="button"
                      className="space-pool-import-overlay__close"
                      aria-label="关闭导入层"
                      onClick={closeSpacePoolImportOverlay}
                      disabled={manualImportTextBusy}
                    >
                      <X size={16} strokeWidth={2} />
                    </button>
                  </div>

                  {spacePoolImportOverlayStep === 'input' ? (
                    <div
                      className="space-pool-import-overlay__input-layout"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <textarea
                        className="space-pool-import-overlay__textarea"
                        value={manualImportTextDraft}
                        onChange={(event) => {
                          setManualImportTextDraft(event.target.value);
                          if (manualImportTextError) {
                            setManualImportTextError(null);
                          }
                        }}
                        placeholder="粘贴凭证文本或 auth JSON"
                        spellCheck={false}
                        disabled={manualImportTextBusy}
                      />
                      {manualImportTextError ? (
                        <div className="space-pool-import-overlay__error" role="alert">
                          {manualImportTextError}
                        </div>
                      ) : null}
                      <div className="space-pool-import-overlay__actions">
                        <button
                          type="button"
                          className="space-pool-import-overlay__action is-secondary"
                          onClick={closeSpacePoolImportOverlay}
                          disabled={manualImportTextBusy}
                        >
                          取消
                        </button>
                        <button
                          type="button"
                          className="space-pool-import-overlay__action is-primary"
                          onClick={() => {
                            void handleImportManualText();
                          }}
                          disabled={manualImportTextBusy || manualImportTextDraft.trim().length === 0}
                        >
                          {manualImportTextBusy ? (
                            <LoaderCircle size={15} strokeWidth={1.8} className="spin" />
                          ) : (
                            <Pencil size={15} strokeWidth={1.8} />
                          )}
                          {manualImportTextBusy ? '识别中' : '导入'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="space-pool-import-overlay__chooser"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <button
                        type="button"
                        className="space-pool-import-choice"
                        onClick={() => {
                          void handlePickLocalImportFiles();
                        }}
                      >
                        <span className="space-pool-import-choice__icon">
                          <FolderOpen size={20} strokeWidth={1.9} />
                        </span>
                        <span className="space-pool-import-choice__title">导入文件</span>
                        <span className="space-pool-import-choice__meta">JSON / ZIP / 拖入</span>
                      </button>
                      <button
                        type="button"
                        className="space-pool-import-choice"
                        onClick={() => {
                          setManualImportTextError(null);
                          setSpacePoolImportOverlayStep('input');
                        }}
                      >
                        <span className="space-pool-import-choice__icon">
                          <Pencil size={20} strokeWidth={1.9} />
                        </span>
                        <span className="space-pool-import-choice__title">手动输入</span>
                        <span className="space-pool-import-choice__meta">粘贴凭证文本</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
            {spacePoolDragActive ? (
              <div
                className="space-pool-import-overlay space-pool-import-overlay--drag"
                style={spacePoolImportOverlayBounds}
                aria-hidden="true"
              >
                <div className="space-pool-import-overlay__drag-panel">
                  <strong>拖动导入</strong>
                  <span>放开鼠标导入文件</span>
                  <em>.json / .zip</em>
                </div>
              </div>
            ) : null}
          </>,
          document.body,
        )
      : null;

  return (
    <div
      className={`shell-app${showStartupLoadingOverlay ? ' is-startup-loading' : ''}${
        showStartupLoadingOverlay && startupBackdropVisible ? ' is-startup-visible' : ''
      }`}
    >
      <div
        className={`shell-scene${packagedOverlayBlurFallbackActive ? ' is-overlay-fallback' : ''}${
          windowState.maximized ? ' is-window-maximized' : ''
        }${showStartupLoadingOverlay ? ' is-startup-loading' : ''}${
          showStartupLoadingOverlay && startupBackdropVisible ? ' is-startup-visible' : ''
        }`}
        style={
          {
            ['--oauth-quick-dock-scale' as string]: String(oauthQuickDockScale),
          } as CSSProperties
        }
      >
        <input
          ref={avatarInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => void handleAvatarFileChange(event)}
        />
        <div
          className="shell-windowbar"
          onDoubleClick={() => {
            void handleWindowToggleMaximize();
          }}
        >
          <div className="shell-windowbar__title">
            <span className="shell-windowbar__brand" aria-hidden="true">
              <OpenAIKnotIcon />
            </span>
            <span>{APP_DISPLAY_NAME}</span>
          </div>
          <div className="shell-windowbar__actions">
            <button
              type="button"
              className="window-dot window-dot--minimize"
              title="最小化窗口"
              aria-label="最小化窗口"
              onClick={() => {
                void handleWindowMinimize();
              }}
            />
            <button
              type="button"
              className={`window-dot window-dot--maximize${windowState.maximized ? ' is-active' : ''}`}
              title={windowState.maximized ? '还原窗口' : '最大化窗口'}
              aria-label={windowState.maximized ? '还原窗口' : '最大化窗口'}
              onClick={() => {
                void handleWindowToggleMaximize();
              }}
            />
            <button
              type="button"
              className="window-dot window-dot--close"
              title="关闭窗口并保留托盘驻留"
              aria-label="关闭窗口并保留托盘驻留"
              onClick={() => {
                void handleWindowHide();
              }}
            />
          </div>
        </div>
        <header className="shell-header">
          <div className="shell-header__left">
            <div className="active-indicator">
              <span className="active-indicator__dot" />
              <span>
                当前激活:{' '}
                <strong>
                  {state.summary.activeProfileName ??
                    (state.summary.currentAuthExists ? '已检测到待导入' : '未检测到')}
                </strong>
              </span>
            </div>
          </div>

          <div className="shell-header__right">
            <label className="header-mode-select">
              <span className="header-mode-select__label">模式</span>
              <select
                className="header-mode-select__control"
                value={state.settings.switchMode}
                onChange={(event) => {
                  const nextMode = event.target.value as SwitchMode;
                  setState((current) => ({
                    ...current,
                    settings: {
                      ...current.settings,
                      switchMode: nextMode,
                    },
                  }));
                  void handleUpdateWarningSettings(
                    { switchMode: nextMode },
                    `切换模式已更新为 ${mapSwitchModeLabel(nextMode)}`,
                  );
                }}
              >
                <option value="codex">Codex / Trae</option>
                <option value="openclaw">OpenClaw</option>
              </select>
            </label>
            <div
              className={`header-license-pill is-${headerLicenseTone}${headerLicenseStacked ? ' is-stacked' : ' is-inline'}`}
              title={
                authState.license.permanent
                  ? `${authState.license.licenseTypeLabel} / 永久授权`
                  : authState.license.expiresAt
                    ? `${authState.license.licenseTypeLabel} / 到期时间：${formatDateTime(authState.license.expiresAt)}`
                    : authState.license.licenseTypeLabel
              }
            >
              <span className="header-license-pill__label">授权期限</span>
              <strong>{headerLicenseText}</strong>
            </div>
            {updateUserBadgeLabel ? (
              <span
                className={`badge ${updateState.channel === 'beta' ? 'badge--recommended' : 'badge--manual'}`}
                title={updateState.testerId ? `${updateUserBadgeLabel} / ${updateState.testerId}` : updateUserBadgeLabel}
              >
                {updateState.testerId ? `${updateUserBadgeLabel} · ${updateState.testerId}` : updateUserBadgeLabel}
              </span>
            ) : null}
            <div className="header-actions">
              <HeaderIconButton
                title="反馈中心"
                onClick={() => {
                  setFeedbackCenterOpen(true);
                }}
                indicatorDot={feedbackHeaderUnreadVisible}
              >
                <Mail size={18} strokeWidth={1.8} />
              </HeaderIconButton>
              {authState.sessionStatus !== 'authenticated' ? (
                <HeaderIconButton
                  title="打开 OAuth 鉴权"
                  onClick={() => {
                    void handleOpenWorkspaceOAuth();
                  }}
                >
                  <OpenAIKnotIcon />
                </HeaderIconButton>
              ) : null}
              <HeaderIconButton title="客服支持" onClick={() => setFooterHelpOpen(true)}>
                <BadgeHelp size={18} strokeWidth={1.8} />
              </HeaderIconButton>
              <HeaderIconButton
                title="添加 Profile"
                onClick={() => openAddDialog()}
              >
                <Plus size={18} strokeWidth={1.8} />
              </HeaderIconButton>
                  <HeaderIconButton title="刷新列表" onClick={() => void refreshState()}>
                {busyAction === 'refresh' ? (
                  <LoaderCircle size={18} strokeWidth={1.8} className="spin" />
                ) : (
                  <RefreshCw size={18} strokeWidth={1.8} />
                )}
              </HeaderIconButton>
              <HeaderIconButton
                title={settingsPanelOpen ? '收起参数面板' : '展开参数面板'}
                onClick={() => setSettingsPanelOpen((current) => !current)}
              >
                <Settings size={18} strokeWidth={1.8} />
              </HeaderIconButton>
              <HeaderIconButton
                title="更新状态"
                onClick={handleOpenUpdateDialog}
                indicatorDot={updateBadgeVisible}
              >
                {updateBusyAction === 'check' ? (
                  <LoaderCircle size={18} strokeWidth={1.8} className="spin" />
                ) : (
                  <ArrowUpCircle size={18} strokeWidth={1.8} />
                )}
              </HeaderIconButton>
            </div>
            {IS_LOCAL_FAST_IMPORT_BUILD ? (
              <button
                type="button"
                className="header-cloud-edition-link"
                title="升级到云同步版"
                onClick={() => {
                  void handleOpenExternal(APP_WEBSITE_URL);
                }}
              >
                <Cloud size={15} strokeWidth={2} />
                升级云同步版
              </button>
            ) : null}
            <button
              type="button"
              className={`user-avatar user-avatar--button${
                userCenterSession.status === 'authenticated' ? ' is-authenticated' : ' is-guest'
              }${avatarImageSrc ? ' has-image' : ''}`}
              onClick={() => setUserCenterOpen(true)}
              title="打开用户中心"
            >
              {avatarImageSrc ? (
                <img className="user-avatar__image" src={avatarImageSrc} alt="" />
              ) : (
                <span className="user-avatar__text">{userCenterSession.avatarText}</span>
              )}
              {userCenterSession.hasAlert ? (
                <span className="header-indicator-dot header-indicator-dot--avatar" />
              ) : null}
            </button>
          </div>
        </header>

        <div className="shell-core">
          <div className="shell-workspace">
            <aside className="shell-left">
              <section
                className={`active-profile-card${hasPendingCurrentAuthImport ? ' is-import-pending' : ''}`}
              >
                <div className="active-profile-card__header">
                  <div className="active-profile-card__copy">
                    <div className="section-kicker">当前 Profile</div>
                    <div className="active-profile-title">
                      {activeProfile?.name ??
                        (state.summary.currentAuthExists ? '已检测到当前 auth.json' : '未识别 auth.json')}
                    </div>
                  </div>
                  <div className="active-profile-card__brand" aria-hidden="true">
                    <OpenAIKnotIcon />
                  </div>
                </div>
                <div className="active-profile-subtitle">
                  空间名: {getProfileDisplayName(activeProfile)} / 套餐:{' '}
                  {activeProfile ? mapPlanLabel(activeProfile.planType) : 'UNKNOWN'}
                </div>
                <div className="active-profile-meta">
                  {state.summary.authRecognized
                    ? `目录: ${activeProfile?.storageDirName ?? '未记录'} / auth: ${state.summary.currentAuthPath}`
                    : state.summary.currentAuthExists
                      ? '已检测到当前 auth.json，但还没纳入 profiles'
                      : '当前 .codex 目录下未检测到 auth.json'}
                </div>
                {hasPendingCurrentAuthImport ? (
                  <button
                    type="button"
                    className="active-profile-action"
                    onClick={() => void handleImportCurrentAuth()}
                  >
                    <span className="active-profile-action__status-dot" aria-hidden="true" />
                    <span className="active-profile-action__icon-shell" aria-hidden="true">
                      {busyAction === 'add' ? (
                        <LoaderCircle size={16} strokeWidth={1.8} className="spin" />
                      ) : (
                        <RefreshCw size={16} strokeWidth={1.8} />
                      )}
                    </span>
                    <span className="active-profile-action__copy">
                      <span className="active-profile-action__eyebrow">检测到新空间</span>
                      <span className="active-profile-action__title">
                        {busyAction === 'add' ? '正在纳入当前 auth' : '把当前 auth 纳入空间池'}
                      </span>
                    </span>
                    <span className="active-profile-action__suffix">
                      {busyAction === 'add' ? '导入中' : '马上导入'}
                    </span>
                  </button>
                ) : null}
              </section>

              <section className="sidebar-section">
                <h2 className="sidebar-section__title">当前状态</h2>
                <div className="sidebar-metrics">
                  <SidebarMetric
                    label="auth 已识别"
                    value={
                      state.summary.authRecognized
                        ? '已纳入 profile'
                        : state.summary.currentAuthExists
                          ? '已检测到未纳入'
                          : '未检测到'
                    }
                    green={state.summary.currentAuthExists}
                  />
                  <SidebarMetric label="Profile 数量" value={String(displayProfiles.length)} />
                  <SidebarMetric
                    label="备份策略"
                    value={state.summary.backupBeforeSwitch ? '切换前备份' : '未启用'}
                  />
                  <SidebarMetric
                    label="最近切换"
                    value={formatDateTime(state.summary.lastSwitchedAt)}
                  />
                  <SidebarMetric
                    label="总体剩余额度"
                    value={
                      state.summary.overallRemainingPercent !== null
                        ? `${state.summary.overallRemainingPercent}%`
                        : '未设置'
                    }
                    green={(state.summary.overallRemainingPercent ?? 0) >= 50}
                  />
                </div>
              </section>

              <section className="sidebar-section">
                <h2 className="sidebar-section__title">最近备份</h2>
                {latestBackup ? (
                  <div className="storage-meta">
                    <div className="storage-meta__row">
                      <span className="storage-meta__label">文件</span>
                      <span className="storage-meta__value storage-meta__value--name">
                        {latestBackup.name}
                      </span>
                    </div>
                    <div className="storage-meta__row">
                      <span className="storage-meta__label">时间</span>
                      <span className="storage-meta__value">
                        {formatDateTime(latestBackup.createdAt)}
                      </span>
                    </div>
                    <div className="storage-meta__row">
                      <span className="storage-meta__label">大小</span>
                      <span className="storage-meta__value">
                        {formatPreciseFileSize(latestBackup.sizeBytes)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="empty-side-note">暂无备份，切换 profile 后会自动写入。</div>
                )}
              </section>

              <section className="sidebar-section sidebar-section--quick-actions">
                <div className="sidebar-quick-actions">
                  <button
                    type="button"
                    className="sidebar-button sidebar-quick-action"
                    onClick={() => {
                      void handleOpenExternal(CHATGPT_HOME_URL);
                    }}
                  >
                    <OpenAIKnotIcon />
                    ChatGPT
                  </button>
                  <button
                    type="button"
                    className={`sidebar-button sidebar-button--secondary sidebar-quick-action${
                      workspaceSelectionMode === 'send' ? ' is-active' : ''
                    }`}
                    disabled={visibleRealProfiles.length === 0 || workspaceSelectionBusyAction !== null}
                    onClick={() => {
                      if (workspaceSelectionMode === 'send') {
                        resetWorkspaceSelectionState();
                        return;
                      }
                      openIssueTransferDialog();
                    }}
                  >
                    <Mail size={14} strokeWidth={1.8} />
                    发送卡片
                  </button>
                  <button
                    type="button"
                    className={`sidebar-button sidebar-button--secondary sidebar-quick-action${
                      workspaceSelectionMode === 'batch' ? ' is-active' : ''
                    }`}
                    onClick={openBatchNoteDialog}
                    disabled={
                      (busyAction === 'edit' && editDialogOpen) ||
                      batchNoteEligibleProfiles.length === 0 ||
                      workspaceSelectionBusyAction !== null
                    }
                  >
                    <Pencil size={14} strokeWidth={1.8} />
                    批量操作
                  </button>
                  <button
                    type="button"
                    className={`sidebar-button sidebar-button--secondary sidebar-quick-action${
                      mainView === 'statistics' ? ' is-active' : ''
                    }`}
                    onClick={toggleStatisticsView}
                  >
                    <span className="sidebar-quick-action__content">
                      {mainView === 'statistics' ? (
                        <LayoutGrid size={14} strokeWidth={1.8} />
                      ) : (
                        <BarChart3 size={14} strokeWidth={1.8} />
                      )}
                      <span>{mainView === 'statistics' ? '空间池' : '统计'}</span>
                    </span>
                    <ArrowRightLeft
                      size={14}
                      strokeWidth={1.8}
                      className="sidebar-quick-action__switch"
                    />
                  </button>
                  <button
                    type="button"
                    className={`sidebar-button sidebar-button--secondary sidebar-quick-action sidebar-quick-action--api-mode${
                      authCredentialMode === 'api' ? ' is-active' : ''
                    }`}
                    onClick={toggleAuthCredentialMode}
                  >
                    <span className="sidebar-quick-action__content">
                      <KeyRound size={14} strokeWidth={1.8} />
                      <span>{authCredentialMode === 'api' ? 'OAuth模式' : 'API模式'}</span>
                    </span>
                    <ArrowRightLeft
                      size={14}
                      strokeWidth={1.8}
                      className="sidebar-quick-action__switch"
                    />
                  </button>
                </div>
              </section>

              <section className="sidebar-section sidebar-section--manual-import">
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary sidebar-manual-import"
                  disabled={
                    busyAction === 'import' ||
                    pendingLocalImportOverwrite !== null ||
                    hasReachedProfileSlotLimit
                  }
                  onClick={() => {
                    resetSpacePoolImportOverlay();
                    setSpacePoolImportOverlayStep('select');
                  }}
                >
                  {busyAction === 'import' ? (
                    <LoaderCircle size={15} strokeWidth={1.8} className="spin" />
                  ) : (
                    <FolderOpen size={15} strokeWidth={1.8} />
                  )}
                  {busyAction === 'import' ? '导入中' : '手动导入'}
                </button>
              </section>

              {errorMessage ? (
                <div className="sidebar-error" role="alert">
                  <div className="sidebar-error__header">
                    <span className="sidebar-error__title">当前提醒</span>
                    <button
                      type="button"
                      className="sidebar-error__dismiss"
                      aria-label="关闭当前提醒"
                      onClick={() => setErrorMessage(null)}
                    >
                      <X size={14} strokeWidth={2} />
                    </button>
                  </div>
                  <div className="sidebar-error__body">{errorMessage}</div>
                </div>
              ) : null}
            </aside>

            <main ref={shellMainRef} className="shell-main">
              {mainView === 'api-config' ? (
                <ApiConfigurationPanel />
              ) : mainView === 'statistics' ? (
                <StatisticsCenter
                  dashboard={resolvedStatisticsDashboard}
                  activeRange={statisticsRange}
                  mode={statisticsMode}
                  profileMetaById={statisticsProfileMetaById}
                  contextMeta={statisticsContextMeta}
                  refreshSource={statisticsRefreshSource}
                  loading={statisticsLoading || busyAction === 'refresh'}
                  errorMessage={statisticsError}
                  onRangeChange={(range) => {
                    setStatisticsRange(range);
                    void loadStatisticsDashboard(range, {
                      modeOverride: statisticsMode,
                      source: 'range',
                    });
                  }}
                  onModeChange={(mode) => {
                    if (mode === statisticsMode) {
                      return;
                    }

                    setStatisticsMode(mode);
                    void loadStatisticsDashboard(statisticsRange, {
                      modeOverride: mode,
                      source: 'mode',
                    });
                  }}
                  onRefresh={() => {
                    void syncUsage();
                    void loadStatisticsDashboard(statisticsRange, {
                      modeOverride: statisticsMode,
                      source: 'manual',
                    });
                  }}
                  onClose={closeStatisticsView}
                />
              ) : (
                <>
              <div
                className={`space-pool-drop-shell${spacePoolDragActive ? ' is-dragging' : ''}`}
                onDragEnter={handleSpacePoolDragEnter}
                onDragOver={handleSpacePoolDragOver}
                onDragLeave={handleSpacePoolDragLeave}
                onDrop={handleSpacePoolDrop}
              >
              <div className="shell-main__sticky">
                <div className="main-toolbar">
                  <h2 className="main-toolbar__title">Codex空间池</h2>
                  <div className="main-toolbar__controls">
                    {canAccessEngineerFeatures ? (
                      <button
                        type="button"
                        className={`main-toolbar__action main-toolbar__action--force-enable${
                          forceEnableProbeCards ? ' is-active' : ''
                        }`}
                        onClick={() => setForceEnableProbeCards((current) => !current)}
                        title="开启后会忽略探活导致的停用状态；关闭后恢复默认按探活结果显示。"
                      >
                        <ShieldCheck size={14} strokeWidth={1.9} />
                        强制启用
                      </button>
                    ) : null}
                    {!IS_LOCAL_FAST_IMPORT_BUILD ? (
                      <button
                        type="button"
                        className={`main-toolbar__action main-toolbar__action--cloud${
                          cloudBackupSummary.pendingCount > 0
                            ? ' is-alert'
                            : cloudBackupSummary.syncedCount > 0
                              ? ' is-success'
                              : ''
                        }`}
                        onClick={() => setCloudBackupCenterOpen(true)}
                        title="查看当前设备所有空间的云备份状态"
                      >
                        <Cloud size={14} strokeWidth={1.9} />
                        云备份
                        <span
                          className={`main-toolbar__action-badge${
                            activeCloudSyncVisualProgress ? ' main-toolbar__action-badge--progress' : ''
                          }`}
                        >
                          {activeCloudSyncVisualProgress ? (
                            <CloudSyncProgressInline
                              count={activeCloudSyncVisualProgress.total}
                              text={`同步 ${activeCloudSyncVisualProgress.total}`}
                              running
                              compact
                            />
                          ) : cloudBackupSummary.pendingCount > 0 ? (
                            `待补 ${cloudBackupSummary.pendingCount}`
                          ) : (
                            `已备 ${cloudBackupSummary.syncedCount}`
                          )}
                        </span>
                      </button>
                    ) : null}
                    <input
                      className="search-input"
                      placeholder="搜索 profile..."
                      type="text"
                      value={searchValue}
                      onChange={(event) => setSearchValue(event.target.value)}
                    />
                    <select
                      className="plan-select"
                      value={planFilter}
                      onChange={(event) => setPlanFilter(event.target.value as PlanFilterValue)}
                    >
                      <option value="ALL">全部套餐</option>
                      {availablePlanFilters.map((planType) => (
                        <option key={planType} value={planType}>
                          {planType}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="main-status-bar">
                  <div className="main-status-chip">
                    扫描时间: <strong>{formatDateTime(state.lastScannedAt)}</strong>
                  </div>
                  <div className="main-status-chip">
                    卡片使用: <strong>{cardUsageText}</strong>
                  </div>
                  <div
                    className={`main-status-chip${
                      updateBadgeVisible
                        ? ' is-update-alert'
                        : updateState.blockedByMandatory
                          ? ' is-danger'
                          : ''
                    }`}
                  >
                    更新状态: <strong>{updateStatusDisplayLabel}</strong>
                  </div>
                  {state.summary.currentAuthExists && !state.summary.authRecognized ? (
                    <button
                      type="button"
                      className="main-status-action"
                      onClick={() => void handleImportCurrentAuth()}
                    >
                      {busyAction === 'add' ? (
                        <LoaderCircle size={13} strokeWidth={1.8} className="spin" />
                      ) : (
                        <Plus size={13} strokeWidth={1.8} />
                      )}
                      纳入当前登录
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="main-status-action"
                    onClick={() => void handleCheckForUpdates()}
                  >
                    {updateBusyAction === 'check' ? (
                      <LoaderCircle size={13} strokeWidth={1.8} className="spin" />
                    ) : (
                      <RefreshCw size={13} strokeWidth={1.8} />
                    )}
                    检查更新
                  </button>
                  {proxyInlineAlertText ? (
                    <button
                      type="button"
                      className="main-status-inline-alert"
                      onClick={() => void handleRecoverProxyCards()}
                      title={networkAccessState?.detail ?? proxyInlineAlertText}
                    >
                      <span className="main-status-inline-alert__dot" aria-hidden="true" />
                      {proxyInlineAlertText}
                    </button>
                  ) : null}
                  {workspaceSelectionMode ? (
                    <div className="workspace-selection-bar">
                      <div className="workspace-selection-bar__meta">
                        <span className="workspace-selection-bar__mode">
                          {workspaceSelectionMode === 'send' ? '发送卡片' : '批量操作'}
                        </span>
                        <strong>{workspaceSelectionIds.length} 张</strong>
                      </div>
                      <button
                        type="button"
                        className="main-status-action workspace-selection-bar__button"
                        onClick={handleSelectAllWorkspaceProfiles}
                        disabled={
                          workspaceSelectionBusyAction !== null ||
                          workspaceSelectionEligibleProfiles.length === 0
                        }
                      >
                        全选
                      </button>
                      <button
                        type="button"
                        className="main-status-action workspace-selection-bar__button"
                        onClick={handleInvertWorkspaceSelection}
                        disabled={
                          workspaceSelectionBusyAction !== null ||
                          workspaceSelectionEligibleProfiles.length === 0
                        }
                      >
                        反选
                      </button>
                      <button
                        type="button"
                        className="main-status-action workspace-selection-bar__button is-secondary"
                        onClick={handleClearWorkspaceSelection}
                        disabled={
                          workspaceSelectionBusyAction !== null ||
                          workspaceSelectionIds.length === 0
                        }
                      >
                        取消
                      </button>
                      {workspaceSelectionMode === 'send' ? (
                        <>
                          <label className="workspace-selection-field workspace-selection-field--target">
                            <span>接收方</span>
                            <input
                              type="text"
                              value={workspaceSendTargetAccount}
                              onChange={(event) => setWorkspaceSendTargetAccount(event.target.value)}
                              placeholder="邮箱 / ID"
                              disabled={workspaceSelectionBusyAction !== null}
                            />
                          </label>
                          <label className="workspace-selection-field workspace-selection-field--note">
                            <span>备注</span>
                            <input
                              type="text"
                              value={workspaceSendNote}
                              onChange={(event) => setWorkspaceSendNote(event.target.value)}
                              placeholder="可不填"
                              disabled={workspaceSelectionBusyAction !== null}
                            />
                          </label>
                          <button
                            type="button"
                            className="main-status-action workspace-selection-bar__button is-primary"
                            disabled={
                              workspaceSelectionBusyAction !== null ||
                              workspaceSelectionIds.length === 0
                            }
                            onClick={() => {
                              void handleSubmitIssueTransfer();
                            }}
                          >
                            {workspaceSelectionBusyAction === 'send' ? (
                              <LoaderCircle size={13} strokeWidth={1.8} className="spin" />
                            ) : (
                              <Mail size={13} strokeWidth={1.8} />
                            )}
                            发送
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="main-status-action workspace-selection-bar__button"
                            disabled={
                              workspaceSelectionBusyAction !== null ||
                              workspaceSelectionIds.length === 0
                            }
                            onClick={() => {
                              void handleWorkspaceSelectionAvailability(true);
                            }}
                          >
                            启用
                          </button>
                          <button
                            type="button"
                            className="main-status-action workspace-selection-bar__button"
                            disabled={
                              workspaceSelectionBusyAction !== null ||
                              workspaceSelectionIds.length === 0
                            }
                            onClick={() => {
                              void handleWorkspaceSelectionAvailability(false);
                            }}
                          >
                            停用
                          </button>
                          <button
                            type="button"
                            className="main-status-action workspace-selection-bar__button is-danger"
                            disabled={
                              workspaceSelectionBusyAction !== null ||
                              workspaceSelectionIds.length === 0
                            }
                            onClick={() =>
                              openBatchDeleteConfirmDialog({
                                source: 'workspace-selected',
                                profileIds: getWorkspaceSelectedProfileIds(),
                              })
                            }
                          >
                            删除
                          </button>
                          <button
                            type="button"
                            className="main-status-action workspace-selection-bar__button is-danger"
                            disabled={
                              workspaceSelectionBusyAction !== null || visibleRealProfiles.length === 0
                            }
                            onClick={() =>
                              openBatchDeleteConfirmDialog({
                                source: 'workspace-all',
                                profileIds: visibleRealProfiles.map((profile) => profile.id),
                                deletedProfiles: [...visibleRealProfiles],
                                successMessage: `已清空当前空间池的 ${visibleRealProfiles.length} 张本地卡片。`,
                                failureMessage: '全部删除失败。',
                              })
                            }
                          >
                            全部删除
                          </button>
                        </>
                      )}
                    </div>
                  ) : null}
                  {updateState.updateAvailable ? (
                    <button
                      type="button"
                      className="main-status-action"
                      onClick={() => void handleDownloadAndInstallUpdate()}
                    >
                      {updateBusyAction === 'install' ? (
                        <LoaderCircle size={13} strokeWidth={1.8} className="spin" />
                      ) : (
                        <RefreshCw size={13} strokeWidth={1.8} />
                      )}
                      {updatePrimaryActionLabel}
                    </button>
                  ) : null}
                </div>
              </div>

              <section className="cards-grid">
                {filteredProfiles.length === 0 &&
                localImportPlaceholders.length === 0 &&
                cloudDownloadPlaceholders.length === 0 &&
                !currentAuthImportPlaceholder ? (
                  <div className="empty-grid-card">
                    <h3>{hasOnlyHiddenForeignProfiles ? '当前账号下没有可见卡片' : '还没有可用的 Profile'}</h3>
                    <p>
                      {hasOnlyHiddenForeignProfiles
                        ? `当前登录账号是 ${currentAccountDisplayLabel}。本地已有 ${hiddenForeignProfileCount} 张卡片，但它们都绑定到了其他账号，所以已自动隐藏，不会再被当前账号直接识别。`
                        : '当前根目录下没有扫描到任何 auth.json。先点击右上角“添加 Profile”，导入一份 auth.json，再回来确认卡片和切换流程。'}
                    </p>
                    {hasOnlyHiddenForeignProfiles && !IS_LOCAL_FAST_IMPORT_BUILD ? (
                      <p>
                        这符合你要求的“异账号卡片不展示”规则。当前账号如果在云端有自己的卡片，请点上方“云备份”查看并下载覆盖到本地。
                      </p>
                    ) : null}
                    {authState.sessionStatus !== 'authenticated' ? (
                    <button
                      type="button"
                      className="empty-grid-login-card"
                      onClick={() => {
                          void handleOpenWorkspaceOAuth();
                      }}
                    >
                      <span className="empty-grid-login-card__icon">
                        <OpenAIKnotIcon />
                      </span>
                      <span className="empty-grid-login-card__title">请先 OAuth 鉴权</span>
                      <span className="empty-grid-login-card__copy">
                        先在浏览器里完成 auth.openai.com 登录，并让本地 `.codex\\auth.json` 更新后，再回来导入新空间。
                      </span>
                    </button>
                    ) : null}
                    <button
                      type="button"
                      className="sidebar-button"
                      onClick={() => openAddDialog()}
                    >
                      <Plus size={16} />
                      添加第一个 Profile
                    </button>
                    {state.summary.currentAuthExists ? (
                      <button
                        type="button"
                        className="sidebar-button sidebar-button--secondary"
                        onClick={() => void handleImportCurrentAuth()}
                      >
                        <RefreshCw size={16} />
                        扫描当前 auth.json
                      </button>
                    ) : null}
                    {hasOnlyHiddenForeignProfiles ? (
                      <button
                        type="button"
                        className="sidebar-button sidebar-button--secondary"
                        onClick={() => setCloudBackupCenterOpen(true)}
                      >
                        <Cloud size={16} />
                        查看当前账号云备份
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <>
                    {currentAuthImportPlaceholder ? (
                      !currentAuthImportResolvedProfile ? (
                        <ProfileImportPlaceholderCard
                          key={currentAuthImportPlaceholder.id}
                          placeholder={currentAuthImportPlaceholder}
                          currentDeviceId={authState.device.deviceId}
                        />
                      ) : null
                    ) : null}
                    {localImportPlaceholders.map((placeholder, index) => {
                      const resolvedProfile = placeholder.importedProfileId
                        ? (profilesById.get(placeholder.importedProfileId) as DisplayProfile | undefined)
                        : undefined;
                      const importRevealToken = resolvedProfile ? placeholder.revealToken : 0;

                      if (!resolvedProfile) {
                        return (
                          <ProfileImportPlaceholderCard
                            key={placeholder.id}
                            placeholder={placeholder}
                            currentDeviceId={authState.device.deviceId}
                          />
                        );
                      }

                      if (workspaceRemovingProfileIdSet.has(resolvedProfile.id)) {
                        return null;
                      }

                      return (
                        <ProfileShellCard
                          key={`${placeholder.id}:${placeholder.revealToken}`}
                          profile={resolvedProfile}
                          currentDeviceId={authState.device.deviceId}
                          currentDeviceCloudBackupStatus={buildCurrentDeviceCloudBackupStatus(
                            resolvedProfile,
                            currentDeviceCloudBackupMap,
                          )}
                          isRecommended={recommendedProfileId === resolvedProfile.id}
                          selectionMode={workspaceSelectionMode}
                          selected={workspaceSelectionIdSet.has(resolvedProfile.id)}
                          actionOverlay={workspaceActionOverlayById[resolvedProfile.id] ?? null}
                          removing={workspaceRemovingProfileIds.includes(resolvedProfile.id)}
                          ignoreProbeUnavailable={forceEnableProbeCards}
                          weeklyJustReset={Boolean(recentlyResetKeys[`${resolvedProfile.id}:weekly`])}
                          fiveHourJustReset={Boolean(recentlyResetKeys[`${resolvedProfile.id}:five-hour`])}
                          startupIntroPhase={startupCardIntroPhase}
                          startupIntroIndex={
                            index +
                            localImportPlaceholders.length +
                            (currentAuthImportPlaceholder ? 1 : 0)
                          }
                          cardSwitchPhase={
                            importRevealToken > 0
                              ? null
                              : cardSwitchInteraction?.profileId === resolvedProfile.id
                                ? cardSwitchInteraction.phase
                                : null
                          }
                          cardSwitchProgressEvent={
                            importRevealToken > 0
                              ? null
                              : cardSwitchInteraction?.profileId === resolvedProfile.id
                                ? currentCardSwitchEvent
                                : null
                          }
                          cardSwitchWaveToken={cardSwitchWaveToken}
                          cardSwitchSweepToken={
                            cardSwitchSweepState?.profileId === resolvedProfile.id
                              ? cardSwitchSweepState.token
                              : importRevealToken
                          }
                          quotaRevealToken={importRevealToken}
                          menuOpen={menuProfileId === resolvedProfile.id}
                          onOpenMenu={(profileId) =>
                            setMenuProfileId((current) => (current === profileId ? null : profileId))
                          }
                          onPreviewSwitch={(target) => {
                            setMenuProfileId(null);
                            handleCardSwitchPreview(target);
                          }}
                          onClearPreview={(target) => {
                            setMenuProfileId(null);
                            handleCardSwitchPreviewExit(target);
                          }}
                          onDirectSwitch={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片只支持本地翻面预览，不执行真实切换。');
                              return;
                            }
                            setMenuProfileId(null);
                            handleCardSwitchDirect(target);
                          }}
                          onEnable={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片只支持本地翻面预览，不执行真实切换。');
                              return;
                            }
                            setMenuProfileId(null);
                            handleCardSwitchEnable(target);
                          }}
                          onManualEnable={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片不接后端存储，不能修改启停状态。');
                              return;
                            }
                            setMenuProfileId(null);
                            void handleSetProfileAvailability(target, true);
                          }}
                          onManualDisable={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片不接后端存储，不能修改启停状态。');
                              return;
                            }
                            setMenuProfileId(null);
                            void handleSetProfileAvailability(target, false);
                          }}
                          onActiveInfo={(target) => {
                            setMenuProfileId(null);
                            handleActiveCardNotice(target);
                          }}
                          onSend={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片不接后端存储，不能发送给其他账号。');
                              return;
                            }
                            setMenuProfileId(null);
                            openIssueTransferDialog([target.id]);
                          }}
                          onEdit={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片不接后端存储，不能编辑。');
                              return;
                            }
                            setMenuProfileId(null);
                            setEditTarget(target);
                            setEditDialogOpen(true);
                          }}
                          onDelete={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片关闭模拟模式后就会消失，不需要删除。');
                              return;
                            }
                            setMenuProfileId(null);
                            openDeleteConfirmDialog(target);
                          }}
                          onOpenDir={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片没有真实目录，只用于本地展示。');
                              return;
                            }
                            setMenuProfileId(null);
                            void handleOpenPath(target.authFilePath.replace(/[\\/]auth\.json$/i, ''));
                          }}
                          onToggleSelected={(target) => {
                            toggleWorkspaceSelectionForProfile(target.id);
                          }}
                          onPlayFlipSound={(pattern) => {
                            playCardFlipAudio(pattern);
                          }}
                          cloudDownloadRevealToken={importRevealToken}
                        />
                      );
                    })}
                    {cloudDownloadPlaceholders.map((placeholder, index) => {
                      const resolvedProfile = placeholder.importedProfileId
                        ? (profilesById.get(placeholder.importedProfileId) as DisplayProfile | undefined)
                        : undefined;
                      const importRevealToken = resolvedProfile ? placeholder.revealToken : 0;

                      if (!resolvedProfile) {
                        return (
                          <ProfileImportPlaceholderCard
                            key={placeholder.id}
                            placeholder={placeholder}
                            currentDeviceId={authState.device.deviceId}
                          />
                        );
                      }

                      if (workspaceRemovingProfileIdSet.has(resolvedProfile.id)) {
                        return null;
                      }

                      return (
                        <ProfileShellCard
                          key={`${placeholder.id}:${placeholder.revealToken}`}
                          profile={resolvedProfile}
                          currentDeviceId={authState.device.deviceId}
                          currentDeviceCloudBackupStatus={buildCurrentDeviceCloudBackupStatus(
                            resolvedProfile,
                            currentDeviceCloudBackupMap,
                          )}
                          isRecommended={recommendedProfileId === resolvedProfile.id}
                          selectionMode={workspaceSelectionMode}
                          selected={workspaceSelectionIdSet.has(resolvedProfile.id)}
                          actionOverlay={workspaceActionOverlayById[resolvedProfile.id] ?? null}
                          removing={workspaceRemovingProfileIds.includes(resolvedProfile.id)}
                          ignoreProbeUnavailable={forceEnableProbeCards}
                          weeklyJustReset={Boolean(recentlyResetKeys[`${resolvedProfile.id}:weekly`])}
                          fiveHourJustReset={Boolean(recentlyResetKeys[`${resolvedProfile.id}:five-hour`])}
                          startupIntroPhase={startupCardIntroPhase}
                          startupIntroIndex={index + (currentAuthImportPlaceholder ? 1 : 0)}
                          cardSwitchPhase={
                            importRevealToken > 0
                              ? null
                              : cardSwitchInteraction?.profileId === resolvedProfile.id
                                ? cardSwitchInteraction.phase
                                : null
                          }
                          cardSwitchProgressEvent={
                            importRevealToken > 0
                              ? null
                              : cardSwitchInteraction?.profileId === resolvedProfile.id
                                ? currentCardSwitchEvent
                                : null
                          }
                          cardSwitchWaveToken={cardSwitchWaveToken}
                          cardSwitchSweepToken={
                            cardSwitchSweepState?.profileId === resolvedProfile.id
                              ? cardSwitchSweepState.token
                              : importRevealToken
                          }
                          quotaRevealToken={importRevealToken}
                          menuOpen={menuProfileId === resolvedProfile.id}
                          onOpenMenu={(profileId) =>
                            setMenuProfileId((current) => (current === profileId ? null : profileId))
                          }
                          onPreviewSwitch={(target) => {
                            setMenuProfileId(null);
                            handleCardSwitchPreview(target);
                          }}
                          onClearPreview={(target) => {
                            setMenuProfileId(null);
                            handleCardSwitchPreviewExit(target);
                          }}
                          onDirectSwitch={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片只支持本地翻面预览，不执行真实切换。');
                              return;
                            }
                            setMenuProfileId(null);
                            handleCardSwitchDirect(target);
                          }}
                          onEnable={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片只支持本地翻面预览，不执行真实切换。');
                              return;
                            }
                            setMenuProfileId(null);
                            handleCardSwitchEnable(target);
                          }}
                          onManualEnable={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片不接后端存储，不能修改启停状态。');
                              return;
                            }
                            setMenuProfileId(null);
                            void handleSetProfileAvailability(target, true);
                          }}
                          onManualDisable={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片不接后端存储，不能修改启停状态。');
                              return;
                            }
                            setMenuProfileId(null);
                            void handleSetProfileAvailability(target, false);
                          }}
                          onActiveInfo={(target) => {
                            setMenuProfileId(null);
                            handleActiveCardNotice(target);
                          }}
                          onSend={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片不接后端存储，不能发送给其他账号。');
                              return;
                            }
                            setMenuProfileId(null);
                            openIssueTransferDialog([target.id]);
                          }}
                          onEdit={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片不接后端存储，不能编辑。');
                              return;
                            }
                            setMenuProfileId(null);
                            setEditTarget(target);
                            setEditDialogOpen(true);
                          }}
                          onDelete={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片关闭模拟模式后就会消失，不需要删除。');
                              return;
                            }
                            setMenuProfileId(null);
                            openDeleteConfirmDialog(target);
                          }}
                          onOpenDir={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片没有真实目录，只用于本地展示。');
                              return;
                            }
                            setMenuProfileId(null);
                            void handleOpenPath(target.authFilePath.replace(/[\\/]auth\.json$/i, ''));
                          }}
                          onToggleSelected={(target) => {
                            toggleWorkspaceSelectionForProfile(target.id);
                          }}
                          onPlayFlipSound={(pattern) => {
                            playCardFlipAudio(pattern);
                          }}
                          cloudDownloadRevealToken={importRevealToken}
                        />
                      );
                    })}
                    {renderedProfiles.map((profile, index) => {
                      const currentAuthRevealTokenForProfile =
                        currentAuthImportResolvedProfile?.id === profile.id ? currentAuthImportRevealToken : 0;

                      return (
                        <ProfileShellCard
                          key={profile.id}
                          profile={profile}
                          currentDeviceId={authState.device.deviceId}
                          currentDeviceCloudBackupStatus={buildCurrentDeviceCloudBackupStatus(
                            profile,
                            currentDeviceCloudBackupMap,
                          )}
                          isRecommended={recommendedProfileId === profile.id}
                          selectionMode={workspaceSelectionMode}
                          selected={workspaceSelectionIdSet.has(profile.id)}
                          actionOverlay={workspaceActionOverlayById[profile.id] ?? null}
                          removing={workspaceRemovingProfileIds.includes(profile.id)}
                          ignoreProbeUnavailable={forceEnableProbeCards}
                          weeklyJustReset={Boolean(recentlyResetKeys[`${profile.id}:weekly`])}
                          fiveHourJustReset={Boolean(recentlyResetKeys[`${profile.id}:five-hour`])}
                          startupIntroPhase={startupCardIntroPhase}
                          startupIntroIndex={index + leadingPlaceholderCount}
                          cardSwitchPhase={
                            currentAuthRevealTokenForProfile > 0
                              ? null
                              : cardSwitchInteraction?.profileId === profile.id
                                ? cardSwitchInteraction.phase
                                : null
                          }
                          cardSwitchProgressEvent={
                            currentAuthRevealTokenForProfile > 0
                              ? null
                              : cardSwitchInteraction?.profileId === profile.id
                                ? currentCardSwitchEvent
                                : null
                          }
                          cardSwitchWaveToken={cardSwitchWaveToken}
                          cardSwitchSweepToken={
                            cardSwitchSweepState?.profileId === profile.id
                              ? cardSwitchSweepState.token
                              : currentAuthRevealTokenForProfile
                          }
                          quotaRevealToken={currentAuthRevealTokenForProfile}
                          menuOpen={menuProfileId === profile.id}
                          onOpenMenu={(profileId) =>
                            setMenuProfileId((current) => (current === profileId ? null : profileId))
                          }
                          onPreviewSwitch={(target) => {
                            setMenuProfileId(null);
                            handleCardSwitchPreview(target);
                          }}
                          onClearPreview={(target) => {
                            setMenuProfileId(null);
                            handleCardSwitchPreviewExit(target);
                          }}
                          onDirectSwitch={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片只支持本地翻面预览，不执行真实切换。');
                              return;
                            }
                            setMenuProfileId(null);
                            handleCardSwitchDirect(target);
                          }}
                          onEnable={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片只支持本地翻面预览，不执行真实切换。');
                              return;
                            }
                            setMenuProfileId(null);
                            handleCardSwitchEnable(target);
                          }}
                          onManualEnable={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片不接后端存储，不能修改启停状态。');
                              return;
                            }
                            setMenuProfileId(null);
                            void handleSetProfileAvailability(target, true);
                          }}
                          onManualDisable={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片不接后端存储，不能修改启停状态。');
                              return;
                            }
                            setMenuProfileId(null);
                            void handleSetProfileAvailability(target, false);
                          }}
                          onActiveInfo={(target) => {
                            setMenuProfileId(null);
                            handleActiveCardNotice(target);
                          }}
                          onSend={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片不接后端存储，不能发送给其他账号。');
                              return;
                            }
                            setMenuProfileId(null);
                            openIssueTransferDialog([target.id]);
                          }}
                          onEdit={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片不接后端存储，不能编辑。');
                              return;
                            }
                            setMenuProfileId(null);
                            setEditTarget(target);
                            setEditDialogOpen(true);
                          }}
                          onDelete={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片关闭模拟模式后就会消失，不需要删除。');
                              return;
                            }
                            setMenuProfileId(null);
                            openDeleteConfirmDialog(target);
                          }}
                          onOpenDir={(target) => {
                            if (isSimulatedProfile(target)) {
                              handleSimulationOnlyAction('模拟卡片没有真实目录，只用于本地展示。');
                              return;
                            }
                            setMenuProfileId(null);
                            void handleOpenPath(target.authFilePath.replace(/[\\/]auth\.json$/i, ''));
                          }}
                          onToggleSelected={(target) => {
                            toggleWorkspaceSelectionForProfile(target.id);
                          }}
                          onPlayFlipSound={(pattern) => {
                            playCardFlipAudio(pattern);
                          }}
                          cloudDownloadRevealToken={currentAuthRevealTokenForProfile}
                        />
                      );
                    })}
                  </>
                )}
              </section>
              <div className="oauth-quick-dock-zone" aria-label="OAuth 快捷入口">
                <div className="oauth-quick-dock">
                  {OAUTH_QUICK_PROVIDER_ENTRIES.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      className={`oauth-quick-dock__item oauth-quick-dock__item--${entry.id}`}
                      aria-label={entry.label}
                      title={`${entry.label} · ${entry.tooltip}`}
                      onClick={() => {
                        void handleQuickOAuthProviderAction(entry);
                      }}
                    >
                      <span className={`oauth-quick-dock__icon oauth-quick-dock__icon--${entry.id}`}>
                        <QuickOAuthProviderIcon providerId={entry.id} />
                      </span>
                      <span className="oauth-quick-dock__tooltip" aria-hidden="true">
                        {`${entry.label} · ${entry.tooltip}`}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              </div>
                </>
              )}
            </main>
          </div>

        <aside className={`shell-right${settingsPanelOpen ? '' : ' is-collapsed'}`}>
          <SidebarPanel defaultOpen title="根目录管理">
            <div className="root-box">
              <div className="root-box__label">主目录</div>
              <code className="root-box__value">{state.settings.profilesRootDir}</code>
            </div>
            <button className="sidebar-button" type="button" onClick={() => void handleChangeRootDirectory()}>
              {busyAction === 'root' ? (
                <LoaderCircle size={14} strokeWidth={1.8} className="spin" />
              ) : null}
              {busyAction === 'root' ? '选择中...' : '更改根目录'}
            </button>
            <button
              className="sidebar-button sidebar-button--secondary"
              type="button"
              onClick={() => void handleOpenPath(state.settings.profilesRootDir)}
            >
              <FolderOpen size={14} strokeWidth={1.8} />
              打开目录
            </button>
          </SidebarPanel>

          <SidebarPanel defaultOpen title="额度与更新">
            <div className="settings-stack">
              <label className="settings-toggle">
                <span className="settings-toggle__label">启用额度预警</span>
                <span className="settings-switch">
                  <input
                    className="settings-switch__input"
                    type="checkbox"
                    checked={state.settings.lowQuotaWarningEnabled}
                    onChange={(event) => {
                      void handleUpdateWarningSettings(
                        { lowQuotaWarningEnabled: event.target.checked },
                        event.target.checked ? '已启用额度预警。' : '已关闭额度预警。',
                      );
                    }}
                  />
                  <span className="settings-switch__track" />
                  <span className="settings-switch__thumb" />
                </span>
              </label>

              <div
                className="settings-threshold"
                title="当前空间的周额度或 5 小时额度剩余低于阈值时，会弹出推荐切换提示，并高亮最适合切换的空间。"
              >
                <div className="settings-threshold__label">剩余低于此值时提醒切换</div>
                <div className="settings-threshold__controls">
                  <input
                    className="settings-input"
                    type="number"
                    min={5}
                    max={95}
                    step={1}
                    value={warningThresholdDraft}
                    onChange={(event) => setWarningThresholdDraft(event.target.value)}
                    onBlur={() => void handleCommitWarningThreshold()}
                  />
                  <span className="settings-suffix">%</span>
                </div>
              </div>

              <div className="settings-divider" />

              <div className="settings-group-title">更新通道</div>
              <label
                className="settings-toggle"
                title={buildHoverTitle(
                  hasPrereleaseEntitlement
                    ? state.settings.betaModeEnabled
                      ? `当前设备保留了${updateEntitlementLabel}资格。开启时会优先检查${updateEntitlementLabel}，同时继续兼容正式版。`
                      : `当前设备保留了${updateEntitlementLabel}资格，但你已手动关闭${updateEntitlementLabel}模式；现在只接收正式版。`
                    : '拿到内测授权后，这里可以手动决定是否接收内测版本；关闭时始终只接收正式版。',
                )}
              >
                <span className="settings-toggle__label">
                  {hasPrereleaseEntitlement ? '开启内测模式' : '内测模式（需内测授权）'}
                </span>
                <span className="settings-switch">
                  <input
                    className="settings-switch__input"
                    type="checkbox"
                    checked={hasPrereleaseEntitlement ? state.settings.betaModeEnabled : false}
                    disabled={!hasPrereleaseEntitlement}
                    onChange={(event) => {
                      void handleUpdateWarningSettings(
                        { betaModeEnabled: event.target.checked },
                        event.target.checked
                          ? `已开启${updateEntitlementLabel}模式；现在会继续接收${updateEntitlementLabel}和正式版。`
                          : `已关闭${updateEntitlementLabel}模式；现在只接收正式版。`,
                      );
                    }}
                  />
                  <span className="settings-switch__track" />
                  <span className="settings-switch__thumb" />
                </span>
              </label>

              <div className="settings-divider" />

              {isCodexMode ? (
                <>
                  <div
                    className="settings-group-title"
                    title="切换前会先提示你保存当前 CLI、IDE 或对话内容；下面两个开关互相独立，只会自动启动你勾选的应用。若自动识别失败，可直接点“选择”，或在终端执行 where Codex.exe、where Trae.exe 查看实际路径。"
                  >
                    切换后自动启动
                  </div>
                  <label className="settings-toggle">
                    <span className="settings-toggle__label">切换后自动启动 Codex</span>
                    <span className="settings-switch">
                      <input
                        className="settings-switch__input"
                        type="checkbox"
                        checked={effectiveCodexAutoRestart}
                        onChange={(event) => {
                          handleManagedAutoRestartChange('codex', event.target.checked);
                        }}
                      />
                      <span className="settings-switch__track" />
                      <span className="settings-switch__thumb" />
                    </span>
                  </label>

                  <div
                    className="root-box"
                    title={buildHoverTitle(
                      state.summary.codexExecutablePath ?? '未识别',
                      mapExecutableSourceLabel(state.summary.codexExecutablePathSource),
                    )}
                  >
                    <div className="root-box__label">Codex 可执行文件</div>
                    <code className="root-box__value">{state.summary.codexExecutablePath ?? '未识别'}</code>
                  </div>
                  <div className="settings-inline-actions">
                    <button
                      className="sidebar-button"
                      type="button"
                      onClick={() => void handlePickManagedAppExecutable('codex')}
                    >
                      选择 Codex
                    </button>
                    <button
                      className="sidebar-button sidebar-button--secondary"
                      type="button"
                      onClick={() =>
                        void handleUpdateWarningSettings(
                          { codexExecutablePathOverride: null },
                          '已恢复为自动识别 Codex 路径。',
                        )
                      }
                    >
                      自动识别
                    </button>
                  </div>

                  <label className="settings-toggle">
                    <span className="settings-toggle__label">切换后自动启动 Trae</span>
                    <span className="settings-switch">
                      <input
                        className="settings-switch__input"
                        type="checkbox"
                        checked={effectiveTraeAutoRestart}
                        onChange={(event) => {
                          handleManagedAutoRestartChange('trae', event.target.checked);
                        }}
                      />
                      <span className="settings-switch__track" />
                      <span className="settings-switch__thumb" />
                    </span>
                  </label>

                  <div
                    className="root-box"
                    title={buildHoverTitle(
                      state.summary.traeExecutablePath ?? '未识别',
                      mapExecutableSourceLabel(state.summary.traeExecutablePathSource),
                    )}
                  >
                    <div className="root-box__label">Trae 可执行文件</div>
                    <code className="root-box__value">{state.summary.traeExecutablePath ?? '未识别'}</code>
                  </div>
                  <div className="settings-inline-actions">
                    <button
                      className="sidebar-button"
                      type="button"
                      onClick={() => void handlePickManagedAppExecutable('trae')}
                    >
                      选择 Trae
                    </button>
                    <button
                      className="sidebar-button sidebar-button--secondary"
                      type="button"
                      onClick={() =>
                        void handleUpdateWarningSettings(
                          { traeExecutablePathOverride: null },
                          '已恢复为自动识别 Trae 路径。',
                        )
                      }
                    >
                      自动识别
                    </button>
                  </div>

                </>
              ) : (
                <>
                  <div className="settings-group-title">OpenClaw 自动切换</div>
                  <label className="settings-toggle">
                    <span className="settings-toggle__label">启用自动切换</span>
                    <span className="settings-switch">
                      <input
                        className="settings-switch__input"
                        type="checkbox"
                        checked={state.settings.autoSwitchEnabled}
                        onChange={(event) => {
                          void handleUpdateWarningSettings(
                            { autoSwitchEnabled: event.target.checked },
                            event.target.checked ? '已启用 OpenClaw 自动切换。' : '已关闭 OpenClaw 自动切换。',
                          );
                        }}
                      />
                      <span className="settings-switch__track" />
                      <span className="settings-switch__thumb" />
                    </span>
                  </label>

                  <div className="settings-threshold">
                    <div className="settings-threshold__label">剩余低于此值时开始 30 秒自动切换</div>
                    <div className="settings-threshold__controls">
                      <input
                        className="settings-input"
                        type="number"
                        min={5}
                        max={95}
                        step={1}
                        value={autoSwitchThresholdDraft}
                        onChange={(event) => setAutoSwitchThresholdDraft(event.target.value)}
                        onBlur={() => void handleCommitAutoSwitchThreshold()}
                      />
                      <span className="settings-suffix">%</span>
                    </div>
                  </div>

                  <div className="settings-threshold">
                    <div className="settings-threshold__label">自动切换倒计时</div>
                    <div className="settings-threshold__controls">
                      <input
                        className="settings-input"
                        type="number"
                        min={5}
                        max={180}
                        step={1}
                        value={autoSwitchCountdownDraft}
                        onChange={(event) => setAutoSwitchCountdownDraft(event.target.value)}
                        onBlur={() => void handleCommitAutoSwitchCountdown()}
                      />
                      <span className="settings-suffix">秒</span>
                    </div>
                  </div>

                  <div className="settings-divider" />

                  <div className="settings-group-title">OpenClaw 同步</div>
                  <div className="root-box">
                    <div className="root-box__label">状态目录</div>
                    <code className="root-box__value">{state.summary.openclawStateDir}</code>
                  </div>
                  <div className="settings-inline-actions">
                    <button className="sidebar-button" type="button" onClick={() => void handlePickOpenClawDirectory()}>
                      选择目录
                    </button>
                    <button
                      className="sidebar-button sidebar-button--secondary"
                      type="button"
                      onClick={() =>
                        void handleUpdateWarningSettings(
                          { openclawStateDirOverride: null },
                          '已恢复为自动识别 OpenClaw 状态目录。',
                        )
                      }
                    >
                      自动识别
                    </button>
                  </div>

                  <label
                    className="settings-toggle"
                    title={buildHoverTitle(
                      `网关命令：${state.summary.openclawGatewayAvailable ? '已检测到' : '未检测到'}${
                        state.summary.openclawGatewayCommandPath
                          ? ` (${state.summary.openclawGatewayCommandPath})`
                          : ''
                      }`,
                    )}
                  >
                    <span className="settings-toggle__label">切换后自动重启网关</span>
                    <span className="settings-switch">
                      <input
                        className="settings-switch__input"
                        type="checkbox"
                        checked={state.settings.openclawAutoRestartGateway}
                        onChange={(event) => {
                          void handleUpdateWarningSettings(
                            { openclawAutoRestartGateway: event.target.checked },
                            event.target.checked
                              ? 'OpenClaw 切换后将自动重启网关。'
                              : '已关闭 OpenClaw 网关自动重启。',
                          );
                        }}
                      />
                      <span className="settings-switch__track" />
                      <span className="settings-switch__thumb" />
                    </span>
                  </label>
                </>
              )}
            </div>
          </SidebarPanel>

          <SidebarPanel defaultOpen title="视觉与交互">
            <div className="settings-stack settings-stack--airy">
              <label
                className="settings-toggle settings-toggle--surface"
                title={buildHoverTitle(
                  state.summary.launchAtLoginSupported
                    ? '当前为 Windows 打包版，可直接跟随系统启动。'
                    : '当前环境仅 Windows 打包版会真正写入开机启动。',
                  networkAccessState ? `当前网络检测：${networkAccessState.summary}` : null,
                )}
              >
                <div className="settings-toggle__main">
                  <span className="settings-toggle__label">开机启动</span>
                </div>
                <span className="settings-switch">
                  <input
                    className="settings-switch__input"
                    type="checkbox"
                    checked={state.settings.launchAtLoginEnabled}
                    disabled={!state.summary.launchAtLoginSupported}
                    onChange={(event) => {
                      void handleUpdateWarningSettings(
                        { launchAtLoginEnabled: event.target.checked },
                        event.target.checked ? '已启用开机启动。' : '已关闭开机启动。',
                      );
                    }}
                  />
                  <span className="settings-switch__track" />
                  <span className="settings-switch__thumb" />
                </span>
              </label>

              <label
                className="settings-toggle settings-toggle--surface"
                title="关闭后只保留中间悬浮的 OpenAI 图标与柔光，不再显示外围玻璃框。"
              >
                <div className="settings-toggle__main">
                  <span className="settings-toggle__label">品牌图标玻璃框</span>
                </div>
                <span className="settings-switch">
                  <input
                    className="settings-switch__input"
                    type="checkbox"
                    checked={userCenterBrandFrameEnabled}
                    onChange={(event) => handleUserCenterBrandFrameEnabledChange(event.target.checked)}
                  />
                  <span className="settings-switch__track" />
                  <span className="settings-switch__thumb" />
                </span>
              </label>

              {FLOATING_WIDGET_FEATURE_ENABLED ? (
                <>
                  <label
                    className="settings-toggle settings-toggle--surface"
                    title="和托盘图标右键菜单里的“托盘悬停卡片”完全同步。"
                  >
                    <div className="settings-toggle__main">
                      <span className="settings-toggle__label">托盘悬停卡片</span>
                    </div>
                    <span className="settings-switch">
                      <input
                        className="settings-switch__input"
                        type="checkbox"
                        checked={effectiveTrayHoverCardEnabled}
                        onChange={(event) => {
                          void handleTrayHoverCardEnabledChange(event.target.checked);
                        }}
                      />
                      <span className="settings-switch__track" />
                      <span className="settings-switch__thumb" />
                    </span>
                  </label>

                  <label
                    className="settings-toggle settings-toggle--surface"
                    title="开启后会显示默认置顶的磨砂悬浮卡片，并支持在卡片态和圆球态之间切换。"
                  >
                    <div className="settings-toggle__main">
                      <span className="settings-toggle__label">悬浮窗</span>
                    </div>
                    <span className="settings-switch">
                      <input
                        className="settings-switch__input"
                        type="checkbox"
                        checked={effectiveFloatingWidgetEnabled}
                        onChange={(event) => {
                          void handleFloatingWidgetEnabledChange(event.target.checked);
                        }}
                      />
                      <span className="settings-switch__track" />
                      <span className="settings-switch__thumb" />
                    </span>
                  </label>

                  <div
                    className="settings-slider-card"
                    title="同时作用于卡片模式和圆球模式，数值越高越通透。"
                  >
                    <div className="settings-slider-card__header">
                      <div className="settings-toggle__main">
                        <span className="settings-toggle__label">悬浮窗透明度</span>
                      </div>
                      <strong>{effectiveFloatingWidgetOpacityPercent}%</strong>
                    </div>
                    <input
                      className="settings-slider"
                      type="range"
                      min="35"
                      max="95"
                      step="1"
                      value={effectiveFloatingWidgetOpacityPercent}
                      onChange={(event) => {
                        handleFloatingWidgetOpacityPercentChange(Number(event.target.value));
                      }}
                    />
                  </div>
                </>
              ) : null}

              <div
                className="settings-slider-card"
                title="启动错峰翻牌、单张翻面和切换成功时的翻页音效都共用这一档音量。"
              >
                <div className="settings-slider-card__header">
                  <div className="settings-toggle__main">
                    <span className="settings-toggle__label">卡片翻转音量</span>
                  </div>
                  <strong>{effectiveCardFlipVolumePercent}%</strong>
                </div>
                <input
                  className="settings-slider"
                  type="range"
                  min={String(MIN_CARD_FLIP_VOLUME_PERCENT)}
                  max={String(MAX_CARD_FLIP_VOLUME_PERCENT)}
                  step="1"
                  value={effectiveCardFlipVolumePercent}
                  onChange={(event) => {
                    handleCardFlipVolumePercentChange(Number(event.target.value));
                  }}
                />
              </div>
            </div>
          </SidebarPanel>

          {canAccessEngineerFeatures ? (
            <SidebarPanel defaultOpen title="工程师模式">
              <div className={`hidden-mode-shell${hiddenModeUnlocked ? ' is-unlocked' : ''}`}>
                <div className={`hidden-mode-shell__content${hiddenModeUnlocked ? '' : ' is-obscured'}`}>
                  <div
                    className="settings-stack"
                    title="这里只对本地开发环境和测试用户显示，密码只保存在当前设备浏览器存储里；重新隐藏后只会收起这块菜单，已启用的模拟卡片会继续保留。"
                  >
                    <div className="settings-inline-actions hidden-mode-toolbar">
                      <button
                        className="sidebar-button sidebar-button--secondary"
                        type="button"
                        onClick={() => openHiddenModeDialog(hiddenModeStoredPassword ? 'change' : 'setup')}
                      >
                        {hiddenModeStoredPassword ? '修改密码' : '设置密码'}
                      </button>
                      <button
                        className="sidebar-button sidebar-button--secondary"
                        type="button"
                        onClick={() => handleLockHiddenMode()}
                      >
                        重新隐藏
                      </button>
                    </div>

                    <div
                      className="settings-toggle settings-toggle--surface"
                      title={`当前共识别 ${recognizedDisabledProfileIds.length} 张；下面两颗按钮只作用于这批已识别停用卡片，不会碰其他正常卡片。`}
                    >
                      <div className="settings-toggle__main">
                        <span className="settings-toggle__label">已识别停用卡片</span>
                      </div>
                      <div className="settings-inline-actions">
                        <button
                          className="sidebar-button sidebar-button--secondary"
                          type="button"
                          disabled={
                            recognizedDisabledActionBusy !== null ||
                            profileAvailabilityBusy ||
                            batchActionBusy ||
                            recognizedDisabledProfileIds.length === 0
                          }
                          onClick={() => void handleApplyRecognizedDisabledProfiles(true)}
                        >
                          {recognizedDisabledActionBusy === 'enable' ? (
                            <LoaderCircle size={16} strokeWidth={1.8} className="spin" />
                          ) : (
                            <ArrowUpCircle size={16} strokeWidth={1.8} />
                          )}
                          一键启用这批卡
                        </button>
                        <button
                          className="sidebar-button sidebar-button--secondary"
                          type="button"
                          disabled={
                            recognizedDisabledActionBusy !== null ||
                            profileAvailabilityBusy ||
                            batchActionBusy ||
                            recognizedDisabledProfileIds.length === 0
                          }
                          onClick={() => void handleApplyRecognizedDisabledProfiles(false)}
                        >
                          {recognizedDisabledActionBusy === 'disable' ? (
                            <LoaderCircle size={16} strokeWidth={1.8} className="spin" />
                          ) : (
                            <ArrowDownCircle size={16} strokeWidth={1.8} />
                          )}
                          恢复这批停用
                        </button>
                      </div>
                    </div>

                    <label
                      className="settings-toggle settings-toggle--surface"
                      title="开启后只模拟前端动画和状态流转；关闭时会恢复到启用前。"
                    >
                      <div className="settings-toggle__main">
                        <span className="settings-toggle__label">UI 交互测试</span>
                      </div>
                      <span className="settings-switch">
                        <input
                          className="settings-switch__input"
                          type="checkbox"
                          checked={uiInteractionTestEnabled}
                          onChange={(event) => {
                            if (event.target.checked) {
                              enableUiInteractionTest();
                              return;
                            }
                            disableUiInteractionTest();
                          }}
                        />
                        <span className="settings-switch__track" />
                        <span className="settings-switch__thumb" />
                      </span>
                    </label>

                    <div
                      className="settings-toggle settings-toggle--with-inline-input"
                      title={buildHoverTitle(
                        '关闭后模拟卡片会立即消失，也不会写入任何后端文件。',
                        '模拟卡片会排在正常真实卡片后面，手动停用的真实卡片会继续沉到最后；名称按 axx / bxx / cxx 这种格式随机生成，最近登录、同步时间、5 小时额度、周额度和重置倒计时都会单独随机。',
                      )}
                    >
                      <div className="settings-toggle__main">
                        <span className="settings-toggle__label">启用模拟卡片</span>
                      </div>
                      <div className="settings-toggle__inline">
                        <div className="settings-count-chip">
                          <input
                            className="settings-input settings-input--compact"
                            type="number"
                            min={1}
                            max={MAX_LOCAL_SIMULATION_CARD_COUNT}
                            step={1}
                            value={localSimulationCardCount}
                            onChange={(event) =>
                              setLocalSimulationCardCount(
                                clampSimulationCardCount(Number(event.target.value) || 1),
                              )
                            }
                          />
                          <span className="settings-suffix">个</span>
                        </div>
                        <span className="settings-switch">
                          <input
                            className="settings-switch__input"
                            type="checkbox"
                            checked={localSimulationEnabled}
                            onChange={(event) => {
                              const nextChecked = event.target.checked;
                              const nextCount =
                                nextChecked && localSimulationPlanTypes.length > 0
                                  ? clampSimulationCardCount(localSimulationCardCount)
                                  : 0;
                              setLocalSimulationEnabled(nextChecked);
                              setToast({
                                tone: 'info',
                                message: nextChecked
                                  ? `已启用本地模拟模式，追加 ${nextCount} 张模拟卡片。`
                                  : '已关闭本地模拟模式，模拟卡片已全部移除。',
                              });
                            }}
                          />
                          <span className="settings-switch__track" />
                          <span className="settings-switch__thumb" />
                        </span>
                      </div>
                    </div>

                    <div
                      className="settings-threshold"
                      title="只会生成你当前选中的套餐；未选中的套餐不会出现在模拟卡片里。模拟卡片的周额度和 5 小时额度固定按满额展示，重置倒计时会按各自的随机重置时间持续走动。"
                    >
                      <div className="settings-threshold__label">显示这些模拟套餐</div>
                      <div className="settings-inline-actions settings-inline-actions--simulation-plans">
                        {LOCAL_SIMULATION_PLAN_TYPES.map((planType) => {
                          const selected = localSimulationPlanTypes.includes(planType);
                          return (
                            <button
                              key={planType}
                              className={selected ? 'sidebar-button' : 'sidebar-button sidebar-button--secondary'}
                              type="button"
                              aria-pressed={selected}
                              onClick={() =>
                                setLocalSimulationPlanTypes((current) => toggleSimulationPlanType(current, planType))
                              }
                            >
                              {planType}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {!hiddenModeUnlocked ? (
                  <div className="hidden-mode-shell__overlay">
                    <button
                      className="hidden-mode-shell__enter"
                      type="button"
                      onClick={() => handleRequestHiddenModeAccess()}
                    >
                      工程师模式
                    </button>
                    <div className="hidden-mode-shell__note">
                        {hasHiddenModePassword
                          ? '输入密码后才能查看本地测试菜单；已启用的模拟卡片不会因为重新隐藏而消失。'
                          : '首次进入会先让你设置一个仅保存在本机的密码。'}
                    </div>
                  </div>
                ) : null}
              </div>
            </SidebarPanel>
          ) : null}

          <SidebarPanel defaultOpen title="备份历史">
            <div className="backup-list">
              {state.backups.length === 0 ? (
                <div className="empty-side-note">还没有备份记录，切换 profile 后会自动写入。</div>
              ) : (
                state.backups.slice(0, 6).map((item) => (
                  <BackupRow key={item.filePath} item={item} />
                ))
              )}
            </div>
          </SidebarPanel>

          <SidebarPanel title="最近操作">
            <div className="side-log-list">
              {displayLogEntries.slice(0, 6).map((item) => (
                <div key={item.id} className={`side-log-item is-${item.level}`}>
                  <div className="side-log-item__time">{formatDateTime(item.createdAt)}</div>
                  <div className="side-log-item__message">{item.displayMessage}</div>
                </div>
              ))}
            </div>
          </SidebarPanel>

          <SidebarPanel title="安全提示">
            <div className="security-tip">
              不展示 access_token 或 refresh_token。当前工具只执行 auth.json 复制、备份和元数据维护。
            </div>
          </SidebarPanel>
        </aside>
        </div>

      </div>

        <footer className="console-footer">
          <div className="console-label">
            <TerminalSquare size={13} strokeWidth={2} />
            <span>控制台</span>
          </div>
          <div className="console-stream">
            {state.logs.length === 0 ? (
              <span className="console-entry">
                <span className="console-entry__time">[--:--:--]</span>
                等待操作日志...
              </span>
            ) : (
              displayLogEntries.slice(0, 12).map((entry) => (
                <span
                  key={entry.id}
                  className={`console-entry${entry.level === 'success' ? ' is-success' : ''}${
                    entry.level === 'info' ? ' is-info' : ''
                  }${entry.level === 'warn' ? ' is-warn' : ''}${entry.level === 'error' ? ' is-error' : ''}`}
                >
                  <span className="console-entry__time">[{formatDateTime(entry.createdAt)}]</span>
                  {entry.displayMessage}
                </span>
              ))
            )}
          </div>
          <button
            type="button"
            className="console-version console-version--button"
            onClick={() => setFooterHelpOpen((current) => !current)}
          >
            v{displayedCurrentVersion}
          </button>
        </footer>

      <AuthGate
        open={authGateWorkflowVisible && !licenseCenterOpen}
        previewMode={authGatePreviewMode}
        interactiveMode={authGateInteractiveMode}
        authenticated={authState.sessionStatus === 'authenticated'}
        authenticatedDisplayName={authState.profile?.displayName ?? '已登录账号'}
        authenticatedEmail={authState.profile?.email ?? null}
        authenticatedDisplayId={authState.profile?.displayId ?? null}
        needsEmailBinding={authState.profile?.needsEmailBinding ?? false}
        showAuthenticatedSummary={authGateShowsAuthenticatedSession}
        manualNotice={authGateManualNotice}
        authMode={authMode}
        entryMode={authEntryMode}
        authForm={authForm}
        authBusy={
          authBusyAction === 'login' ||
          authBusyAction === 'register' ||
          authBusyAction === 'forgot'
        }
        otpBusy={currentAuthOtpBusy}
        otpCooldownSeconds={currentAuthOtpCooldownSeconds}
        logoutBusy={authBusyAction === 'logout'}
          scanBusy={scanBusy !== null}
          scanCodeUrl={scanCodeUrl}
          scanCodeChannel={scanCodeChannel}
          scanChannel={scanChannel}
          scanStatus={(scanTicket?.status ?? null) as ScanTicketStatus | null}
        scanStatusTitle={scanStatusTitle}
        scanStatusHint={scanStatusHint}
        scanError={scanError}
        deviceId={authState.device.deviceId}
        licenseLabel={authState.license.licenseTypeLabel}
        remainingLabel={authGateCountdownText}
        cardLimitLabel={cardLimitText}
        overlayNotice={authGateOverlayNotice}
        onClose={handleCloseAuthCenterOverlay}
        onEntryModeChange={handleAuthEntryModeChange}
        onAuthModeChange={handleAuthModeChange}
        onAuthFormChange={handleAuthFormChange}
        onSubmitAuth={() => void handleSubmitAuth()}
        onSendOtp={() => void handleSendAuthOtp()}
        onLogout={() => void handleLogout()}
        onOpenLicenseCenter={handleOpenLicenseCenter}
        onOpenWebsite={() => void handleOpenExternal(APP_WEBSITE_URL)}
        onOpenNiuq={() => void handleOpenExternal(APP_NIUQ_URL)}
        onOpenDoubao={() => void handleOpenExternal(APP_DOUBAO_URL)}
        onOpenMiniProgramPreview={() => void handleOpenExternal(APP_MINI_PROGRAM_PREVIEW_URL)}
        onScanChannelChange={handleScanChannelChange}
        onRetryScan={() => void handleRetryScanLogin()}
      />

      <ConfirmActionDialog
        open={scanRescanConfirmOpen}
        title="未完成绑定，确认刷新二维码吗？"
        description="切换回扫码后会生成新的二维码，当前待绑定流程会被放弃，需要重新扫码后再完成绑定。"
        eyebrow="确认操作"
        confirmLabel="刷新二维码"
        onOpenChange={setScanRescanConfirmOpen}
        onConfirm={() => {
          setScanRescanConfirmOpen(false);
          resetScanWorkflow({ clearPendingBinding: true });
          setAuthEntryMode('scan');
        }}
      />

      <ConfirmActionDialog
        open={oauthModeConfirmOpen}
        title="切换到 OAuth 模式"
        description="将离开 API 配置页；如已写入 API 生效配置，需要删除 config.toml 中的 API 相关配置并重启 Codex。"
        eyebrow="模式切换"
        tone="danger"
        layout="slim"
        confirmLabel="切换"
        cancelLabel="取消"
        footerAccessory={
          <label className="api-mode-confirm-checkbox">
            <input
              type="checkbox"
              checked={restartAfterOauthSwitch}
              onChange={(event) => setRestartAfterOauthSwitch(event.target.checked)}
            />
            <span>切换后立即重启 Codex</span>
          </label>
        }
        onOpenChange={(open) => {
          setOauthModeConfirmOpen(open);
          if (!open) {
            setRestartAfterOauthSwitch(false);
          }
        }}
        onConfirm={confirmSwitchToOauthMode}
      />

      <Dialog.Root
        open={emailBindingReminderOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleDeferEmailBindingReminder();
            return;
          }
          setEmailBindingReminderOpen(true);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="shell-dialog-overlay" />
          <Dialog.Content className="shell-dialog-panel auth-binding-reminder-panel">
            <div className="shell-dialog-header">
              <div className="shell-dialog-title-group">
                <Dialog.Title className="shell-dialog-title">绑定邮箱密码</Dialog.Title>
                <Dialog.Description className="shell-dialog-description">
                  为了保证数据安全，请尽快完成邮箱注册绑定。
                </Dialog.Description>
              </div>
            </div>
            <div className="shell-dialog-body auth-binding-reminder-body">
              <p className="auth-binding-reminder-copy">
                当前扫码账号已获得 1 天试用权限和最高卡片上限，绑定邮箱后可继续安全使用同一账号数据。
              </p>
              <div className="shell-dialog-actions">
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  onClick={handleDeferEmailBindingReminder}
                >
                  稍后
                </button>
                <button
                  type="button"
                  className="sidebar-button"
                  onClick={handleOpenEmailBindingRegistration}
                >
                  去注册
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {mandatoryUpdateGuard}
      {switchRuntimeOverlayPortal}
      {spacePoolImportOverlayPortal}

      {showStartupLoadingOverlay ? (
        <div
          className={`startup-loading-overlay${startupBackdropVisible ? ' is-revealing' : ''}`}
          aria-live="polite"
          aria-busy="true"
        >
          <div className="startup-loading-overlay__main">
            <div className="startup-loading-overlay__content">
              <div className="startup-loading-overlay__brand" aria-hidden="true">
                <OpenAIKnotIcon className="is-spinning" syncedSpin />
              </div>
              {startupLoadingHintText ? (
                <p className="startup-loading-overlay__text is-delayed">{startupLoadingHintText}</p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <FooterHelpCard
        open={footerHelpOpen}
        versionLabel={`当前版本 ${versionWithChannelLabel}`}
        updateBadgeVisible={updateBadgeVisible}
        checkingUpdate={updateBusyAction === 'check'}
        qrItems={FOOTER_HELP_QR_ITEMS}
        onClose={() => setFooterHelpOpen(false)}
        onOpenAbout={handleOpenAboutDialog}
        onCheckForUpdates={() => void handleCheckForUpdates()}
        onPreviewQr={(item) => handleOpenAuthGateQrPreview(item, 'footer-help')}
      />

      <AboutDialog
        open={aboutDialogOpen}
        appName={APP_DISPLAY_NAME}
        subtitle={APP_SUBTITLE}
        brandIconSrc={OPENAI_BRAND_ICON_SRC}
        buildChannelLabel={updateChannelLabel}
        platformTags={platformTags}
        versionLabel={versionWithChannelLabel}
        updateState={updateState}
        updateBusyAction={updateBusyAction}
        links={ABOUT_VISIBLE_CONTACT_LINKS}
        qrItems={ABOUT_QR_ITEMS}
        products={ABOUT_PRODUCT_ITEMS}
        onOpenChange={setAboutDialogOpen}
        onCheckForUpdates={() => void handleCheckForUpdates()}
        onInstallUpdate={() => void handleDownloadAndInstallUpdate()}
        onOpenUpdateNotes={() => void handleOpenUpdateNotes()}
        onOpenExternal={(url) => void handleOpenExternal(url)}
        onCopyValue={(label, value) => void handleCopyValue(label, value)}
        onPreviewQr={(item) => handleOpenAuthGateQrPreview(item, 'about')}
      />

      <UpdateDialog
        open={updateDialogOpen}
        appName={APP_DISPLAY_NAME}
        updateState={updateState}
        busyAction={updateBusyAction}
        downloadCenterUrl={APP_DOWNLOAD_CENTER_URL}
        onOpenChange={setUpdateDialogOpen}
        onCheckForUpdates={() => void handleCheckForUpdates()}
        onInstallUpdate={() => void handleDownloadAndInstallUpdate()}
        onOpenNotes={() => void handleOpenUpdateNotes()}
        onOpenHelp={handleOpenUpdateSupport}
        onOpenExternal={(url) => void handleOpenExternal(url)}
      />

      <Dialog.Root open={avatarUploadOpen} onOpenChange={handleAvatarUploadOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="avatar-upload-dialog-overlay" />
          <Dialog.Content className="avatar-upload-dialog-panel">
            <header className="shell-dialog-header avatar-upload-dialog-panel__header">
              <div>
                <div className="shell-dialog-eyebrow">Avatar Upload</div>
                <Dialog.Title className="shell-dialog-title">上传头像</Dialog.Title>
                <Dialog.Description className="shell-dialog-description">
                  先拖入预览或手动选择图片，确认无误后再正式保存。系统会居中裁切并输出
                  {' '}192 × 192{' '}头像。
                </Dialog.Description>
              </div>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="shell-dialog-close"
                  aria-label="关闭头像上传卡片"
                >
                  <X size={18} />
                </button>
              </Dialog.Close>
            </header>

            <div className="shell-dialog-body avatar-upload-dialog-body">
              <div className="avatar-upload-dialog-grid">
                <section className="avatar-upload-card avatar-upload-card--preview">
                  <div className="avatar-upload-card__eyebrow">PREVIEW</div>
                  <h3 className="avatar-upload-card__title">{avatarPreviewTitle}</h3>
                  <p className="avatar-upload-card__copy">
                    这里展示最终保存时的圆形头像效果。未确认前不会写入本地。
                  </p>
                  <div className="avatar-upload-preview-frame">
                    {avatarPreviewSrc ? (
                      <div className="avatar-upload-preview-frame__crop-shell">
                        <img
                          className={`avatar-upload-preview-frame__image${avatarUploadDraft ? ' is-adjustable' : ''}`}
                          src={avatarPreviewSrc}
                          alt=""
                          style={avatarPreviewStyle}
                        />
                      </div>
                    ) : (
                      <span className="avatar-upload-preview-frame__fallback">
                        {userCenterSession.avatarText}
                      </span>
                    )}
                  </div>
                  <div className="avatar-upload-preview-caption">
                    <span>当前账号</span>
                    <strong>{userCenterSession.displayName}</strong>
                    <small>{userCenterSession.email ?? '未绑定邮箱'}</small>
                  </div>
                </section>

                <section className="avatar-upload-card avatar-upload-card--controls">
                  <div className="avatar-upload-card__eyebrow">UPLOAD</div>
                  <h3 className="avatar-upload-card__title">拖入上传或手动选择</h3>
                  <div
                    className={`avatar-upload-dropzone${avatarUploadDragging ? ' is-dragging' : ''}${avatarUploadBusy ? ' is-busy' : ''}`}
                    onDragOver={handleAvatarDragOver}
                    onDragLeave={handleAvatarDragLeave}
                    onDrop={handleAvatarDrop}
                  >
                    <ArrowUpCircle size={28} />
                    <strong>{avatarUploadBusy ? '正在处理图片...' : '把头像拖到这里'}</strong>
                    <span>支持 PNG / JPG / WEBP，最大 5 MB。</span>
                    <button
                      type="button"
                      className="sidebar-button"
                      onClick={handlePickAvatarFile}
                      disabled={avatarUploadBusy}
                    >
                      {avatarUploadDraft ? <Pencil size={16} /> : <Plus size={16} />}
                      {avatarUploadDraft ? '重新选择图片' : '手动上传图片'}
                    </button>
                  </div>

                  <div className="avatar-upload-metrics">
                    <div className="avatar-upload-metric">
                      <span>原始尺寸</span>
                      <strong>
                        {avatarUploadDraft
                          ? `${avatarUploadDraft.sourceWidth} × ${avatarUploadDraft.sourceHeight}`
                          : '等待选择'}
                      </strong>
                    </div>
                    <div className="avatar-upload-metric">
                      <span>原始体积</span>
                      <strong>
                        {avatarUploadDraft
                          ? formatAvatarFileSize(avatarUploadDraft.fileSize)
                          : '等待选择'}
                      </strong>
                    </div>
                    <div className="avatar-upload-metric">
                      <span>输出尺寸</span>
                      <strong>{AVATAR_OUTPUT_SIZE} × {AVATAR_OUTPUT_SIZE}</strong>
                    </div>
                    <div className="avatar-upload-metric">
                      <span>格式限制</span>
                      <strong>图片 / ≤ 5 MB</strong>
                    </div>
                  </div>

                  <div className="avatar-upload-file-meta">
                    <div>
                      <span>当前文件</span>
                      <strong>{avatarUploadDraft?.fileName ?? '尚未选择头像文件'}</strong>
                    </div>
                    <div>
                      <span>文件类型</span>
                      <strong>{avatarUploadDraft?.sourceType ?? 'image/*'}</strong>
                    </div>
                  </div>

                  {avatarUploadDraft ? (
                    <div className="avatar-upload-adjustment-summary">
                      <div className="avatar-upload-adjustment-summary__copy">
                        <strong>图片构图</strong>
                        <span>如需缩放或调整位置，请打开二级弹窗处理后再保存头像。</span>
                      </div>
                      <button
                        type="button"
                        className="sidebar-button sidebar-button--secondary"
                        onClick={() => handleAvatarAdjustOpenChange(true)}
                      >
                        <Pencil size={16} />
                        调整缩放与位置
                      </button>
                    </div>
                  ) : null}

                  <div className="avatar-upload-note-list">
                    <div className="avatar-upload-note-item">
                      <CheckCircle2 size={16} />
                      <span>点击“确认保存头像”后才会正式保存到当前账号本地头像。</span>
                    </div>
                    <div className="avatar-upload-note-item">
                      <CheckCircle2 size={16} />
                      <span>如需改构图，会另外弹出一张调整卡片，不会把控制条直接塞进当前主卡。</span>
                    </div>
                    <div className="avatar-upload-note-item">
                      <CheckCircle2 size={16} />
                      <span>关闭或取消本卡片，不会覆盖你现在正在使用的头像。</span>
                    </div>
                  </div>

                  {avatarUploadError ? (
                    <div className="avatar-upload-error">
                      <CircleAlert size={16} />
                      <span>{avatarUploadError}</span>
                    </div>
                  ) : null}
                </section>
              </div>
            </div>

            <footer className="shell-dialog-footer avatar-upload-dialog-footer">
              <div className="shell-dialog-footer__hint">
                头像只保存在当前账号本地资料里；如果你不点确认，系统不会写入新的头像记录。
              </div>
              <div className="shell-dialog-footer__actions">
                <button
                  type="button"
                  className="sidebar-button--secondary"
                  onClick={() => handleAvatarUploadOpenChange(false)}
                  disabled={avatarUploadBusy}
                >
                  取消
                </button>
                <button
                  type="button"
                  className="sidebar-button avatar-upload-dialog__primary"
                  onClick={() => void handleConfirmAvatarUpload()}
                  disabled={avatarUploadBusy || !avatarUploadDraft}
                >
                  {avatarUploadBusy ? <LoaderCircle size={16} className="spin" /> : <CheckCircle2 size={16} />}
                  确认保存头像
                </button>
              </div>
            </footer>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root open={avatarAdjustOpen} onOpenChange={handleAvatarAdjustOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="avatar-adjust-dialog-overlay" />
          <Dialog.Content className="avatar-adjust-dialog-panel">
            <header className="shell-dialog-header avatar-adjust-dialog-panel__header">
              <div>
                <div className="shell-dialog-eyebrow">Avatar Adjust</div>
                <Dialog.Title className="shell-dialog-title">调整头像构图</Dialog.Title>
                <Dialog.Description className="shell-dialog-description">
                  在这里单独调缩放和位置。确认后回写到上一张上传卡，再决定是否正式保存头像。
                </Dialog.Description>
              </div>
              <Dialog.Close asChild>
                <button type="button" className="shell-dialog-close" aria-label="关闭头像构图调整弹窗">
                  <X size={18} />
                </button>
              </Dialog.Close>
            </header>

            <div className="shell-dialog-body avatar-adjust-dialog-body">
              <div className="avatar-adjust-dialog-grid">
                <section className="avatar-upload-card avatar-upload-card--preview">
                  <div className="avatar-upload-card__eyebrow">CROP PREVIEW</div>
                  <h3 className="avatar-upload-card__title">构图预览</h3>
                  <p className="avatar-upload-card__copy">
                    这里看到的就是保存到头像位的最终取景效果。
                  </p>
                  <div className="avatar-upload-preview-frame avatar-adjust-dialog__preview-frame">
                    {avatarAdjustPreviewSrc ? (
                      <div className="avatar-upload-preview-frame__crop-shell">
                        <img
                          className={`avatar-upload-preview-frame__image${avatarAdjustDraft ? ' is-adjustable' : ''}`}
                          src={avatarAdjustPreviewSrc}
                          alt=""
                          style={avatarAdjustStyle}
                        />
                      </div>
                    ) : (
                      <span className="avatar-upload-preview-frame__fallback">
                        {userCenterSession.avatarText}
                      </span>
                    )}
                  </div>
                </section>

                <section className="avatar-upload-card avatar-upload-card--controls">
                  <div className="avatar-upload-card__eyebrow">ADJUST</div>
                  <h3 className="avatar-upload-card__title">缩放与位置</h3>
                  {avatarAdjustDraft && avatarAdjustMetrics ? (
                    <div className="avatar-upload-adjustments avatar-upload-adjustments--dialog">
                      <div className="avatar-upload-adjustment">
                        <div className="avatar-upload-adjustment__label-row">
                          <span>缩放</span>
                          <strong>{Math.round(avatarAdjustDraft.scale * 100)}%</strong>
                        </div>
                        <input
                          className="avatar-upload-adjustment__slider"
                          type="range"
                          min={avatarAdjustDraft.minScale}
                          max={avatarAdjustDraft.maxScale}
                          step="0.01"
                          value={avatarAdjustDraft.scale}
                          onChange={(event) => {
                            handleAvatarDraftChange({ scale: Number(event.target.value) });
                          }}
                        />
                      </div>

                      <div className="avatar-upload-adjustment">
                        <div className="avatar-upload-adjustment__label-row">
                          <span>水平位置</span>
                          <strong>{avatarAdjustDraft.offsetX}px</strong>
                        </div>
                        <input
                          className="avatar-upload-adjustment__slider"
                          type="range"
                          min={-avatarAdjustMetrics.maxOffsetX}
                          max={avatarAdjustMetrics.maxOffsetX}
                          step="1"
                          value={avatarAdjustDraft.offsetX}
                          onChange={(event) => {
                            handleAvatarDraftChange({ offsetX: Number(event.target.value) });
                          }}
                        />
                      </div>

                      <div className="avatar-upload-adjustment">
                        <div className="avatar-upload-adjustment__label-row">
                          <span>垂直位置</span>
                          <strong>{avatarAdjustDraft.offsetY}px</strong>
                        </div>
                        <input
                          className="avatar-upload-adjustment__slider"
                          type="range"
                          min={-avatarAdjustMetrics.maxOffsetY}
                          max={avatarAdjustMetrics.maxOffsetY}
                          step="1"
                          value={avatarAdjustDraft.offsetY}
                          onChange={(event) => {
                            handleAvatarDraftChange({ offsetY: Number(event.target.value) });
                          }}
                        />
                      </div>

                      <button
                        type="button"
                        className="sidebar-button sidebar-button--secondary avatar-upload-adjustment__reset"
                        onClick={handleResetAvatarDraftPosition}
                      >
                        重置缩放与位置
                      </button>
                    </div>
                  ) : (
                    <div className="avatar-adjust-dialog__empty">请先在上一张卡片中选择一张图片。</div>
                  )}
                </section>
              </div>
            </div>

            <footer className="shell-dialog-footer avatar-adjust-dialog-footer">
              <div className="shell-dialog-footer__hint">
                这里的调整只会先回写到上传卡预览，不会立即保存到账号头像。
              </div>
              <div className="shell-dialog-footer__actions">
                <button
                  type="button"
                  className="sidebar-button--secondary"
                  onClick={() => handleAvatarAdjustOpenChange(false)}
                >
                  取消
                </button>
                <button
                  type="button"
                  className="sidebar-button avatar-upload-dialog__primary"
                  onClick={handleConfirmAvatarAdjust}
                  disabled={!avatarAdjustDraft}
                >
                  <CheckCircle2 size={16} />
                  应用当前构图
                </button>
              </div>
            </footer>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root
        open={feedbackCenterOpen}
        onOpenChange={(open) => {
          setFeedbackCenterOpen(open);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="feedback-center-overlay" />
          <Dialog.Content className="feedback-center-dialog">
            <div className="feedback-center-dialog__header">
              <div className="feedback-center-dialog__brand">
                <span className="feedback-center-dialog__brand-icon">
                  <OpenAIKnotIcon />
                </span>
                <div className="feedback-center-dialog__brand-copy">
                  <div className="feedback-center-dialog__title-row">
                    <Dialog.Title>反馈中心</Dialog.Title>
                    <div className="feedback-center-dialog__title-actions">
                      {FOOTER_HELP_QR_ITEMS.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className="feedback-center-dialog__title-action"
                          onClick={() => handleOpenFeedbackQrPreview(item.id)}
                        >
                          {item.title}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Dialog.Description>
                    遇到登录、授权、更新或使用问题时，可在这里直接联系人工客服。
                  </Dialog.Description>
                </div>
              </div>
              <Dialog.Close asChild>
                <button type="button" className="feedback-center-dialog__close" aria-label="关闭反馈中心">
                  <X size={16} />
                </button>
              </Dialog.Close>
            </div>

            <div className="feedback-center-dialog__meta">
              <span>{authState.profile?.displayName ?? '未登录账号'}</span>
              <span>{authState.profile?.email ?? '登录后可发送到客服收件箱'}</span>
              {!feedbackComposerIsNewThread && feedbackConversationStatusLabel ? (
                <span
                  className={`feedback-center-dialog__status-pill feedback-center-dialog__status-pill--inline${
                    activeFeedbackThread ? ` is-${activeFeedbackThread.status}` : ''
                  }`}
                >
                  {feedbackConversationStatusLabel}
                </span>
              ) : null}
              <span>进行中 {authState.feedback.pendingCount}</span>
              <span>已回执 {authState.feedback.acknowledgedCount}</span>
            </div>

            <div className="feedback-center-dialog__layout is-chat-only">
              <section className="feedback-center-dialog__conversation is-chat-only">
                <div ref={feedbackMessageListRef} className="feedback-center-dialog__message-list">
                  {visibleFeedbackMessages.length ? (
                    visibleFeedbackMessages.map((message) => {
                      const isLatestVisibleMessage = message.id === latestVisibleFeedbackMessageId;
                      const messageStatus =
                        message.deliveryState === 'sending' ? (
                          <span className="feedback-center-dialog__message-status is-sending">
                            <LoaderCircle size={12} strokeWidth={2} className="spin" />
                            发送中
                          </span>
                        ) : message.deliveryState === 'failed' ? (
                          <span
                            className="feedback-center-dialog__message-status is-failed"
                            title={message.errorMessage ?? '发送失败'}
                          >
                            <CircleAlert size={12} strokeWidth={2} />
                            发送失败
                          </span>
                        ) : (
                          <span className="feedback-center-dialog__message-status">
                            {message.deliveryState === 'unread'
                              ? '未读'
                              : message.deliveryState === 'read'
                                ? '已读'
                                : '已发送'}
                          </span>
                        );

                      return (
                        <article
                          key={message.id}
                          className={`feedback-center-dialog__message-row${
                            message.sender === 'host' ? ' is-host' : ' is-user'
                          }`}
                        >
                          <span
                            className={`feedback-center-dialog__avatar${
                              message.sender === 'user' && avatarImageSrc ? ' has-image' : ''
                            }`}
                            aria-hidden="true"
                          >
                            {message.sender === 'host' ? (
                              <OpenAIKnotIcon />
                            ) : avatarImageSrc ? (
                              <img src={avatarImageSrc} alt="" />
                            ) : (
                              <span>{userCenterSession.avatarText}</span>
                            )}
                          </span>
                          {message.sender === 'user' && isLatestVisibleMessage ? messageStatus : null}
                          <div
                            className={`feedback-center-dialog__message${
                              message.sender === 'host' ? ' is-host' : ' is-user'
                            }`}
                          >
                            <div className="feedback-center-dialog__message-meta">
                              <strong>
                                {message.sender === 'host' ? FEEDBACK_SERVICE_DISPLAY_NAME : '我'}
                              </strong>
                              <span>{formatDateTime(message.createdAt)}</span>
                            </div>
                            <div className="feedback-center-dialog__message-body">{message.body}</div>
                          </div>
                          {message.sender === 'host' && isLatestVisibleMessage ? messageStatus : null}
                        </article>
                      );
                    })
                  ) : (
                    <div className="feedback-center-dialog__message-empty">
                      这条会话还没有更多往来记录，你现在发出的内容会作为首条消息保存。
                    </div>
                  )}
                </div>

                <div className="feedback-center-dialog__composer">
                  <textarea
                    className="feedback-center-dialog__textarea"
                    value={feedbackDraft}
                    onChange={(event) => setFeedbackDraft(event.target.value)}
                    onKeyDown={handleFeedbackTextareaKeyDown}
                    rows={4}
                  />

                  <div className="feedback-center-dialog__composer-actions">
                    <button
                      type="button"
                      className="sidebar-button sidebar-button--secondary"
                      onClick={() => setFeedbackCenterOpen(false)}
                      disabled={authBusyAction === 'feedback'}
                    >
                      关闭
                    </button>
                    <button
                      type="button"
                      className="sidebar-button"
                      onClick={() => void handleSubmitFeedback()}
                      disabled={authBusyAction === 'feedback' || !feedbackDraft.trim()}
                    >
                      {authBusyAction === 'feedback' ? (
                        <LoaderCircle size={16} strokeWidth={1.8} className="spin" />
                      ) : (
                        <Mail size={16} strokeWidth={1.8} />
                      )}
                      {feedbackComposerIsNewThread ? '发送反馈' : '发送回复'}
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <UserCenterDrawer
        open={userCenterOpen && !showStartupLoadingOverlay}
        session={userCenterSession}
        avatarImageSrc={avatarImageSrc}
        brandIconSrc={OPENAI_BRAND_ICON_SRC}
        brandFrameEnabled={userCenterBrandFrameEnabled}
        authMode={authMode}
        authForm={authForm}
        updateBadgeVisible={updateBadgeVisible}
        updateActionBusy={updateBusyAction === 'check'}
        authBusyAction={
          authBusyAction === 'login' ||
          authBusyAction === 'register' ||
          authBusyAction === 'forgot' ||
          authBusyAction === 'logout'
            ? authBusyAction
            : null
        }
        otpBusy={currentAuthOtpBusy}
        otpCooldownSeconds={currentAuthOtpCooldownSeconds}
        securityActionBusy={secondaryPasswordDialogBusy}
        onOpenChange={setUserCenterOpen}
        onAuthModeChange={handleAuthModeChange}
        onAuthFormChange={handleAuthFormChange}
        onSubmitAuth={() => void handleSubmitAuth()}
        onSendOtp={() => void handleSendAuthOtp()}
        onLogout={() => void handleLogout()}
        onEditAvatar={handleEditAvatar}
        onCopyDeviceCode={() => void handleCopyValue('设备编号', userCenterSession.deviceId)}
        onCopyFingerprint={() => void handleCopyValue('机器指纹', userCenterSession.fingerprintHash)}
        onOpenLicenseCenter={handleOpenLicenseCenter}
        onCheckForUpdates={() => void handleCheckForUpdates()}
        onOpenAbout={handleOpenAboutDialog}
        onOpenHelp={() => {
          setUserCenterOpen(false);
          setFooterHelpOpen(true);
        }}
        onRestartApp={() => {
          window.codexWorkspace.restartApp();
        }}
        onPreviewLoginScreen={handleOpenAuthGatePreview}
        onOpenSecondaryPasswordDialog={() => openSecondaryPasswordDialog({ mode: 'set' })}
        onOpenSecondaryPasswordEmailResetDialog={() =>
          openSecondaryPasswordDialog({ mode: 'reset-by-email' })
        }
        onVerifyCurrentDevice={() => openSecondaryPasswordDialog({ mode: 'verify-device' })}
        onBindScanLogin={handleOpenScanBinding}
        showPreviewLoginScreen={canAccessEngineerFeatures && localSimulationEnabled}
      />

      {(() => {
        const dialog = secondaryPasswordDialog;
        const confirmLabel =
          dialog?.mode === 'set'
            ? authState.security.secondaryPassword.enabled
              ? '保存新密码'
              : '启用保护'
            : dialog?.mode === 'reset-by-email'
              ? '重置二次密码'
            : '确认验证';
        const footerHint =
          dialog?.mode === 'verify-local-import'
            ? '非本人卡片请不要本地直接导入，改用主机远程批准；主机从客户端拉取卡片不受这条限制。'
            : dialog?.mode === 'reset-by-email'
              ? authState.profile?.email
                ? `验证码会发送到 ${authState.profile.email}。`
                : '当前账号需要先绑定邮箱后才能重置二次密码。'
            : dialog?.mode === 'set'
              ? ''
              : '验证通过后才会继续当前动作。';
        const leadingIcon =
          dialog?.mode === 'set'
            ? <KeyRound size={18} />
            : dialog?.mode === 'reset-by-email'
              ? <Mail size={18} />
              : <ShieldCheck size={18} />;

        return (
          <Dialog.Root
            open={Boolean(dialog)}
            onOpenChange={(open) => {
              if (!open) {
                closeSecondaryPasswordDialog();
              }
            }}
          >
            <Dialog.Portal>
              <Dialog.Overlay className="shell-dialog-overlay" />
              <Dialog.Content className="shell-dialog-panel shell-dialog-panel--secondary-password">
                <div className="shell-dialog-header">
                  <div>
                    <Dialog.Title className="shell-dialog-title">安全中心</Dialog.Title>
                  </div>
                  <Dialog.Close asChild>
                    <button
                      type="button"
                      className="shell-dialog-close"
                      aria-label="关闭二次密码弹窗"
                    >
                      <X size={16} />
                    </button>
                  </Dialog.Close>
                </div>

                <form
                  className="secondary-password-dialog__form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void handleConfirmSecondaryPasswordDialog();
                  }}
                >
                  <div className="shell-dialog-body secondary-password-dialog__body">
                    <section className="secondary-password-dialog__card secondary-password-dialog__card--plain">
                      <div className="secondary-password-dialog__card-head">
                        <span className="secondary-password-dialog__card-icon">{leadingIcon}</span>
                        <div>
                          <strong className="secondary-password-dialog__card-title">
                            {dialog?.mode === 'set'
                              ? '先启用当前账号的二次密码，再继续当前操作。'
                              : dialog?.mode === 'reset-by-email'
                                ? '通过绑定邮箱验证码重置二次密码。'
                              : '请输入当前账号的二次密码完成确认。'}
                          </strong>
                          {footerHint ? (
                            <p className="secondary-password-dialog__card-description">
                              {footerHint}
                            </p>
                          ) : null}
                        </div>
                      </div>

                      <div className="secondary-password-dialog__field-stack">
                        {dialog?.mode === 'set' ? (
                          <>
                            {authState.security.secondaryPassword.enabled ? (
                              <label className="secondary-password-dialog__field">
                                <span className="secondary-password-dialog__field-label">
                                  当前二次密码
                                </span>
                                <input
                                  className="license-input"
                                  type="password"
                                  placeholder="请输入当前二次密码"
                                  value={secondaryPasswordCurrentDraft}
                                  onChange={(event) =>
                                    setSecondaryPasswordCurrentDraft(event.target.value)
                                  }
                                />
                              </label>
                            ) : null}
                            <label className="secondary-password-dialog__field">
                              <span className="secondary-password-dialog__field-label">
                                新的二次密码
                              </span>
                              <input
                                className="license-input"
                                type="password"
                                placeholder="至少 4 位"
                                value={secondaryPasswordNextDraft}
                                onChange={(event) => setSecondaryPasswordNextDraft(event.target.value)}
                              />
                            </label>
                            <label className="secondary-password-dialog__field">
                              <span className="secondary-password-dialog__field-label">
                                再输入一次
                              </span>
                              <input
                                className="license-input"
                                type="password"
                                placeholder="再次确认新的二次密码"
                                value={secondaryPasswordConfirmDraft}
                                onChange={(event) =>
                                  setSecondaryPasswordConfirmDraft(event.target.value)
                                }
                              />
                            </label>
                          </>
                        ) : dialog?.mode === 'reset-by-email' ? (
                          <>
                            <label className="secondary-password-dialog__field">
                              <span className="secondary-password-dialog__field-label">绑定邮箱</span>
                              <input
                                className="license-input"
                                type="text"
                                value={authState.profile?.email ?? '未绑定邮箱'}
                                disabled
                              />
                            </label>
                            <label className="secondary-password-dialog__field">
                              <span className="secondary-password-dialog__field-label">邮箱验证码</span>
                              <div className="secondary-password-dialog__otp-row">
                                <input
                                  className="license-input"
                                  type="text"
                                  placeholder="输入邮箱验证码"
                                  value={secondaryPasswordEmailOtpDraft}
                                  onChange={(event) =>
                                    setSecondaryPasswordEmailOtpDraft(event.target.value)
                                  }
                                />
                                <button
                                  type="button"
                                  className="sidebar-button sidebar-button--secondary secondary-password-dialog__otp-button"
                                  onClick={() => void handleSendSecondaryPasswordResetOtp()}
                                  disabled={
                                    secondaryPasswordDialogBusy ||
                                    secondaryPasswordResetOtpBusy ||
                                    secondaryPasswordResetOtpCooldownSeconds > 0 ||
                                    !authState.profile?.email
                                  }
                                >
                                  {secondaryPasswordResetOtpBusy ? (
                                    <LoaderCircle size={14} className="spin" />
                                  ) : (
                                    <Mail size={14} />
                                  )}
                                  {secondaryPasswordResetOtpCooldownSeconds > 0
                                    ? `${secondaryPasswordResetOtpCooldownSeconds} 秒后重试`
                                    : secondaryPasswordResetOtpBusy
                                      ? '发送中...'
                                      : '发送验证码'}
                                </button>
                              </div>
                            </label>
                            <label className="secondary-password-dialog__field">
                              <span className="secondary-password-dialog__field-label">
                                新的二次密码
                              </span>
                              <input
                                className="license-input"
                                type="password"
                                placeholder="至少 4 位"
                                value={secondaryPasswordNextDraft}
                                onChange={(event) => setSecondaryPasswordNextDraft(event.target.value)}
                              />
                            </label>
                            <label className="secondary-password-dialog__field">
                              <span className="secondary-password-dialog__field-label">
                                再输入一次
                              </span>
                              <input
                                className="license-input"
                                type="password"
                                placeholder="再次确认新的二次密码"
                                value={secondaryPasswordConfirmDraft}
                                onChange={(event) =>
                                  setSecondaryPasswordConfirmDraft(event.target.value)
                                }
                              />
                            </label>
                          </>
                        ) : (
                          <label className="secondary-password-dialog__field">
                            <span className="secondary-password-dialog__field-label">二次密码</span>
                            <input
                              className="license-input"
                              type="password"
                              placeholder="请输入当前账号的二次密码"
                              value={secondaryPasswordVerifyDraft}
                              onChange={(event) =>
                                setSecondaryPasswordVerifyDraft(event.target.value)
                              }
                            />
                          </label>
                        )}
                      </div>

                      {secondaryPasswordDialogError ? (
                        <div className="secondary-password-dialog__error" role="alert">
                          <CircleAlert size={14} />
                          <span>{secondaryPasswordDialogError}</span>
                        </div>
                      ) : null}
                    </section>
                  </div>

                  <div className="shell-dialog-footer">
                    {dialog?.mode === 'set' ? null : (
                      <p className="shell-dialog-footer__hint">
                        验证成功后会继续执行当前导入、恢复或接卡操作。
                      </p>
                    )}
                    <div className="shell-dialog-footer__actions">
                      <button
                        type="button"
                        className="sidebar-button--secondary"
                        onClick={() => closeSecondaryPasswordDialog()}
                        disabled={secondaryPasswordDialogBusy}
                      >
                        取消
                      </button>
                      <button
                        type="submit"
                        className="sidebar-button"
                        disabled={secondaryPasswordDialogBusy}
                      >
                        {secondaryPasswordDialogBusy ? (
                          <>
                            <LoaderCircle size={14} strokeWidth={2} className="spin" />
                            处理中
                          </>
                        ) : (
                          confirmLabel
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        );
      })()}

      <AddProfileDialog
        open={addDialogOpen}
        mode="add"
        initialValues={addDialogInitialValues}
        discoveryHint={addDialogDiscoveryHint}
        busy={busyAction === 'add'}
        onOpenChange={(open) => {
          if (open) {
            setAddDialogOpen(true);
            return;
          }

          closeAddDialog();
        }}
        onPickAuthFile={handlePickAuthFile}
        onSubmit={handleAddProfile}
      />

      <AddProfileDialog
        open={editDialogOpen}
        mode="edit"
        initialProfile={editTarget}
        busy={busyAction === 'edit'}
        footerAction={
          editTarget
            ? {
                label: '发送当前卡片',
                onClick: () => {
                  const targetProfile = editTarget;
                  setEditDialogOpen(false);
                  setEditTarget(null);
                  openIssueTransferDialog(targetProfile ? [targetProfile.id] : []);
                },
              }
            : null
        }
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) {
            setEditTarget(null);
          }
        }}
        onPickAuthFile={handlePickAuthFile}
        onSubmit={handleEditProfile}
      />

      <Dialog.Root
        open={issueTransferDialogOpen}
        onOpenChange={(open) => {
          setIssueTransferDialogOpen(open);
          if (!open) {
            setIssueTransferTargetAccount('');
            setIssueTransferNote('');
            setIssueTransferSelectedProfileIds([]);
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="shell-dialog-overlay" />
          <Dialog.Content className="shell-dialog-panel cloud-sync-dialog-panel">
            <header className="shell-dialog-header">
              <div>
                <div className="shell-dialog-eyebrow">Profile Transfer</div>
                <Dialog.Title className="shell-dialog-title">发送空间卡片</Dialog.Title>
                <Dialog.Description className="shell-dialog-description">
                  输入目标用户邮箱或 ID，再在同一个面板里勾选要发送的卡片。接收方会看到同样聚合的确认弹窗。
                </Dialog.Description>
              </div>
              <Dialog.Close asChild>
                <button type="button" className="shell-dialog-close" aria-label="关闭发送卡片弹窗">
                  <X size={16} />
                </button>
              </Dialog.Close>
            </header>

            <div className="shell-dialog-body cloud-sync-dialog-body">
              <section className="license-input-card selection-dialog__form-card">
                <div className="selection-dialog__field-grid">
                  <label className="secondary-password-dialog__field">
                    <span className="secondary-password-dialog__field-label">目标用户邮箱 / ID</span>
                    <input
                      className="license-input"
                      type="text"
                      placeholder="例如 someone@example.com 或 user_xxx"
                      value={issueTransferTargetAccount}
                      onChange={(event) => setIssueTransferTargetAccount(event.target.value)}
                    />
                  </label>
                  <label className="secondary-password-dialog__field">
                    <span className="secondary-password-dialog__field-label">备注</span>
                    <input
                      className="license-input"
                      type="text"
                      placeholder="给接收方看的备注"
                      value={issueTransferNote}
                      onChange={(event) => setIssueTransferNote(event.target.value)}
                    />
                  </label>
                </div>
              </section>

              <section className="selection-dialog__summary">
                <div className="selection-dialog__summary-card">
                  <span>可发送</span>
                  <strong>{issueTransferEligibleSnapshots.length}</strong>
                </div>
                <div className="selection-dialog__summary-card">
                  <span>已勾选</span>
                  <strong>{issueTransferSelectedProfileIds.length}</strong>
                </div>
                <div className="selection-dialog__summary-card">
                  <span>发送方</span>
                  <strong>{authState.profile?.email ?? authState.profile?.id ?? '未登录'}</strong>
                </div>
              </section>

              <div className="selection-dialog__list">
                {issueTransferEligibleSnapshots.map((snapshot) => {
                  const selected = issueTransferSelectedProfileIds.includes(snapshot.id);
                  return (
                    <label
                      key={snapshot.id}
                      className={`selection-dialog__card${selected ? ' is-selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleSelectionId(setIssueTransferSelectedProfileIds, snapshot.id)}
                      />
                      <div className="selection-dialog__card-main">
                        <div className="selection-dialog__card-title-row">
                          <strong>{snapshot.workspaceName ?? snapshot.name}</strong>
                          <span className="selection-dialog__card-badge">
                            {mapPlanLabel(snapshot.planType)}
                          </span>
                        </div>
                        <div className="selection-dialog__meta-row">
                          <span>{snapshot.name}</span>
                          <span>{formatDateTime(snapshot.updatedAt)}</span>
                          <span>
                            {snapshot.overallRemainingPercent === null
                              ? '额度待识别'
                              : `剩余 ${snapshot.overallRemainingPercent}%`}
                          </span>
                        </div>
                        <div className="selection-dialog__note">
                          {getVisibleNoteFromText(snapshot.notes) || '未填写备注'}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <footer className="shell-dialog-footer">
              <p className="shell-dialog-footer__hint">
                发送后不会删除你的本地卡片，只会向对方创建待确认的接卡记录。
              </p>
              <div className="shell-dialog-footer__actions">
                <button
                  type="button"
                  className="sidebar-button--secondary"
                  disabled={issueTransferBusy}
                  onClick={() => setIssueTransferDialogOpen(false)}
                >
                  取消
                </button>
                <button
                  type="button"
                  className="sidebar-button"
                  disabled={issueTransferBusy}
                  onClick={() => {
                    void handleSubmitIssueTransfer();
                  }}
                >
                  {issueTransferBusy ? (
                    <>
                      <LoaderCircle size={14} strokeWidth={2} className="spin" />
                      发送中
                    </>
                  ) : (
                    '发送卡片'
                  )}
                </button>
              </div>
            </footer>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root
        open={batchNoteDialogOpen}
        onOpenChange={(open) => {
          if (open) {
            setBatchNoteDialogOpen(true);
            return;
          }

          closeBatchNoteDialog();
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="shell-dialog-overlay" />
          <Dialog.Content className="shell-dialog-panel batch-note-dialog-panel">
            <header className="shell-dialog-header">
              <div>
                <div className="shell-dialog-eyebrow">Batch Actions</div>
                <Dialog.Title className="shell-dialog-title">批量操作</Dialog.Title>
                <Dialog.Description className="shell-dialog-description">
                  只处理当前筛选结果，可以统一备注，也可以批量启用或停用卡片。
                </Dialog.Description>
              </div>
              <Dialog.Close asChild>
                <button type="button" className="shell-dialog-close" aria-label="关闭批量操作">
                  <X size={16} />
                </button>
              </Dialog.Close>
            </header>

            <div className="shell-dialog-body batch-note-dialog-body">
              <section className="batch-note-dialog-summary">
                <div className="batch-note-dialog-summary__metrics">
                  <div className="batch-note-dialog-summary__metric">
                    <span>当前可见</span>
                    <strong>{batchFilteredEligibleProfiles.length} 张</strong>
                  </div>
                  <div className="batch-note-dialog-summary__metric">
                    <span>当前模式</span>
                    <strong>
                      {batchActionMode === 'notes'
                        ? '批量备注'
                        : batchActionMode === 'enable'
                          ? '批量启用'
                          : batchActionMode === 'disable'
                            ? '批量停用'
                            : '批量删除'}
                    </strong>
                  </div>
                  <div className="batch-note-dialog-summary__metric">
                    <span>当前已选</span>
                    <strong>{batchNoteSelectedProfileIds.length} 张</strong>
                  </div>
                </div>
                <div className="batch-note-dialog-summary__actions">
                  <button
                    type="button"
                    className="sidebar-button--secondary"
                    onClick={() =>
                      setBatchNoteSelectedProfileIds(
                        batchFilteredEligibleProfiles.map((profile) => profile.id),
                      )
                    }
                  >
                    全选当前筛选
                  </button>
                  <button
                    type="button"
                    className="sidebar-button--secondary"
                    onClick={() => setBatchNoteSelectedProfileIds(getBatchDefaultProfileIds(batchActionMode))}
                  >
                    {batchActionMode === 'notes'
                      ? '预选空备注'
                      : batchActionMode === 'enable'
                        ? '预选已停用'
                        : batchActionMode === 'disable'
                          ? '预选可停用'
                          : '删除需手选'}
                  </button>
                  <button
                    type="button"
                    className="sidebar-button--secondary"
                    onClick={() => setBatchNoteSelectedProfileIds([])}
                  >
                    清空选择
                  </button>
                </div>
              </section>

              <div className="batch-note-dialog-grid">
                <section className="batch-note-dialog-section">
                  <div className="batch-note-dialog-section__header">
                    <strong>选择卡片</strong>
                    <span>
                      {batchActionMode === 'notes'
                        ? '勾选后会统一覆盖这些卡片当前备注。'
                        : batchActionMode === 'enable'
                          ? '勾选后会统一清掉手动停用或系统 auto-disabled 状态。'
                          : batchActionMode === 'disable'
                            ? '勾选后会统一把这些卡片标记成手动停用。'
                            : '勾选后会批量删除这些卡片，并同步删除对应本地文件夹和 auth.json。'}
                    </span>
                  </div>
                  <div className="batch-note-dialog-filter-row">
                    <span className="batch-note-dialog-filter-row__label">状态筛选</span>
                    <div className="batch-note-dialog-filter-row__actions">
                      {(
                        [
                          { key: 'all', label: '全部' },
                          { key: 'enabled', label: '可用' },
                          { key: 'disabled', label: '已停用' },
                        ] satisfies Array<{ key: BatchProfileSelectionFilter; label: string }>
                      ).map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          className={`sidebar-button--secondary batch-note-dialog-filter-button${
                            batchSelectionFilter === option.key ? ' is-active' : ''
                          }`}
                          onClick={() => {
                            setBatchSelectionFilter(option.key);
                            setBatchNoteSelectedProfileIds(
                              getBatchDefaultProfileIds(batchActionMode, option.key),
                            );
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="batch-note-dialog-list" role="list">
                    {batchFilteredEligibleProfiles.length === 0 ? (
                      <div className="batch-note-dialog-empty">
                        当前状态筛选下没有可处理的卡片。可以切回“全部”或换成另一种状态再看。
                      </div>
                    ) : (
                      batchFilteredEligibleProfiles.map((profile) => {
                        const checked = batchNoteSelectedProfileIds.includes(profile.id);
                        const currentNote = getProfileCustomNote(profile);
                        return (
                          <label
                            key={profile.id}
                            className={`batch-note-dialog-item${checked ? ' is-selected' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(event) => {
                                const nextChecked = event.target.checked;
                                setBatchNoteSelectedProfileIds((current) => {
                                  if (nextChecked) {
                                    return current.includes(profile.id)
                                      ? current
                                      : [...current, profile.id];
                                  }

                                  return current.filter((profileId) => profileId !== profile.id);
                                });
                              }}
                            />
                            <div className="batch-note-dialog-item__main">
                              <div className="batch-note-dialog-item__title-row">
                                <strong>{getProfileDisplayName(profile)}</strong>
                                <span>{profile.storageDirName}</span>
                              </div>
                              <p>
                                {batchActionMode === 'notes'
                                  ? currentNote || '当前还没有自定义备注。'
                                  : profile.manuallyDisabled
                                    ? '当前状态：手动停用'
                                    : profile.autoDisabled
                                      ? '当前状态：系统自动停用'
                                      : '当前状态：可正常使用'}
                              </p>
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>
                </section>

                <section className="batch-note-dialog-section">
                  <div className="batch-note-dialog-section__header">
                    <strong>批量动作</strong>
                    <span>先选动作类型，再决定要处理哪一批卡片。</span>
                  </div>
                  <label className="batch-note-dialog-field">
                    <span>动作类型</span>
                    <select
                      className="plan-select"
                      value={batchActionMode}
                      onChange={(event) => {
                        const nextMode = event.target.value as BatchProfileActionMode;
                        const nextFilter = getBatchSelectionFilterDefault(nextMode);
                        setBatchActionMode(nextMode);
                        setBatchSelectionFilter(nextFilter);
                        setBatchNoteSelectedProfileIds(getBatchDefaultProfileIds(nextMode, nextFilter));
                      }}
                    >
                      <option value="notes">批量备注</option>
                      <option value="enable">批量启用</option>
                      <option value="disable">批量停用</option>
                      <option value="delete">批量删除卡片</option>
                    </select>
                  </label>
                  {batchActionMode === 'notes' ? (
                    <label className="batch-note-dialog-field">
                      <span>备注内容</span>
                      <textarea
                        className="batch-note-dialog-field__input"
                        value={batchNoteDraft}
                        onChange={(event) => setBatchNoteDraft(event.target.value)}
                        placeholder="例如：3 月导入 / 团队共享 / 备用池"
                      />
                    </label>
                  ) : (
                    <div className="batch-note-dialog-action-card">
                      <strong>
                        {batchActionMode === 'enable'
                          ? '批量启用说明'
                          : batchActionMode === 'disable'
                            ? '批量停用说明'
                            : '批量删除说明'}
                      </strong>
                      <p>
                        {batchActionMode === 'enable'
                          ? '会统一取消手动停用，并清掉系统记录的 auto-disabled 标记。适合探活误判后一次恢复多张卡片。'
                          : batchActionMode === 'disable'
                            ? '会统一把所选卡片标记成手动停用。卡片和目录都会保留，只是不再参与切换。'
                            : '会逐张彻底删除所选卡片，并同步删掉对应本地目录与 auth.json，删除后不会再被重新识别回来。'}
                      </p>
                    </div>
                  )}
                </section>
              </div>
            </div>

            <footer className="shell-dialog-footer">
              <div className="shell-dialog-footer__hint">
                {batchActionMode === 'notes'
                  ? '当前按你的筛选结果批量处理备注；如果要换一批卡片，先调整搜索或套餐筛选再打开。'
                  : batchActionMode === 'delete'
                    ? '删除会同时移除卡片和对应本地文件；如果要换一批卡片，先调整搜索或套餐筛选再打开。'
                    : '当前按你的筛选结果批量改启停状态；如果要换一批卡片，先调整搜索或套餐筛选再打开。'}
              </div>
              <div className="shell-dialog-footer__actions">
                <Dialog.Close asChild>
                  <button type="button" className="sidebar-button--secondary">
                    取消
                  </button>
                </Dialog.Close>
                <button
                  type="button"
                  className="sidebar-button"
                  onClick={() => void handleBatchApplyProfileAction()}
                  disabled={batchActionBusy}
                >
                  {batchActionBusy ? (
                    <LoaderCircle size={16} strokeWidth={1.8} className="spin" />
                  ) : (
                    batchActionMode === 'delete' ? <Trash2 size={16} /> : <CheckCircle2 size={16} />
                  )}
                  {batchActionMode === 'notes'
                    ? '保存批量备注'
                    : batchActionMode === 'enable'
                      ? '执行批量启用'
                      : batchActionMode === 'disable'
                        ? '执行批量停用'
                        : '执行批量删除'}
                </button>
              </div>
            </footer>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <LicenseCenterDialog
        open={licenseCenterOpen}
        snapshot={licenseCenterSnapshot}
        busyAction={
          authBusyAction === 'activation' ||
          authBusyAction === 'reward' ||
          authBusyAction === 'request-invite' ||
          authBusyAction === 'claim-reward'
            ? authBusyAction
            : null
        }
        onOpenChange={setLicenseCenterOpen}
        onCopyInviteCode={() =>
          void handleCopyValue(
            '邀请码',
            licenseCenterSnapshot.requestedInviteCode ?? licenseCenterSnapshot.inviteCode,
          )
        }
        onApplyActivationCode={(code) => void handleApplyActivationCode(code)}
        onApplyRewardCode={(code) => void handleApplyRewardCode(code)}
        onRequestInviteCode={() => void handleRequestInviteCode()}
        onClaimInviteReward={() => void handleClaimInviteReward()}
      />

      <ConfirmActionDialog
        open={Boolean(pendingLocalImportOverwrite)}
        layout="workspace"
        eyebrow="本地导入覆盖"
        title={
          pendingLocalImportOverwrite
            ? `检测到 ${pendingLocalImportOverwrite.duplicateCandidates.length} 张本地已有空间`
            : '检测到本地已有空间'
        }
        description={
          pendingLocalImportOverwrite
            ? '同一张本地空间卡片已经存在。确认覆盖会按拖入文件重写本地 auth 与空间信息；取消则终止本次导入。'
            : ''
        }
        confirmLabel="覆盖已有"
        cancelLabel="取消"
        busy={busyAction === 'import'}
        onConfirm={() => {
          void resolvePendingLocalImportOverwrite(true);
        }}
        onOpenChange={(open) => {
          if (!open) {
            void resolvePendingLocalImportOverwrite(false);
          }
        }}
        extraContent={
          pendingLocalImportOverwrite ? (
            <div className="flex flex-col gap-3">
              {pendingLocalImportOverwrite.duplicateCandidates.slice(0, 4).map((candidate) => (
                <div
                  key={candidate.id}
                  className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4 overflow-hidden rounded-[18px] border border-white/10 bg-white/[0.03] px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="break-all text-sm font-semibold leading-5 text-white">
                      {candidate.package.sourceWorkspaceName ||
                        candidate.package.sourceProfileName ||
                        candidate.fileName}
                    </div>
                    <div className="mt-1 space-y-1 text-xs leading-5 text-slate-400">
                      <div className="truncate">本地：{candidate.matchedProfileName || '本地空间'}</div>
                      <div className="break-all text-slate-500">导入：{candidate.fileName}</div>
                    </div>
                  </div>
                  <div className="inline-flex shrink-0 items-center rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                    {candidate.sourceKind === 'zip-entry'
                      ? 'ZIP'
                      : candidate.sourceKind === 'manual-text'
                        ? '文本'
                        : 'JSON'}
                  </div>
                </div>
              ))}
              {pendingLocalImportOverwrite.duplicateCandidates.length > 4 ? (
                <div className="text-xs text-slate-500">
                  其余 {pendingLocalImportOverwrite.duplicateCandidates.length - 4} 张会按同样规则处理。
                </div>
              ) : null}
            </div>
          ) : null
        }
      />

      <ConfirmActionDialog
        open={Boolean(pendingAddOverwrite)}
        layout="workspace"
        eyebrow="覆盖同命名空间"
        title={
          pendingAddOverwrite
            ? `发现同命名空间：${getProfileDisplayName(pendingAddOverwrite.target)}`
            : '发现同命名空间'
        }
        description={
          pendingAddOverwrite
            ? `当前准备导入的空间名称与已有空间“${getProfileDisplayName(pendingAddOverwrite.target)}”相同。确认后会直接覆盖原空间的 auth、套餐和备注，不会再额外新增一张卡片。`
            : ''
        }
        confirmLabel="确认覆盖"
        cancelLabel="继续编辑"
        busy={busyAction === 'add'}
        onConfirm={() => {
          void handleConfirmAddOverwrite();
        }}
        onOpenChange={(open) => {
          if (!open) {
            setPendingAddOverwrite(null);
          }
        }}
        extraContent={
          pendingAddOverwrite ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">已有空间</div>
                <div className="mt-2 text-sm font-semibold text-white">
                  {getProfileDisplayName(pendingAddOverwrite.target)}
                </div>
                <div className="mt-1 text-xs text-slate-400">
                  套餐 {mapPlanLabel(pendingAddOverwrite.target.planType)} / 目录{' '}
                  {pendingAddOverwrite.target.storageDirName}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">准备导入</div>
                <div className="mt-2 text-sm font-semibold text-white">
                  {pendingAddOverwrite.values.name}
                </div>
                <div className="mt-1 text-xs text-slate-400">
                  套餐 {mapPlanLabel(pendingAddOverwrite.values.planType)} / 文件{' '}
                  {pendingAddOverwrite.values.authSourcePath}
                </div>
              </div>
            </div>
          ) : null
        }
      />

      <ConfirmActionDialog
        open={Boolean(pendingProfileTransferPrompt)}
        layout="transfer"
        eyebrow="空间卡片传送"
        title={
          authState.pendingProfileTransfers.length > 0
            ? `收到 ${authState.pendingProfileTransfers.length} 张空间卡片`
            : '收到空间卡片'
        }
        description={
          authState.pendingProfileTransfers.length > 0
            ? '请在同一个面板里勾选要接收的卡片。确认后会批量导入本地卡片池，拒绝只会关闭所选传卡记录。'
            : ''
        }
        confirmLabel="确认接收所选"
        showCancel={false}
        busy={pendingProfileTransferBusy}
        onConfirm={() => {
          void handleRespondPendingProfileTransfer('accept');
        }}
        onOpenChange={() => {}}
        extraContent={
          authState.pendingProfileTransfers.length > 0 ? (
            <div className="selection-dialog">
              <section className="selection-dialog__summary">
                <div className="selection-dialog__summary-card">
                  <span>待处理</span>
                  <strong>{authState.pendingProfileTransfers.length}</strong>
                </div>
                <div className="selection-dialog__summary-card">
                  <span>已勾选</span>
                  <strong>{pendingTransferSelectionIds.length}</strong>
                </div>
                <div className="selection-dialog__summary-card">
                  <span>导入目标</span>
                  <strong>{authState.device.deviceId}</strong>
                </div>
              </section>

              <div className="selection-dialog__list">
                {authState.pendingProfileTransfers.map((entry) => {
                  const selected = pendingTransferSelectionIds.includes(entry.id);
                  return (
                    <label
                      key={entry.id}
                      className={`selection-dialog__card${selected ? ' is-selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleSelectionId(setPendingTransferSelectionIds, entry.id)}
                      />
                      <div className="selection-dialog__card-main">
                        <div className="selection-dialog__card-title-row">
                          <strong>{entry.sourceWorkspaceName ?? entry.sourceProfileName}</strong>
                          <span className="selection-dialog__card-badge">
                            {mapPlanLabel(entry.sourcePlanType)}
                          </span>
                        </div>
                        <div className="selection-dialog__meta-row">
                          <span>来源 {entry.senderHostLabel}</span>
                          <span>{formatDateTime(entry.createdAt)}</span>
                          <span>目标 {entry.targetDeviceId ?? authState.device.deviceId}</span>
                        </div>
                        <div className="selection-dialog__note">
                          {entry.note?.trim() || '未附带备注'}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="profile-transfer-dialog__decision">
                <div className="profile-transfer-dialog__decision-copy">
                  勾选后会在当前设备批量接收这些卡片。需要立刻切过去时，可直接使用“接收并切换”。
                </div>
                <div className="profile-transfer-dialog__decision-actions">
                  <button
                    type="button"
                    className="sidebar-button"
                    disabled={pendingProfileTransferBusy}
                    onClick={() => {
                      void handleRespondPendingProfileTransfer('accept', true);
                    }}
                  >
                    接收并切换
                  </button>
                  <button
                    type="button"
                    className="sidebar-button sidebar-button--secondary"
                    disabled={pendingProfileTransferBusy}
                    onClick={() => {
                      void handleRespondPendingProfileTransfer('decline');
                    }}
                  >
                    拒绝所选
                  </button>
                </div>
              </div>
            </div>
          ) : null
        }
      />

      <Dialog.Root
        open={!IS_LOCAL_FAST_IMPORT_BUILD && legacyCloudBackupReminderOpen}
        onOpenChange={setLegacyCloudBackupReminderOpen}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="shell-dialog-overlay" />
          <Dialog.Content className="shell-dialog-panel shell-dialog-panel--update">
            <header className="shell-dialog-header">
              <div>
                <div className="secondary-password-dialog__eyebrow-row">
                  <span className="shell-dialog-eyebrow">账号安全</span>
                  <span className="secondary-password-dialog__mode-pill">云备份升级</span>
                </div>
                <Dialog.Title className="shell-dialog-title">
                  云备份现在需要先启用二次密码
                </Dialog.Title>
              </div>
              <Dialog.Close asChild>
                <button type="button" className="shell-dialog-close" aria-label="关闭云备份提醒">
                  <X size={16} />
                </button>
              </Dialog.Close>
            </header>

            <div className="shell-dialog-body secondary-password-dialog__body">
              <section className="license-input-card secondary-password-dialog__card">
                <div className="legacy-cloud-reminder-dialog__grid">
                  <section className="legacy-cloud-reminder-dialog__item">
                    <div className="legacy-cloud-reminder-dialog__label">升级后规则</div>
                    <div className="legacy-cloud-reminder-dialog__value">
                      云备份已改为强制绑定二次密码
                    </div>
                  </section>
                  <section className="legacy-cloud-reminder-dialog__item">
                    <div className="legacy-cloud-reminder-dialog__label">你现在需要做的事</div>
                    <div className="legacy-cloud-reminder-dialog__value">到用户中心启用二次密码</div>
                  </section>
                </div>
              </section>
            </div>

            <footer className="shell-dialog-footer">
              <div className="shell-dialog-footer__actions">
                <Dialog.Close asChild>
                  <button type="button" className="sidebar-button--secondary">
                    稍后处理
                  </button>
                </Dialog.Close>
                <button
                  type="button"
                  className="sidebar-button"
                  onClick={() => {
                    setLegacyCloudBackupReminderOpen(false);
                    setUserCenterOpen(true);
                    openSecondaryPasswordDialog({ mode: 'set' });
                  }}
                >
                  立即启用二次密码
                </button>
              </div>
            </footer>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ConfirmActionDialog
        open={!IS_LOCAL_FAST_IMPORT_BUILD && Boolean(newDeviceCloudRestorePrompt)}
        title={
          newDeviceCloudRestorePrompt
            ? `识别到云端有 ${newDeviceCloudRestoreEntries.length} 张卡片，是否同步到本机？`
            : '是否同步云端卡片？'
        }
        description="确认后会按云端内容快速同步到当前电脑，弹窗关闭后后台继续执行。"
        layout="slim"
        confirmLabel="确认同步"
        cancelLabel="暂不处理"
        onConfirm={handleConfirmNewDeviceCloudRestore}
        onOpenChange={(open) => {
          if (!open) {
            setNewDeviceCloudRestorePrompt(null);
          }
        }}
      />

      <ConfirmActionDialog
        open={!IS_LOCAL_FAST_IMPORT_BUILD && Boolean(pendingCloudBackupPrompt)}
        layout="transfer"
        eyebrow="云端备份"
        title={
          pendingCloudBackupPrompt
            ? `你的账号在 ${pendingCloudBackupPrompt.sourceHostLabel} 留有空间备份`
            : '检测到云端空间备份'
        }
        description={
          pendingCloudBackupPrompt
            ? `登录到新设备后，可以把这张云端空间直接接入当前卡片池。`
            : ''
        }
        confirmLabel="接入到卡片池"
        showCancel={false}
        busy={pendingCloudBackupBusy}
        onConfirm={() => {
          void handleRestoreCloudProfileBackup(false);
        }}
        onOpenChange={(open) => {
          if (!open && pendingCloudBackupPrompt && !pendingCloudBackupBusy) {
            setPendingCloudBackupPrompt(null);
          }
        }}
        extraContent={
          pendingCloudBackupPrompt ? (
            <div className="profile-transfer-dialog profile-transfer-dialog--backup">
              <div className="profile-transfer-dialog__hero">
                <section className="profile-transfer-dialog__hero-card">
                  <div className="profile-transfer-dialog__eyebrow">云端保留</div>
                  <h3 className="profile-transfer-dialog__hero-title">
                    {pendingCloudBackupPrompt.sourceWorkspaceName ??
                      pendingCloudBackupPrompt.sourceProfileName}
                  </h3>
                  <p className="profile-transfer-dialog__hero-subtitle">
                    Profile {pendingCloudBackupPrompt.sourceProfileName} / 套餐{' '}
                    {mapPlanLabel(pendingCloudBackupPrompt.sourcePlanType)}
                  </p>
                  <div className="profile-transfer-dialog__pill-row">
                    <span className="profile-transfer-dialog__pill">
                      设备 {pendingCloudBackupPrompt.sourceDeviceId}
                    </span>
                    <span className="profile-transfer-dialog__pill">
                      {formatDateTime(pendingCloudBackupPrompt.updatedAt)}
                    </span>
                  </div>
                </section>

                <section className="profile-transfer-dialog__hero-card profile-transfer-dialog__hero-card--target">
                  <div className="profile-transfer-dialog__eyebrow">当前接入</div>
                  <div className="profile-transfer-dialog__metric-grid">
                    <div className="profile-transfer-dialog__metric">
                      <span>来源主机</span>
                      <strong>{pendingCloudBackupPrompt.sourceHostLabel}</strong>
                    </div>
                    <div className="profile-transfer-dialog__metric">
                      <span>机器名</span>
                      <strong>{pendingCloudBackupPrompt.sourceMachineName}</strong>
                    </div>
                    <div className="profile-transfer-dialog__metric">
                      <span>导入结果</span>
                      <strong>新增到本地卡片池</strong>
                    </div>
                  </div>
                </section>
              </div>

              <div className="profile-transfer-dialog__detail-grid">
                <section className="profile-transfer-dialog__detail-card">
                  <div className="profile-transfer-dialog__detail-label">指纹摘要</div>
                  <div className="profile-transfer-dialog__detail-value">
                    {pendingCloudBackupPrompt.sourceFingerprintSummary}
                  </div>
                  <p className="profile-transfer-dialog__detail-copy">
                    这张卡片来自同一在线账号在另一台设备上的最近快照。
                  </p>
                </section>
                <section className="profile-transfer-dialog__detail-card">
                  <div className="profile-transfer-dialog__detail-label">附加备注</div>
                  <div className="profile-transfer-dialog__detail-value">
                    {pendingCloudBackupPrompt.note ?? '未附带备注'}
                  </div>
                  <p className="profile-transfer-dialog__detail-copy">
                    现在接入后，可和本地卡片一样参与切换与推荐。
                  </p>
                </section>
              </div>

              <div className="profile-transfer-dialog__decision">
                <div className="profile-transfer-dialog__decision-copy">
                  如果这是你刚刚在另一台机器上用过的空间，可以直接一键接入并马上切过去继续使用。
                </div>
                <div className="profile-transfer-dialog__decision-actions">
                  <button
                    type="button"
                    className="sidebar-button"
                    disabled={pendingCloudBackupBusy}
                    onClick={() => {
                      void handleRestoreCloudProfileBackup(true);
                    }}
                  >
                    直接接入并使用
                  </button>
                  <button
                    type="button"
                    className="sidebar-button sidebar-button--secondary"
                    disabled={pendingCloudBackupBusy}
                    onClick={() => {
                      setPendingCloudBackupPrompt(null);
                    }}
                  >
                    稍后再说
                  </button>
                </div>
              </div>
            </div>
          ) : null
        }
      />

      <Dialog.Root
        open={!IS_LOCAL_FAST_IMPORT_BUILD && Boolean(cloudSyncDialog)}
        onOpenChange={(open) => {
          if (!open) {
            resetCloudSyncDialogState();
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="shell-dialog-overlay" />
          <Dialog.Content className="shell-dialog-panel cloud-sync-dialog-panel">
            <header className="shell-dialog-header">
              <div>
                <Dialog.Title className="shell-dialog-title">
                  {cloudSyncDialog?.step === 'confirm' ? '确认本次同步内容' : '选择本次云同步卡片'}
                </Dialog.Title>
              </div>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="shell-dialog-close shell-dialog-close--plain"
                  aria-label="关闭云同步弹窗"
                >
                  <X size={16} />
                </button>
              </Dialog.Close>
            </header>

            <div className="shell-dialog-body cloud-sync-dialog-body">
              {cloudSyncDialog?.step === 'confirm' ? (
                <div className="cloud-sync-compare">
                  <section className="cloud-dialog-summary-row cloud-dialog-summary-row--three">
                    <div className="cloud-dialog-summary-item">
                      <span>本次操作</span>
                      <strong>
                        {cloudSyncDialog.action === 'download-cloud-to-device'
                          ? '下载覆盖本地'
                          : cloudSyncDialog.action === 'merge-local-to-cloud'
                            ? '更新云端'
                            : '覆盖云端'}
                      </strong>
                    </div>
                    <div className="cloud-dialog-summary-item">
                      <span>本地已选</span>
                      <strong>{selectedCloudSyncLocalSnapshots.length}</strong>
                    </div>
                    <div className="cloud-dialog-summary-item">
                      <span>云端已选</span>
                      <strong>{selectedCloudSyncEntries.length}</strong>
                    </div>
                  </section>

                  <div className="cloud-sync-compare__list">
                    {cloudSyncDialog.action === 'download-cloud-to-device'
                      ? selectedCloudSyncEntries.map((entry) => {
                          const row =
                            cloudBackupCenterRows.find((candidate) => candidate.entry.id === entry.id) ?? null;
                          return (
                            <div key={entry.id} className="cloud-sync-compare__row">
                              <section className="cloud-sync-compare__card">
                                <div className="cloud-sync-compare__eyebrow">本地当前</div>
                                <strong>
                                  {row?.localProfile
                                    ? getProfileDisplayName(row.localProfile)
                                    : '本地无对应卡片'}
                                </strong>
                                <p>
                                  {row?.localProfile
                                    ? `最近本地改动 ${formatDateTime(row.localProfile.updatedAt)}`
                                    : '确认后会在本地新增这张卡片。'}
                                </p>
                                <div className="cloud-sync-compare__note">
                                  {row?.localProfile ? getProfileVisibleNote(row.localProfile) || '未填写备注' : '未填写备注'}
                                </div>
                              </section>

                              <section className="cloud-sync-compare__card cloud-sync-compare__card--target">
                                <div className="cloud-sync-compare__eyebrow">云端版本</div>
                                <strong>{getCloudBackupCardTitle(entry)}</strong>
                                <p>
                                  套餐 {mapPlanLabel(entry.sourcePlanType)} /{' '}
                                  {formatDateTime(entry.updatedAt)}
                                </p>
                                <div className="cloud-sync-compare__note">
                                  {getVisibleNoteFromText(entry.note, entry.ownerUserEmail) || '未填写备注'}
                                </div>
                              </section>
                            </div>
                          );
                        })
                      : selectedCloudSyncLocalCompareRows.map((row) => (
                          <div key={row.localSnapshot.id} className="cloud-sync-compare__row">
                            <section className="cloud-sync-compare__card">
                              <div className="cloud-sync-compare__eyebrow">本地版本</div>
                              <strong>
                                {row.localProfile
                                  ? getProfileDisplayName(row.localProfile)
                                  : getLocalSnapshotCardTitle(row.localSnapshot)}
                              </strong>
                              <p>
                                套餐 {mapPlanLabel(row.localSnapshot.planType)} /{' '}
                                {formatDateTime(row.localSnapshot.updatedAt)}
                              </p>
                              <div className="cloud-sync-compare__note">
                                {getVisibleNoteFromText(row.localSnapshot.notes) || '未填写备注'}
                              </div>
                            </section>

                            <section className="cloud-sync-compare__card cloud-sync-compare__card--target">
                              <div className="cloud-sync-compare__eyebrow">云端对应</div>
                              <strong>
                                {row.cloudEntry
                                  ? getCloudBackupCardTitle(row.cloudEntry)
                                  : cloudSyncDialog.action === 'merge-local-to-cloud'
                                    ? '云端无对应项'
                                    : '将覆盖云端同步源'}
                              </strong>
                              <p>
                                {row.cloudEntry
                                  ? `最近云端快照 ${formatDateTime(row.cloudEntry.updatedAt)}`
                                  : cloudSyncDialog.action === 'merge-local-to-cloud'
                                    ? '这张卡片在云端没有对应项，当前更新动作会跳过它。'
                                  : '这张卡片会参与本次覆盖云端同步源。'}
                              </p>
                              <div className="cloud-sync-compare__note">
                                {getVisibleNoteFromText(
                                  row.cloudEntry?.note,
                                  row.cloudEntry?.ownerUserEmail,
                                ) || '未填写备注'}
                              </div>
                            </section>
                          </div>
                        ))}
                  </div>
                </div>
              ) : (
                <div className="cloud-sync-selector">
                  <section className="cloud-dialog-summary-row cloud-dialog-summary-row--three">
                    <div className="cloud-dialog-summary-item">
                      <span>当前设备可同步</span>
                      <strong>{availableLocalRemoteSnapshots.length}</strong>
                    </div>
                    <div className="cloud-dialog-summary-item">
                      <span>云端卡片</span>
                      <strong>{availableCloudBackupEntries.length}</strong>
                    </div>
                    <div className="cloud-dialog-summary-item">
                      <span>异账号隐藏</span>
                      <strong>{hiddenForeignProfileCount}</strong>
                    </div>
                  </section>

                  <div className="cloud-sync-selector__panes">
                    <section className="cloud-sync-selector__pane">
                      <div className="cloud-sync-selector__pane-header">
                        <div>
                          <strong>本地卡片</strong>
                          <span>{availableLocalRemoteSnapshots.length} 张</span>
                        </div>
                        <div className="cloud-sync-selector__pane-actions">
                          <button
                            type="button"
                            className="sidebar-button sidebar-button--secondary"
                            onClick={() =>
                              setCloudSyncSelectedLocalIds(availableLocalRemoteSnapshots.map((snapshot) => snapshot.id))
                            }
                          >
                            全选
                          </button>
                          <button
                            type="button"
                            className="sidebar-button sidebar-button--secondary"
                            onClick={() => setCloudSyncSelectedLocalIds([])}
                          >
                            清空
                          </button>
                        </div>
                      </div>
                      <div className="selection-dialog__list">
                        {availableLocalRemoteSnapshots.map((snapshot) => {
                          const selected = cloudSyncSelectedLocalIds.includes(snapshot.id);
                          const matchedProfile = visibleRealProfileById.get(snapshot.id) ?? null;
                          const disabledLabel = getDisabledBadgeLabel({
                            manuallyDisabled: snapshot.manuallyDisabled,
                            autoDisabled: matchedProfile?.autoDisabled,
                          });
                          return (
                            <label
                              key={snapshot.id}
                              className={`selection-dialog__card${selected ? ' is-selected' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleSelectionId(setCloudSyncSelectedLocalIds, snapshot.id)}
                              />
                              <div className="selection-dialog__card-main">
                                <div className="compact-card__title-row">
                                  <strong className="compact-card__title">
                                    {getLocalSnapshotCardTitle(snapshot)}
                                  </strong>
                                  <div className="compact-card__badges">
                                    <span className={`badge ${getPlanBadgeClassName(snapshot.planType)}`}>
                                      {mapPlanLabel(snapshot.planType)}
                                    </span>
                                    {disabledLabel ? (
                                      <span className="badge badge--disabled">{disabledLabel}</span>
                                    ) : null}
                                  </div>
                                </div>
                                <div className="compact-card__line compact-card__line--quota">
                                  <span className="compact-card__label">周额度</span>
                                  <span className={getCompactQuotaValueClassName(snapshot.weeklyQuota)}>
                                    {formatCompactQuotaValue(snapshot.weeklyQuota)}
                                  </span>
                                  <span className="compact-card__label">5小时额度</span>
                                  <span className={getCompactQuotaValueClassName(snapshot.fiveHourQuota)}>
                                    {formatCompactQuotaValue(snapshot.fiveHourQuota)}
                                  </span>
                                </div>
                                <div className="compact-card__line compact-card__line--owner">
                                  <span className="compact-card__label">所属</span>
                                  <span className="compact-card__owner">
                                    {matchedProfile
                                      ? getCompactOwnerLabel(
                                          matchedProfile.notes,
                                          matchedProfile.authDerivedEmail,
                                        )
                                      : getCompactOwnerLabel(snapshot.notes)}
                                  </span>
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </section>

                    <section className="cloud-sync-selector__pane">
                      <div className="cloud-sync-selector__pane-header">
                        <div>
                          <strong>云端卡片</strong>
                          <span>{availableCloudBackupEntries.length} 张</span>
                        </div>
                        <div className="cloud-sync-selector__pane-actions">
                          <button
                            type="button"
                            className="sidebar-button sidebar-button--secondary"
                            onClick={() =>
                              setCloudSyncSelectedCloudIds(availableCloudBackupEntries.map((entry) => entry.id))
                            }
                          >
                            全选
                          </button>
                          <button
                            type="button"
                            className="sidebar-button sidebar-button--secondary"
                            onClick={() => setCloudSyncSelectedCloudIds([])}
                          >
                            清空
                          </button>
                          <button
                            type="button"
                            className="sidebar-button sidebar-button--secondary"
                            disabled={cloudSyncSelectedCloudIds.length === 0 || pendingCloudBackupBusy}
                            onClick={() => handleRequestClearSelectedCloudEntries()}
                          >
                            清空云端
                          </button>
                        </div>
                      </div>
                      <div className="selection-dialog__list">
                        {availableCloudBackupEntries.map((entry) => {
                          const selected = cloudSyncSelectedCloudIds.includes(entry.id);
                          const matchedRow =
                            cloudBackupCenterRows.find((candidate) => candidate.entry.id === entry.id) ?? null;
                          const disabledLabel = getDisabledBadgeLabel({
                            manuallyDisabled:
                              entry.transferPackage.manuallyDisabled ||
                              matchedRow?.localSnapshot?.manuallyDisabled,
                            autoDisabled: matchedRow?.localProfile?.autoDisabled,
                          });
                          return (
                            <label
                              key={entry.id}
                              className={`selection-dialog__card${selected ? ' is-selected' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleSelectionId(setCloudSyncSelectedCloudIds, entry.id)}
                              />
                              <div className="selection-dialog__card-main">
                                <div className="compact-card__title-row">
                                  <strong className="compact-card__title">
                                    {getCloudBackupCardTitle(entry)}
                                  </strong>
                                  <div className="compact-card__badges">
                                    <span className={`badge ${getPlanBadgeClassName(entry.sourcePlanType)}`}>
                                      {mapPlanLabel(entry.sourcePlanType)}
                                    </span>
                                    {disabledLabel ? (
                                      <span className="badge badge--disabled">{disabledLabel}</span>
                                    ) : null}
                                  </div>
                                </div>
                                <div className="compact-card__line compact-card__line--quota">
                                  <span className="compact-card__label">周额度</span>
                                  <span
                                    className={getCompactQuotaValueClassName(
                                      entry.transferPackage.weeklyQuota,
                                    )}
                                  >
                                    {formatCompactQuotaValue(entry.transferPackage.weeklyQuota)}
                                  </span>
                                  <span className="compact-card__label">5小时额度</span>
                                  <span
                                    className={getCompactQuotaValueClassName(
                                      entry.transferPackage.fiveHourQuota,
                                    )}
                                  >
                                    {formatCompactQuotaValue(entry.transferPackage.fiveHourQuota)}
                                  </span>
                                </div>
                                <div className="compact-card__line compact-card__line--owner">
                                  <span className="compact-card__label">所属</span>
                                  <span className="compact-card__owner">
                                    {getCompactOwnerLabel(entry.note, entry.ownerUserEmail)}
                                  </span>
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </section>
                  </div>
                </div>
              )}
            </div>

            <footer className="shell-dialog-footer">
              <div className="shell-dialog-footer__actions">
                {cloudSyncDialog?.step === 'confirm' ? (
                  <>
                    <button
                      type="button"
                      className="sidebar-button--secondary"
                      disabled={pendingCloudBackupBusy}
                      onClick={() =>
                        setCloudSyncDialog((current) =>
                          current
                            ? {
                                ...current,
                                step: 'select',
                              }
                            : current,
                        )
                      }
                    >
                      返回选择
                    </button>
                    <button
                      type="button"
                      className="sidebar-button"
                      disabled={pendingCloudBackupBusy}
                      onClick={handleConfirmClawSyncDialog}
                    >
                      {pendingCloudBackupBusy ? (
                        activeCloudSyncVisualProgress ? (
                          <CloudSyncProgressInline
                            count={activeCloudSyncVisualProgress.total}
                            text={`同步中 ${activeCloudSyncVisualProgress.total}张`}
                            running
                          />
                        ) : (
                          <>
                            <LoaderCircle size={14} strokeWidth={2} className="spin" />
                            同步中
                          </>
                        )
                      ) : cloudSyncDialog?.action === 'download-cloud-to-device' ? (
                        '确认下载覆盖'
                      ) : cloudSyncDialog?.action === 'merge-local-to-cloud' ? (
                        '确认更新云端'
                      ) : (
                        '确认覆盖云端'
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="sidebar-button--secondary"
                      onClick={() => resetCloudSyncDialogState()}
                    >
                      取消
                    </button>
                    <button
                      type="button"
                      className="sidebar-button sidebar-button--secondary"
                      onClick={() =>
                        openClawSyncConfirm('download-cloud-to-device', {
                          purgeUnmatchedLocalBeforeImport: true,
                        })
                      }
                    >
                      下载覆盖本地
                    </button>
                    <button
                      type="button"
                      className="sidebar-button"
                      onClick={() => openClawSyncConfirm('overwrite-device-to-cloud')}
                    >
                      覆盖云端
                    </button>
                  </>
                )}
              </div>
            </footer>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root
        open={!IS_LOCAL_FAST_IMPORT_BUILD && cloudBackupCenterOpen}
        onOpenChange={setCloudBackupCenterOpen}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="shell-dialog-overlay" />
          <Dialog.Content className="shell-dialog-panel cloud-backup-dialog-panel">
            <header className="shell-dialog-header">
              <div>
                <Dialog.Title className="shell-dialog-title">当前设备云备份</Dialog.Title>
              </div>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="shell-dialog-close shell-dialog-close--plain"
                  aria-label="关闭云备份面板"
                >
                  <X size={16} />
                </button>
              </Dialog.Close>
            </header>

            <div className="shell-dialog-body cloud-backup-dialog-body">
              <section className="cloud-dialog-summary-row cloud-dialog-summary-row--four">
                <button
                  type="button"
                  className={`cloud-dialog-summary-item cloud-dialog-summary-item--switch${
                    cloudBackupCenterView === 'local' ? ' is-active' : ''
                  }`}
                  aria-pressed={cloudBackupCenterView === 'local'}
                  onClick={() => setCloudBackupCenterView('local')}
                >
                  <span>本地可见</span>
                  <strong>{visibleRealProfiles.length}</strong>
                </button>
                <button
                  type="button"
                  className={`cloud-dialog-summary-item cloud-dialog-summary-item--switch${
                    cloudBackupCenterView === 'cloud' ? ' is-active' : ''
                  }`}
                  aria-pressed={cloudBackupCenterView === 'cloud'}
                  onClick={() => setCloudBackupCenterView('cloud')}
                >
                  <span>云端卡片</span>
                  <strong>{authState.cloudProfileBackups.length}</strong>
                </button>
                <button
                  type="button"
                  className={`cloud-dialog-summary-item cloud-dialog-summary-item--switch${
                    cloudBackupCenterView === 'pending' ? ' is-active' : ''
                  }`}
                  aria-pressed={cloudBackupCenterView === 'pending'}
                  onClick={() => setCloudBackupCenterView('pending')}
                >
                  <span>待上传</span>
                  <strong>{cloudBackupPendingRows.length}</strong>
                </button>
                <button
                  type="button"
                  className={`cloud-dialog-summary-item cloud-dialog-summary-item--switch${
                    cloudBackupCenterView === 'cloud-only' ? ' is-active' : ''
                  }`}
                  aria-pressed={cloudBackupCenterView === 'cloud-only'}
                  onClick={() => setCloudBackupCenterView('cloud-only')}
                >
                  <span>仅云端</span>
                  <strong>{cloudBackupCloudOnlyRows.length}</strong>
                </button>
              </section>

              <div className="cloud-backup-dialog__toolbar">
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  disabled={pendingCloudBackupBusy || pendingProfileTransferBusy}
                  title={
                    cloudSyncPaused
                      ? '当前已暂停自动云同步；点击后恢复自动上传和自动拉取提示。'
                      : '当前已启用自动云同步；点击后只保留手动同步。'
                  }
                  onClick={() => {
                    void handleToggleCloudSyncPaused();
                  }}
                >
                  {cloudSyncPaused ? <Play size={15} strokeWidth={1.9} /> : <Pause size={15} strokeWidth={1.9} />}
                  {cloudSyncPaused ? '启动同步' : '暂停同步'}
                </button>
                <button
                  type="button"
                  className="cloud-backup-dialog__bulk-button"
                  disabled={
                    authState.sessionStatus !== 'authenticated' ||
                    pendingCloudBackupBusy ||
                    Boolean(cloudBackupSyncBusyProfileId)
                  }
                  onClick={() => {
                    void handleManualCloudBackup(null);
                  }}
                >
                  {cloudBackupSyncBusyProfileId === '__all__' && backupRefreshVisualProgress ? (
                    <CloudSyncProgressInline
                      count={backupRefreshVisualProgress.total}
                      text={`同步中 ${backupRefreshVisualProgress.total}张`}
                      running
                    />
                  ) : cloudBackupSyncBusyProfileId === '__all__' ? (
                    <LoaderCircle size={15} strokeWidth={1.8} className="spin" />
                  ) : (
                    <>
                      <Cloud size={15} strokeWidth={1.9} />
                      当前设备上传云端
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  disabled={
                    pendingCloudBackupBusy ||
                    pendingProfileTransferBusy ||
                    authState.sessionStatus !== 'authenticated'
                  }
                  onClick={handleOpenAvailableCloudBackupPrompt}
                >
                  {deviceToCloudVisualProgress ? (
                    <CloudSyncProgressInline
                      count={deviceToCloudVisualProgress.total}
                      text={`同步中 ${deviceToCloudVisualProgress.total}张`}
                      running
                    />
                  ) : (
                    <>
                      <Cloud size={15} strokeWidth={1.9} />
                      一键同步
                      <span className="main-toolbar__action-badge">{availableCloudBackupEntries.length}</span>
                    </>
                  )}
                </button>
              </div>

              <div className="cloud-backup-dialog__list">
                {cloudBackupCenterView === 'local' || cloudBackupCenterView === 'pending' ? (
                  cloudBackupCenterLocalDisplayRows.length === 0 ? (
                    <div className="cloud-backup-dialog__empty">
                      {cloudBackupCenterView === 'pending'
                        ? '当前没有待上传的本地卡片。'
                        : '当前设备没有可显示的本地卡片。'}
                    </div>
                  ) : (
                    cloudBackupCenterLocalDisplayRows.map((row) => {
                      const { profile, backupStatus, localSnapshot, cloudEntry } = row;
                      const disabledLabel = getDisabledBadgeLabel({
                        manuallyDisabled: profile.manuallyDisabled || localSnapshot?.manuallyDisabled,
                        autoDisabled: profile.autoDisabled,
                      });
                      return (
                        <section key={profile.id} className="cloud-backup-dialog__item">
                          <div className="compact-card__title-row">
                            <strong className="compact-card__title">{getProfileDisplayName(profile)}</strong>
                            <div className="compact-card__badges">
                              <span className={`badge ${getPlanBadgeClassName(profile.planType)}`}>
                                {mapPlanLabel(profile.planType)}
                              </span>
                              {disabledLabel ? (
                                <span className="badge badge--disabled">{disabledLabel}</span>
                              ) : null}
                            </div>
                          </div>
                          <div className="compact-card__line compact-card__line--quota">
                            <span className="compact-card__label">周额度</span>
                            <span className={getCompactQuotaValueClassName(profile.weeklyQuota)}>
                              {formatCompactQuotaValue(profile.weeklyQuota)}
                            </span>
                            <span className="compact-card__label">5小时额度</span>
                            <span className={getCompactQuotaValueClassName(profile.fiveHourQuota)}>
                              {formatCompactQuotaValue(profile.fiveHourQuota)}
                            </span>
                          </div>
                          <div className="compact-card__line compact-card__line--owner">
                            <span className="compact-card__label">所属</span>
                            <span className="compact-card__owner">
                              {getCompactOwnerLabel(profile.notes, profile.authDerivedEmail)}
                            </span>
                          </div>
                          <div className="cloud-backup-dialog__item-status">
                            {backupStatus.state === 'synced' ? '已同步' : '待上传'}
                          </div>
                          <div className="cloud-backup-dialog__item-actions">
                            <button
                              type="button"
                              className="sidebar-button"
                              disabled={
                                pendingCloudBackupBusy ||
                                pendingProfileTransferBusy ||
                                localSnapshot === null
                              }
                              onClick={() => {
                                if (!localSnapshot) {
                                  return;
                                }
                                openClawSyncConfirm('merge-local-to-cloud', {
                                  selectedLocalIds: [localSnapshot.id],
                                  selectedCloudIds: cloudEntry ? [cloudEntry.id] : [],
                                });
                              }}
                            >
                              <ArrowUpCircle size={15} strokeWidth={1.8} />
                              更新云端
                            </button>
                            <button
                              type="button"
                              className="sidebar-button sidebar-button--secondary"
                              disabled={
                                pendingCloudBackupBusy ||
                                pendingProfileTransferBusy ||
                                cloudEntry === null
                              }
                              onClick={() => {
                                if (!cloudEntry) {
                                  return;
                                }
                                openClawSyncConfirm('download-cloud-to-device', {
                                  selectedCloudIds: [cloudEntry.id],
                                });
                              }}
                            >
                              <ArrowDownCircle size={15} strokeWidth={1.8} />
                              下载覆盖
                            </button>
                          </div>
                        </section>
                      );
                    })
                  )
                ) : cloudBackupCenterCloudDisplayRows.length === 0 ? (
                  <div className="cloud-backup-dialog__empty">
                    {cloudBackupCenterView === 'cloud-only'
                      ? '当前没有仅云端卡片。'
                      : '当前账号云端还没有可显示的卡片。'}
                  </div>
                ) : (
                  cloudBackupCenterCloudDisplayRows.map((row) => {
                    const { entry, localProfile, localSnapshot, status } = row;
                    const disabledLabel = getDisabledBadgeLabel({
                      manuallyDisabled:
                        entry.transferPackage.manuallyDisabled || localSnapshot?.manuallyDisabled,
                      autoDisabled: localProfile?.autoDisabled,
                    });
                    return (
                      <section key={entry.id} className="cloud-backup-dialog__item">
                        <div className="compact-card__title-row">
                          <strong className="compact-card__title">{getCloudBackupCardTitle(entry)}</strong>
                          <div className="compact-card__badges">
                            <span className={`badge ${getPlanBadgeClassName(entry.sourcePlanType)}`}>
                              {mapPlanLabel(entry.sourcePlanType)}
                            </span>
                            {disabledLabel ? (
                              <span className="badge badge--disabled">{disabledLabel}</span>
                            ) : null}
                          </div>
                        </div>
                        <div className="compact-card__line compact-card__line--quota">
                          <span className="compact-card__label">周额度</span>
                          <span className={getCompactQuotaValueClassName(entry.transferPackage.weeklyQuota)}>
                            {formatCompactQuotaValue(entry.transferPackage.weeklyQuota)}
                          </span>
                          <span className="compact-card__label">5小时额度</span>
                          <span className={getCompactQuotaValueClassName(entry.transferPackage.fiveHourQuota)}>
                            {formatCompactQuotaValue(entry.transferPackage.fiveHourQuota)}
                          </span>
                        </div>
                        <div className="compact-card__line compact-card__line--owner">
                          <span className="compact-card__label">所属</span>
                          <span className="compact-card__owner">
                            {getCompactOwnerLabel(entry.note, entry.ownerUserEmail)}
                          </span>
                        </div>
                        {status.tone === 'stale' || status.tone === 'missing' ? (
                          <div className="cloud-backup-dialog__item-status">{status.label}</div>
                        ) : null}
                        <div className="cloud-backup-dialog__item-actions">
                          <button
                            type="button"
                            className="sidebar-button"
                            disabled={pendingCloudBackupBusy || pendingProfileTransferBusy}
                            onClick={() => {
                              openClawSyncConfirm('download-cloud-to-device', {
                                selectedCloudIds: [entry.id],
                              });
                            }}
                          >
                            <ArrowDownCircle size={15} strokeWidth={1.8} />
                            下载覆盖
                          </button>
                          <button
                            type="button"
                            className="sidebar-button sidebar-button--secondary"
                            disabled={
                              pendingCloudBackupBusy ||
                              pendingProfileTransferBusy ||
                              localSnapshot === null
                            }
                            onClick={() => {
                              if (!localSnapshot) {
                                return;
                              }
                              openClawSyncConfirm('merge-local-to-cloud', {
                                selectedLocalIds: [localSnapshot.id],
                                selectedCloudIds: [entry.id],
                              });
                            }}
                          >
                            <ArrowUpCircle size={15} strokeWidth={1.8} />
                            更新云端
                          </button>
                        </div>
                      </section>
                    );
                  })
                )}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <WorkspaceAutoDisabledDialog
        open={Boolean(autoDisabledPrompt) && !mandatoryUpdateLockActive && !switchPromptBlocked}
        title={
          autoDisabledPromptProfile
            ? `${getProfileDisplayName(autoDisabledPromptProfile)} 已自动停用`
            : '当前空间已自动停用'
        }
        description="当前探活已确认这个空间可能不可用，系统已先停止继续使用。你可以直接前往 ChatGPT 官网核实该空间状态。"
        workspaceName={
          autoDisabledPromptProfile ? getProfileDisplayName(autoDisabledPromptProfile) : null
        }
        workspaceStateHint={
          autoDisabledPromptProfile
            ? autoDisabledPromptProfile.isActive
              ? '当前正在使用中的空间'
              : '已从可切换列表中停用'
            : ''
        }
        checkedAt={
          autoDisabledPromptProfile
            ? formatDateTime(
                autoDisabledPromptProfile.autoDisabledAt ?? autoDisabledPromptProfile.probeCheckedAt,
              )
            : null
        }
        probeMessage={autoDisabledPromptProfile?.probeMessage?.trim() || '认证失效或已过期'}
        confirmLabel="打开 ChatGPT 查看"
        cancelLabel="稍后处理"
        onConfirm={() => {
          setAutoDisabledPrompt(null);
          void handleOpenExternal(CHATGPT_HOME_URL);
        }}
        onOpenChange={(open) => {
          if (!open) {
            setAutoDisabledPrompt(null);
          }
        }}
      />

      <ConfirmActionDialog
        open={Boolean(autoSwitchPrompt) && !autoDisabledPrompt && !mandatoryUpdateLockActive && !quotaPromptBlocked}
        layout="workspace"
        eyebrow="自动切换预警"
        title="即将自动切换到推荐空间"
        description={
          autoSwitchPrompt
            ? `当前空间的${autoSwitchPrompt.suggestion.breachedLabels.join('、')}已低于自动切换阈值。若不取消，系统会在倒计时结束后自动切到更充足的空间。`
            : ''
        }
        confirmLabel="立即切换"
        cancelLabel="取消自动切换"
        busy={busyAction === 'switch'}
        onConfirm={() => {
          if (!autoSwitchPrompt) {
            return;
          }

          setDismissedAutoSwitchKey(autoSwitchPrompt.suggestion.warningKey);
          void executeSwitchProfile(autoSwitchPrompt.suggestion.recommendedProfile, 'recommended');
        }}
        onOpenChange={(open) => {
          if (!open && autoSwitchPrompt) {
            setDismissedAutoSwitchKey(autoSwitchPrompt.suggestion.warningKey);
            setAutoSwitchPrompt(null);
          }
        }}
        extraContent={
          autoSwitchPrompt ? (
            <div className="grid gap-4">
              <div className="auto-switch-countdown">
                <div className="auto-switch-countdown__value">{autoSwitchSecondsLeft}s</div>
                <div className="auto-switch-countdown__label">倒计时结束后自动切换</div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">当前空间</div>
                  <div className="mt-2 text-sm font-semibold text-white">
                    {getProfileDisplayName(autoSwitchPrompt.suggestion.activeProfile)}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">
                    周额度剩余 {getRemainingPercent(autoSwitchPrompt.suggestion.activeProfile.weeklyQuota)}% / 5 小时剩余{' '}
                    {getRemainingPercent(autoSwitchPrompt.suggestion.activeProfile.fiveHourQuota)}%
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">推荐切换</div>
                  <div className="mt-2 text-sm font-semibold text-white">
                    {getProfileDisplayName(autoSwitchPrompt.suggestion.recommendedProfile)}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">
                    周额度剩余 {getRemainingPercent(autoSwitchPrompt.suggestion.recommendedProfile.weeklyQuota)}% / 5 小时剩余{' '}
                    {getRemainingPercent(autoSwitchPrompt.suggestion.recommendedProfile.fiveHourQuota)}%
                  </div>
                </div>
              </div>
            </div>
          ) : null
        }
      />

      <ConfirmActionDialog
        open={Boolean(quotaAlertTarget) && !autoDisabledPrompt && !mandatoryUpdateLockActive && !quotaPromptBlocked}
        layout="workspace"
        eyebrow="额度预警"
        title="建议切换到更充足的空间"
        description={
          quotaAlertTarget
            ? `当前空间的${quotaAlertTarget.breachedLabels.join('、')}剩余已低于 ${quotaAlertTarget.threshold}%。为避免中途掉线，建议现在切换。`
            : ''
        }
        confirmLabel="立即切换"
        cancelLabel="稍后处理"
        hideShortcutHint
        onConfirm={() => {
          if (!quotaAlertTarget) {
            return;
          }

          setDismissedQuotaAlertKey(quotaAlertTarget.warningKey);
          setQuotaAlertTarget(null);
          void executeSwitchProfile(quotaAlertTarget.recommendedProfile, 'recommended');
        }}
        onOpenChange={(open) => {
          if (!open && quotaAlertTarget) {
            applyQuotaAlertSnooze();
            setQuotaAlertTarget(null);
          }
        }}
        footerAccessoryPlacement="actions-leading"
        footerAccessory={
          <div className="quota-alert-snooze">
            <span className="quota-alert-snooze__label">不提醒时间</span>
            <div className={`quota-alert-snooze__picker${quotaAlertSnoozeMenuOpen ? ' is-open' : ''}`}>
              <button
                type="button"
                className="quota-alert-snooze__trigger"
                onClick={() => setQuotaAlertSnoozeMenuOpen((current) => !current)}
              >
                <span>{quotaAlertSnoozeLabel}</span>
                <ChevronDown size={14} strokeWidth={1.8} />
              </button>
              {quotaAlertSnoozeMenuOpen ? (
                <div className="quota-alert-snooze__menu">
                  {QUOTA_ALERT_SNOOZE_OPTIONS.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      className={`quota-alert-snooze__option${
                        quotaAlertSnoozeOption === option.key ? ' is-active' : ''
                      }`}
                      onClick={() => {
                        setQuotaAlertSnoozeOption(option.key);
                        setQuotaAlertSnoozeMenuOpen(false);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        }
        extraContent={
          quotaAlertTarget ? (
            <div className="quota-alert-detail">
              <div className="quota-alert-detail__card">
                <div className="quota-alert-detail__eyebrow">当前空间</div>
                <div className="quota-alert-detail__title">
                  {getProfileDisplayName(quotaAlertTarget.activeProfile)}
                </div>
                <div className="quota-alert-detail__row">
                  <span>周额度</span>
                  <strong
                    className={`quota-alert-detail__value is-${getQuotaTone(quotaAlertTarget.activeProfile.weeklyQuota)}`}
                  >
                    {getRemainingPercent(quotaAlertTarget.activeProfile.weeklyQuota)}%
                  </strong>
                </div>
                <div className="quota-alert-detail__row">
                  <span>5 小时额度</span>
                  <strong
                    className={`quota-alert-detail__value is-${getQuotaTone(quotaAlertTarget.activeProfile.fiveHourQuota)}`}
                  >
                    {getRemainingPercent(quotaAlertTarget.activeProfile.fiveHourQuota)}%
                  </strong>
                </div>
              </div>
              <div className="quota-alert-detail__card is-recommended">
                <div className="quota-alert-detail__eyebrow">推荐切换</div>
                <div className="quota-alert-detail__title">
                  {getProfileDisplayName(quotaAlertTarget.recommendedProfile)}
                </div>
                <div className="quota-alert-detail__row">
                  <span>周额度</span>
                  <strong
                    className={`quota-alert-detail__value is-${getQuotaTone(quotaAlertTarget.recommendedProfile.weeklyQuota)}`}
                  >
                    {getRemainingPercent(quotaAlertTarget.recommendedProfile.weeklyQuota)}%
                  </strong>
                </div>
                <div className="quota-alert-detail__row">
                  <span>5 小时额度</span>
                  <strong
                    className={`quota-alert-detail__value is-${getQuotaTone(quotaAlertTarget.recommendedProfile.fiveHourQuota)}`}
                  >
                    {getRemainingPercent(quotaAlertTarget.recommendedProfile.fiveHourQuota)}%
                  </strong>
                </div>
              </div>
            </div>
          ) : null
        }
      />

      <ConfirmActionDialog
        open={Boolean(switchTarget)}
        layout="workspace"
        eyebrow="切换工作区"
        title="确认切换登录状态"
        description={
          switchTarget
            ? isOpenClawMode
              ? '将使用该空间的认证信息替换当前登录状态，并同步到 OpenClaw 认证存储。你还可以在下方直接选择本次切换要生效的模型。'
              : effectiveCodexTraeAutoRestart
                ? `将使用该空间的认证信息替换当前登录状态。确认后会自动启动你已勾选的应用：${
                    [
                      effectiveCodexAutoRestart ? 'Codex' : null,
                      effectiveTraeAutoRestart ? 'Trae' : null,
                    ]
                      .filter(Boolean)
                      .join(' / ')
                  }。请先保存 CLI、IDE 或对话中的未保存内容。`
                : '将使用该空间的认证信息替换当前登录状态。切换后建议重启相关工具，以确保认证生效。'
            : ''
        }
        confirmLabel={busyAction === 'switch' ? latestSwitchProgressEvent?.title ?? '切换中...' : '立即切换'}
        busy={busyAction === 'switch'}
        headerAside={
          <SwitchProgressHeaderAside
            switchMode={state.settings.switchMode}
            events={switchProgressEvents}
            busy={busyAction === 'switch'}
          />
        }
        extraContent={
          switchTarget ? (
            <div className="switch-confirm-detail-sections grid gap-3">
              <div className="switch-confirm-detail-summary grid gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">目标空间</div>
                  <div className="mt-2 text-sm font-semibold text-white">{switchTarget.name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">套餐</div>
                  <div className="mt-2 text-sm font-semibold text-white">{mapPlanLabel(switchTarget.planType)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">空间名</div>
                  <div className="mt-2 text-sm font-semibold text-white">{getProfileDisplayName(switchTarget)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">切换模式</div>
                  <div className="mt-2 text-sm font-semibold text-white">
                    {mapSwitchModeLabel(state.settings.switchMode)}
                  </div>
                </div>
              </div>

              {isCodexMode && effectiveCodexTraeAutoRestart ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {effectiveCodexAutoRestart ? (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Codex 路径</div>
                      <div className="mt-2 text-sm font-semibold text-white">
                        {state.summary.codexExecutablePath ?? '未识别'}
                      </div>
                      <div className="mt-1 text-xs text-slate-400">
                        {mapExecutableSourceLabel(state.summary.codexExecutablePathSource)}
                      </div>
                    </div>
                  ) : null}
                  {effectiveTraeAutoRestart ? (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Trae 路径</div>
                      <div className="mt-2 text-sm font-semibold text-white">
                        {state.summary.traeExecutablePath ?? '未识别'}
                      </div>
                      <div className="mt-1 text-xs text-slate-400">
                        {mapExecutableSourceLabel(state.summary.traeExecutablePathSource)}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {isOpenClawMode ? (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">OpenClaw 模型</div>
                  {openClawSwitchModelOptions.length > 0 ? (
                    <>
                      <select
                        className="plan-select mt-2 w-full"
                        value={switchOpenClawModel}
                        onChange={(event) => setSwitchOpenClawModel(event.target.value)}
                      >
                        <option value="">沿用当前模型</option>
                        {openClawSwitchModelOptions.map((option) => (
                          <option key={option.key} value={option.key}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="mt-1 text-xs text-slate-400">
                        当前默认：{getOpenClawModelLabel(
                          state.summary.openclawCurrentModel,
                          state.summary.openclawAvailableModels,
                        )}
                        。确认切换后会把认证和所选模型一起写入 OpenClaw。
                      </div>
                    </>
                  ) : (
                    <div className="mt-2 text-sm text-slate-300">
                      当前未读取到 ChatGPT Codex 相关模型列表，本次切换将只同步认证，不额外修改模型。
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ) : null
        }
        onConfirm={() => void handleSwitchProfile()}
        onOpenChange={(open) => {
          if (!open) {
            setSwitchTarget(null);
            setSwitchOpenClawModel('');
            setSwitchProgressRunId(null);
            switchProgressRunIdRef.current = null;
            setSwitchProgressEvents([]);
          }
        }}
      />

      <ConfirmActionDialog
        open={Boolean(deleteTarget)}
        title={deleteTarget ? `确认删除 ${deleteTarget.name}？` : '确认删除配置？'}
        description="确认后会移除当前卡片；勾选后会同时删除本地 profile 文件夹与 auth.json。"
        tone="danger"
        layout="slim"
        confirmLabel="确认删除"
        cancelLabel="取消"
        busy={busyAction === 'delete'}
        extraContent={
          <label className="delete-checkbox">
            <input
              type="checkbox"
              checked={removeFilesOnDelete}
              onChange={(event) => setRemoveFilesOnDelete(event.target.checked)}
            />
            <span>同时删除本地 profile 文件夹与 auth.json</span>
          </label>
        }
        onConfirm={() => void handleDeleteProfile()}
        onOpenChange={(open) => {
          if (!open) {
            if (busyAction === 'delete') {
              return;
            }
            setDeleteTarget(null);
            setRemoveFilesOnDelete(false);
          }
        }}
      />

      <ConfirmActionDialog
        open={Boolean(batchDeleteConfirmRequest)}
        title={
          batchDeleteConfirmRequest?.source === 'workspace-all'
            ? `确认删除当前空间池的 ${batchDeleteConfirmRequest.profileIds.length} 张卡片？`
            : batchDeleteConfirmRequest?.source === 'batch-dialog'
              ? `确认删除批量操作中选中的 ${batchDeleteConfirmRequest.profileIds.length} 张卡片？`
              : `确认删除所选的 ${batchDeleteConfirmRequest?.profileIds.length ?? 0} 张卡片？`
        }
        description={
          batchDeleteConfirmRequest?.source === 'workspace-all'
            ? '确认后会删除当前空间池中的本地卡片与对应文件。'
            : batchDeleteConfirmRequest?.source === 'batch-dialog'
              ? '确认后会删除当前批量选中的本地卡片与对应文件。'
              : '确认后会删除当前选中的本地卡片与对应文件。'
        }
        tone="danger"
        layout="slim"
        confirmLabel="确认删除"
        cancelLabel="取消"
        busy={workspaceSelectionBusyAction === 'delete'}
        onConfirm={() => {
          const confirmRequest = batchDeleteConfirmRequest;
          if (!confirmRequest) {
            return;
          }

          setBatchDeleteConfirmRequest(null);
          setBatchDeleteReturnToDialog(false);
          void handleWorkspaceSelectionDelete({
            profileIds: confirmRequest.profileIds,
            deletedProfiles: confirmRequest.deletedProfiles,
            successMessage: confirmRequest.successMessage,
            failureMessage: confirmRequest.failureMessage,
            resetSelection: confirmRequest.resetSelection,
            closeBatchDialog: confirmRequest.closeBatchDialog,
          });
        }}
        onOpenChange={(open) => {
          if (!open) {
            if (workspaceSelectionBusyAction === 'delete') {
              return;
            }
            setBatchDeleteConfirmRequest(null);
            if (batchDeleteReturnToDialog) {
              setBatchNoteDialogOpen(true);
              setBatchDeleteReturnToDialog(false);
            }
          }
        }}
      />

      <ConfirmActionDialog
        open={Boolean(cloudDeleteSyncPrompt)}
        title={
          cloudDeleteSyncPrompt
            ? cloudDeleteSyncPrompt.source === 'cloud-clear'
              ? `是否清空所选 ${cloudDeleteSyncPrompt.deletedProfileNames.length} 张云端卡片？`
              : `本地已删除 ${cloudDeleteSyncPrompt.deletedProfileNames.length} 张卡片，是否同步删除云端？`
            : '是否同步删除云端卡片？'
        }
        description={
          cloudDeleteSyncPrompt?.source === 'cloud-clear'
            ? '确认后只删除云端记录，不会删除本地卡片。'
            : '确认后会同步删除当前账号云端中的对应卡片记录。'
        }
        tone="danger"
        layout="slim"
        confirmLabel="确定"
        cancelLabel="取消"
        busy={workspaceSelectionBusyAction === 'cloud-delete'}
        onConfirm={() => {
          void handleConfirmCloudDeleteSync();
        }}
        onOpenChange={(open) => {
          if (!open) {
            setCloudDeleteSyncPrompt(null);
          }
        }}
      />

      <Dialog.Root
        open={Boolean(proxyReminder)}
        onOpenChange={(open) => {
          if (!open) {
            setProxyReminder(null);
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="feedback-center-overlay" />
          <Dialog.Content className="proxy-reminder-dialog">
            <div className="proxy-reminder-dialog__icon">
              {proxyReminder?.network.status === 'network-error' ? (
                <WifiOff size={26} strokeWidth={1.9} />
              ) : (
                <Globe size={26} strokeWidth={1.9} />
              )}
            </div>
            <div className="proxy-reminder-dialog__eyebrow">
              {proxyReminder?.source === 'startup' ? '启动检测' : '手动检测'}
            </div>
            <Dialog.Title className="proxy-reminder-dialog__title">
              {proxyReminder?.network.status === 'needs-proxy'
                ? '当前网络未走代理'
                : '当前网络或代理暂时不可用'}
            </Dialog.Title>
            <Dialog.Description className="proxy-reminder-dialog__description">
              {proxyReminder?.network.summary ??
                '当前网络还没有走代理，可能无法正常使用依赖代理的卡片与动画流程。'}
            </Dialog.Description>
            <div className="proxy-reminder-dialog__detail">
              {proxyReminder?.network.detail ??
                (proxyReminder?.network.status === 'needs-proxy'
                  ? '建议先打开代理的 TUN + 全局模式，再点重新检测并刷新恢复。'
                  : '建议先检查代理或网络连通性，恢复后再点重新检测。')}
            </div>
            <div className="proxy-reminder-dialog__actions">
              <button
                type="button"
                className="sidebar-button sidebar-button--secondary"
                onClick={() => setProxyReminder(null)}
              >
                我知道了
              </button>
              <button
                type="button"
                className="sidebar-button"
                onClick={() => void handleRecoverProxyCards()}
              >
                重新检测
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root
        open={hiddenModeDialogOpen}
        onOpenChange={(open) => handleHiddenModeDialogOpenChange(open)}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="special-mode-dialog-overlay" />
          <div className="special-mode-dialog-shell">
            <Dialog.Content
              className="glass-panel special-mode-dialog-panel"
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  handleSubmitHiddenModeDialog();
                }
              }}
            >
              <header className="special-mode-dialog-panel__header">
                <div>
                  <Dialog.Title className="special-mode-dialog-panel__title">
                    {hiddenModeDialogMode === 'unlock'
                      ? '验证工程师模式访问'
                      : hiddenModeDialogMode === 'change'
                        ? '修改工程师模式密码'
                        : '设置工程师模式密码'}
                  </Dialog.Title>
                  <Dialog.Description className="special-mode-dialog-panel__description">
                    {hiddenModeDialogMode === 'unlock'
                      ? '输入本地密码后，才会显示模拟菜单和临时卡片。'
                      : hiddenModeDialogMode === 'change'
                        ? '新的密码只保存在当前设备的浏览器本地存储里，不会写进后端或 profiles 文件。'
                        : '第一次打开工程师模式前，需要先设置一个仅保存在本机的密码。'}
                  </Dialog.Description>
                </div>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="special-mode-dialog-panel__close"
                    aria-label="关闭工程师模式弹窗"
                  >
                    ×
                  </button>
                </Dialog.Close>
              </header>

              <main className="special-mode-dialog-panel__body">
                <div className="special-mode-dialog-panel__grid">
                  <section className="soft-card special-mode-dialog-card">
                    <p className="special-mode-dialog-card__eyebrow">本地访问验证</p>
                    <h2 className="special-mode-dialog-card__headline">工程师模式</h2>
                    <p className="special-mode-dialog-card__copy">
                      这里只用于本地开发环境或测试用户的模拟菜单和临时卡片展示，不会向后端写入测试文件，也不会同步到真实 profile 数据。
                    </p>

                    <div className="special-mode-dialog-card__stats">
                      <div className="status-tile">
                        <p className="tile-label">当前状态</p>
                        <p className="special-mode-dialog-card__stat-value">
                          {hiddenModeUnlocked ? '已显示' : '已隐藏'}
                        </p>
                      </div>
                      <div className="status-tile">
                        <p className="tile-label">密码存储</p>
                        <p className="special-mode-dialog-card__stat-value">仅本机浏览器</p>
                      </div>
                    </div>
                  </section>

                  <section className="soft-card special-mode-dialog-card special-mode-dialog-card--form">
                    <div className="special-mode-dialog-fields">
                      <label className="special-mode-dialog-field">
                        <span className="special-mode-dialog-field__label">
                          {hiddenModeDialogMode === 'unlock' ? '工程师模式密码' : '新密码'}
                        </span>
                        <input
                          className="surface-input special-mode-dialog-field__input"
                          type="password"
                          autoComplete="off"
                          value={hiddenModePasswordDraft}
                          onChange={(event) => {
                            setHiddenModePasswordDraft(event.target.value);
                            if (hiddenModeDialogError) {
                              setHiddenModeDialogError(null);
                            }
                          }}
                        />
                      </label>

                      {hiddenModeDialogMode !== 'unlock' ? (
                        <label className="special-mode-dialog-field">
                          <span className="special-mode-dialog-field__label">确认密码</span>
                          <input
                            className="surface-input special-mode-dialog-field__input"
                            type="password"
                            autoComplete="off"
                            value={hiddenModePasswordConfirmDraft}
                            onChange={(event) => {
                              setHiddenModePasswordConfirmDraft(event.target.value);
                              if (hiddenModeDialogError) {
                                setHiddenModeDialogError(null);
                              }
                            }}
                          />
                        </label>
                      ) : null}

                      <div className="special-mode-dialog-note">
                        密码只保存在当前设备的浏览器本地存储里，用来隐藏本地测试菜单，不会进入后端文件，也不会写入安装包环境。
                      </div>

                      {hiddenModeDialogError ? (
                        <div className="special-mode-dialog-error">{hiddenModeDialogError}</div>
                      ) : null}
                    </div>
                  </section>
                </div>
              </main>

              <footer className="special-mode-dialog-panel__footer">
                <Dialog.Close asChild>
                  <button type="button" className="special-mode-dialog-panel__secondary">
                    取消
                  </button>
                </Dialog.Close>
                <button
                  type="button"
                  className="primary-button special-mode-dialog-panel__primary"
                  onClick={() => handleSubmitHiddenModeDialog()}
                >
                  {hiddenModeDialogMode === 'unlock'
                    ? '验证并显示'
                    : hiddenModeDialogMode === 'change'
                      ? '保存新密码'
                      : '设置并显示'}
                </button>
              </footer>
            </Dialog.Content>
          </div>
        </Dialog.Portal>
      </Dialog.Root>

      {toast ? <ToastBanner toast={toast} /> : null}
      {centerNotice ? <CenterNoticeBanner notice={centerNotice} /> : null}

      <Dialog.Root
        open={Boolean(authGateQrPreviewState)}
        onOpenChange={(open) => {
          if (!open) {
            setAuthGateQrPreviewState(null);
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay
            className={`shell-dialog-overlay${
              authGateQrPreviewState?.source === 'feedback' ? ' shell-dialog-overlay--elevated' : ''
            }`}
          />
          <Dialog.Content
            className={`shell-dialog-panel qr-preview-dialog-panel${
              authGateQrPreviewState?.source === 'feedback'
                ? ' shell-dialog-panel--elevated'
                : ''
            }`}
          >
            <header className="shell-dialog-header">
              <div>
                <div className="shell-dialog-eyebrow">二维码</div>
                <Dialog.Title className="shell-dialog-title">
                  {activeQrPreviewItem?.title ?? '二维码预览'}
                </Dialog.Title>
                <Dialog.Description className="shell-dialog-description">
                  {activeQrPreviewItem?.hint ?? '请使用手机直接扫码。'}
                </Dialog.Description>
              </div>
              <Dialog.Close asChild>
                <button type="button" className="shell-dialog-close" aria-label="关闭二维码预览">
                  <X size={16} />
                </button>
              </Dialog.Close>
            </header>

            <div className="shell-dialog-body qr-preview-dialog-body">
              {authGateQrPreviewState && authGateQrPreviewState.items.length > 1 ? (
                <div className="qr-preview-dialog-tabs">
                  {authGateQrPreviewState.items.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`about-qr-tab${
                        item.id === authGateQrPreviewState.activeId ? ' is-active' : ''
                      }`}
                      onClick={() =>
                        setAuthGateQrPreviewState((current) =>
                          current ? { ...current, activeId: item.id } : current,
                        )
                      }
                    >
                      {item.title}
                    </button>
                  ))}
                </div>
              ) : null}
              {activeQrPreviewItem?.imageSrc ? (
                <div className="qr-preview-dialog-image-shell">
                  <img
                    src={activeQrPreviewItem.imageSrc}
                    alt={activeQrPreviewItem.title}
                    className="qr-preview-dialog-image"
                  />
                </div>
              ) : (
                <div className="qr-preview-dialog-image-shell qr-preview-dialog-image-shell--empty">
                  <div className="qr-preview-dialog-empty">
                    <strong>二维码暂未配置</strong>
                    <span>这个入口已经保留，后续补上素材后会直接在这里展示。</span>
                  </div>
                </div>
              )}
              <div className="qr-preview-dialog-meta">
                <strong>{activeQrPreviewItem?.subtitle ?? '客服联系入口'}</strong>
                <span>
                  {activeQrPreviewItem?.imageSrc
                    ? '手机扫码后即可继续联系或添加客服。'
                    : '当前还没有可展示的二维码图片。'}
                </span>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

function TrayHoverCardApp() {
  const [trayState, setTrayState] = useState<TrayCardState | null>(null);
  const [trayError, setTrayError] = useState<string | null>(null);

  useEffect(() => {
    const runtimeApi = window.codexWorkspace as Partial<typeof window.codexWorkspace>;
    let active = true;

    const applyState = (nextState: TrayCardState) => {
      if (!active) {
        return;
      }

      setTrayState(nextState);
      setTrayError(null);
    };

    if (typeof runtimeApi.getTrayCardState === 'function') {
      runtimeApi
        .getTrayCardState()
        .then((nextState) => {
          applyState(nextState);
        })
        .catch(() => {
          if (active) {
            setTrayError('当前托盘卡片暂时不可用，请回到主窗口查看。');
          }
        });
    } else {
      setTrayError('当前托盘卡片暂时不可用，请回到主窗口查看。');
    }

    const unsubscribe =
      typeof runtimeApi.onTrayCardStateChange === 'function'
        ? runtimeApi.onTrayCardStateChange((nextState) => {
            applyState(nextState);
          })
        : null;

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  const profile = trayState?.activeProfile ?? null;
  const roleBadge = profile ? getProfileRoleBadge(profile) : null;
  const availability = profile ? getProfileAvailability(profile) : null;
  const isTrayFreePlan = profile?.planType === 'Free';
  const trayFooterNote =
    profile?.notes?.trim() ||
    (isTrayFreePlan
      ? '托盘卡片会优先突出当前周额度与总剩余额度。'
      : '托盘卡片会优先突出当前 5 小时额度与总剩余额度。');
  const trayFooterStatus = availability?.footerText || '当前正在使用';
  const detectedName =
    trayState?.currentAuthSuggestedName ??
    trayState?.currentAuthSuggestedWorkspaceName ??
    '待导入空间';

  return (
    <div className="tray-hover-view">
      <div className="tray-hover-window">
        <div className="tray-hover-window__halo" aria-hidden="true" />
        <section
          className={`tray-hover-card${profile ? '' : ' is-empty'}${
            availability?.isUnavailable ? ' is-unavailable' : ''
          }`}
        >
          <div className="tray-hover-card__topbar">
            <span className="tray-hover-card__eyebrow">当前卡片</span>
            <span className="tray-hover-card__stamp">
              {formatDateTime(trayState?.lastScannedAt ?? null)}
            </span>
          </div>

          {profile ? (
            <div className="tray-hover-card__panel">
              <div className="tray-hover-card__badges">
                <span
                  className={`badge badge--plan${
                    getPlanBadgeClassName(profile.planType)
                      ? ` ${getPlanBadgeClassName(profile.planType)}`
                      : ''
                  }`}
                >
                  {mapPlanLabel(profile.planType)}
                </span>
                {roleBadge ? <span className={`badge ${roleBadge.className}`}>{roleBadge.label}</span> : null}
                {availability?.badgeLabel ? (
                  <span className="badge badge--disabled">{availability.badgeLabel}</span>
                ) : null}
              </div>

              <div className="tray-hover-card__title-row">
                <h2 className="tray-hover-card__title">{getProfileDisplayName(profile)}</h2>
                <span className={`footer-auth-status is-${getProfileAuthToneClassName(profile)}`}>
                  {getAuthStatusText(profile)}
                </span>
              </div>

              <p className="tray-hover-card__subtitle">
                {profile.workspaceName ? `空间名：${profile.workspaceName}` : '悬停托盘图标即可查看当前激活卡片。'}
              </p>

              <div className="tray-hover-card__hero">
                <QuotaRow
                  label={isTrayFreePlan ? '周额度' : '5 小时额度'}
                  quota={isTrayFreePlan ? profile.weeklyQuota : profile.fiveHourQuota}
                  compact
                  showCountdown
                />
              </div>

              <div className="tray-hover-card__stats">
                <div className="tray-hover-stat">
                  <span className="tray-hover-stat__label">最近登录</span>
                  <strong className="tray-hover-stat__value tray-hover-stat__value--compact">
                    {formatDateTime(profile.lastActivatedAt || profile.updatedAt)}
                  </strong>
                </div>
                <div className="tray-hover-stat tray-hover-stat--accent">
                  <span className="tray-hover-stat__label">总剩余额度</span>
                  <strong className="tray-hover-stat__value tray-hover-stat__value--accent">
                    {trayState?.overallRemainingPercent ?? '--'}%
                  </strong>
                </div>
              </div>

              {isTrayFreePlan ? null : (
                <div className="tray-hover-card__secondary">
                  <QuotaRow label="周额度" quota={profile.weeklyQuota} compact showCountdown />
                </div>
              )}

              <div className="tray-hover-card__footer">
                <span>{trayFooterNote}</span>
                <span>{trayFooterStatus}</span>
              </div>
            </div>
          ) : (
            <div className="tray-hover-card__panel tray-hover-card__panel--empty">
              <h2 className="tray-hover-card__title">
                {trayState?.currentAuthExists ? '检测到待导入空间' : '暂未识别当前登录'}
              </h2>
              <p className="tray-hover-card__subtitle">
                {trayError
                  ? trayError
                  : trayState?.currentAuthExists
                    ? `当前 auth 尚未匹配到已保存卡片，建议回主窗口确认导入「${detectedName}」。`
                    : '当前没有检测到可用 auth 信息，请先在主窗口完成登录或导入。'}
              </p>

              <div className="tray-hover-card__stats">
                <div className="tray-hover-stat">
                  <span className="tray-hover-stat__label">卡片数量</span>
                  <strong className="tray-hover-stat__value">{trayState?.profilesCount ?? 0}</strong>
                </div>
                <div className="tray-hover-stat">
                  <span className="tray-hover-stat__label">建议名称</span>
                  <strong className="tray-hover-stat__value">{detectedName}</strong>
                </div>
              </div>

              <div className="tray-hover-card__empty-note">
                托盘卡片只做当前状态预览，不会替代主窗口里的完整切换和编辑流程。
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function FloatingWidgetApp() {
  const [widgetState, setWidgetState] = useState<FloatingWidgetState | null>(null);
  const [widgetError, setWidgetError] = useState<string | null>(null);
  const [orbQuotaView, setOrbQuotaView] = useState<'five-hour' | 'weekly'>('five-hour');
  const [widgetCycleArmed, setWidgetCycleArmed] = useState(false);
  const [widgetInteractionToken, setWidgetInteractionToken] = useState(0);
  const [orbAnimatedPercent, setOrbAnimatedPercent] = useState<number | null>(null);
  const widgetGestureRef = useRef<{
    pointerId: number;
    startScreenX: number;
    startScreenY: number;
    moved: boolean;
  } | null>(null);
  const orbAnimationFrameRef = useRef<number | null>(null);
  const lastCompletedOrbTransitionRef = useRef<string | null>(null);

  useEffect(() => {
    const runtimeApi = window.codexWorkspace as Partial<typeof window.codexWorkspace>;
    let active = true;

    const applyState = (nextState: FloatingWidgetState) => {
      if (!active) {
        return;
      }

      setWidgetState(nextState);
      setWidgetError(null);
    };

    if (typeof runtimeApi.getFloatingWidgetState === 'function') {
      runtimeApi
        .getFloatingWidgetState()
        .then((nextState) => {
          applyState(nextState);
        })
        .catch((error) => {
          if (!active) {
            return;
          }

          setWidgetError(normalizeRuntimeErrorMessage(error, '读取悬浮窗状态失败。'));
        });
    }

    const unsubscribe =
      typeof runtimeApi.onFloatingWidgetStateChange === 'function'
        ? runtimeApi.onFloatingWidgetStateChange((nextState) => {
            applyState(nextState);
          })
        : null;

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  const profile = widgetState?.trayState.activeProfile ?? null;
  const isFloatingFreePlan = profile?.planType === 'Free';

  useEffect(() => {
    if (widgetState?.mode === 'orb') {
      setOrbQuotaView(isFloatingFreePlan ? 'weekly' : 'five-hour');
    }
  }, [isFloatingFreePlan, widgetState?.mode]);

  const noteWidgetInteraction = () => {
    setWidgetCycleArmed(true);
    setWidgetInteractionToken((current) => current + 1);
  };

  const availability = profile ? getProfileAvailability(profile) : null;
  const fiveHourRemaining = profile && !isFloatingFreePlan ? getRemainingPercentOrNull(profile.fiveHourQuota) : null;
  const weeklyRemaining = profile ? getRemainingPercentOrNull(profile.weeklyQuota) : null;
  const quotaSources = profile
    ? isFloatingFreePlan
      ? [profile.weeklyQuota, profile.reviewQuota]
      : [profile.fiveHourQuota, profile.weeklyQuota, profile.reviewQuota]
    : [];
  const defaultOrbPercent = isFloatingFreePlan ? weeklyRemaining : fiveHourRemaining;
  const orbTransition = widgetState?.transition ?? null;
  const widgetShowsOrb = widgetState?.mode === 'orb' || orbTransition !== null;
  const widgetViewStep =
    widgetShowsOrb && isFloatingFreePlan
      ? 'weekly'
      : widgetShowsOrb && widgetState?.mode !== 'orb'
      ? 'five-hour'
      : widgetShowsOrb
        ? orbQuotaView
        : 'card';
  const orbPercent = widgetViewStep === 'weekly' ? weeklyRemaining : defaultOrbPercent;
  const orbIsSwitching = widgetShowsOrb && orbTransition?.phase === 'switching';
  const widgetModeToggleLocked = orbTransition !== null;
  const orbAlert = widgetState?.alert ?? {
    tone: 'normal' as const,
    flashing: false,
    breachedLabels: [],
    reason: null,
  };
  const orbIsAlertFlashing = !orbIsSwitching && orbAlert.flashing;
  const floatingWidgetRingColor =
    orbAlert.tone === 'danger'
      ? 'rgba(248, 113, 113, 0.96)'
      : orbAlert.tone === 'warning'
        ? 'rgba(250, 204, 21, 0.98)'
        : 'rgba(34, 197, 94, 0.94)';
  const floatingWidgetGlowColor =
    orbAlert.tone === 'danger'
      ? 'rgba(248, 113, 113, 0.24)'
      : orbAlert.tone === 'warning'
        ? 'rgba(250, 204, 21, 0.2)'
        : 'rgba(34, 197, 94, 0.16)';
  const floatingWidgetAlpha = String(
    Math.min(0.95, Math.max(0.35, (widgetState?.opacityPercent ?? 78) / 100)),
  );
  const displayedOrbPercent = orbIsSwitching ? 0 : orbAnimatedPercent ?? orbPercent;
  const floatingWidgetProgress = displayedOrbPercent !== null ? `${displayedOrbPercent}%` : '0%';
  const orbAlertLabel = orbAlert.breachedLabels.join('、');
  const orbTitle =
    orbIsSwitching
      ? '正在切换空间'
      : orbAlert.reason === 'auto-switch'
        ? `${orbAlertLabel || '当前额度'}已低于 OpenClaw 自动切换阈值`
        : orbAlert.tone === 'danger'
          ? `${orbAlertLabel || '当前额度'}已低于 30%`
          : orbAlert.tone === 'warning'
            ? `${orbAlertLabel || '当前额度'}已低于 50%`
            : widgetViewStep === 'weekly'
              ? '单击显示卡片'
              : '单击显示周额度';
  const profileTitle = profile ? getProfileDisplayName(profile) : '未识别空间';
  const statusText = profile ? getAuthStatusText(profile) : '等待识别当前认证';
  const badgeClassName = profile ? getPlanBadgeClassName(profile.planType) : '';
  const stampTime = profile ? formatDateTime(profile.lastActivatedAt || profile.updatedAt) : '未记录';
  const primaryQuotaStatus = quotaSources.some((quota) => quota.source === 'live')
    ? '实时同步'
    : quotaSources.some((quota) => quota.source === 'detected')
      ? '本地缓存'
      : quotaSources.some((quota) => hasQuotaData(quota))
        ? '手动维护'
        : '未识别';

  useEffect(() => {
    if (widgetViewStep === 'five-hour') {
      setWidgetCycleArmed(false);
    }
  }, [widgetViewStep]);

  useEffect(() => {
    if (orbAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(orbAnimationFrameRef.current);
      orbAnimationFrameRef.current = null;
    }

    if (!widgetShowsOrb) {
      setOrbAnimatedPercent(orbPercent);
      lastCompletedOrbTransitionRef.current = null;
      return;
    }

    if (orbTransition?.phase === 'switching') {
      setOrbAnimatedPercent(0);
      return;
    }

    const completedTransitionKey =
      orbTransition?.phase === 'completed' && orbTransition.completedAt
        ? `${orbTransition.targetProfileId ?? ''}:${orbTransition.completedAt}`
        : null;

    if (completedTransitionKey && completedTransitionKey !== lastCompletedOrbTransitionRef.current) {
      lastCompletedOrbTransitionRef.current = completedTransitionKey;

      if (orbPercent === null || !Number.isFinite(orbPercent)) {
        setOrbAnimatedPercent(null);
        return;
      }

      const animationDurationMs = 2000;
      const targetPercent = Math.max(0, Math.min(100, orbPercent));
      const animate = (startTime: number, now: number) => {
        const elapsed = now - startTime;
        const progress = Math.max(0, Math.min(1, elapsed / animationDurationMs));
        const easedProgress = 1 - (1 - progress) ** 3;
        setOrbAnimatedPercent(targetPercent * easedProgress);

        if (progress < 1) {
          orbAnimationFrameRef.current = window.requestAnimationFrame((nextNow) => {
            animate(startTime, nextNow);
          });
          return;
        }

        orbAnimationFrameRef.current = null;
      };

      setOrbAnimatedPercent(0);
      orbAnimationFrameRef.current = window.requestAnimationFrame((startNow) => {
        animate(startNow, startNow);
      });
      return () => {
        if (orbAnimationFrameRef.current !== null) {
          window.cancelAnimationFrame(orbAnimationFrameRef.current);
          orbAnimationFrameRef.current = null;
        }
      };
    }

    setOrbAnimatedPercent(orbPercent);
    return undefined;
  }, [
    orbPercent,
    orbTransition?.completedAt,
    orbTransition?.phase,
    orbTransition?.targetProfileId,
    widgetShowsOrb,
  ]);

  useEffect(() => {
    if (!widgetCycleArmed || !widgetState?.mode || widgetViewStep === 'five-hour' || isFloatingFreePlan) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setOrbQuotaView('five-hour');
      setWidgetCycleArmed(false);

      if (widgetState.mode !== 'card') {
        return;
      }

      const runtimeApi = window.codexWorkspace as Partial<typeof window.codexWorkspace>;
      if (typeof runtimeApi.toggleFloatingWidgetMode !== 'function') {
        return;
      }

      runtimeApi.toggleFloatingWidgetMode().catch((error) => {
        setWidgetError(normalizeRuntimeErrorMessage(error, '悬浮球自动恢复 5 小时额度失败。'));
      });
    }, 10_000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isFloatingFreePlan, widgetCycleArmed, widgetInteractionToken, widgetState?.mode, widgetViewStep]);

  const requestWidgetMode = (targetMode: 'card' | 'orb') => {
    const runtimeApi = window.codexWorkspace as Partial<typeof window.codexWorkspace>;
    if (
      typeof runtimeApi.toggleFloatingWidgetMode !== 'function' ||
      !widgetState?.mode ||
      widgetState.mode === targetMode
    ) {
      return Promise.resolve();
    }

    return runtimeApi.toggleFloatingWidgetMode().then(() => undefined);
  };

  const handleAdvanceWidgetView = () => {
    if (!widgetState) {
      return;
    }

    if (widgetState.mode === 'orb') {
      if (!isFloatingFreePlan && orbQuotaView === 'five-hour') {
        setOrbQuotaView('weekly');
        return;
      }

      requestWidgetMode('card').catch((error) => {
        setWidgetError(normalizeRuntimeErrorMessage(error, '切换悬浮窗卡片失败。'));
      });
      return;
    }

    setOrbQuotaView(isFloatingFreePlan ? 'weekly' : 'five-hour');
    requestWidgetMode('orb').catch((error) => {
      setWidgetError(normalizeRuntimeErrorMessage(error, '切换悬浮球额度失败。'));
    });
  };

  const handleWidgetPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }

    noteWidgetInteraction();
    widgetGestureRef.current = {
      pointerId: event.pointerId,
      startScreenX: event.screenX,
      startScreenY: event.screenY,
      moved: false,
    };

    if (typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId);
    }

    const runtimeApi = window.codexWorkspace as Partial<typeof window.codexWorkspace>;
    runtimeApi.startFloatingWidgetDrag?.(event.screenX, event.screenY);
  };

  const handleWidgetPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = widgetGestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) {
      return;
    }

    const delta =
      Math.abs(event.screenX - gesture.startScreenX) +
      Math.abs(event.screenY - gesture.startScreenY);
    if (delta >= 3) {
      gesture.moved = true;
    }

    if (!gesture.moved) {
      return;
    }

    const runtimeApi = window.codexWorkspace as Partial<typeof window.codexWorkspace>;
    runtimeApi.updateFloatingWidgetDrag?.(event.screenX, event.screenY);
  };

  const finishWidgetGesture = (
    event: ReactPointerEvent<HTMLDivElement>,
    shouldToggleMode: boolean,
  ) => {
    const gesture = widgetGestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) {
      return;
    }

    if (
      typeof event.currentTarget.hasPointerCapture === 'function' &&
      event.currentTarget.hasPointerCapture(event.pointerId)
    ) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    widgetGestureRef.current = null;

    const runtimeApi = window.codexWorkspace as Partial<typeof window.codexWorkspace>;
    runtimeApi.endFloatingWidgetDrag?.();

    if (shouldToggleMode && !gesture.moved && !widgetModeToggleLocked) {
      handleAdvanceWidgetView();
    }
  };

  const handleWidgetPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    finishWidgetGesture(event, true);
  };

  const handleWidgetPointerCancel = (event: ReactPointerEvent<HTMLDivElement>) => {
    finishWidgetGesture(event, false);
  };

  const handleWidgetKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (widgetModeToggleLocked) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    noteWidgetInteraction();
    handleAdvanceWidgetView();
  };

  return (
    <div
      className="floating-widget-view"
      style={
        {
          ['--floating-widget-alpha' as string]: floatingWidgetAlpha,
          ['--floating-widget-progress' as string]: floatingWidgetProgress,
          ['--floating-widget-ring-color' as string]: floatingWidgetRingColor,
          ['--floating-widget-glow-color' as string]: floatingWidgetGlowColor,
        } as CSSProperties
      }
    >
      <div className="floating-widget-shell">
        {widgetShowsOrb ? (
          <div
            role="button"
            tabIndex={0}
            className={`floating-widget-orb${availability?.isUnavailable ? ' is-unavailable' : ''}${
              orbIsSwitching ? ' is-switching' : ''
            }${orbIsAlertFlashing ? ' is-alert-flashing' : ''}${
              orbAlert.tone === 'warning' ? ' is-warning' : orbAlert.tone === 'danger' ? ' is-danger' : ''
            }`}
            onPointerDown={handleWidgetPointerDown}
            onPointerMove={handleWidgetPointerMove}
            onPointerUp={handleWidgetPointerUp}
            onPointerCancel={handleWidgetPointerCancel}
            onKeyDown={handleWidgetKeyDown}
            title={orbTitle}
          >
            <span className="floating-widget-orb__ring" aria-hidden="true" />
            <span className="floating-widget-orb__core">
              {orbIsSwitching ? (
                <span className="floating-widget-orb__loading" aria-hidden="true">
                  <img
                    src={OPENAI_BRAND_ICON_SRC}
                    alt=""
                    className="floating-widget-orb__loading-icon"
                    draggable={false}
                    onDragStart={(event) => event.preventDefault()}
                    style={{ WebkitUserDrag: 'none' } as CSSProperties}
                  />
                </span>
              ) : (
                <>
                  <span className="floating-widget-orb__value">
                    {displayedOrbPercent !== null ? `${Math.round(displayedOrbPercent)}%` : '--'}
                  </span>
                  <span className="floating-widget-orb__label">
                    {widgetViewStep === 'weekly' ? '周额度' : '5小时'}
                  </span>
                </>
              )}
            </span>
          </div>
        ) : (
          <div
            role="button"
            tabIndex={0}
            className={`floating-widget-card profile-card${profile ? ' is-active' : ' is-empty'}${
              profile && (profile.authStatus !== 'ok' || availability?.isUnavailable) ? ' is-disabled' : ''
            }${availability?.isUnavailable ? ' is-unavailable' : ''}`}
            onPointerDown={handleWidgetPointerDown}
            onPointerMove={handleWidgetPointerMove}
            onPointerUp={handleWidgetPointerUp}
            onPointerCancel={handleWidgetPointerCancel}
            onKeyDown={handleWidgetKeyDown}
            title={isFloatingFreePlan ? '单击显示周额度' : '单击显示 5 小时额度'}
          >
            <span className="floating-widget-card__glow" aria-hidden="true" />

            {profile ? (
              <>
                <div className="profile-card__header floating-widget-card__header">
                  <div>
                    <div className="profile-card__meta-row">
                      <div className="profile-badges">
                        <span className={`badge badge--plan${badgeClassName ? ` ${badgeClassName}` : ''}`}>
                          {mapPlanLabel(profile.planType)}
                        </span>
                        <span className="badge badge--manual">{primaryQuotaStatus}</span>
                        {availability?.badgeLabel ? (
                          <span className="badge badge--disabled">{availability.badgeLabel}</span>
                        ) : null}
                        <span className="badge badge--active">当前空间</span>
                      </div>
                    </div>

                    <div className="profile-card__title-row floating-widget-card__title-row">
                      <h2 className="profile-card__title floating-widget-card__title">{profileTitle}</h2>
                      <span className="profile-card__stamp-time">{`最近登录 ${stampTime}`}</span>
                    </div>
                  </div>

                  <span className="floating-widget-card__menu-hint">右键切换空间</span>
                </div>

                <div className="floating-widget-card__content">
                  {isFloatingFreePlan ? null : (
                    <QuotaRow label="5 小时额度" quota={profile.fiveHourQuota} compact showCountdown />
                  )}
                  <QuotaRow label="周额度" quota={profile.weeklyQuota} compact showCountdown />
                </div>

                <div className="profile-card__footer floating-widget-card__footer">
                  <span className={`footer-auth-status is-${getProfileAuthToneClassName(profile)}`}>
                    {statusText}
                  </span>
                  <span className="footer-expiry">{availability?.footerText ?? '右键切换空间'}</span>
                  <span className="footer-expiry">
                    {isFloatingFreePlan ? '单击显示周额度' : '单击显示 5 小时额度'}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="profile-card__header floating-widget-card__header floating-widget-card__header--empty">
                  <div>
                    <div className="profile-card__meta-row">
                      <div className="profile-badges">
                        <span className="badge badge--disabled">等待识别</span>
                      </div>
                    </div>
                    <div className="profile-card__title-row floating-widget-card__title-row">
                      <h2 className="profile-card__title floating-widget-card__title">{profileTitle}</h2>
                    </div>
                  </div>
                  <span className="floating-widget-card__menu-hint">右键主窗口</span>
                </div>

                <p className="floating-widget-card__empty-text">
                  {widgetError || '当前还没有识别到可用空间。打开主窗口完成登录、导入或切换后，这里会自动更新。'}
                </p>

                <div className="profile-card__footer floating-widget-card__footer">
                  <span className="footer-auth-status is-missing">未识别空间</span>
                  <span className="footer-expiry">{`卡片 ${widgetState?.trayState.profilesCount ?? 0}`}</span>
                  <span className="footer-expiry">
                    {`整体剩余 ${widgetState?.trayState.overallRemainingPercent ?? '--'}%`}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ApiConfigurationPanel() {
  const keyInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedProviderId, setSelectedProviderId] = useState<ApiProviderId>('openai-compatible');
  const selectedProvider =
    API_PROVIDER_OPTIONS.find((provider) => provider.id === selectedProviderId) ?? API_PROVIDER_OPTIONS[0];
  const selectedProviderIndex = Math.max(
    0,
    API_PROVIDER_OPTIONS.findIndex((provider) => provider.id === selectedProviderId),
  );
  const providerDotCount = Math.ceil(API_PROVIDER_OPTIONS.length / 2);
  const activeProviderDot = Math.floor(selectedProviderIndex / 2);
  const [apiName, setApiName] = useState(`${selectedProvider.name} 主账号`);
  const [baseUrl, setBaseUrl] = useState(selectedProvider.baseUrl);
  const [defaultModel, setDefaultModel] = useState(selectedProvider.modelOptions[0] ?? '');
  const [organization, setOrganization] = useState('');
  const [projectName, setProjectName] = useState('');
  const [region, setRegion] = useState('');
  const [providerNote, setProviderNote] = useState('');
  const [enableAfterSave, setEnableAfterSave] = useState(true);
  const [keyDraft, setKeyDraft] = useState('');
  const [saveScope, setSaveScope] = useState<'global' | 'workspace'>('global');
  const [keys, setKeys] = useState<ApiKeyEntry[]>(API_KEY_ENTRIES);
  const [visibleKeyIds, setVisibleKeyIds] = useState<Set<string>>(() => new Set());
  const [statusText, setStatusText] = useState('等待保存');

  const handleProviderChange = (provider: ApiProviderOption) => {
    setSelectedProviderId(provider.id);
    setApiName(`${provider.name} 主账号`);
    setBaseUrl(provider.baseUrl);
    setDefaultModel(provider.modelOptions[0] ?? '');
  };

  const handleSetPrimaryKey = (keyId: string) => {
    setKeys((current) =>
      current.map((key) => ({
        ...key,
        enabled: key.id === keyId ? true : key.enabled,
        primary: key.id === keyId,
      })),
    );
    setStatusText('主用 Key 已更新');
  };

  const handleToggleKey = (keyId: string) => {
    setKeys((current) =>
      current.map((key) =>
        key.id === keyId
          ? {
              ...key,
              enabled: !key.enabled,
              primary: key.primary && key.enabled ? false : key.primary,
            }
          : key,
      ),
    );
    setStatusText('Key 状态已更新');
  };

  const handleDeleteKey = (keyId: string) => {
    setKeys((current) => current.filter((key) => key.id !== keyId));
    setVisibleKeyIds((current) => {
      const next = new Set(current);
      next.delete(keyId);
      return next;
    });
    setStatusText('Key 已删除');
  };

  const handleToggleKeyVisibility = (keyId: string) => {
    setVisibleKeyIds((current) => {
      const next = new Set(current);
      if (next.has(keyId)) {
        next.delete(keyId);
      } else {
        next.add(keyId);
      }
      return next;
    });
  };

  const buildMaskedApiKey = (value: string): string => {
    const normalized = value.trim();
    const suffix = normalized.slice(-4) || '****';
    return normalized.startsWith('sk-') ? `sk-***************${suffix}` : `key-**************${suffix}`;
  };

  const handleAddKey = () => {
    const normalizedKey = keyDraft.trim();
    if (!normalizedKey) {
      setStatusText('请填写 Key');
      keyInputRef.current?.focus();
      return;
    }

    setKeys((current) => [
      ...current,
      {
        id: `key-${Date.now()}`,
        maskedValue: buildMaskedApiKey(normalizedKey),
        secretValue: normalizedKey,
        enabled: enableAfterSave,
        primary: current.length === 0,
        status: 'untested',
        testedAt: '未测试',
      },
    ]);
    setKeyDraft('');
    setStatusText('Key 已新增');
  };

  const handleTestKeys = () => {
    setKeys((current) =>
      current.map((key) =>
        key.enabled
          ? {
              ...key,
              status: 'success',
              testedAt: '刚刚',
            }
          : key,
      ),
    );
    setStatusText('测试状态已更新');
  };

  const handleClearConfig = () => {
    setKeys([]);
    setVisibleKeyIds(new Set());
    setStatusText('配置已清空');
  };

  return (
    <section className="api-config-panel" aria-label="API 配置">
      <div className="api-config-hero">
        <div className="api-config-hero__copy">
          <h2 className="api-config-hero__title">API 配置</h2>
          <p className="api-config-hero__subtitle">
            管理第三方 AI 服务商的 API 连接与密钥，支持多 Key 轮换与健康检查。
          </p>
        </div>
      </div>

      <div className="api-provider-strip" aria-label="API 厂商">
        {API_PROVIDER_OPTIONS.slice(0, 3).map((provider) => (
          <button
            key={provider.id}
            type="button"
            className={`api-provider-card${provider.id === selectedProviderId ? ' is-active' : ''}`}
            onClick={() => handleProviderChange(provider)}
          >
            <ApiProviderMark providerId={provider.id} />
            <span className="api-provider-card__main">
              <strong>{provider.name}</strong>
              <span>{provider.detail}</span>
            </span>
            <span className="api-provider-card__tag">{provider.tag}</span>
            {provider.id === selectedProviderId ? (
              <span className="api-provider-card__star" aria-hidden="true">
                <Star size={20} strokeWidth={1.8} fill="currentColor" />
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="api-provider-dots" aria-hidden="true">
        {Array.from({ length: providerDotCount }).map((_, index) => (
          <span
            key={`api-provider-dot-${index}`}
            className={index === activeProviderDot ? 'is-active' : ''}
          />
        ))}
      </div>

      <div className="api-config-grid">
        <section className="api-config-card api-config-card--form">
          <label className="api-config-field">
            <span className="api-config-field__label">1. API 名称</span>
            <input
              type="text"
              value={apiName}
              onChange={(event) => setApiName(event.target.value)}
            />
          </label>

          <label className="api-config-field">
            <span className="api-config-field__label">2. API 厂商</span>
            <span className="api-config-select-shell">
              <ApiProviderMark providerId={selectedProviderId} />
              <select
                value={selectedProviderId}
                onChange={(event) => {
                  const provider =
                    API_PROVIDER_OPTIONS.find((item) => item.id === event.target.value) ??
                    API_PROVIDER_OPTIONS[0];
                  handleProviderChange(provider);
                }}
              >
                {API_PROVIDER_OPTIONS.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name}
                  </option>
                ))}
              </select>
            </span>
          </label>

          <label className="api-config-field">
            <span className="api-config-field__label">
              3. Base URL
              <Info size={12} strokeWidth={1.8} />
            </span>
            <input
              type="url"
              value={baseUrl}
              onChange={(event) => setBaseUrl(event.target.value)}
              placeholder={selectedProvider.baseUrlPlaceholder}
            />
            <span className="api-config-field__note">
              默认留空使用官方地址，特殊代理或兼容 API 请填写自定义地址。
            </span>
          </label>

          <label className="api-config-field">
            <span className="api-config-field__label">API Key</span>
            <input
              ref={keyInputRef}
              type="password"
              value={keyDraft}
              onChange={(event) => setKeyDraft(event.target.value)}
              placeholder="输入真实 API Key"
            />
          </label>

          <label className="api-config-field">
            <span className="api-config-field__label">默认模型</span>
            <input
              type="text"
              list="api-config-model-options"
              value={defaultModel}
              onChange={(event) => setDefaultModel(event.target.value)}
              placeholder={selectedProvider.modelPlaceholder}
            />
            <datalist id="api-config-model-options">
              {selectedProvider.modelOptions.map((model) => (
                <option key={model} value={model} />
              ))}
            </datalist>
          </label>

          <div className="api-config-form-pair">
            <label className="api-config-field">
              <span className="api-config-field__label">组织 / Project</span>
              <input
                type="text"
                value={organization}
                onChange={(event) => setOrganization(event.target.value)}
                placeholder="组织名、组织 ID 或租户"
              />
            </label>

            <label className="api-config-field">
              <span className="api-config-field__label">项目 / Workspace</span>
              <input
                type="text"
                value={projectName}
                onChange={(event) => setProjectName(event.target.value)}
                placeholder="项目、Workspace、Deployment"
              />
            </label>
          </div>

          <div className="api-config-form-pair">
            <label className="api-config-field">
              <span className="api-config-field__label">区域</span>
              <input
                type="text"
                value={region}
                onChange={(event) => setRegion(event.target.value)}
                placeholder="例如：eastus / cn-hangzhou"
              />
            </label>

            <label className="api-config-field">
              <span className="api-config-field__label">说明</span>
              <input
                type="text"
                value={providerNote}
                onChange={(event) => setProviderNote(event.target.value)}
                placeholder="备注用途、限速、计费线等"
              />
            </label>
          </div>

          <div className="api-config-scope-row">
            <span className="api-config-scope__label">
              5. 保存范围
              <Info size={12} strokeWidth={1.8} />
            </span>
            <label className="api-config-radio">
              <input
                type="radio"
                name="api-config-scope"
                checked={saveScope === 'global'}
                onChange={() => setSaveScope('global')}
              />
              <span>全局</span>
            </label>
            <label className="api-config-radio">
              <input
                type="radio"
                name="api-config-scope"
                checked={saveScope === 'workspace'}
                onChange={() => setSaveScope('workspace')}
              />
              <span>当前空间</span>
            </label>
            <label className="api-config-radio">
              <input
                type="checkbox"
                checked={enableAfterSave}
                onChange={(event) => setEnableAfterSave(event.target.checked)}
              />
              <span>保存后启用</span>
            </label>
          </div>
        </section>

        <section className="api-config-card api-key-card">
          <div className="api-key-card__head">
            <h3>
              4. Key 管理
              <Info size={12} strokeWidth={1.8} />
            </h3>
            <button type="button" className="api-key-card__add" onClick={handleAddKey}>
              <Plus size={13} strokeWidth={2} />
              新增 Key
            </button>
          </div>

          <div className="api-key-table">
            <div className="api-key-table__row api-key-table__head">
              <span>Key（点击查看）</span>
              <span>状态</span>
              <span>主用</span>
              <span>最近测试</span>
              <span>操作</span>
            </div>
            {keys.map((key) => {
              const keyVisible = visibleKeyIds.has(key.id);
              const visibleValue = key.secretValue ?? key.maskedValue;

              return (
                <div key={key.id} className="api-key-table__row">
                  <span className="api-key-table__secret">
                    <button
                      type="button"
                      className="api-key-table__eye"
                      aria-label={keyVisible ? '隐藏 Key' : '显示 Key'}
                      aria-pressed={keyVisible}
                      onClick={() => handleToggleKeyVisibility(key.id)}
                    >
                      {keyVisible ? (
                        <EyeOff size={14} strokeWidth={1.8} />
                      ) : (
                        <Eye size={14} strokeWidth={1.8} />
                      )}
                    </button>
                    <span className="api-key-table__secret-value">
                      {keyVisible ? visibleValue : key.maskedValue}
                    </span>
                  </span>
                  <span className={`api-key-status${key.enabled ? ' is-enabled' : ''}`}>
                    {key.enabled ? '已启用' : '已停用'}
                  </span>
                  <span>{key.primary ? <span className="api-key-primary">主用</span> : '-'}</span>
                  <span className={`api-key-test api-key-test--${key.status}`}>
                    {key.status === 'success' ? (
                      <CheckCircle2 size={14} strokeWidth={1.8} />
                    ) : key.status === 'failed' ? (
                      <CircleAlert size={14} strokeWidth={1.8} />
                    ) : null}
                    {key.status === 'success' ? '成功' : key.status === 'failed' ? '失败' : '未测试'}
                    <em>{key.testedAt}</em>
                  </span>
                  <span className="api-key-actions">
                    <button
                      type="button"
                      disabled={key.primary}
                      onClick={() => handleSetPrimaryKey(key.id)}
                    >
                      设为主用
                    </button>
                    <button type="button" onClick={() => handleToggleKey(key.id)}>
                      {key.enabled ? '停用' : '启用'}
                    </button>
                    <button
                      type="button"
                      className="is-danger"
                      onClick={() => handleDeleteKey(key.id)}
                    >
                      <Trash2 size={13} strokeWidth={1.8} />
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <div className="api-config-actions">
        <button
          type="button"
          className="api-config-primary"
          onClick={() =>
            setStatusText(
              `${saveScope === 'global' ? '全局配置已更新' : '当前空间配置已更新'} · ${enableAfterSave ? '已启用' : '未启用'}`,
            )
          }
        >
          保存配置
        </button>
        <button
          type="button"
          className="api-config-secondary"
          onClick={handleTestKeys}
        >
          测试连接
        </button>
        <button
          type="button"
          className="api-config-danger"
          onClick={handleClearConfig}
        >
          删除配置
        </button>
        <div className="api-config-status">
          <CircleAlert size={15} strokeWidth={1.8} />
          <span>{statusText}</span>
          <CheckCircle2 className="api-config-status__check" size={18} strokeWidth={1.9} />
        </div>
      </div>
    </section>
  );
}

function ApiProviderMark({ providerId }: { providerId: ApiProviderId }) {
  if (providerId === 'openai-compatible') {
    return (
      <span className="api-provider-mark api-provider-mark--openai">
        <OpenAIKnotIcon />
      </span>
    );
  }

  return (
    <span className={`api-provider-mark api-provider-mark--${providerId}`}>
      {providerId === 'anthropic'
        ? 'AI'
        : providerId === 'gemini'
          ? '✦'
          : providerId
              .split('-')
              .map((part) => part[0])
              .join('')
              .slice(0, 2)
              .toUpperCase()}
    </span>
  );
}

function HeaderIconButton({
  children,
  className,
  indicatorDot = false,
  onClick,
  title,
}: {
  children: ReactNode;
  className?: string;
  indicatorDot?: boolean;
  onClick: () => void;
  title: string;
}) {
  return (
    <button
      className={`header-icon-button${className ? ` ${className}` : ''}`}
      title={title}
      type="button"
      onClick={onClick}
    >
      {children}
      {indicatorDot ? <span className="header-indicator-dot header-indicator-dot--button" /> : null}
    </button>
  );
}

function OpenAIKnotIcon({
  className,
  syncedSpin = false,
}: {
  className?: string;
  syncedSpin?: boolean;
}) {
  const iconRef = useRef<HTMLImageElement | null>(null);

  useLayoutEffect(() => {
    if (!syncedSpin) {
      return undefined;
    }

    const target = iconRef.current;
    if (!target) {
      return undefined;
    }

    let frameId: number | null = null;
    const degreesPerMillisecond = 360 / 1550;
    target.style.animation = 'none';
    target.style.willChange = 'transform';

    const applySpin = (now: number) => {
      const angle = (performance.timeOrigin + now) * degreesPerMillisecond;
      target.style.transform = `translate3d(0, 0, 0) rotate(${angle.toFixed(3)}deg)`;
    };

    const spin = (now: number) => {
      applySpin(now);
      frameId = window.requestAnimationFrame(spin);
    };

    applySpin(performance.now());
    frameId = window.requestAnimationFrame(spin);
    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      target.style.animation = '';
      target.style.willChange = '';
      target.style.transform = '';
    };
  }, [syncedSpin]);

  return (
    <img
      ref={iconRef}
      src={OPENAI_BRAND_ICON_SRC}
      className={`brand-icon__svg${className ? ` ${className}` : ''}`}
      alt=""
      aria-hidden="true"
      draggable={false}
    />
  );
}

function QuickOAuthProviderIcon({
  providerId,
}: {
  providerId: OAuthQuickProviderId;
}) {
  const src = OAUTH_PROVIDER_ICON_SRC[providerId];
  if (!src) {
    return null;
  }

  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      draggable={false}
      className="oauth-quick-dock__icon-image"
    />
  );
}

function SidebarMetric({
  label,
  value,
  green = false,
}: {
  label: string;
  value: string;
  green?: boolean;
}) {
  return (
    <div className="sidebar-metric">
      <span className="sidebar-metric__label">{label}</span>
      <span className={green ? 'sidebar-metric__value is-green' : 'sidebar-metric__value'}>
        {value}
      </span>
    </div>
  );
}

function ProfileImportPlaceholderCard({
  placeholder,
  currentDeviceId,
}: {
  placeholder: ImportPlaceholderCardState;
  currentDeviceId: string;
}) {
  const placeholderProfile = buildImportPlaceholderProfile(placeholder);
  const placeholderEvent = buildImportPlaceholderProgressEvent(placeholder);
  const placeholderPhase: CardSwitchInteractionPhase =
    placeholder.status === 'loading'
      ? placeholder.loadingPhase
      : placeholder.status === 'success'
        ? 'success'
        : 'error';
  const placeholderHintText =
    placeholder.status === 'error' ? placeholder.errorTipText : placeholder.loadingTipText;

  return (
    <ProfileShellCard
      profile={placeholderProfile}
      currentDeviceId={currentDeviceId}
      currentDeviceCloudBackupStatus={buildSyncPlaceholderCloudStatus()}
      isRecommended={false}
      ignoreProbeUnavailable={true}
      weeklyJustReset={false}
      fiveHourJustReset={false}
      startupIntroPhase="revealed"
      startupIntroIndex={0}
      cardSwitchPhase={placeholderPhase}
      cardSwitchProgressEvent={placeholderEvent}
      cardSwitchWaveToken={0}
      cardSwitchSweepToken={0}
      menuOpen={false}
      onOpenMenu={() => {}}
      onPreviewSwitch={() => {}}
      onClearPreview={() => {}}
      onDirectSwitch={() => {}}
      onEnable={() => {}}
      onManualEnable={() => {}}
      onManualDisable={() => {}}
      onActiveInfo={() => {}}
      onSend={() => {}}
      onEdit={() => {}}
      onDelete={() => {}}
      onOpenDir={() => {}}
      onPlayFlipSound={() => {}}
      cardSwitchHintText={placeholderHintText}
      cardSwitchForceSpinner={placeholder.status === 'loading'}
      cardSwitchForceHintVisible={placeholder.status === 'loading'}
    />
  );
}

function ProfileShellCard({
  profile,
  currentDeviceCloudBackupStatus,
  isRecommended,
  selectionMode = null,
  selected = false,
  actionOverlay = null,
  removing = false,
  ignoreProbeUnavailable,
  weeklyJustReset,
  fiveHourJustReset,
  startupIntroPhase,
  startupIntroIndex,
  cardSwitchPhase,
  cardSwitchProgressEvent,
  cardSwitchWaveToken,
  cardSwitchSweepToken,
  menuOpen,
  onOpenMenu,
  onPreviewSwitch,
  onClearPreview,
  onDirectSwitch,
  onEnable,
  onManualEnable,
  onManualDisable,
  onActiveInfo,
  onSend,
  onEdit,
  onDelete,
  onOpenDir,
  onToggleSelected,
  onPlayFlipSound,
  currentDeviceId,
  quotaRevealToken = 0,
  cardSwitchHintText = null,
  cardSwitchForceSpinner = false,
  cardSwitchForceHintVisible = false,
  cloudDownloadRevealToken = 0,
}: {
  profile: DisplayProfile;
  currentDeviceCloudBackupStatus: CurrentDeviceCloudBackupStatus;
  isRecommended: boolean;
  selectionMode?: WorkspaceSelectionMode | null;
  selected?: boolean;
  actionOverlay?: ProfileActionOverlayState | null;
  removing?: boolean;
  ignoreProbeUnavailable: boolean;
  weeklyJustReset: boolean;
  fiveHourJustReset: boolean;
  startupIntroPhase: StartupCardIntroPhase;
  startupIntroIndex: number;
  cardSwitchPhase: CardSwitchInteractionPhase | null;
  cardSwitchProgressEvent: SwitchProgressEvent | null;
  cardSwitchWaveToken: number;
  cardSwitchSweepToken: number;
  menuOpen: boolean;
  onOpenMenu: (profileId: string) => void;
  onPreviewSwitch: (profile: DisplayProfile) => void;
  onClearPreview: (profile: DisplayProfile) => void;
  onDirectSwitch: (profile: DisplayProfile) => void;
  onEnable: (profile: DisplayProfile) => void;
  onManualEnable: (profile: DisplayProfile) => void;
  onManualDisable: (profile: DisplayProfile) => void;
  onActiveInfo: (profile: DisplayProfile) => void;
  onSend: (profile: DisplayProfile) => void;
  onEdit: (profile: DisplayProfile) => void;
  onDelete: (profile: DisplayProfile) => void;
  onOpenDir: (profile: DisplayProfile) => void;
  onToggleSelected?: (profile: DisplayProfile) => void;
  onPlayFlipSound: (pattern?: CardFlipAudioPattern) => void;
  currentDeviceId: string;
  quotaRevealToken?: number;
  cardSwitchHintText?: string | null;
  cardSwitchForceSpinner?: boolean;
  cardSwitchForceHintVisible?: boolean;
  cloudDownloadRevealToken?: number;
}) {
  const [reviewExpanded, setReviewExpanded] = useState(false);
  const [unavailableNameVisible, setUnavailableNameVisible] = useState(false);
  const [unavailableDetailVisible, setUnavailableDetailVisible] = useState(false);
  const [availableSwitchHintVisible, setAvailableSwitchHintVisible] = useState(false);
  const unavailableDetailHideTimerRef = useRef<number | null>(null);
  const quotaFoldRef = useRef<HTMLDivElement | null>(null);
  const roleBadge = getProfileRoleBadge(profile);
  const displayPlanType = getDisplayPlanType(profile);
  const isFreePlan = displayPlanType === 'Free';
  const availability = getProfileAvailability(profile, {
    ignoreProbeUnavailable,
  });
  const quotaSources = isFreePlan
    ? [profile.weeklyQuota, profile.reviewQuota]
    : [profile.weeklyQuota, profile.fiveHourQuota, profile.reviewQuota];
  const primaryQuotaStatus = quotaSources.some((quota) => quota.source === 'live')
    ? '实时同步'
    : quotaSources.some((quota) => quota.source === 'detected')
      ? '本地缓存'
      : quotaSources.some((quota) => hasQuotaData(quota))
        ? '手动维护'
        : '未识别';
  const displayNote = getProfileVisibleNote(profile);
  const securityIndicators = getProfileSecurityIndicators(profile, currentDeviceId);
  const securityDetail = getProfileSecurityDetail(profile, currentDeviceId);
  const cloudBackupIndicatorTitle =
    currentDeviceCloudBackupStatus.state === 'synced'
      ? `云端已备份：${currentDeviceCloudBackupStatus.detail}`
      : currentDeviceCloudBackupStatus.state === 'stale'
        ? `云端待更新：${currentDeviceCloudBackupStatus.detail}`
        : `未云备份：${currentDeviceCloudBackupStatus.detail}`;
  const cloudBackupIndicatorStateClass =
    currentDeviceCloudBackupStatus.state === 'synced'
      ? 'is-synced'
      : currentDeviceCloudBackupStatus.state === 'stale'
        ? 'is-stale'
        : 'is-missing';
  const cloudBackupIndicatorAriaLabel =
    currentDeviceCloudBackupStatus.state === 'synced'
      ? '当前空间已完成云端备份'
      : currentDeviceCloudBackupStatus.state === 'stale'
        ? '当前空间云端备份待更新'
        : '当前空间尚未云端备份';
  const displayTitle =
    getProfileDisplayName(profile);
  const stampTime = formatDateTime(profile.lastActivatedAt || profile.updatedAt);
  const usageStateText = profile.usageSyncPending
    ? '后台同步中'
    : profile.usageError
      ? '同步失败'
      : profile.usageUpdatedAt
        ? `同步 ${formatDateTime(profile.usageUpdatedAt)}`
        : '尚未同步';
  const introTiming = getStartupCardIntroTiming(profile.id, startupIntroIndex);
  const switchWaveTiming = getCardSwitchWaveTiming(profile.id, startupIntroIndex);
  const startupIntroActive = startupIntroPhase !== 'idle';
  const simulatedProfile = isSimulatedProfile(profile);
  const keepBackFace = availability.lockBackFace;
  const showCardSwitchBackFace = Boolean(cardSwitchPhase || actionOverlay);
  const selectionEnabled = typeof onToggleSelected === 'function' && !simulatedProfile;
  const selectedInSelectionMode = selectionMode !== null && selectionEnabled && Boolean(selected);
  const revealFrontFace =
    startupIntroPhase === 'revealed' &&
    (!keepBackFace || unavailableDetailVisible || selectedInSelectionMode) &&
    !showCardSwitchBackFace;
  const startupIntroStyle = {
    ['--profile-intro-delay' as string]: `${startupIntroPhase === 'revealed' ? 0 : introTiming.delayMs}ms`,
    ['--profile-intro-duration' as string]: `${startupIntroPhase === 'revealed' ? 420 : introTiming.durationMs}ms`,
    ['--profile-switch-wave-delay' as string]: `${switchWaveTiming.delayMs}ms`,
    ['--profile-switch-wave-duration' as string]: `${switchWaveTiming.durationMs}ms`,
  } as CSSProperties;
  const showStartupBackFace = startupIntroPhase !== 'revealed' && !keepBackFace && !showCardSwitchBackFace;
  const cardSwitchCopy = actionOverlay
    ? {
        title: actionOverlay.title,
        detail: actionOverlay.detail,
        showSuccessMark: actionOverlay.success === true,
      }
    : cardSwitchPhase
      ? getCardSwitchCopy(cardSwitchPhase, displayTitle, cardSwitchProgressEvent)
      : null;
  const cardSwitchBusy =
    actionOverlay?.busy === true ||
    cardSwitchForceSpinner ||
    (cardSwitchPhase === 'running' && (cardSwitchProgressEvent?.status ?? 'active') === 'active');
  const selectionActive = selectionMode !== null;
  const cardSwitchHoverTipVisible =
    !actionOverlay &&
    (availableSwitchHintVisible ||
      cardSwitchForceHintVisible ||
      Boolean(cardSwitchHintText && cardSwitchPhase && cardSwitchPhase !== 'preview'));
  const cardSwitchHoverTipText = simulatedProfile
    ? '仅本地预览，不执行切换'
    : cardSwitchHintText?.trim() || '双击进行切换';
  const cardActionVisualState =
    actionOverlay?.success === true ? 'success' : actionOverlay ? 'running' : cardSwitchPhase;
  const showSwitchAction = !keepBackFace && !profile.isActive && profile.authStatus === 'ok';
  const showManualEnableAction =
    profile.authStatus === 'ok' && (profile.manuallyDisabled || profile.autoDisabled);
  const showManualDisableAction =
    profile.authStatus === 'ok' && !profile.manuallyDisabled && !profile.autoDisabled;
  const cardMarkedDisabled = profile.authStatus !== 'ok' || availability.isUnavailable;

  const handleSelectionToggle = () => {
    if (!selectionEnabled) {
      return;
    }

    if (keepBackFace) {
      onPlayFlipSound('single');
    }

    onToggleSelected(profile);
  };

  const clearUnavailableDetailHideTimer = () => {
    if (unavailableDetailHideTimerRef.current !== null) {
      window.clearTimeout(unavailableDetailHideTimerRef.current);
      unavailableDetailHideTimerRef.current = null;
    }
  };

  const openUnavailableDetail = () => {
    clearUnavailableDetailHideTimer();
    setUnavailableNameVisible(false);
    if (!unavailableDetailVisible) {
      onPlayFlipSound('single');
    }
    setUnavailableDetailVisible(true);
  };

  const scheduleUnavailableDetailHide = () => {
    clearUnavailableDetailHideTimer();
    unavailableDetailHideTimerRef.current = window.setTimeout(() => {
      unavailableDetailHideTimerRef.current = null;
      setUnavailableDetailVisible(false);
      setUnavailableNameVisible(false);
      onPlayFlipSound('single');
    }, 2400);
  };

  useEffect(() => {
    if (!keepBackFace) {
      clearUnavailableDetailHideTimer();
      setUnavailableDetailVisible(false);
      setUnavailableNameVisible(false);
    }
  }, [keepBackFace]);

  useEffect(() => {
    if (cardSwitchPhase !== 'preview') {
      setAvailableSwitchHintVisible(false);
    }
  }, [cardSwitchPhase]);

  useEffect(() => {
    if (!reviewExpanded) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && quotaFoldRef.current?.contains(target)) {
        return;
      }

      setReviewExpanded(false);
    };

    window.addEventListener('pointerdown', handlePointerDown);
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, [reviewExpanded]);

  useEffect(() => () => clearUnavailableDetailHideTimer(), []);

  const renderCardMenu = () =>
    menuOpen ? (
      <div className="card-menu" onClick={(event) => event.stopPropagation()}>
        {showSwitchAction ? (
          <button type="button" onClick={() => onEnable(profile)}>
            <ArrowUpCircle size={14} />
            切换到此卡
          </button>
        ) : null}
        {showManualEnableAction ? (
          <button type="button" onClick={() => onManualEnable(profile)}>
            <ArrowUpCircle size={14} />
            手动启用
          </button>
        ) : null}
        {showManualDisableAction ? (
          <button type="button" onClick={() => onManualDisable(profile)}>
            <ArrowDownCircle size={14} />
            手动停用
          </button>
        ) : null}
        <button type="button" onClick={() => onSend(profile)}>
          <Mail size={14} />
          发送卡片
        </button>
        <button type="button" onClick={() => onEdit(profile)}>
          <Pencil size={14} />
          编辑
        </button>
        <button type="button" onClick={() => onOpenDir(profile)}>
          <FolderOpen size={14} />
          打开目录
        </button>
        <button type="button" onClick={() => onDelete(profile)} className="is-danger">
          <Trash2 size={14} />
          删除
        </button>
      </div>
    ) : null;

  const renderCardMenuButton = () => (
    <div className="card-menu-wrap" onClick={(event) => event.stopPropagation()}>
      {selectionActive && selectionEnabled ? (
        selected ? (
          <button
            className="menu-button profile-card__selection-toggle is-selected"
            type="button"
            title="取消选择"
            aria-label={`取消选择：${displayTitle}`}
            aria-pressed={true}
            onClick={(event) => {
              event.stopPropagation();
              handleSelectionToggle();
            }}
          >
            <CheckCircle2 className="profile-card__selection-check" size={18} strokeWidth={2.2} />
          </button>
        ) : (
          <button
            className="menu-button"
            type="button"
            title="选择这张卡片"
            aria-label={`选择：${displayTitle}`}
            aria-pressed={false}
            onClick={(event) => {
              event.stopPropagation();
              handleSelectionToggle();
            }}
          >
            <EllipsisVertical size={18} strokeWidth={2} />
          </button>
        )
      ) : (
        <button
          className="menu-button"
          type="button"
          title="更多操作"
          aria-label={`更多操作：${displayTitle}`}
          onClick={(event) => {
            event.stopPropagation();
            onOpenMenu(profile.id);
          }}
        >
          <EllipsisVertical size={18} strokeWidth={2} />
        </button>
      )}

      {!selectionActive ? renderCardMenu() : null}
    </div>
  );

  return (
    <article
      className={`profile-card${profile.isActive ? ' is-active' : ''}${
        cardMarkedDisabled ? ' is-disabled' : ''
      }${availability.isUnavailable ? ' is-unavailable' : ''}${
        isRecommended ? ' is-recommended' : ''
      }${menuOpen ? ' is-menu-open' : ''}${
        reviewExpanded ? ' is-review-expanded' : ''
      }${unavailableDetailVisible ? ' is-unavailable-detail-visible' : ''}${
        unavailableNameVisible ? ' is-unavailable-name-visible' : ''
      }${cardSwitchPhase ? ` is-card-switch-${cardSwitchPhase}` : ''}${
        selectionActive ? ' is-workspace-selectable' : ''
      }${
        selected ? ' is-workspace-selected' : ''
      }${
        removing ? ' is-workspace-removing' : ''
      }${
        cardSwitchWaveToken > 0 && !keepBackFace && !cardSwitchPhase
          ? ` is-switch-wave-${cardSwitchWaveToken % 2 === 0 ? 'even' : 'odd'}`
          : ''
      }${
        cardSwitchSweepToken > 0 ? ` is-switch-sweep-${cardSwitchSweepToken % 2 === 0 ? 'even' : 'odd'}` : ''
      }${cloudDownloadRevealToken > 0 ? ' is-cloud-download-reveal' : ''}`}
      style={startupIntroStyle}
      onClick={() => {
        if (selectionActive) {
          handleSelectionToggle();
          return;
        }

        if (availability.isUnavailable) {
          openUnavailableDetail();
          return;
        }

        if (showCardSwitchBackFace) {
          return;
        }

        if (profile.authStatus === 'ok') {
          if (profile.isActive) {
            onActiveInfo(profile);
            return;
          }

          onPreviewSwitch(profile);
        }
      }}
      role="button"
      tabIndex={0}
      onMouseEnter={() => {
        if (keepBackFace) {
          clearUnavailableDetailHideTimer();
        }
      }}
      onMouseLeave={() => {
        setAvailableSwitchHintVisible(false);

        if (selectionActive) {
          return;
        }

        if (!keepBackFace) {
          if (cardSwitchPhase === 'preview') {
            onClearPreview(profile);
          }
          return;
        }

        setUnavailableNameVisible(false);
        if (unavailableDetailVisible) {
          scheduleUnavailableDetailHide();
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (selectionActive) {
            handleSelectionToggle();
            return;
          }
          if (availability.isUnavailable) {
            openUnavailableDetail();
            return;
          }

          if (showCardSwitchBackFace) {
            return;
          }

          if (profile.authStatus === 'ok') {
            if (profile.isActive) {
              onActiveInfo(profile);
              return;
            }

            onPreviewSwitch(profile);
          }
        }
      }}
    >
      <div className="profile-card__flip-scene">
        <div
          className={`profile-card__flip-inner${startupIntroActive ? ' has-startup-intro' : ''}${
            revealFrontFace ? ' is-flipped' : ''
          }${keepBackFace ? ' is-locked-back' : ''}`}
        >
          <div
            className={`profile-card__face profile-card__face--back${keepBackFace ? ' is-unavailable' : ''}`}
            aria-hidden={revealFrontFace}
          >
            <div
              className={`profile-card__back-shell${keepBackFace ? ' is-unavailable' : ''}${
                showCardSwitchBackFace ? ' is-switch-preview' : ''
              }${showStartupBackFace ? ' is-startup-intro' : ''}`}
            >
              {keepBackFace ? (
                <div className="profile-card__back-unavailable">
                  <div
                    className="profile-card__back-brand-trigger"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (selectionActive) {
                        handleSelectionToggle();
                        return;
                      }
                      openUnavailableDetail();
                    }}
                    onMouseEnter={() => {
                      clearUnavailableDetailHideTimer();
                      if (!unavailableDetailVisible) {
                        setUnavailableNameVisible(true);
                      }
                    }}
                    onMouseLeave={() => {
                      if (!unavailableDetailVisible) {
                        setUnavailableNameVisible(false);
                      }
                    }}
                  >
                    <div className="profile-card__back-brand" aria-hidden="true">
                      <OpenAIKnotIcon />
                    </div>
                    <span className="profile-card__back-status is-unavailable">已停用</span>
                    <span
                      className={`profile-card__back-hover-title${
                        unavailableNameVisible ? ' is-visible' : ''
                      }`}
                    >
                      {displayTitle}
                    </span>
                  </div>
                </div>
              ) : showCardSwitchBackFace && cardSwitchCopy ? (
                <div className="profile-card__back-unavailable is-switch-preview">
                  <div
                    className="profile-card__back-brand-trigger is-switch-preview"
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                    onDoubleClick={(event) => {
                      event.stopPropagation();
                      if (cardSwitchPhase === 'preview') {
                        onDirectSwitch(profile);
                      }
                    }}
                    onMouseEnter={() => {
                      if (cardSwitchPhase === 'preview') {
                        setAvailableSwitchHintVisible(true);
                      }
                    }}
                    onMouseLeave={() => {
                      setAvailableSwitchHintVisible(false);
                    }}
                  >
                    <span
                      className={`profile-card__back-hover-tip${
                        cardSwitchHoverTipVisible ? ' is-visible' : ''
                      }`}
                    >
                      {cardSwitchHoverTipText}
                    </span>
                    <div className="profile-card__back-brand" aria-hidden="true">
                      <OpenAIKnotIcon
                        className={
                          cardSwitchBusy
                            ? 'is-switch-spinning'
                            : cardActionVisualState === 'success'
                              ? 'is-switch-bright'
                              : ''
                        }
                      />
                    </div>
                    {cardSwitchPhase === 'preview' ? (
                      <span className="profile-card__back-switch-name">{displayTitle}</span>
                    ) : (
                      <div
                        className={`profile-card__back-switch-copy${
                          cardActionVisualState ? ` is-${cardActionVisualState}` : ''
                        }`}
                      >
                        <span className="profile-card__back-switch-title">{cardSwitchCopy.title}</span>
                        <span className="profile-card__back-switch-detail">
                          {cardSwitchCopy.detail}
                          {cardSwitchCopy.showSuccessMark ? (
                            <OpenAIKnotIcon className="profile-card__back-switch-mark" />
                          ) : null}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="profile-card__back-brand" aria-hidden="true">
                    <OpenAIKnotIcon />
                  </div>
                  <div className={`profile-card__back-copy${showStartupBackFace ? ' is-startup-intro' : ''}`}>
                    <strong
                      className={`profile-card__back-title${showStartupBackFace ? ' is-startup-intro' : ''}`}
                    >
                      {displayTitle}
                    </strong>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="profile-card__face profile-card__face--front" aria-hidden={!revealFrontFace}>
            <div className="profile-card__header">
              <div>
                <div className="profile-card__meta-row">
                  <div className="profile-badges">
                    {availability.badgeLabel ? (
                      <span className="badge badge--disabled">{availability.badgeLabel}</span>
                    ) : null}
                    {isRecommended ? <span className="badge badge--recommended">推荐切换</span> : null}
                    <span
                      className={`badge badge--plan${
                        getPlanBadgeClassName(displayPlanType)
                          ? ` ${getPlanBadgeClassName(displayPlanType)}`
                          : ''
                      }`}
                    >
                      {mapPlanLabel(displayPlanType)}
                    </span>
                      <span className="badge badge--manual">{primaryQuotaStatus}</span>
                      <span className={`badge ${roleBadge.className}`}>{roleBadge.label}</span>
                      <span
                        className={`profile-card__cloud-indicator ${cloudBackupIndicatorStateClass}`}
                        title={cloudBackupIndicatorTitle}
                        aria-label={cloudBackupIndicatorAriaLabel}
                      >
                        <Cloud size={12} strokeWidth={2.1} />
                      </span>
                    </div>
                  </div>
                  <div className="profile-card__title-row">
                    <h3 className="profile-card__title">{displayTitle}</h3>
                    <span className="profile-card__stamp-time">{`最近登录 ${stampTime}`}</span>
                </div>
                {displayNote ? <p className="profile-card__note">{displayNote}</p> : null}
                {securityIndicators.length > 0 ? (
                  <div className="profile-card__security-row">
                    {securityIndicators.map((indicator) => (
                      <span
                        key={indicator.key}
                        className={`profile-card__security-pill profile-card__security-pill--${indicator.tone}`}
                      >
                        {renderProfileSecurityIndicatorIcon(indicator.tone)}
                        <span>{indicator.label}</span>
                      </span>
                    ))}
                  </div>
                ) : null}
                {securityDetail ? <p className="profile-card__security-copy">{securityDetail}</p> : null}
              </div>

              {renderCardMenuButton()}
            </div>

            <div className="profile-card__content">
              {isFreePlan ? null : (
                <QuotaRow
                  label="5 小时额度"
                  quota={profile.fiveHourQuota}
                  showCountdown
                  animateReset={fiveHourJustReset}
                  revealToken={quotaRevealToken}
                />
              )}
              <QuotaRow
                label="周额度"
                quota={profile.weeklyQuota}
                showCountdown
                animateReset={weeklyJustReset}
                revealToken={quotaRevealToken}
              />
              <div
                ref={quotaFoldRef}
                className={`quota-fold${reviewExpanded ? ' is-open' : ''}`}
              >
                <button
                  className="quota-fold__toggle"
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setReviewExpanded((current) => !current);
                  }}
                >
                  <span className="quota-fold__line" />
                  <ChevronDown
                    className={`quota-fold__chevron${reviewExpanded ? ' is-open' : ''}`}
                    size={15}
                    strokeWidth={2}
                  />
                </button>

                {reviewExpanded ? (
                  <div className="quota-fold__popover" onClick={(event) => event.stopPropagation()}>
                    <div className="quota-fold__popover-title">代码审查额度</div>
                    <QuotaRow
                      label="代码审查额度"
                      quota={profile.reviewQuota}
                      compact
                      assumeFullWhenUnknown
                      revealToken={quotaRevealToken}
                    />
                  </div>
                ) : null}
              </div>
            </div>

            <div className="profile-card__footer">
              <span
                className={`footer-auth-status is-${getProfileAuthToneClassName(profile, {
                  ignoreProbeUnavailable,
                })}`}
              >
                {getAuthStatusText(profile, { ignoreProbeUnavailable })}
              </span>
              <span className="footer-expiry">{usageStateText}</span>
              <span className="footer-expiry">
                {availability.footerText || (profile.isActive ? '当前正在使用' : '可切换到此空间')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

function QuotaRow({
  label,
  quota,
  compact = false,
  assumeFullWhenUnknown = false,
  showCountdown = false,
  animateReset = false,
  revealToken = 0,
}: {
  label: string;
  quota: UsageQuota;
  compact?: boolean;
  assumeFullWhenUnknown?: boolean;
  showCountdown?: boolean;
  animateReset?: boolean;
  revealToken?: number;
}) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const treatAsFull = !hasQuotaData(quota) && assumeFullWhenUnknown;
  const remainingPercent = getRemainingPercent(quota);
  const displayRemainingPercent = treatAsFull ? 100 : remainingPercent;
  const quotaRevealFrameRef = useRef<number | null>(null);
  const [animatedRemainingPercent, setAnimatedRemainingPercent] = useState(() =>
    revealToken > 0 ? 0 : displayRemainingPercent,
  );

  useEffect(() => {
    if (!showCountdown || !quota.resetAt) {
      return;
    }

    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [quota.resetAt, showCountdown]);
  const countdownCycleMs = showCountdown ? getQuotaCountdownCycleMs(label) : null;
  const countdownText = showCountdown ? formatCountdown(quota.resetAt, nowMs, countdownCycleMs) : null;
  const countdownTone = showCountdown
    ? getCountdownTone(quota.resetAt, nowMs, countdownCycleMs)
    : 'idle';

  useEffect(() => {
    if (quotaRevealFrameRef.current !== null) {
      window.cancelAnimationFrame(quotaRevealFrameRef.current);
      quotaRevealFrameRef.current = null;
    }

    if (revealToken <= 0 || displayRemainingPercent <= 0) {
      setAnimatedRemainingPercent(displayRemainingPercent);
      return;
    }

    let startAt: number | null = null;
    const durationMs = 1500;
    setAnimatedRemainingPercent(0);

    const step = (timestamp: number) => {
      if (startAt === null) {
        startAt = timestamp;
      }

      const progress = Math.min(1, (timestamp - startAt) / durationMs);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const nextValue = Math.round(displayRemainingPercent * easedProgress);
      setAnimatedRemainingPercent(progress >= 1 ? displayRemainingPercent : nextValue);

      if (progress < 1) {
        quotaRevealFrameRef.current = window.requestAnimationFrame(step);
      } else {
        quotaRevealFrameRef.current = null;
      }
    };

    quotaRevealFrameRef.current = window.requestAnimationFrame(step);

    return () => {
      if (quotaRevealFrameRef.current !== null) {
        window.cancelAnimationFrame(quotaRevealFrameRef.current);
        quotaRevealFrameRef.current = null;
      }
    };
  }, [displayRemainingPercent, revealToken]);

  if (!hasQuotaData(quota) && !treatAsFull) {
    return (
      <div className={`quota-row${compact ? ' is-compact' : ''}`}>
        <div className="quota-row__meta">
          <span className="quota-row__meta-main">{label}</span>
          <div className="quota-row__meta-side">
            <strong className="quota-value is-muted">未识别</strong>
          </div>
        </div>
        <div className="quota-row__track">
          <div className={getQuotaBarClassName(quota)} style={{ width: '18%' }} />
        </div>
        <div className="quota-row__submeta">
          <span>{getQuotaSourceText(quota)}</span>
          <span>可在编辑中维护</span>
        </div>
      </div>
    );
  }

  const displayPercentUsed = treatAsFull ? 0 : quota.percentUsed;
  const displaySourceText = treatAsFull ? '默认满额' : getQuotaSourceText(quota);
  const displayResetAt = treatAsFull ? '未限制' : formatDateTime(quota.resetAt);
  const displayBarClassName = treatAsFull ? 'quota-fill is-ok' : getQuotaBarClassName(quota);

  return (
      <div className={`quota-row${compact ? ' is-compact' : ''}`}>
        <div className="quota-row__meta">
          <span className="quota-row__meta-main">{label}</span>
          {countdownText ? (
            <span className={`quota-row__countdown is-${countdownTone}`}>{countdownText}</span>
          ) : (
            <span className="quota-row__meta-spacer" />
          )}
          <div className="quota-row__meta-side">
            <strong className="quota-value">剩余 {animatedRemainingPercent}%</strong>
          </div>
        </div>
      <div className="quota-row__track">
        <div
          className={`${displayBarClassName}${animateReset || revealToken > 0 ? ' is-resetting' : ''}`}
          style={{ width: `${displayRemainingPercent}%` }}
        />
      </div>
      <div className="quota-row__submeta">
        <span>
          {displaySourceText}
          {!treatAsFull && quota.label ? ` / ${quota.label}` : ''}
          {' / '}已用 {displayPercentUsed}%
        </span>
        <span>{displayResetAt}</span>
      </div>
    </div>
  );
}

function SidebarPanel({
  children,
  defaultOpen = false,
  title,
}: {
  children: ReactNode;
  defaultOpen?: boolean;
  title: string;
}) {
  return (
    <details className="sidebar-panel" open={defaultOpen}>
      <summary className="sidebar-panel__summary">
        <span>{title}</span>
        <ChevronDown className="sidebar-panel__chevron" size={16} strokeWidth={2} />
      </summary>
      <div className="sidebar-panel__body">{children}</div>
    </details>
  );
}

function BackupRow({ item }: { item: BackupEntry }) {
  return (
    <div className="backup-row">
      <div className="backup-row__main">
        <span>{item.name}</span>
        <small>{formatDateTime(item.createdAt)}</small>
      </div>
      <span>{formatFileSize(item.sizeBytes)}</span>
    </div>
  );
}

function ToastBanner({ toast }: { toast: ToastState }) {
  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className={`shell-toast is-${toast.tone}`}>
      {toast.message}
    </div>,
    document.body,
  );
}

function CenterNoticeBanner({ notice }: { notice: CenterNotice }) {
  if (typeof document === 'undefined') {
    return null;
  }

  const className = `center-notice-banner${notice.variant === 'active-card' ? ' is-active-card' : ''}`;

  return createPortal(
    <div className={className}>
      {notice.title ? <div className="center-notice-banner__title">{notice.title}</div> : null}
      {notice.detail ? <div className="center-notice-banner__detail">{notice.detail}</div> : null}
      {!notice.title ? notice.message : null}
    </div>,
    document.body,
  );
}

